/* eslint-disable react-hooks/exhaustive-deps, no-script-url */
import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import versionInfo from "./version.json";

const APP_VERSION = versionInfo.version;

// Inject gap-coach animations once
if (typeof document !== "undefined" && !document.getElementById("gap-coach-css")) {
  const _s = document.createElement("style");
  _s.id = "gap-coach-css";
  _s.textContent = `
    @keyframes gapDots { 0%,20%{opacity:0.2} 50%{opacity:1} 80%,100%{opacity:0.2} }
    details > summary::-webkit-details-marker { display:none; }
    details > summary { list-style:none; }
    .gap-dots { animation: gapDots 1.4s infinite; font-weight:700; letter-spacing:2px; }
    @keyframes gapFadeIn { from{opacity:0; transform:translateY(6px)} to{opacity:1; transform:none} }
    @keyframes gapPulse { 0%,100%{box-shadow:0 0 0 0 rgba(239,68,68,0.5)} 50%{box-shadow:0 0 0 6px rgba(239,68,68,0)} }
  `;
  document.head.appendChild(_s);
}  // increment with every meaningful release

const API_URL = process.env.REACT_APP_API_URL || "";

// ── Auth helpers ──────────────────────────────────────────────────────────
const getToken = () => localStorage.getItem("jwt");
const setToken = (t) => localStorage.setItem("jwt", t);
const clearToken = () => localStorage.removeItem("jwt");

// ── API helper (module-level so all components can use it) ────────────────
let _authPurged = false;   // one reload max - never loop
const api = async (path, opts = {}) => {
  const token = getToken();
  const r = await fetch(`${API_URL}${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { "Authorization": `Bearer ${token}` } : {}),
      ...(opts.headers || {}),
    },
  });
  const data = await r.json().catch(() => ({ error: `HTTP ${r.status}` }));
  // A dead token used to leave the app in a zombie state: every call returned
  // an auth error body, the UI showed "server busy" forever, and only a manual
  // sign-out recovered it. If a request WE ATTACHED A TOKEN TO comes back as
  // an auth failure, purge the session once and drop to the login screen.
  if (token && !_authPurged &&
      (r.status === 401 ||
       /invalid token|token expired|session expired|not authenticated|unauthoriz/i
         .test(String((data && data.error) || "")))) {
    _authPurged = true;
    try { clearToken(); localStorage.removeItem("username"); sessionStorage.removeItem("username"); } catch(e) {}
    try { sessionStorage.setItem("auth_expired_msg", "1"); } catch(e) {}
    window.location.href = window.location.pathname;   // clean reload -> login
    return data;
  }
  return data;
};

// Download a .docx from an authed GET endpoint (streams a Word file). A plain
// <a href> can't send the JWT header, so fetch with auth then save the blob.
const downloadDocx = async (path, fallbackName = "Resume.docx") => {
  try {
    const RAILWAY = "https://job-pipeline-production-8cff.up.railway.app";
    const r = await fetch(`${RAILWAY}${path}`, { headers: { "Authorization": "Bearer " + getToken() } });
    if (!r.ok) { alert("Could not generate the Word file. Please try again."); return; }
    const blob = await r.blob();
    const cd = r.headers.get("Content-Disposition") || "";
    const m = cd.match(/filename="([^"]+)"/);
    const name = (m && m[1]) || fallbackName;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = name; document.body.appendChild(a); a.click();
    a.remove(); setTimeout(() => URL.revokeObjectURL(url), 2000);
  } catch (e) { alert("Word download failed. Please try again."); }
};

// PDF download: fetch with auth from the backend attachment endpoint and save the
// blob, so the clean Name_Role_JobID filename is honoured. A plain <a download>
// on the cross-origin Supabase URL is ignored by browsers (saves the storage key).
const downloadPdf = async (jobId, fallbackName = "Resume.pdf") => {
  try {
    const RAILWAY = "https://job-pipeline-production-8cff.up.railway.app";
    const r = await fetch(`${RAILWAY}/api/jobs/${jobId}/resume-download`, { headers: { "Authorization": "Bearer " + getToken() } });
    if (!r.ok) { alert("Could not prepare the PDF. Please try again."); return; }
    const blob = await r.blob();
    const cd = r.headers.get("Content-Disposition") || "";
    const m = cd.match(/filename="([^"]+)"/);
    const name = (m && m[1]) || fallbackName;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = name; document.body.appendChild(a); a.click();
    a.remove(); setTimeout(() => URL.revokeObjectURL(url), 2000);
  } catch (e) { alert("PDF download failed. Please try again."); }
};

// Upload a file with TRUE progress %, a hard timeout, and clean error handling.
// onProgress(pct, phase) is called with 0-100 during transfer, then phase
// switches to "processing" once the bytes are up and the server is working.
// Returns the parsed JSON, or throws with a friendly message.
const uploadWithProgress = (path, formData, onProgress, { timeoutMs = 60000, fieldName } = {}) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${API_URL}${path}`);
    const token = getToken();
    if (token) xhr.setRequestHeader("Authorization", "Bearer " + token);
    xhr.timeout = timeoutMs;
    xhr.upload.onprogress = (ev) => {
      if (ev.lengthComputable && onProgress) {
        const pct = Math.round((ev.loaded / ev.total) * 100);
        onProgress(pct, pct < 100 ? "uploading" : "processing");
      }
    };
    xhr.onload = () => {
      try { resolve(JSON.parse(xhr.responseText)); }
      catch { reject(new Error("The server sent back an unexpected response.")); }
    };
    xhr.onerror = () => reject(new Error("Connection error. Check your network and try again."));
    xhr.ontimeout = () => reject(new Error("This is taking too long. Try a smaller file, or paste the text instead."));
    xhr.send(formData);
  });

// Honest progress for AI/build waits where a true % is impossible (the server is
// thinking, not transferring). Cycles real stage labels and ticks an elapsed
// counter so the user knows it's alive, without faking a percentage number.
function useStagedProgress(active, stages) {
  const [elapsed, setElapsed] = useState(0);
  const [stageIdx, setStageIdx] = useState(0);
  useEffect(() => {
    if (!active) { setElapsed(0); setStageIdx(0); return; }
    const started = Date.now();
    const t = setInterval(() => {
      const s = Math.floor((Date.now() - started) / 1000);
      setElapsed(s);
      setStageIdx(Math.min(stages.length - 1, Math.floor(s / 8)));
    }, 500);
    return () => clearInterval(t);
  }, [active, stages.length]);
  const label = stages[stageIdx] || stages[stages.length - 1] || "Working…";
  return { label, elapsed, text: `${label} (${elapsed}s)` };
}


// ── Score / location helpers ──────────────────────────────────────────────
const scoreColor = s => s >= 8 ? "#16A34A" : s >= 6 ? "#D97706" : "#DC2626";
const scoreBg    = s => s >= 8 ? "#F0FDF4" : s >= 6 ? "#FFFBEB" : "#FFF1F2";
const scoreBorder= s => s >= 8 ? "#86EFAC" : s >= 6 ? "#FDE047" : "#FCA5A5";
const kwBadgeColor = "#6D28D9";
const relDate=(d)=>{if(!d)return '';const s=Math.floor((Date.now()-new Date(d).getTime())/1000);if(s<3600)return Math.floor(s/60)+'m ago';if(s<86400)return Math.floor(s/3600)+'h ago';if(s<604800)return Math.floor(s/86400)+'d ago';return new Date(d).toLocaleDateString('en-AU',{day:'numeric',month:'short'});};

// ── Auth screens ─────────────────────────────────────────────────────────
// ── Public landing page (logged-out front door) ─────────────────────────
function LandingPage({ onGetStarted, onSignIn }) {
  const [w, setW] = useState(typeof window !== "undefined" ? window.innerWidth : 1200);
  useEffect(() => {
    const r = () => setW(window.innerWidth);
    window.addEventListener("resize", r);
    return () => window.removeEventListener("resize", r);
  }, []);
  const m = w < 760;
  const NAVY="#0D1225", CARD="#141A33", GOLD="#B8934A", GOLD_L="#E5C77E", INK="#EEF2FF", MUT="#8893A7";
  const wrap = { maxWidth: 1020, margin: "0 auto", padding: m ? "0 18px" : "0 28px" };
  const h2 = { fontSize: m ? 22 : 28, fontWeight: 800, color: INK, margin: "0 0 6px", letterSpacing: "-0.3px" };
  const sub = { fontSize: m ? 13 : 14.5, color: MUT, margin: "0 0 22px", lineHeight: 1.6 };
  const btnP = { background: "#16A34A", color: "white", border: "none", borderRadius: 12,
    padding: m ? "13px 22px" : "14px 28px", fontSize: m ? 14.5 : 15.5, fontWeight: 800, cursor: "pointer" };
  const btnS = { background: "none", color: INK, border: "1px solid rgba(255,255,255,0.25)", borderRadius: 12,
    padding: m ? "12px 20px" : "13px 26px", fontSize: m ? 14 : 15, fontWeight: 700, cursor: "pointer" };
  const card = { background: CARD, border: "1px solid rgba(255,255,255,0.08)", borderRadius: 16,
    padding: m ? "16px 16px" : "20px 20px", flex: m ? "1 1 100%" : "1 1 46%", boxSizing: "border-box" };

  const feat = (ico, title, body) => (
    <div style={card} key={title}>
      <div style={{ fontSize: 24, marginBottom: 6 }}>{ico}</div>
      <div style={{ fontSize: 15.5, fontWeight: 800, color: INK, marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: 12.5, color: MUT, lineHeight: 1.6 }}>{body}</div>
    </div>
  );

  return (
    <div style={{ background: NAVY, minHeight: "100vh", color: INK, overflowX: "hidden" }}>
      {/* Nav */}
      <div style={{ ...wrap, display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: m ? "16px 18px" : "20px 28px" }}>
        <div style={{ fontSize: 19, fontWeight: 800 }}>aiBoost<span style={{ color: GOLD }}> My Career</span></div>
        <button onClick={onSignIn} style={{ ...btnS, padding: "8px 16px", fontSize: 13 }}>Sign in</button>
      </div>

      {/* Hero */}
      <div style={{ ...wrap, padding: m ? "26px 18px 34px" : "48px 28px 56px", textAlign: m ? "left" : "center" }}>
        <div style={{ fontSize: m ? 30 : 46, fontWeight: 800, lineHeight: 1.12, letterSpacing: "-0.8px" }}>
          Stop searching.<br /><span style={{ color: GOLD_L }}>Start getting hunted for.</span>
        </div>
        <p style={{ fontSize: m ? 14.5 : 17, color: "#B8C2D8", lineHeight: 1.65,
          maxWidth: 640, margin: m ? "14px 0 22px" : "18px auto 26px" }}>
          Every morning at 10:00 AM, aiBoost scans the Australian market, scores every role against
          your <b style={{ color: INK }}>real experience</b>, and builds tailored, ATS-safe resumes
          on demand. Built for job seekers who are done doom-scrolling job boards.
        </p>
        <div style={{ display: "flex", gap: 12, justifyContent: m ? "flex-start" : "center", flexWrap: "wrap" }}>
          <button onClick={onGetStarted} style={btnP}>Get started free</button>
          <button onClick={onSignIn} style={btnS}>I have an account</button>
        </div>
        <div style={{ fontSize: 11.5, color: MUT, marginTop: 14 }}>
          Free during early access · No card required · Australian job market
        </div>
      </div>

      {/* How it works */}
      <div style={{ ...wrap, paddingBottom: m ? 30 : 44 }}>
        <h2 style={h2}>How it works</h2>
        <p style={sub}>Set up once. Then the pipeline does the daily grind.</p>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          {feat("1️⃣", "Show it your career", "Upload your resume or LinkedIn PDF export. AI reads it into a living profile and a competency evidence bank you own and edit.")}
          {feat("2️⃣", "It hunts every morning", "At 10:00 AM Melbourne time, new roles are scanned and scored 1-10 against your actual experience. Only genuine matches reach your pipeline.")}
          {feat("3️⃣", "One tap to apply-ready", "Approve a match and get a tailored, ATS-safe resume in PDF and Word, in your choice of four professional templates.")}
        </div>
      </div>

      {/* Differentiators */}
      <div style={{ ...wrap, paddingBottom: m ? 30 : 44 }}>
        <h2 style={h2}>Why it wins interviews</h2>
        <p style={sub}>Nobody else combines the hunt, the evidence, and the coaching in one loop.</p>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          {feat("🧠", "Competency Capital", "A growing bank of dated, real evidence from your career. Every tailored resume draws from it, so documents get stronger over time instead of generic.")}
          {feat("🎯", "Gap Coach", "Weak match? The coach interviews you about the gap and files your answers as reusable evidence, then rescores the job.")}
          {feat("🎤", "Interview Prep Packs", "Likely questions with STAR answers built from YOUR evidence, sharp talking points with your real numbers, and questions to ask the panel.")}
          {feat("📊", "Match reports", "See exactly which of a job's keywords your profile proves, and which to close before you apply.")}
        </div>
      </div>

      {/* Pricing */}
      <div style={{ ...wrap, paddingBottom: m ? 30 : 44 }}>
        <h2 style={h2}>Pricing</h2>
        <p style={sub}>Free while we are in early access. Early accounts keep full access until paid plans launch.</p>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <div style={{ ...card, border: "1px solid rgba(255,255,255,0.12)" }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: INK }}>Builder</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: INK, margin: "6px 0" }}>Free</div>
            <div style={{ fontSize: 12.5, color: MUT, lineHeight: 1.7 }}>
              Resume builder with all four templates · profile and evidence bank · documents hub
            </div>
          </div>
          <div style={{ ...card, border: `2px solid ${GOLD}`, position: "relative" }}>
            <div style={{ position: "absolute", top: -11, right: 14, background: GOLD, color: NAVY,
              fontSize: 10.5, fontWeight: 800, borderRadius: 8, padding: "3px 10px" }}>EARLY ACCESS: FREE</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: GOLD_L }}>Pro</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: INK, margin: "6px 0" }}>
              A$29<span style={{ fontSize: 13, color: MUT, fontWeight: 600 }}>/month at launch</span>
            </div>
            <div style={{ fontSize: 12.5, color: MUT, lineHeight: 1.7 }}>
              Everything in Builder plus the daily 10:00 AM hunt · unlimited tailored builds ·
              Gap Coach · Interview Prep Packs · match reports
            </div>
          </div>
        </div>
      </div>

      {/* FAQ */}
      <div style={{ ...wrap, paddingBottom: m ? 30 : 44 }}>
        <h2 style={h2}>Fair questions</h2>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          {feat("🔒", "Is my career data safe?", "Encrypted in transit, passwords hashed, never sold, no ads. AI processing is used only to score matches and build your documents. Full policy linked below.")}
          {feat("🇦🇺", "Which job market?", "Australia first: LinkedIn and Seek, salaries in AUD, hunts on Melbourne time.")}
          {feat("✍️", "Will it invent things about me?", "No. Documents are built only from the profile and evidence you provide and approve. You review everything before it goes anywhere.")}
          {feat("📱", "Where does it run?", "Any browser, installable on your phone as an app, with an Android app on the way.")}
        </div>
      </div>

      {/* CTA + footer */}
      <div style={{ ...wrap, padding: m ? "8px 18px 26px" : "8px 28px 30px", textAlign: "center" }}>
        <button onClick={onGetStarted} style={btnP}>Get started free</button>
        <div style={{ marginTop: 26, paddingTop: 16, borderTop: "1px solid rgba(255,255,255,0.08)",
          fontSize: 11.5, color: MUT, display: "flex", gap: 16, justifyContent: "center", flexWrap: "wrap" }}>
          <a href="/privacy.html" style={{ color: MUT }}>Privacy</a>
          <a href="/terms.html" style={{ color: MUT }}>Terms</a>
          <a href="mailto:aowias1@gmail.com" style={{ color: MUT }}>Contact</a>
          <span>© 2026 aiBoost My Career</span>
        </div>
      </div>
    </div>
  );
}

// ── Voice-dictation-safe textarea ────────────────────────────────────────
// Android/Gboard voice typing streams words as IME "compositions". A React
// CONTROLLED textarea writes value back mid-composition, which makes the
// keyboard commit the phrase a second time - every word doubles. This keeps
// the DOM as the source of truth while the field is focused/composing and
// only syncs state outward, which is immune to the double-commit.
function VoiceSafeTextarea({ value, onChange, ...rest }) {
  const ref = useRef(null);
  const composing = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (el && document.activeElement !== el && el.value !== (value ?? "")) {
      el.value = value ?? "";
    }
  }, [value]);
  return (
    <textarea ref={ref} defaultValue={value ?? ""} {...rest}
      onCompositionStart={() => { composing.current = true; }}
      onCompositionEnd={(e) => { composing.current = false; onChange && onChange(e); }}
      onInput={(e) => { if (!composing.current) onChange && onChange(e); }}
    />
  );
}

function RegisterScreen({ onRegistered, onBackToLogin }) {
  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [error,    setError]    = useState("");
  const [loading,  setLoading]  = useState(false);
  const [pending,  setPending]  = useState(false);
  const [invited,  setInvited]  = useState(false);
  const inviteCode = new URLSearchParams(window.location.search).get("invite") || "";
  const [inviteOk, setInviteOk] = useState(null);
  useEffect(() => {
    if (!inviteCode) return;
    fetch(`${API_URL}/api/invite/${encodeURIComponent(inviteCode)}`)
      .then(r=>r.json()).then(d=>setInviteOk(!!d.valid)).catch(()=>setInviteOk(false));
  }, []);

  if (pending) return (
    <div style={styles.loginWrap}><div style={styles.loginBox}>
      <div style={styles.loginLogo}>AS</div>
      <h1 style={styles.loginTitle}>{invited ? "You're In 🎉" : "Request Submitted"}</h1>
      <p style={{fontSize:14,color:"#64748B",textAlign:"center",lineHeight:1.6,marginBottom:20}}>
        {invited
          ? "Your invite pre-approved this account. Check your email, click the verification link, then sign straight in."
          : "Your request has been sent to the admin for approval. You can log in once approved."}
      </p>
      <button onClick={onBackToLogin} style={styles.loginBtn}>Back to Sign In</button>
    </div></div>
  );

  const submit = async () => {
    if (!email.trim() || !password) { setError("Email and password are required"); return; }
    if (!email.includes("@") || !email.split("@")[1]?.includes(".")) { setError("Enter a valid email address"); return; }
    setLoading(true); setError("");
    const r = await fetch(`${API_URL}/api/auth/register`, {
      method:"POST", headers:{"Content-Type":"application/json"},
      body: JSON.stringify({ email: email.trim(), password,
        invite_code: (inviteOk && inviteCode) ? inviteCode : undefined })
    });
    const data = await r.json(); setLoading(false);
    if (!r.ok) { setError(data.error || "Registration failed"); return; }
    setInvited(!!data.invited);
    setPending(true);
  };

  return (
    <div style={styles.loginWrap}><div style={styles.loginBox}>
      <div style={styles.loginLogo}>AS</div>
      <h1 style={styles.loginTitle}>{inviteOk ? "Join aiBoost" : "Request Access"}</h1>
      {inviteOk && (
        <div style={{background:"#FFFBEB", border:"1px solid #F0D9A8", borderRadius:10,
          padding:"9px 12px", fontSize:12.5, color:"#92400E", marginBottom:12, textAlign:"center"}}>
          🎟 You've been invited — your account will be <b>pre-approved</b>.
        </div>
      )}
      {inviteOk === false && (
        <div style={{background:"#FEF2F2", border:"1px solid #FECACA", borderRadius:10,
          padding:"9px 12px", fontSize:12.5, color:"#B91C1C", marginBottom:12, textAlign:"center"}}>
          This invite link has expired or been used — you can still request access below.
        </div>
      )}
      <p style={styles.loginSub}>Enter your email to {inviteOk ? "create your account" : "request an account"} <span style={{opacity:0.4}}>{APP_VERSION}</span></p>
      {error && <p style={styles.loginError}>{error}</p>}
      <input placeholder="Email address" type="email" value={email}
        onChange={e=>setEmail(e.target.value)}
        style={styles.loginInput} />
      <input placeholder="Password" type="password" value={password}
        onChange={e=>setPassword(e.target.value)}
        style={{...styles.loginInput, marginTop:10}} />
      <button onClick={submit} disabled={loading}
        style={{...styles.loginBtn, marginTop:16}}>
        {loading ? "Submitting..." : "Request Access"}
      </button>
      <p style={{fontSize:12,color:"#94A3B8",marginTop:12,textAlign:"center"}}>
        Already have an account?{" "}
        <span onClick={onBackToLogin}
          style={{color:"#B8934A",cursor:"pointer",textDecoration:"underline"}}>
          Sign in
        </span>
      </p>
    </div></div>
  );
}

function LoginScreen({ onLogin }) {
  const [email,        setEmail]        = useState("");
  const [password,     setPassword]     = useState("");
  const [error,        setError]        = useState("");
  const [loading,      setLoading]      = useState(false);
  const [showRegister, setShowRegister] = useState(
    !!new URLSearchParams(window.location.search).get("invite"));
  // STEALTH MODE: the marketing landing page stays hidden from the public until
  // launch. Flip LANDING_LIVE to true to make it the logged-out front door.
  // Until then it is viewable only via aiboostmycareer.com/?preview=landing
  const LANDING_LIVE = false;
  const _landingPreview = new URLSearchParams(window.location.search).get("preview") === "landing";
  const [showLanding, setShowLanding] = useState(
    (LANDING_LIVE || _landingPreview) &&
    !new URLSearchParams(window.location.search).get("invite"));
  if (showLanding && !showRegister) return (
    <LandingPage
      onGetStarted={() => { setShowLanding(false); setShowRegister(true); }}
      onSignIn={() => setShowLanding(false)} />
  );
  if (showRegister) return (
    <RegisterScreen
      onRegistered={uname => { setShowRegister(false); onLogin(uname); }}
      onBackToLogin={() => setShowRegister(false)}
    />
  );
  const submit = async () => {
    if (!email.trim() || !password) { setError("Email and password required"); return; }
    setLoading(true); setError("");
    try {
      const r = await fetch(`${API_URL}/api/auth/login`, {
        method: "POST", headers: {"Content-Type":"application/json"},
        body: JSON.stringify({ username: email.trim(), password }),
      });
      const data = await r.json();
      if (!r.ok) {
        setError(data.error==="pending_approval" ? "Your account is pending admin approval." : (data.error||"Login failed"));
        setLoading(false); return;
      }
      setToken(data.token);
      onLogin(data.username);
    } catch (e) { setError("Network error, please try again"); }
    setLoading(false);
  };
  return (
    <div style={styles.loginWrap}>
      <div style={styles.loginBox}>
        <div style={styles.loginLogo}>AS</div>
        <h1 style={styles.loginTitle}>Job Pipeline</h1>
      {(() => { try { return sessionStorage.getItem("auth_expired_msg")==="1"; } catch(e){ return false; } })() && (
        <div style={{background:"#FFFBEB", border:"1px solid #F0D9A8", borderRadius:10,
          padding:"9px 12px", fontSize:12.5, color:"#92400E", marginBottom:12, textAlign:"center"}}
          onClick={()=>{ try{ sessionStorage.removeItem("auth_expired_msg"); }catch(e){} }}>
          Your session expired — sign in again and everything will be right where you left it.
        </div>
      )}
        <p style={styles.loginSub}>Sign in to your account</p>
        {error && <p style={styles.loginError}>{error}</p>}
        <input placeholder="Email address" type="email" value={email}
          onChange={e=>setEmail(e.target.value)} style={styles.loginInput} />
        <input placeholder="Password" type="password" value={password}
          onChange={e=>setPassword(e.target.value)}
          style={{...styles.loginInput, marginTop:10}} />
        <button onClick={submit} disabled={loading} style={{...styles.loginBtn, marginTop:16}}>
          {loading ? "Signing in..." : "Sign In"}
        </button>
      {(LANDING_LIVE || _landingPreview) && (
        <button onClick={()=>setShowLanding(true)} style={{background:"none",border:"none",
          color:"#64748B",fontSize:12,cursor:"pointer",marginTop:10}}>← Back to site</button>
      )}
        <p style={{fontSize:12,color:"#94A3B8",marginTop:16,textAlign:"center"}}>
          New user?{" "}
          <span onClick={()=>setShowRegister(true)}
            style={{color:"#B8934A",cursor:"pointer",textDecoration:"underline"}}>
            Register here
          </span>
        </p>
      </div>
    </div>
  );
}

function OnboardingWizard({ onDone }) {
  const [step, setStep] = useState("start");          // start | upload | liexport | scratch | targets | aha | done
  const [busy, setBusy] = useState(false);
  const [msg, setMsg]   = useState("");
  // from-scratch fields
  const [sName, setSName] = useState(""); const [sTitle, setSTitle] = useState("");
  const [sYears, setSYears] = useState(""); const [sSummary, setSSummary] = useState("");
  // targeting
  const [tRoles, setTRoles] = useState("");   const [tLocs, setTLocs] = useState("Melbourne");
  const [tSalary, setTSalary] = useState(""); const [tSen, setTSen] = useState("");
  const [tRights, setTRights] = useState(""); const [tRemote, setTRemote] = useState("");
  // aha
  const [ahaUrl, setAhaUrl] = useState(""); const [ahaJob, setAhaJob] = useState(null);

  const NAVY = "#1C2B4A", GOLD = "#B8934A";
  const card = {background:"white", borderRadius:16, padding:"26px 24px", maxWidth:560, width:"92%",
    boxShadow:"0 18px 50px rgba(0,0,0,0.35)", maxHeight:"88vh", overflowY:"auto"};
  const h  = {fontSize:19, fontWeight:800, color:NAVY, margin:"0 0 6px"};
  const p  = {fontSize:13, color:"#64748B", margin:"0 0 16px", lineHeight:1.5};
  const btn = (primary)=>({padding:"12px 18px", borderRadius:10, cursor:"pointer", fontSize:14, fontWeight:700,
    border: primary?"none":"1px solid #E2E8F0", background: primary?"#16A34A":"#F8FAFC",
    color: primary?"white":"#475569", width:"100%", marginTop:8});
  const inp = {width:"100%", padding:"11px 12px", borderRadius:8, border:"1px solid #E2E8F0",
    fontSize:14, marginBottom:10, boxSizing:"border-box"};
  const lbl = {fontSize:11.5, fontWeight:700, color:NAVY, marginBottom:4, display:"block"};
  const sel = {...inp, background:"white"};

  const uploadFile = async (file) => {
    if(!file) return;
    if(file.size > 15*1024*1024){ setMsg("File is over 15MB - please use a smaller one."); return; }
    setBusy(true); setMsg("Reading your resume with AI... this takes up to a minute.");
    try {
      const form = new FormData(); form.append("file", file);
      const r = await fetch(`${API_URL}/api/profile/upload-resume-file`, {
        method:"POST", headers:{"Authorization":`Bearer ${getToken()}`}, body: form });
      const d = await r.json();
      if(d && d.ok){ setMsg(""); setStep("targets"); }
      else setMsg((d && d.error) || "Could not read that file - try another format.");
    } catch(e){ setMsg("Upload failed: "+e.message); }
    setBusy(false);
  };

  const saveScratch = async () => {
    if(!sName.trim() || !sTitle.trim()){ setMsg("Name and current title are the minimum to get going."); return; }
    setBusy(true); setMsg("");
    const profile = {
      name: sName.trim(), title: sTitle.trim(),
      summary: sSummary.trim() || `${sTitle.trim()} with ${sYears.trim()||"several"} years of experience.`,
      email: "", phone: "", linkedin: "", location: tLocs || "",
      experience: [], achievements: [], capabilities: [], certifications: [], education: [],
      technicalSkills: {},
    };
    const d = await api("/api/profile/resume", {method:"POST", body: JSON.stringify({resume_profile: profile})});
    setBusy(false);
    if(d && !d.error){ setStep("targets"); }
    else setMsg((d && d.error) || "Could not save - try again.");
  };

  const savePrefs = async (skip) => {
    setBusy(true); setMsg("");
    const body = skip ? {} : {
      target_roles: tRoles.split(",").map(s=>s.trim()).filter(Boolean),
      locations:    tLocs.split(",").map(s=>s.trim()).filter(Boolean),
      salary_floor: tSalary, seniority: tSen, work_rights: tRights, remote_pref: tRemote,
    };
    const d = await api("/api/onboarding/preferences", {method:"POST", body: JSON.stringify(body)});
    setBusy(false);
    if(d && d.ok){ skip ? onDone() : setStep("aha"); }
    else setMsg((d && d.error) || "Could not save preferences.");
  };

  const ahaAdd = async () => {
    if(!ahaUrl.trim()) return;
    setBusy(true); setAhaJob(null); setMsg("Fetching the posting... usually 5-40 seconds.");
    const d = await api("/api/add-job", {method:"POST", body: JSON.stringify({url: ahaUrl.trim()})});
    setBusy(false);
    if(d && d.ok && d.job){ setMsg(""); setAhaJob(d.job); }
    else if(d && d.need_manual){ setMsg("That one needs a manual paste - you can do it from the main screen later. Try another URL, or continue."); }
    else if(d && d.error === "duplicate"){ setMsg("That job is already in your pipeline - try another, or continue."); }
    else setMsg((d && d.error) || "Could not fetch that URL - try another, or continue.");
  };

  const tiles = [
    ["upload",  "📄", "Upload your resume", "PDF, Word or text - AI reads it into your profile"],
    ["liexport","🔗", "Use your LinkedIn",  "Upload LinkedIn's own PDF export of your profile"],
    ["scratch", "✏️", "Start from scratch", "No resume handy? Three quick fields and you're in"],
  ];

  return (
    <div style={{position:"fixed", inset:0, zIndex:1000, background:"rgba(13,18,37,0.92)",
      display:"flex", alignItems:"center", justifyContent:"center"}}>
      <div style={card}>
        {step==="start" && <>
          <div style={{fontSize:26, marginBottom:4}}>👋</div>
          <h3 style={h}>Welcome to aiBoost</h3>
          <p style={p}>Set up your profile once - then aiBoost hunts jobs for you daily, scores every match against your real experience, and builds tailored resumes on demand. Two minutes, three ways to start:</p>
          {tiles.map(([k, ico, t, d]) => (
            <div key={k} onClick={()=>{setMsg(""); setStep(k);}} style={{display:"flex", gap:12, alignItems:"center",
              padding:"14px 14px", border:"1px solid #E2E8F0", borderRadius:12, marginBottom:8, cursor:"pointer",
              background:"#F8FAFC"}}>
              <div style={{fontSize:22}}>{ico}</div>
              <div>
                <div style={{fontSize:14, fontWeight:700, color:NAVY}}>{t}</div>
                <div style={{fontSize:12, color:"#64748B"}}>{d}</div>
              </div>
            </div>
          ))}
          <button onClick={()=>savePrefs(true)} disabled={busy}
            style={{background:"none", border:"none", color:"#94A3B8", fontSize:12, cursor:"pointer", marginTop:10}}>
            Skip setup for now
          </button>
        </>}

        {(step==="upload" || step==="liexport") && <>
          <h3 style={h}>{step==="upload" ? "Upload your resume" : "Use your LinkedIn profile"}</h3>
          {step==="liexport" && <div style={{background:"#F0F9FF", border:"1px solid #BAE6FD", borderRadius:10,
            padding:"10px 12px", fontSize:12.5, color:"#0C4A6E", marginBottom:12, lineHeight:1.6}}>
            <b>15-second export:</b> open your LinkedIn profile → tap <b>More</b> (or Resources) →
            <b> Save to PDF</b> → upload that file below. It contains your whole profile.
          </div>}
          <p style={p}>PDF, Word (.docx) or plain text. AI reads it into a structured profile you can edit any time.</p>
          <label style={{...btn(true), display:"block", textAlign:"center", opacity:busy?0.6:1}}>
            {busy ? "Reading..." : "Choose file"}
            <input type="file" accept=".pdf,.docx,.txt" disabled={busy} style={{display:"none"}}
              onChange={e=>{ const f=e.target.files?.[0]; e.target.value=""; uploadFile(f); }}/>
          </label>
          {msg && <div style={{fontSize:12.5, color:"#B45309", marginTop:10}}>{msg}</div>}
          <button onClick={()=>{setMsg(""); setStep("start");}} disabled={busy} style={btn(false)}>Back</button>
        </>}

        {step==="scratch" && <>
          <h3 style={h}>Start from scratch</h3>
          <p style={p}>The minimum to get you hunting - you can build the rest with the Coach later.</p>
          <label style={lbl}>Your name</label>
          <input style={inp} value={sName} onChange={e=>setSName(e.target.value)} placeholder="Jane Citizen"/>
          <label style={lbl}>Current or most recent title</label>
          <input style={inp} value={sTitle} onChange={e=>setSTitle(e.target.value)} placeholder="Service Delivery Manager"/>
          <label style={lbl}>Years of experience</label>
          <input style={inp} value={sYears} onChange={e=>setSYears(e.target.value)} placeholder="8"/>
          <label style={lbl}>One-line summary (optional)</label>
          <input style={inp} value={sSummary} onChange={e=>setSSummary(e.target.value)} placeholder="ITSM leader across healthcare platforms..."/>
          {msg && <div style={{fontSize:12.5, color:"#B45309", marginBottom:6}}>{msg}</div>}
          <button onClick={saveScratch} disabled={busy} style={btn(true)}>{busy?"Saving...":"Save and continue"}</button>
          <button onClick={()=>{setMsg(""); setStep("start");}} disabled={busy} style={btn(false)}>Back</button>
        </>}

        {step==="targets" && <>
          <h3 style={h}>What are we hunting?</h3>
          <p style={p}>These steer your daily 10am job hunt and how matches are scored.</p>
          <label style={lbl}>Target roles (comma-separated)</label>
          <input style={inp} value={tRoles} onChange={e=>setTRoles(e.target.value)} placeholder="Service Delivery Manager, ITSM Lead, Head of Operations"/>
          <label style={lbl}>Locations</label>
          <input style={inp} value={tLocs} onChange={e=>setTLocs(e.target.value)} placeholder="Melbourne, Remote"/>
          <div style={{display:"flex", gap:8}}>
            <div style={{flex:1}}>
              <label style={lbl}>Salary floor</label>
              <select style={sel} value={tSalary} onChange={e=>setTSalary(e.target.value)}>
                <option value="">Any</option><option>$80k+</option><option>$100k+</option>
                <option>$130k+</option><option>$160k+</option><option>$200k+</option>
              </select>
            </div>
            <div style={{flex:1}}>
              <label style={lbl}>Seniority</label>
              <select style={sel} value={tSen} onChange={e=>setTSen(e.target.value)}>
                <option value="">Any</option><option value="junior">Junior</option>
                <option value="mid">Mid</option><option value="senior">Senior</option>
                <option value="exec">Executive</option>
              </select>
            </div>
          </div>
          <div style={{display:"flex", gap:8}}>
            <div style={{flex:1}}>
              <label style={lbl}>Work rights</label>
              <select style={sel} value={tRights} onChange={e=>setTRights(e.target.value)}>
                <option value="">Prefer not to say</option>
                <option value="citizen_pr">Citizen / PR</option>
                <option value="visa">Visa holder</option>
              </select>
            </div>
            <div style={{flex:1}}>
              <label style={lbl}>Work style</label>
              <select style={sel} value={tRemote} onChange={e=>setTRemote(e.target.value)}>
                <option value="">Any</option><option value="remote">Remote</option>
                <option value="hybrid">Hybrid</option><option value="onsite">On-site</option>
              </select>
            </div>
          </div>
          {msg && <div style={{fontSize:12.5, color:"#B45309", marginBottom:6}}>{msg}</div>}
          <button onClick={()=>savePrefs(false)} disabled={busy} style={btn(true)}>{busy?"Saving...":"Save targeting"}</button>
        </>}

        {step==="aha" && <>
          <h3 style={h}>Try it right now</h3>
          <p style={p}>Paste any LinkedIn or Seek job URL - watch it fetch, score against your profile, and land in your pipeline.</p>
          <input style={inp} value={ahaUrl} onChange={e=>setAhaUrl(e.target.value)}
            placeholder="https://www.linkedin.com/jobs/view/..." onKeyDown={e=>e.key==="Enter"&&ahaAdd()}/>
          <button onClick={ahaAdd} disabled={busy||!ahaUrl.trim()} style={btn(true)}>{busy?"Fetching...":"Fetch and score"}</button>
          {msg && <div style={{fontSize:12.5, color:"#B45309", marginTop:8}}>{msg}</div>}
          {ahaJob && <div style={{marginTop:12, border:"1px solid #E2E8F0", borderRadius:12, padding:"12px 14px", background:"#F8FAFC"}}>
            <div style={{fontSize:14, fontWeight:700, color:NAVY}}>{ahaJob.title}</div>
            <div style={{fontSize:12.5, color:"#64748B"}}>{ahaJob.company_name}</div>
            <div style={{fontSize:13, marginTop:6}}>Fit score: <b style={{color:GOLD}}>{ahaJob.fit_score}/10</b></div>
            <div style={{fontSize:11.5, color:"#94A3B8", marginTop:4}}>It's in your pipeline now - approve it there to build a tailored resume.</div>
          </div>}
          <button onClick={()=>setStep("done")} style={btn(false)}>{ahaJob ? "Continue" : "Skip this"}</button>
        </>}

        {step==="done" && <>
          <div style={{fontSize:26, marginBottom:4}}>🎯</div>
          <h3 style={h}>You're set</h3>
          <p style={p}>Your daily hunt runs every morning at <b>10:00 AM Melbourne time</b> - new matches are scored
          against your profile and only genuine fits (5+) reach your pipeline. Approve any job to build a tailored
          resume, and use the Gap Coach to strengthen weak matches.</p>
          <button onClick={onDone} style={btn(true)}>Open my pipeline</button>
        </>}
      </div>
    </div>
  );
}

function CapitalPage({ onBack }) {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState({});
  const [tgtInput, setTgtInput] = useState("");
  const [tgtBusy, setTgtBusy] = useState(false);
  const saveTargets = async () => {
    const roles = tgtInput.split(",").map(s => s.trim()).filter(Boolean);
    if (!roles.length || tgtBusy) return;
    setTgtBusy(true);
    try {
      await api("/api/onboarding/preferences", { method: "POST",
        body: JSON.stringify({ target_roles: roles }) });
      const d = await api("/api/profile/competencies");
      setData(d); setTgtInput("");
    } catch (e) {}
    setTgtBusy(false);
  };
  useEffect(() => { api("/api/profile/competencies").then(setData).catch(()=>setData({ok:false})); }, []);
  const comps = ((data && data.competencies) || []).filter(x =>
    !q.trim() || x.name.toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <div style={{padding:24, maxWidth:760, margin:"0 auto"}}>
      <button onClick={onBack} style={{marginBottom:16, background:"none", border:"none",
        color:"#B8934A", cursor:"pointer", fontSize:14}}>← Back</button>
      <h2 style={{fontSize:20, fontWeight:800, color:"#EEF2FF", marginBottom:4}}>🧠 Competency Capital</h2>
      <p style={{fontSize:12.5, color:"#8893A7", margin:"0 0 16px", lineHeight:1.55}}>
        Your evidence bank. Every competency here is backed by real, dated proof from your career -
        the Gap Coach grows it with every job you work, and every tailored resume draws from it.
      </p>
      {!data && <p style={{color:"#8893A7"}}>Loading…</p>}
      {data && data.ok===false && <p style={{color:"#F87171"}}>Could not load your capital.</p>}
      {data && data.ok !== false && <>
        <div style={{background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.08)",
          borderRadius:14, padding:"14px 16px", marginBottom:14}}>
          <div style={{display:"flex", alignItems:"center", gap:12, marginBottom:6}}>
            <div style={{fontSize:12.5, fontWeight:800, color:"#EEF2FF"}}>Capital strength</div>
            <div style={{flex:1, height:8, background:"#1E2A44", borderRadius:5, overflow:"hidden"}}>
              <div style={{width:`${data.strength||0}%`, height:"100%",
                background:"linear-gradient(90deg,#B8934A,#E5C77E)"}}/>
            </div>
            <div style={{fontSize:14, fontWeight:800, color:"#B8934A"}}>{data.strength||0}</div>
          </div>
          <div style={{fontSize:11.5, color:"#8893A7"}}>
            {(data.competencies||[]).length} competencies · {data.total_evidence||0} pieces of evidence
          </div>
          {(data.missing_themes||[]).length>0 && (
            <div style={{marginTop:10, paddingTop:10, borderTop:"1px solid rgba(255,255,255,0.07)"}}>
              <div style={{fontSize:11, fontWeight:800, color:"#FBBF24", marginBottom:5}}>
                Not yet evidenced for your targets{(data.targets||[]).length?` (${data.targets.join(", ")})`:""}:
              </div>
              {data.missing_themes.map(t=><span key={t} style={{display:"inline-block", fontSize:10.5,
                padding:"3px 9px", margin:"0 5px 5px 0", borderRadius:7,
                background:"rgba(245,158,11,0.13)", color:"#FBBF24"}}>{t}</span>)}
              <div style={{fontSize:10.5, color:"#8893A7", marginTop:4}}>
                Answer Gap Coach questions on matching jobs to grow evidence in these areas.
              </div>
            </div>
          )}
        </div>
        {(data.targets||[]).length===0 && (
          <div style={{background:"rgba(184,147,74,0.08)", border:"1px solid rgba(184,147,74,0.3)",
            borderRadius:14, padding:"13px 15px", marginBottom:14}}>
            <div style={{fontSize:12.5, fontWeight:800, color:"#E5C77E", marginBottom:4}}>
              🎯 Set your target roles to unlock the gap analysis
            </div>
            <div style={{fontSize:11.5, color:"#8893A7", marginBottom:9, lineHeight:1.5}}>
              Tell the capital what you're aiming at and it will show which themes your evidence
              doesn't cover yet. The daily hunt uses these too.
            </div>
            <div style={{display:"flex", gap:8}}>
              <input value={tgtInput} onChange={e=>setTgtInput(e.target.value)}
                placeholder="e.g. Head of Cyber Security, Service Delivery Manager"
                style={{flex:1, padding:"9px 11px", borderRadius:9,
                  border:"1px solid rgba(255,255,255,0.15)", background:"rgba(255,255,255,0.06)",
                  color:"#EEF2FF", fontSize:12.5}}/>
              <button onClick={saveTargets} disabled={tgtBusy||!tgtInput.trim()}
                style={{padding:"9px 14px", background:"#B8934A", color:"#0D1225", border:"none",
                  borderRadius:9, fontSize:12.5, fontWeight:800, cursor:"pointer",
                  opacity:(tgtBusy||!tgtInput.trim())?0.5:1, whiteSpace:"nowrap"}}>
                {tgtBusy?"Saving…":"Save"}
              </button>
            </div>
          </div>
        )}
        <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search your competencies…"
          style={{width:"100%", boxSizing:"border-box", padding:"10px 12px", borderRadius:10,
            border:"1px solid rgba(255,255,255,0.12)", background:"rgba(255,255,255,0.05)",
            color:"#EEF2FF", fontSize:13, marginBottom:10}}/>
        {comps.map(comp => (
          <div key={comp.i} style={{background:"rgba(255,255,255,0.04)",
            border:"1px solid rgba(255,255,255,0.08)", borderRadius:12, marginBottom:8, overflow:"hidden"}}>
            <div onClick={()=>setOpen(o=>({...o,[comp.i]:!o[comp.i]}))}
              style={{display:"flex", alignItems:"center", gap:10, padding:"11px 14px", cursor:"pointer"}}>
              <div style={{flex:1, fontSize:13, fontWeight:700, color:"#EEF2FF"}}>{comp.name}</div>
              <span style={{fontSize:10.5, fontWeight:800, padding:"2px 8px", borderRadius:7,
                background:"rgba(184,147,74,0.16)", color:"#B8934A"}}>{comp.evidence_count} evidence</span>
              <span style={{color:"#64748B", fontSize:12}}>{open[comp.i]?"▾":"▸"}</span>
            </div>
            {open[comp.i] && (
              <div style={{padding:"0 14px 12px"}}>
                {comp.evidence.map((e,i)=>(
                  <div key={i} style={{fontSize:11.5, color:"#B8C2D8", lineHeight:1.55,
                    padding:"8px 10px", background:"rgba(0,0,0,0.25)", borderRadius:8, marginBottom:6,
                    borderLeft:"2px solid #B8934A"}}>{e.text}</div>
                ))}
                {comp.evidence_count > comp.evidence.length &&
                  <div style={{fontSize:10.5, color:"#64748B"}}>+ {comp.evidence_count - comp.evidence.length} more</div>}
              </div>
            )}
          </div>
        ))}
        {comps.length===0 && <div style={{textAlign:"center", color:"#8893A7", fontSize:12.5, padding:"26px 0"}}>
          {q ? "No competencies match that search." :
           "No competencies yet - build them from your profile in Career Coach, then grow them through the Gap Coach."}
        </div>}
      </>}
    </div>
  );
}

function InterviewsHubPage({ jobs, onOpen, onBack }) {
  const ready = (jobs||[]).filter(j => j.prep_pack && !j.is_archived)
    .sort((a,b) => String(b.prep_pack?.generated_at||"").localeCompare(String(a.prep_pack?.generated_at||"")));
  const rank = (s) => s==="interviewing" ? 0 : s==="applied" ? 1 : 2;
  const suggested = (jobs||[]).filter(j => ["interviewing","applied","approved"].includes(j.status)
      && !j.prep_pack && !j.is_archived)
    .sort((a,b) => rank(a.status)-rank(b.status) || (b.fit_score||0)-(a.fit_score||0)).slice(0, 8);
  const card = {background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.08)",
    borderRadius:13, padding:"13px 15px", marginBottom:8, cursor:"pointer"};
  return (
    <div style={{padding:24, maxWidth:760, margin:"0 auto"}}>
      <button onClick={onBack} style={{marginBottom:16, background:"none", border:"none",
        color:"#B8934A", cursor:"pointer", fontSize:14}}>← Back</button>
      <h2 style={{fontSize:20, fontWeight:800, color:"#EEF2FF", marginBottom:4}}>🎤 Interview Coach</h2>
      <p style={{fontSize:12.5, color:"#8893A7", margin:"0 0 18px", lineHeight:1.55}}>
        Every prep room in one place. Each one decodes the role, plays your strongest cards
        from your Competency Capital, drills the full question bank, and researches the room.
      </p>

      <div style={{fontSize:11.5, fontWeight:800, color:"#E5C77E", margin:"0 0 8px", letterSpacing:0.4}}>
        PREP ROOMS READY ({ready.length})
      </div>
      {ready.length===0 && <div style={{fontSize:12, color:"#8893A7", marginBottom:14}}>
        None yet — pick a job below and generate its coach.
      </div>}
      {ready.map(j => (
        <div key={j.id} style={{...card, borderColor:"rgba(184,147,74,0.3)"}} onClick={()=>onOpen(j.id)}>
          <div style={{display:"flex", alignItems:"center", gap:10}}>
            <div style={{flex:1, minWidth:0}}>
              <div style={{fontSize:13.5, fontWeight:700, color:"#EEF2FF", whiteSpace:"nowrap",
                overflow:"hidden", textOverflow:"ellipsis"}}>{j.title}</div>
              <div style={{fontSize:11, color:"#8893A7"}}>
                {j.company_name}{j.prep_pack?.generated_at ? ` · prepared ${String(j.prep_pack.generated_at).substring(0,10)}` : ""}
              </div>
              <div style={{marginTop:6, display:"flex", gap:5, flexWrap:"wrap"}}>
                {["🧭 Role","🧠 You","❓ Bank","🃏 Practice","🔎 Research"].map(s=>(
                  <span key={s} style={{fontSize:9, fontWeight:700, padding:"2px 7px", borderRadius:6,
                    background:"rgba(184,147,74,0.12)", color:"#B8934A"}}>{s}</span>
                ))}
              </div>
            </div>
            <span style={{fontSize:12, fontWeight:800, color:"#B8934A", whiteSpace:"nowrap"}}>Enter →</span>
          </div>
        </div>
      ))}

      {suggested.length>0 && <>
        <div style={{fontSize:11.5, fontWeight:800, color:"#E5C77E", margin:"18px 0 8px", letterSpacing:0.4}}>
          WORTH PREPARING NEXT
        </div>
        {suggested.map(j => (
          <div key={j.id} style={card} onClick={()=>onOpen(j.id)}>
            <div style={{display:"flex", alignItems:"center", gap:10}}>
              <span style={{fontSize:12, fontWeight:800, color: (j.fit_score||0)>=8?"#4ADE80":"#C9A55A",
                background:"rgba(255,255,255,0.06)", borderRadius:8, padding:"4px 9px"}}>{j.fit_score??"?"}</span>
              <div style={{flex:1, minWidth:0}}>
                <div style={{fontSize:13, fontWeight:700, color:"#EEF2FF", whiteSpace:"nowrap",
                  overflow:"hidden", textOverflow:"ellipsis"}}>{j.title}</div>
                <div style={{fontSize:10.5, color:"#8893A7"}}>{j.company_name} · {j.status}</div>
              </div>
              <span style={{fontSize:11.5, fontWeight:700, color:"#8893A7", whiteSpace:"nowrap"}}>Prepare →</span>
            </div>
          </div>
        ))}
      </>}
    </div>
  );
}

function DocumentsPage({ onBack }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api("/api/jobs").then(d => {
      const all = (d && (d.jobs || d)) || [];
      setRows(all.filter(j => j.resume_url).sort((a,b)=>(b.id||0)-(a.id||0)));
    }).catch(()=>setRows([]));
  }, []);
  const fmt = (iso) => { try { return new Date(iso).toLocaleDateString("en-AU",{day:"numeric",month:"short"}); } catch(e){ return ""; } };
  const markReviewed = async (id) => {
    setRows(r => (r||[]).map(x => x.id === id ? { ...x, resume_status: "approved" } : x));
    try { await api(`/api/jobs/${id}/approve-resume`, { method: "POST", body: JSON.stringify({}) }); }
    catch(e) { setRows(r => (r||[]).map(x => x.id === id ? { ...x, resume_status: "pending_review" } : x)); }
  };
  return (
    <div style={{padding:24, maxWidth:760, margin:"0 auto"}}>
      <button onClick={onBack} style={{marginBottom:16, background:"none", border:"none",
        color:"#B8934A", cursor:"pointer", fontSize:14}}>← Back</button>
      <h2 style={{fontSize:20, fontWeight:800, color:"#EEF2FF", marginBottom:4}}>Documents</h2>
      <p style={{fontSize:12.5, color:"#8893A7", margin:"0 0 18px"}}>
        Every tailored resume you have built, newest first. The master resume lives in Resume Studio.
      </p>
      {rows===null && <p style={{color:"#8893A7"}}>Loading…</p>}
      {rows!==null && rows.length===0 && (
        <div style={{textAlign:"center", padding:"40px 20px", color:"#8893A7", fontSize:13, lineHeight:1.6}}>
          <div style={{fontSize:40, marginBottom:12, opacity:0.5}}>📄</div>
          No tailored resumes yet. Approve any job in your pipeline and the build lands here.
        </div>
      )}
      {(rows||[]).map(j => (
        <div key={j.id} style={{display:"flex", alignItems:"center", gap:10, padding:"12px 14px",
          background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.08)",
          borderRadius:12, marginBottom:8}}>
          <div style={{flex:1, minWidth:0}}>
            <div style={{fontSize:13.5, fontWeight:700, color:"#EEF2FF", whiteSpace:"nowrap",
              overflow:"hidden", textOverflow:"ellipsis"}}>{j.title}</div>
            <div style={{fontSize:11.5, color:"#8893A7"}}>
              {j.company_name} · built {fmt(j.updated_at || j.created_at)}
              {j.resume_status==="pending_review" && <span style={{color:"#B8934A"}}> · awaiting your review</span>}
            </div>
          </div>
          {j.resume_status==="pending_review" && (
            <button onClick={()=>markReviewed(j.id)} title="Mark this resume as reviewed"
              style={{padding:"6px 10px", background:"rgba(34,197,94,0.15)", color:"#4ADE80",
                border:"1px solid rgba(34,197,94,0.35)", borderRadius:8, fontSize:11.5,
                fontWeight:700, cursor:"pointer"}}>✓ Reviewed</button>
          )}
          <a href={j.resume_url} target="_blank" rel="noreferrer"
            style={{padding:"6px 10px", background:"#B8934A", color:"#0D1225", borderRadius:8,
              fontSize:11.5, fontWeight:700, textDecoration:"none"}}>PDF</a>
          <button onClick={()=>downloadDocx(`/api/jobs/${j.id}/resume-docx`, `${(j.title||"Resume").replace(/[^a-z0-9]+/gi,"_")}.docx`)}
            style={{padding:"6px 10px", background:"rgba(255,255,255,0.08)", color:"#EEF2FF",
              border:"1px solid rgba(255,255,255,0.15)", borderRadius:8, fontSize:11.5,
              fontWeight:700, cursor:"pointer"}}>Word</button>
          <button onClick={()=>{onBack(); window.location.hash=`job-${j.id}`;}}
            style={{padding:"6px 10px", background:"none", color:"#8893A7",
              border:"1px solid rgba(255,255,255,0.12)", borderRadius:8, fontSize:11.5,
              cursor:"pointer"}}>Open job</button>
        </div>
      ))}
    </div>
  );
}

// ── Admin Hub: the one page for everything admin. Schedule at top, user
// management embedded below; future sections (billing, caps, flags) stack here.
function AdminHubPage({ onBack, setSchedule, toast }) {
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api("/api/admin/settings").then(r => {
      if (r && r.ok) setDraft({...r.settings, mel: r.melbourne_now});
      else setDraft({scrape_enabled:true, scrape_hour:10, scrape_minute:0,
                     scrape_mode:"daily", scrape_days:[0,1,2,3,4], mel:""});
    }).catch(()=>setDraft({scrape_enabled:true, scrape_hour:10, scrape_minute:0,
                           scrape_mode:"daily", scrape_days:[0,1,2,3,4], mel:""}));
  }, []);
  const save = async () => {
    setBusy(true);
    const r = await api("/api/admin/settings", {method:"POST", body: JSON.stringify({
      scrape_enabled: draft.scrape_enabled, scrape_hour: draft.scrape_hour,
      scrape_minute: draft.scrape_minute, scrape_mode: draft.scrape_mode || "daily",
      scrape_days: draft.scrape_days || [] })});
    setBusy(false);
    if (r && r.ok) { setSchedule(r.settings);
      toast(!r.settings.scrape_enabled ? "Saved — daily hunt is paused for all users"
        : r.settings.next_label === "no days selected"
          ? "Saved — but no days are selected, so the hunt will not run"
          : `Saved — next hunt ${r.settings.next_label}`); }
    else toast("Save failed" + (r && r.error ? ": " + r.error : ""), "err");
  };
  const pillOn = {background:"#B8934A", color:"#0D1225", border:"none"};
  const pillOff = {background:"#F1F5F9", color:"#64748B", border:"1px solid #E2E8F0"};
  return (
    <div style={{background:"#F8FAFC", minHeight:"100vh"}}>
      <div style={{padding:24, maxWidth:760, margin:"0 auto"}}>
        <button onClick={onBack} style={{marginBottom:16, background:"none", border:"none",
          color:"#B8934A", cursor:"pointer", fontSize:14}}>← Back</button>
        <h2 style={{fontSize:20, fontWeight:700, color:"#1C2B4A", marginBottom:2}}>🛠 Admin</h2>
        <p style={{fontSize:12, color:"#64748B", margin:"0 0 16px"}}>
          Platform controls. Changes apply to all users, no redeploy needed.
        </p>

        {/* ── Daily hunt schedule ── */}
        <div style={{background:"white", border:"1px solid #E8EDF4", borderRadius:14,
          padding:"16px 18px", marginBottom:22}}>
          <div style={{fontSize:14.5, fontWeight:800, color:"#1C2B4A", marginBottom:10}}>
            ⏰ Daily hunt schedule
          </div>
          {!draft && <div style={{color:"#64748B", fontSize:12.5}}>Loading current settings…</div>}
          {draft && (<>
            <button onClick={()=>setDraft(d=>({...d, scrape_enabled: !d.scrape_enabled}))}
              style={{width:"100%", display:"flex", alignItems:"center", justifyContent:"space-between",
                background: draft.scrape_enabled ? "#F0FDF4" : "#F8FAFC",
                border:"1px solid " + (draft.scrape_enabled ? "#86EFAC" : "#E2E8F0"),
                borderRadius:12, padding:"12px 14px", cursor:"pointer", marginBottom:12}}>
              <span style={{fontSize:13, fontWeight:700, color:"#1C2B4A"}}>Hunt runs</span>
              <span style={{fontSize:12, fontWeight:800,
                color: draft.scrape_enabled ? "#16A34A" : "#94A3B8"}}>
                {draft.scrape_enabled ? "● ON" : "○ OFF"}
              </span>
            </button>
            <div style={{display:"flex", gap:10, alignItems:"center", marginBottom:10,
              opacity: draft.scrape_enabled ? 1 : 0.45}}>
              <span style={{fontSize:12.5, color:"#64748B", flex:1}}>Scan time (Melbourne)</span>
              <select value={draft.scrape_hour}
                onChange={e=>setDraft(d=>({...d, scrape_hour: parseInt(e.target.value)}))}
                style={{background:"white", color:"#1C2B4A", border:"1px solid #E2E8F0",
                  borderRadius:8, padding:"8px 10px", fontSize:13}}>
                {Array.from({length:24},(_,h)=>(
                  <option key={h} value={h}>{((h+11)%12)+1} {h<12?"AM":"PM"}</option>
                ))}
              </select>
              <select value={draft.scrape_minute}
                onChange={e=>setDraft(d=>({...d, scrape_minute: parseInt(e.target.value)}))}
                style={{background:"white", color:"#1C2B4A", border:"1px solid #E2E8F0",
                  borderRadius:8, padding:"8px 10px", fontSize:13}}>
                {[0,15,30,45].map(m=><option key={m} value={m}>:{String(m).padStart(2,"0")}</option>)}
              </select>
            </div>
            <div style={{fontSize:12.5, color:"#64748B", margin:"6px 0 6px",
              opacity: draft.scrape_enabled ? 1 : 0.45}}>How often</div>
            <div style={{display:"flex", gap:6, marginBottom:10,
              opacity: draft.scrape_enabled ? 1 : 0.45}}>
              {[["daily","Every day"],["alternate","Every 2nd day"],["weekly","Fixed days"]].map(([k,l])=>(
                <button key={k} onClick={()=>setDraft(d=>({...d, scrape_mode:k}))}
                  style={{flex:1, padding:"9px 4px", borderRadius:9, fontSize:11.5, fontWeight:700,
                    cursor:"pointer", ...(draft.scrape_mode===k ? pillOn : pillOff)}}>{l}</button>
              ))}
            </div>
            {draft.scrape_mode==="weekly" && (
              <div style={{display:"flex", gap:5, marginBottom:10,
                opacity: draft.scrape_enabled ? 1 : 0.45}}>
                {["M","T","W","T","F","S","S"].map((d,i)=>{
                  const on = (draft.scrape_days||[]).includes(i);
                  return (
                    <button key={i} onClick={()=>setDraft(dd=>({...dd,
                        scrape_days: on ? (dd.scrape_days||[]).filter(x=>x!==i)
                                        : [...(dd.scrape_days||[]), i].sort()}))}
                      style={{flex:1, padding:"9px 0", borderRadius:9, fontSize:12, fontWeight:800,
                        cursor:"pointer",
                        background: on ? "rgba(184,147,74,0.15)" : "#F8FAFC",
                        color: on ? "#8a6a2b" : "#94A3B8",
                        border:"1px solid " + (on ? "rgba(184,147,74,0.55)" : "#E2E8F0")}}>{d}</button>
                  );
                })}
              </div>
            )}
            {draft.scrape_mode==="alternate" && (
              <div style={{fontSize:11, color:"#94A3B8", marginBottom:10}}>
                Runs every second day, counting from the day you save.
              </div>
            )}
            {draft.scrape_mode==="weekly" && (draft.scrape_days||[]).length===0 && (
              <div style={{fontSize:11, color:"#DC2626", marginBottom:10}}>
                ⚠ No days selected — the hunt will not run until you pick at least one.
              </div>
            )}
            {draft.mel && <div style={{fontSize:11, color:"#94A3B8", marginBottom:12}}>
              Server time in Melbourne right now: {draft.mel}</div>}
            <button disabled={busy} onClick={save}
              style={{width:"100%", padding:"12px 0", background:"#B8934A", color:"#0D1225",
                border:"none", borderRadius:10, fontSize:13.5, fontWeight:800, cursor:"pointer",
                opacity: busy ? 0.6 : 1}}>
              {busy ? "Saving…" : "Save schedule"}
            </button>
          </>)}
        </div>

        {/* ── User management (embedded) ── */}
        <div style={{fontSize:14.5, fontWeight:800, color:"#1C2B4A", marginBottom:10}}>
          👥 User management
        </div>
        <AdminPanel embedded onBack={onBack} toast={toast} />
      </div>
    </div>
  );
}

function AdminPanel({ onBack, embedded, toast }) {
  const [users,setUsers]=useState([]); const [loading,setLoading]=useState(true); const [msg,setMsg]=useState("");
  const notify = toast || ((m)=>setMsg(m));
  const [pwBusy, setPwBusy] = useState({});      // id -> true while reset in flight
  const [pwTemp, setPwTemp] = useState({});      // id -> temp password shown inline
  const [invites,setInvites]=useState([]); const [invNote,setInvNote]=useState("");
  useEffect(()=>{api("/api/admin/invites").then(d=>setInvites((d&&d.invites)||[])).catch(()=>{});},[]);
  const makeInvite=async()=>{
    const d=await api("/api/admin/invites",{method:"POST",body:JSON.stringify({note:invNote})});
    if(d&&d.ok){
      setInvites(p=>[{...(d.invite||{}),url:d.url,active:true},...p]); setInvNote("");
      try{ await navigator.clipboard.writeText(d.url); setMsg("Invite created — link copied to clipboard."); }
      catch(e){ setMsg("Invite created: "+d.url); }
    } else setMsg((d&&d.error)||"Could not create invite.");
  };
  const copyInvite=async(u)=>{ try{ await navigator.clipboard.writeText(u); setMsg("Link copied."); }catch(e){ setMsg(u); } };
  const shareInvite=(u)=>{ if(navigator.share) navigator.share({title:"Join me on aiBoost", url:u}).catch(()=>{}); else copyInvite(u); };
  const revokeInvite=(code)=>{
    if(!window.confirm("Revoke this invite link?")) return;
    const gone=invites.find(i=>i.code===code);
    setInvites(p=>p.filter(i=>i.code!==code));
    notify("Invite revoked");
    api(`/api/admin/invites/${code}`,{method:"DELETE"}).then(d=>{
      if(!(d&&d.ok)) throw new Error();
    }).catch(()=>{ if(gone) setInvites(p=>[gone,...p]); notify("Revoke failed — restored","err"); });
  };
  useEffect(()=>{api("/api/admin/users").then(d=>{setUsers(Array.isArray(d)?d:[]);setLoading(false);});},[]);
  // All actions are OPTIMISTIC (state first, network in background, revert +
  // error toast on failure) - the old await-first handlers left buttons dead
  // for the whole round-trip and reported into a top-of-page banner nobody
  // scrolled up to see, which read as flaky.
  const approve=(uid)=>{
    setUsers(p=>p.map(u=>u.id===uid?{...u,status:"approved"}:u));
    notify("Approved");
    api(`/api/admin/users/${uid}/approve`,{method:"POST"}).then(d=>{
      if(d&&d.ok===false) throw new Error(d.error||"failed");
    }).catch(()=>{ setUsers(p=>p.map(u=>u.id===uid?{...u,status:"pending"}:u));
      notify("Approve failed — user is back in pending","err"); });
  };
  const reject=(uid)=>{
    if(!window.confirm("Reject and delete?"))return;
    const gone=users.find(u=>u.id===uid);
    setUsers(p=>p.filter(u=>u.id!==uid));
    notify("Rejected");
    api(`/api/admin/users/${uid}/reject`,{method:"POST"}).then(d=>{
      if(d&&d.ok===false) throw new Error();
    }).catch(()=>{ if(gone) setUsers(p=>[gone,...p]); notify("Reject failed — restored","err"); });
  };
  const resetPw=async(uid)=>{
    setPwBusy(b=>({...b,[uid]:true}));
    try{
      const d=await api(`/api/admin/users/${uid}/reset-password`,{method:"POST"});
      if(d && d.ok){ setPwTemp(t=>({...t,[uid]:d.temp_password})); notify("Temp password ready — shown on the row"); }
      else notify("Reset failed","err");
    }catch(e){ notify("Reset failed","err"); }
    setPwBusy(b=>({...b,[uid]:false}));
  };
  const toggleScrape=(uid,current)=>{
    const paused=!current;
    setUsers(p=>p.map(u=>u.id===uid?{...u,scrape_paused:paused}:u));
    api(`/api/admin/users/${uid}/scrape-pause`,{method:"POST",body:JSON.stringify({paused})}).then(d=>{
      if(d&&d.ok===false) throw new Error();
    }).catch(()=>{ setUsers(p=>p.map(u=>u.id===uid?{...u,scrape_paused:current}:u));
      notify("Could not update scraping — reverted","err"); });
  };
  const toggleResumeOnly=(uid,current)=>{
    const resume_only=!current;
    const prev=users.find(u=>u.id===uid);
    setUsers(p=>p.map(u=>u.id===uid?{...u,resume_only_mode:resume_only,scrape_paused:resume_only?true:u.scrape_paused}:u));
    api(`/api/admin/users/${uid}/resume-only`,{method:"POST",body:JSON.stringify({resume_only})}).then(d=>{
      if(d&&d.ok===false) throw new Error();
    }).catch(()=>{ if(prev) setUsers(p=>p.map(u=>u.id===uid?{...u,resume_only_mode:prev.resume_only_mode,scrape_paused:prev.scrape_paused}:u));
      notify("Could not update mode — reverted","err"); });
  };
  const deleteUser=(uid,username,jobCount)=>{
    if(!window.confirm(`Permanently DELETE user "${username}" and all their data (${jobCount??"?"} jobs, profile)?\n\nThis cannot be undone.`))return;
    if(!window.confirm(`Are you absolutely sure? This will erase everything for "${username}".`))return;
    const gone=users.find(u=>u.id===uid);
    setUsers(p=>p.filter(u=>u.id!==uid));
    notify(`Deleting ${username}…`);
    api(`/api/admin/users/${uid}/delete`,{method:"POST",body:JSON.stringify({confirm:true})}).then(d=>{
      if(d&&d.ok) notify(`Deleted ${username} (${d.deleted_jobs} jobs removed)`);
      else throw new Error((d&&d.error)||"failed");
    }).catch(()=>{ if(gone) setUsers(p=>[...p,gone]); notify("Delete failed — user restored","err"); });
  };
  const pending=users.filter(u=>u.status==="pending"); const approved=users.filter(u=>u.status==="approved");
  const pill=(on,onTxt,offTxt,onClick,color)=>(
    <button onClick={onClick} style={{padding:"4px 9px",borderRadius:6,cursor:"pointer",fontSize:11,fontWeight:600,
      border:`1px solid ${on?color:"#CBD5E1"}`,background:on?color+"1A":"white",color:on?color:"#64748B"}}>
      {on?onTxt:offTxt}
    </button>
  );
  return (
    <div style={embedded ? {} : {background:"#F8FAFC", minHeight:"100vh"}}>
    <div style={embedded ? {} : {padding:24,maxWidth:760,margin:"0 auto"}}>
      {!embedded && <button onClick={onBack} style={{marginBottom:16,background:"none",border:"none",color:"#B8934A",cursor:"pointer",fontSize:14}}>Back</button>}
      {!embedded && <h2 style={{fontSize:20,fontWeight:700,color:"#1C2B4A",marginBottom:12}}>User Management</h2>}
      {msg&&<p style={{color:"#0F766E",fontSize:13,marginBottom:10,background:"#F0FDFA",padding:"8px 12px",borderRadius:6,border:"1px solid #99F6E4"}}>{msg}</p>}

      {/* ── Invite Links ── */}
      <div style={{background:"white",border:"1px solid #E8EDF4",borderRadius:14,padding:"14px 16px",marginBottom:18}}>
        <div style={{fontSize:14,fontWeight:800,color:"#1C2B4A",marginBottom:4}}>🎟 Invite Links</div>
        <div style={{fontSize:11.5,color:"#64748B",marginBottom:10,lineHeight:1.5}}>
          Anyone who signs up through an invite link is <b>pre-approved</b> (email verification still applies).
          Links are single-use and expire after 14 days.
        </div>
        <div style={{display:"flex",gap:8,marginBottom:10}}>
          <input value={invNote} onChange={e=>setInvNote(e.target.value)} placeholder="Note (who is this for?)"
            style={{flex:1,padding:"9px 11px",borderRadius:8,border:"1px solid #E2E8F0",fontSize:12.5}}/>
          <button onClick={makeInvite} style={{padding:"9px 14px",background:"#1C2B4A",color:"white",
            border:"none",borderRadius:8,fontSize:12.5,fontWeight:700,cursor:"pointer",whiteSpace:"nowrap"}}>
            ＋ New link
          </button>
        </div>
        {invites.length===0 && <div style={{fontSize:12,color:"#94A3B8"}}>No invite links yet.</div>}
        {invites.map(inv=>(
          <div key={inv.code} style={{display:"flex",alignItems:"center",gap:8,padding:"9px 10px",
            background:inv.active?"#F8FAFC":"#F1F5F9",border:"1px solid #E8EDF4",borderRadius:10,
            marginBottom:6,opacity:inv.active?1:0.55}}>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:12.5,fontWeight:700,color:"#1C2B4A",whiteSpace:"nowrap",
                overflow:"hidden",textOverflow:"ellipsis"}}>{inv.note||inv.code}</div>
              <div style={{fontSize:10.5,color:"#94A3B8"}}>
                {inv.used_count||0}/{inv.max_uses||1} used · expires {String(inv.expires_at||"").substring(0,10)}
                {!inv.active && " · inactive"}
              </div>
            </div>
            <button onClick={()=>copyInvite(inv.url)} style={{padding:"6px 10px",background:"#FFFBEB",
              color:"#92400E",border:"1px solid #F0D9A8",borderRadius:8,fontSize:11,fontWeight:700,cursor:"pointer"}}>Copy</button>
            <button onClick={()=>shareInvite(inv.url)} style={{padding:"6px 10px",background:"#EFF6FF",
              color:"#1D4ED8",border:"1px solid #BFDBFE",borderRadius:8,fontSize:11,fontWeight:700,cursor:"pointer"}}>Share</button>
            <button onClick={()=>revokeInvite(inv.code)} style={{padding:"6px 10px",background:"#FEF2F2",
              color:"#B91C1C",border:"1px solid #FECACA",borderRadius:8,fontSize:11,cursor:"pointer"}}>✕</button>
          </div>
        ))}
      </div>
      {loading&&<p style={{color:"#94A3B8"}}>Loading...</p>}
      {pending.length>0&&<div style={{marginBottom:20}}>
        <h3 style={{fontSize:15,fontWeight:600,color:"#C2410C",marginBottom:8}}>Pending ({pending.length})</h3>
        {pending.map(u=>(
          <div key={u.id} style={{display:"flex",alignItems:"center",gap:10,padding:"12px 14px",background:"#FFF7ED",border:"1px solid #FED7AA",borderRadius:8,marginBottom:6}}>
            <div style={{flex:1}}>
              <div style={{fontWeight:600,fontSize:14}}>{u.username}</div>
              <div style={{fontSize:12,color:"#64748B"}}>{u.email}</div>
              <div style={{fontSize:11,color:"#94A3B8"}}>{new Date(u.created_at).toLocaleDateString()}</div>
            </div>
            <button onClick={()=>approve(u.id)} style={{padding:"6px 12px",background:"#16A34A",color:"white",border:"none",borderRadius:6,cursor:"pointer",fontSize:13}}>Approve</button>
            <button onClick={()=>reject(u.id)}  style={{padding:"6px 12px",background:"#DC2626",color:"white",border:"none",borderRadius:6,cursor:"pointer",fontSize:13}}>Reject</button>
          </div>
        ))}
      </div>}
      {approved.length>0&&<div>
        <h3 style={{fontSize:15,fontWeight:600,color:"#1C2B4A",marginBottom:8}}>Active ({approved.length})</h3>
        {approved.map(u=>(<div key={u.id} style={{padding:"12px 14px",background:"#F8FAFC",border:"1px solid #E2E8F0",borderRadius:8,marginBottom:8}}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
            <div style={{flex:1}}>
              <span style={{fontWeight:600,fontSize:13}}>{u.username}</span>
              {u.id===1&&<span style={{fontSize:10,color:"#B8934A",fontWeight:700,marginLeft:8}}>ADMIN</span>}
              <span style={{fontSize:12,color:"#64748B",marginLeft:10}}>{u.email}</span>
              <div style={{fontSize:11,color:"#94A3B8"}}>{u.job_count!=null?`${u.job_count} jobs`:""}{u.resume_only_mode?" · resume-only":u.scrape_paused?" · scraping paused":" · scraping on"}</div>
            </div>
          </div>
          {u.id!==1&&<div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            {pill(!u.scrape_paused && !u.resume_only_mode,"Scraping ON","Scraping OFF",
              ()=>{ if(u.resume_only_mode){notify("Turn off Resume-only first — it disables scraping by design");return;} toggleScrape(u.id,u.scrape_paused);},"#2563EB")}
            {pill(u.resume_only_mode,"Resume-only ON","Resume-only OFF",()=>toggleResumeOnly(u.id,u.resume_only_mode),"#7C3AED")}
            <button onClick={()=>resetPw(u.id)} disabled={!!pwBusy[u.id]}
              style={{padding:"4px 9px",background:"none",border:"1px solid #CBD5E1",borderRadius:6,cursor:"pointer",fontSize:11,color:"#64748B",opacity:pwBusy[u.id]?0.5:1}}>
              {pwBusy[u.id]?"Resetting…":"Reset PW"}</button>
            <button onClick={()=>deleteUser(u.id,u.username,u.job_count)}
              style={{padding:"4px 9px",background:"none",border:"1px solid #FCA5A5",borderRadius:6,cursor:"pointer",fontSize:11,color:"#DC2626",fontWeight:600,marginLeft:"auto"}}>Delete user</button>
          </div>}
          {pwTemp[u.id] && (
            <div onClick={()=>{ try{navigator.clipboard.writeText(pwTemp[u.id]); notify("Password copied");}catch(e){} }}
              style={{marginTop:8, padding:"8px 11px", background:"#FFFBEB", border:"1px dashed #F0D9A8",
                borderRadius:8, fontSize:12, color:"#92400E", cursor:"pointer"}}>
              🔑 Temp password: <b style={{fontFamily:"monospace"}}>{pwTemp[u.id]}</b>
              <span style={{fontSize:10.5, color:"#B45309"}}> — tap to copy, share it securely</span>
            </div>
          )}
        </div>))}
      </div>}
      {!loading&&users.length===0&&<p style={{color:"#94A3B8"}}>No users yet.</p>}
    </div>
    </div>
  );
}

function ProfilePage({ onBack }) {
  // Steps: "start" | "extracted" | "converting" | "done"
  const [step,      setStep]      = useState("start");
  const [profile,   setProfile]   = useState(null);
  const [text,      setText]      = useState("");
  const [error,     setError]     = useState("");
  const [loadingMsg,setLoadingMsg]= useState("");
  const [keywords,  setKeywords]  = useState([]);
  const [newKw,     setNewKw]     = useState("");
  const [kwSaving,  setKwSaving]  = useState(false);
  const [titles,    setTitles]    = useState([]);
  const [newTitle,  setNewTitle]  = useState("");
  const [onbOpen,   setOnbOpen]   = useState(true);
  const [advStep,   setAdvStep]   = useState("idle");
  const [advQs,     setAdvQs]     = useState([]);
  const [advAns,    setAdvAns]    = useState({});
  const [advProp,   setAdvProp]   = useState(null);
  const [advErr,    setAdvErr]    = useState("");
  const [comps,     setComps]     = useState([]);
  const [openGaps,  setOpenGaps]  = useState([]);
  const [growthLog, setGrowthLog] = useState([]);
  const [chatMsgs,  setChatMsgs]  = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatBusy,  setChatBusy]  = useState(false);
  const [migStep,   setMigStep]   = useState("idle");   // idle|loading|review|saving
  const [migProp,   setMigProp]   = useState([]);
  const [migError,  setMigError]  = useState("");
  const [expandedComp, setExpandedComp] = useState(null);

  const runMigration = async () => {
    setMigStep("loading");
    setMigError("");
    try {
      const d = await api("/api/profile/competencies/migrate", { method:"POST" });
      if (d.ok && d.proposal?.length) {
        setMigProp(d.proposal);
        setMigStep("review");
      } else if (d.ok && !d.proposal?.length) {
        // Call succeeded but found nothing to propose
        setMigError("The AI couldn't extract competencies from your profile. Make sure your resume is uploaded with work experience, then try again.");
        setMigStep("idle");
      } else {
        // Backend returned an error (e.g. no profile, AI failure)
        setMigError(d.error === "No profile found"
          ? "Upload your resume first - there's no profile to build competencies from yet."
          : (d.error || "Something went wrong building your competency map. Please try again."));
        setMigStep("idle");
      }
    } catch(e){
      setMigError("Couldn't reach the server. Check your connection and try again.");
      setMigStep("idle");
    }
  };
  const [migRescoreMsg, setMigRescoreMsg] = useState("");
  const confirmMigration = async () => {
    setMigStep("saving");
    try {
      const d = await api("/api/profile/competencies/migrate-confirm", { method:"POST",
        body: JSON.stringify({ competencies: migProp }) });
      if (d.ok) {
        const fresh = await api("/api/profile/competencies");
        if (fresh.ok) { setComps(fresh.competencies||[]); setGrowthLog(fresh.growth_log||[]); }
        if (d.rescored) setMigRescoreMsg(`✨ Re-scored ${d.rescored} jobs · ${d.improved} improved`);
        setMigStep("idle"); setMigProp([]);
        setTimeout(()=>setMigRescoreMsg(""), 8000);
      } else setMigStep("review");
    } catch(e){ setMigStep("review"); }
  };

  const totalEvidence = comps.reduce((n,c)=>n+(c.evidence||[]).length,0);
  const profileStrength = Math.min(100, 35
    + Math.min(30, comps.length*5)
    + Math.min(20, totalEvidence*2)
    + (profile?.summary?15:0));

  const sendChat = async () => {
    const msg = chatInput.trim();
    if (!msg || chatBusy) return;
    const msgs = [...chatMsgs, {role:"user", content:msg}];
    setChatMsgs(msgs); setChatInput(""); setChatBusy(true);
    try {
      const d = await api("/api/coach/chat", { method:"POST",
        body: JSON.stringify({ message: msg, messages: chatMsgs }) });
      if (d.ok && d.reply) setChatMsgs([...msgs, {role:"assistant", content:d.reply}]);
      else if (d.error) setChatMsgs([...msgs, {role:"assistant", content:"⚠ "+d.error}]);
    } catch(e) { setChatMsgs([...msgs, {role:"assistant", content:"⚠ "+e.message}]); }
    setChatBusy(false);
  };

  useEffect(() => {
    api("/api/profile/competencies").then(d => {
      if (d.ok) {
        setComps(d.competencies || []);
        setOpenGaps(d.open_gaps || []);
        setGrowthLog(d.growth_log || []);
      }
    }).catch(()=>{});
    api("/api/profile/resume").then(d => {
      if (d.resume_profile) {
        setProfile(d.resume_profile);
        setKeywords(d.resume_profile.search_keywords || []);
        setTitles(d.resume_profile.target_titles || []);
        setOnbOpen(!d.resume_profile.onboarding_dismissed);
        setStep("done");
      }
    });
  }, []);

  const saveKeywords = async (kws) => {
    setKeywords(kws);
    setKwSaving(true);
    try { await api("/api/profile/search-keywords", { method:"POST", body: JSON.stringify({ keywords: kws }) }); }
    catch (e) { /* keep local state; will retry on next change */ }
    setKwSaving(false);
  };
  const addKeyword = () => {
    const k = newKw.trim();
    if (k && !keywords.some(x => x.toLowerCase() === k.toLowerCase())) saveKeywords([...keywords, k]);
    setNewKw("");
  };
  const removeKeyword = (k) => saveKeywords(keywords.filter(x => x !== k));

  const saveTitles = async (ts) => {
    setTitles(ts);
    try { await api("/api/profile/preferences", { method:"POST", body: JSON.stringify({ target_titles: ts }) }); }
    catch (e) { /* keep local */ }
  };
  const addTitle = () => {
    const t = newTitle.trim();
    if (t && !titles.some(x => x.toLowerCase() === t.toLowerCase())) saveTitles([...titles, t]);
    setNewTitle("");
  };
  const removeTitle = (t) => saveTitles(titles.filter(x => x !== t));
  const dismissOnb = async () => {
    setOnbOpen(false);
    try { await api("/api/profile/preferences", { method:"POST", body: JSON.stringify({ onboarding_dismissed: true }) }); }
    catch (e) { /* ignore */ }
  };
  const startAdvisor = async () => {
    setAdvStep("loading"); setAdvErr("");
    try {
      const d = await api("/api/profile/gap-questions", { method:"POST" });
      if (d.questions && d.questions.length) { setAdvQs(d.questions); setAdvAns({}); setAdvStep("answering"); }
      else { setAdvErr(d.error || "Could not generate questions."); setAdvStep("idle"); }
    } catch (e) { setAdvErr("Error: " + e.message); setAdvStep("idle"); }
  };
  const submitAdvisor = async () => {
    const qa = advQs.map((q,i) => ({ question:q, answer:(advAns[i]||"").trim() })).filter(x => x.answer);
    if (!qa.length) { setAdvErr("Answer at least one question first."); return; }
    setAdvStep("processing"); setAdvErr("");
    try {
      const d = await api("/api/profile/apply-answers", { method:"POST", body: JSON.stringify({ qa }) });
      if (d.updated_profile) { setAdvProp(d.updated_profile); setAdvStep("reviewing"); }
      else { setAdvErr(d.error || "Could not process answers."); setAdvStep("answering"); }
    } catch (e) { setAdvErr("Error: " + e.message); setAdvStep("answering"); }
  };
  const approveAdvisor = async () => {
    setAdvStep("saving");
    try {
      await api("/api/profile/resume", { method:"POST", body: JSON.stringify({ resume_profile: advProp }) });
      setProfile(advProp); setAdvProp(null); setAdvQs([]); setAdvAns({}); setAdvStep("idle");
    } catch (e) { setAdvErr("Save failed: " + e.message); setAdvStep("reviewing"); }
  };

  // Step 1: User picks a file → extract text from it
  const onFileChange = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    e.target.value = "";
    if (file.size > 15 * 1024 * 1024) { setError("That file is over 15MB. Please use a smaller one."); return; }
    setError(""); setLoadingMsg("Uploading 0%…");
    setStep("start");   // keep showing spinner while extracting
    const form = new FormData();
    form.append("file", file);
    try {
      const d = await uploadWithProgress("/api/profile/extract-text", form,
        (pct, phase) => setLoadingMsg(phase === "uploading" ? `Uploading ${pct}%…` : "Reading your resume…"));
      setLoadingMsg("");
      if (!d || d.error) { setError((d && d.error) || "Could not read file"); setStep("start"); return; }
      setText(d.text);
      setStep("extracted");
    } catch (err) {
      setLoadingMsg(""); setError(err.message); setStep("start");
    }
  };

  // Step 2: User confirms text → Claude converts it
  const convert = async () => {
    setStep("converting");
    setLoadingMsg("Claude is reading your resume... (usually 10-20 seconds)");
    setError("");
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 90000); // 90s client timeout
      const token = getToken();
      const r = await fetch(`${API_URL}/api/profile/generate-resume`, {
        method:"POST",
        headers: {"Content-Type":"application/json", ...(token ? {"Authorization":`Bearer ${token}`} : {})},
        body: JSON.stringify({ text }),
        signal: controller.signal
      });
      clearTimeout(timeout);
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.error || `Server error (${r.status})`);
      }
      const d = await r.json();
      if (d.error) throw new Error(d.error);
      if (!d.resume_profile) throw new Error("No profile returned from Claude. Try again.");
      await api("/api/profile/resume", { method:"POST", body: JSON.stringify({ resume_profile: d.resume_profile }) });
      setProfile(d.resume_profile);
      setLoadingMsg(""); setStep("done");
    } catch(e) {
      setLoadingMsg("");
      setStep("extracted");
      if (e.name === "AbortError") {
        setError("Request timed out after 90 seconds. Try with a shorter resume or use Paste text instead.");
      } else {
        setError(e.message || "Something went wrong. Please try again.");
      }
    }
  };


  return (
    <div style={{ minHeight:"100vh", background:"#F4F6FB", paddingBottom:40 }}>
      <div style={{ padding:"16px 20px 90px", maxWidth:1060, margin:"0 auto" }}>
      <button onClick={onBack}
        style={{ marginBottom:16, background:"none", border:"none", color:"#B8934A", cursor:"pointer", fontSize:14 }}>
        Back to Dashboard
      </button>
      <h2 style={{ fontSize:20, fontWeight:700, color:"#1C2B4A", marginBottom:2 }}>Career Coach</h2>
      <p style={{ fontSize:12, color:"#64748B", margin:"0 0 12px" }}>Goals, competencies and growth, guided by AI. Resume building lives in Resume Studio.</p>
      <p style={{ fontSize:12.5, color:"#94A3B8", margin:"0 0 16px", lineHeight:1.5 }}>Your career home: goals, competencies, gaps, and profile. To build or refine your actual resume document, use <b style={{color:"#B8934A"}}>Resume Studio</b>.</p>

      {/* ── DONE: show saved profile summary ───────────────────────────── */}
      {/* ── DONE: redesigned profile — competency map is the hero ── */}
      {step === "done" && profile && (
        <div style={{ maxWidth:640, margin:"0 auto" }}>

          {/* Hero: identity + profile strength */}
          <div style={{ background:"linear-gradient(135deg,#1C2B4A 0%,#0F1B33 100%)",
            borderRadius:18, padding:"22px 24px", marginBottom:16, color:"white",
            boxShadow:"0 8px 32px rgba(28,43,74,0.25)", position:"relative", overflow:"hidden" }}>
            <div style={{ position:"absolute", top:-40, right:-40, width:180, height:180,
              borderRadius:"50%", background:"radial-gradient(circle,rgba(184,147,74,0.18),transparent 70%)" }}/>
            <p style={{ fontSize:10, fontWeight:700, letterSpacing:1.2, color:"rgba(255,255,255,0.45)",
              textTransform:"uppercase", margin:"0 0 6px" }}>My Career Profile</p>
            <p style={{ fontSize:22, fontWeight:800, margin:"0 0 3px", letterSpacing:"-0.3px" }}>{profile.name || "—"}</p>
            <p style={{ fontSize:13, color:"#D4B476", margin:0, fontWeight:500 }}>{profile.title || profile.experience?.[0]?.title || ""}</p>
            <div style={{ marginTop:14 }}>
              <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                <span style={{ fontSize:10, fontWeight:700, letterSpacing:0.8, color:"rgba(255,255,255,0.6)",
                  textTransform:"uppercase" }}>Profile Strength</span>
                <span style={{ fontSize:12, fontWeight:800, color:"#B8934A" }}>{profileStrength}%</span>
              </div>
              <div style={{ height:6, background:"rgba(255,255,255,0.15)", borderRadius:4, overflow:"hidden" }}>
                <div style={{ height:"100%", width:`${profileStrength}%`,
                  background:"linear-gradient(90deg,#B8934A,#E8C87A)", borderRadius:4,
                  transition:"width 0.6s ease" }}/>
              </div>
              <div style={{ fontSize:10, color:"rgba(255,255,255,0.5)", marginTop:5 }}>
                {comps.length===0
                  ? "Build your competency map below to grow this"
                  : `${comps.length} competencies · ${totalEvidence} evidence items · grows as you answer gap questions`}
              </div>
            </div>
          </div>

          {/* ═══ SECTION 1 (HERO): Competency Map — collapsible ═══ */}
          <details open={comps.length===0 || migStep!=="idle"}
            style={{ background:"white", border:"1px solid #E8EDF4", borderRadius:16,
            padding:18, marginBottom:14, boxShadow:"0 2px 12px rgba(15,27,51,0.05)" }}>
            <summary style={{ display:"flex", justifyContent:"space-between", alignItems:"center",
              marginBottom:12, flexWrap:"wrap", gap:8, cursor:"pointer", listStyle:"none" }}>
              <p style={{ fontSize:15, fontWeight:800, color:"#1C2B4A", margin:0 }}>
                Competency Map
                {comps.length>0 && <span style={{fontWeight:500, color:"#94A3B8", fontSize:13}}> · {comps.length}</span>}
              </p>
              {comps.length>0 && migStep==="idle" && (
                <span onClick={(e)=>{e.preventDefault(); e.stopPropagation(); runMigration();}}
                  style={{ fontSize:10.5, color:"#B8934A", background:"none",
                    border:"1px solid rgba(184,147,74,0.35)", borderRadius:7,
                    padding:"5px 11px", cursor:"pointer", fontWeight:600 }}>
                  ↻ Rebuild from resume
                </span>
              )}
            </summary>

            {migRescoreMsg && (
              <div style={{ fontSize:11.5, fontWeight:600, color:"#B8934A",
                background:"rgba(184,147,74,0.08)", border:"1px solid rgba(184,147,74,0.2)",
                borderRadius:9, padding:"8px 12px", marginBottom:12 }}>
                {migRescoreMsg}
              </div>
            )}

            {migError && (
              <div style={{ fontSize:12, fontWeight:500, color:"#B91C1C",
                background:"rgba(220,38,38,0.06)", border:"1px solid rgba(220,38,38,0.2)",
                borderRadius:10, padding:"11px 14px", marginBottom:12, lineHeight:1.5 }}>
                {migError}
              </div>
            )}

            {/* Empty state → the primary call to action */}
            {comps.length===0 && migStep==="idle" && (
              <div style={{ textAlign:"center", padding:"14px 8px 8px" }}>
                <div style={{ fontSize:34, marginBottom:10 }}>🗂️</div>
                <p style={{ fontSize:14, fontWeight:700, color:"#1C2B4A", margin:"0 0 5px" }}>
                  Turn your resume into career capital
                </p>
                <p style={{ fontSize:12.5, color:"#94A3B8", margin:"0 auto 16px", lineHeight:1.55, maxWidth:380 }}>
                  AI reads your profile and organises your experience into named
                  competencies backed by evidence. You review everything before it saves.
                </p>
                <button onClick={runMigration}
                  style={{ padding:"13px 26px", background:"#1C2B4A", color:"white",
                    border:"none", borderRadius:11, fontSize:14, fontWeight:700, cursor:"pointer",
                    boxShadow:"0 4px 14px rgba(28,43,74,0.28)" }}>
                  ✨ Build My Competency Map
                </button>
              </div>
            )}

            {migStep==="loading" && (
              <div style={{ textAlign:"center", padding:"28px 0", color:"#B8934A", fontSize:13 }}>
                <div style={{ fontSize:24, marginBottom:8 }}>⏳</div>
                Reading your resume and organising your experience…
              </div>
            )}

            {/* Migration review: editable proposal tiles */}
            {migStep==="review" && (<>
              <p style={{ fontSize:12.5, color:"#64748B", margin:"0 0 12px", lineHeight:1.5 }}>
                Found <b style={{color:"#1C2B4A"}}>{migProp.length} competencies</b> in your profile.
                Remove any that don't fit, edit names if needed, then save.
              </p>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(min(100%,250px),1fr))",
                gap:10, marginBottom:14 }}>
                {migProp.map((c,i)=>(
                  <div key={i} style={{ background:"#FFFDF7", border:"1px solid rgba(184,147,74,0.3)",
                    borderRadius:12, padding:"12px 13px", position:"relative" }}>
                    <span onClick={()=>setMigProp(p=>p.filter((_,xi)=>xi!==i))}
                      style={{ position:"absolute", top:8, right:10, cursor:"pointer",
                        fontSize:13, color:"#CBD5E1", lineHeight:1 }} title="Remove">✕</span>
                    <input value={c.name}
                      onChange={e=>setMigProp(p=>p.map((x,xi)=>xi===i?{...x,name:e.target.value}:x))}
                      style={{ fontSize:12.5, fontWeight:700, color:"#1C2B4A", border:"none",
                        background:"none", outline:"none", width:"88%", marginBottom:6, padding:0 }}/>
                    {(c.evidence||[]).slice(0,2).map((e,ei)=>(
                      <div key={ei} style={{ fontSize:11, color:"#64748B", lineHeight:1.45,
                        marginBottom:4, paddingLeft:8, borderLeft:"2px solid rgba(184,147,74,0.3)" }}>
                        {String(typeof e==="string"?e:(e.text||"")).substring(0,110)}
                        {String(typeof e==="string"?e:(e.text||"")).length>110?"…":""}
                      </div>
                    ))}
                    {(c.evidence||[]).length>2 && (
                      <div style={{ fontSize:10, color:"#B8934A" }}>+{(c.evidence||[]).length-2} more</div>
                    )}
                  </div>
                ))}
              </div>
              <div style={{ display:"flex", gap:8 }}>
                <button onClick={confirmMigration} disabled={!migProp.length}
                  style={{ flex:1, padding:"13px 0", background:"#16A34A", color:"white",
                    border:"none", borderRadius:11, fontSize:13.5, fontWeight:700, cursor:"pointer" }}>
                  ✓ Save {migProp.length} Competencies
                </button>
                <button onClick={()=>{setMigStep("idle");setMigProp([]);}}
                  style={{ padding:"13px 18px", background:"#F8FAFC", color:"#64748B",
                    border:"1px solid #E2E8F0", borderRadius:11, fontSize:12.5, cursor:"pointer" }}>
                  Cancel
                </button>
              </div>
            </>)}

            {migStep==="saving" && (
              <div style={{ textAlign:"center", padding:"22px 0", color:"#16A34A", fontSize:13 }}>
                Saving your competency map…
              </div>
            )}

            {/* Competency tiles */}
            {comps.length>0 && migStep==="idle" && (
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(min(100%,250px),1fr))",
                gap:10 }}>
                {comps.map((c,i)=>{
                  const ev = c.evidence||[];
                  const open = expandedComp===i;
                  return (
                    <div key={i} onClick={()=>setExpandedComp(open?null:i)}
                      style={{ background: open?"#FFFDF7":"#FAFBFD",
                        border:`1px solid ${open?"rgba(184,147,74,0.4)":"#EEF2F7"}`,
                        borderRadius:12, padding:"12px 13px", cursor:"pointer",
                        transition:"all 0.15s", position:"relative",
                        gridColumn: open?"1/-1":"auto" }}>
                      <div style={{ display:"flex", justifyContent:"space-between",
                        alignItems:"flex-start", gap:6 }}>
                        <span style={{ fontSize:12.5, fontWeight:700, color:"#1C2B4A",
                          lineHeight:1.3 }}>{c.name}</span>
                        <span style={{ fontSize:9.5, fontWeight:700, color:"#B8934A", flexShrink:0,
                          background:"rgba(184,147,74,0.12)", padding:"2px 8px", borderRadius:8 }}>
                          {ev.length}
                        </span>
                      </div>
                      {!open && ev[0] && (
                        <div style={{ fontSize:10.5, color:"#94A3B8", marginTop:6, lineHeight:1.4,
                          overflow:"hidden", display:"-webkit-box", WebkitLineClamp:2,
                          WebkitBoxOrient:"vertical" }}>
                          {(typeof ev[0]==="string"?ev[0]:ev[0].text||"").substring(0,100)}
                        </div>
                      )}
                      {open && (<>
                        {ev.map((e,ei)=>(
                          <div key={ei} style={{ fontSize:11.5, color:"#475569", marginTop:8,
                            lineHeight:1.55, paddingLeft:10, borderLeft:"2px solid rgba(184,147,74,0.35)" }}>
                            {typeof e==="string"?e:e.text}
                            <div style={{ fontSize:10, color:"#B8934A", marginTop:2 }}>
                              {e.source==="migration" ? "from your resume" : e.source ? `from ${e.source}` : ""}
                              {e.added_at ? ` · ${new Date(e.added_at).toLocaleDateString("en-AU",{day:"numeric",month:"short"})}` : ""}
                            </div>
                          </div>
                        ))}
                        <div onClick={async ev2=>{
                          ev2.stopPropagation();
                          if(!window.confirm(`Remove "${c.name}"?`)) return;
                          const r = await api(`/api/profile/competencies/${i}`, {method:"DELETE"});
                          if(r.ok){ setComps(prev=>prev.filter((_,xi)=>xi!==i)); setExpandedComp(null); }
                        }} style={{ fontSize:10.5, color:"#CBD5E1", marginTop:10, textAlign:"right", cursor:"pointer" }}>
                          🗑 Remove competency
                        </div>
                      </>)}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Gaps + growth, only when there are competencies */}
            {comps.length>0 && migStep==="idle" && openGaps.length>0 && (
              <div style={{ marginTop:14 }}>
                <p style={{ fontSize:10.5, fontWeight:700, color:"#94A3B8", margin:"0 0 6px",
                  textTransform:"uppercase", letterSpacing:0.6 }}>Open Gaps</p>
                <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                  {openGaps.map((gp,i)=>(
                    <span key={i} style={{ fontSize:11, color:"#64748B", background:"#F8FAFC",
                      border:"1px dashed #E2E8F0", borderRadius:14, padding:"4px 11px" }}>
                      ○ {(typeof gp==="string"?gp:gp.text||"").substring(0,55)}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {comps.length>0 && migStep==="idle" && growthLog.length>0 && (
              <div style={{ marginTop:12 }}>
                <p style={{ fontSize:10.5, fontWeight:700, color:"#94A3B8", margin:"0 0 6px",
                  textTransform:"uppercase", letterSpacing:0.6 }}>Recent Growth</p>
                {growthLog.slice(-4).reverse().map((lg,i)=>(
                  <div key={i} style={{ fontSize:11.5, color:"#15803D", padding:"2px 0" }}>
                    + {lg.competency} <span style={{color:"#94A3B8", fontSize:10.5}}>
                      {lg.source==="migration" ? "from resume" : (lg.source||"").substring(0,35)} · {lg.at ? new Date(lg.at).toLocaleDateString("en-AU",{day:"numeric",month:"short"}) : ""}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </details>

          {/* ═══ SECTION 2: Career chat + suggested questions ═══ */}
          <div style={{ background:"white", border:"1px solid #E8EDF4", borderRadius:16,
            padding:16, marginBottom:14, boxShadow:"0 2px 12px rgba(15,27,51,0.05)" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
              <p style={{ fontSize:14, fontWeight:800, color:"#1C2B4A", margin:0 }}>💬 Talk to your coach</p>
              <button onClick={()=>{window.location.hash='coach';}}
                style={{ fontSize:11, color:"#B8934A", background:"none", border:"1px solid rgba(184,147,74,0.35)",
                  borderRadius:7, padding:"4px 10px", cursor:"pointer", fontWeight:600 }}>
                Resume Studio →
              </button>
            </div>
            {chatMsgs.length===0 && (
              <p style={{ fontSize:12, color:"#94A3B8", margin:"0 0 10px", lineHeight:1.5 }}>
                Ask about your goals, gaps, or career direction. Tell me something new about your experience and I can add it to your profile.
              </p>
            )}
            {/* Suggested questions (folds in the old AI Profile Advisor) */}
            {chatMsgs.length===0 && (
              <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:12 }}>
                {advStep==="idle" && (
                  <button onClick={startAdvisor}
                    style={{ fontSize:11, color:"#5B21B6", background:"#F5F3FF", border:"1px solid #DDD6FE",
                      borderRadius:14, padding:"5px 11px", cursor:"pointer", fontWeight:600 }}>
                    ✨ Suggest questions to strengthen my profile
                  </button>
                )}
                {["What are my biggest skill gaps?","What roles am I strongest for?","How do I pivot to a new domain?"].map((q,i)=>(
                  <button key={i} onClick={()=>{setChatInput(q); setTimeout(()=>sendChat(),50);}}
                    style={{ fontSize:11, color:"#64748B", background:"#F8FAFC", border:"1px solid #E2E8F0",
                      borderRadius:14, padding:"5px 11px", cursor:"pointer" }}>
                    {q}
                  </button>
                ))}
              </div>
            )}
            {/* Advisor questions inline, when generated */}
            {advStep==="answering" && advQs.length>0 && (
              <div style={{ background:"#F5F3FF", border:"1px solid #DDD6FE", borderRadius:10, padding:12, marginBottom:12 }}>
                <p style={{ fontSize:12, fontWeight:700, color:"#5B21B6", margin:"0 0 8px" }}>Answer these to strengthen your profile</p>
                {advQs.map((q,i)=>(
                  <div key={i} style={{ marginBottom:8 }}>
                    <p style={{ fontSize:12, fontWeight:600, color:"#374151", margin:"0 0 4px" }}>{i+1}. {q}</p>
                    <VoiceSafeTextarea value={advAns[i]||""} onChange={e=>setAdvAns({...advAns,[i]:e.target.value})}
                      placeholder="Your answer (optional)"
                      style={{ width:"100%", height:52, padding:8, border:"1px solid #DDD6FE", borderRadius:8, fontSize:12.5, boxSizing:"border-box", background:"white" }}/>
                  </div>
                ))}
                <button onClick={submitAdvisor}
                  style={{ width:"100%", padding:10, background:"#6D28D9", color:"white", border:"none", borderRadius:8, cursor:"pointer", fontSize:13, fontWeight:600 }}>
                  Get profile suggestions
                </button>
              </div>
            )}
            {advStep==="loading" && <p style={{ fontSize:12, color:"#6D28D9", marginBottom:10 }}>Generating tailored questions…</p>}
            {advStep==="processing" && <p style={{ fontSize:12, color:"#6D28D9", marginBottom:10 }}>Strengthening your profile…</p>}
            {advStep==="reviewing" && advProp && (
              <div style={{ background:"#F0FDF4", border:"1px solid #BBF7D0", borderRadius:10, padding:12, marginBottom:12 }}>
                <p style={{ fontSize:12, fontWeight:700, color:"#15803D", margin:"0 0 6px" }}>Proposed profile updates</p>
                <p style={{ fontSize:12, color:"#374151", margin:"0 0 2px" }}>Skill areas: {Object.keys(profile.technicalSkills||{}).length} → {Object.keys(advProp.technicalSkills||{}).length}</p>
                <p style={{ fontSize:12, color:"#374151", margin:"0 0 2px" }}>Capabilities: {(profile.capabilities||[]).length} → {(advProp.capabilities||[]).length}</p>
                <p style={{ fontSize:12, color:"#374151", margin:"0 0 10px" }}>Achievements: {(profile.achievements||[]).length} → {(advProp.achievements||[]).length}</p>
                <div style={{ display:"flex", gap:8 }}>
                  <button onClick={approveAdvisor} style={{ flex:1, padding:9, background:"#16A34A", color:"white", border:"none", borderRadius:7, cursor:"pointer", fontSize:12.5, fontWeight:700 }}>Approve &amp; save</button>
                  <button onClick={()=>{ setAdvStep("idle"); setAdvProp(null); }} style={{ padding:"9px 14px", background:"white", border:"1px solid #E2E8F0", borderRadius:7, cursor:"pointer", fontSize:12.5, color:"#64748B" }}>Discard</button>
                </div>
              </div>
            )}
            {advStep==="saving" && <p style={{ fontSize:12, color:"#16A34A", marginBottom:10 }}>Saving…</p>}
            {advErr && <p style={{ fontSize:12, color:"#DC2626", marginBottom:8 }}>{advErr}</p>}
            {chatMsgs.length>0 && (
              <div style={{ maxHeight:260, overflowY:"auto", marginBottom:10 }}>
                {chatMsgs.map((m,i)=>(
                  <div key={i} style={{ fontSize:12, lineHeight:1.55, marginBottom:6,
                    padding:"8px 11px", borderRadius:9,
                    background:m.role==="user"?"#EEF2FF":"#F8FAFC",
                    border:"1px solid #EEF2F7", color:"#374151", whiteSpace:"pre-wrap" }}>
                    <b style={{ fontSize:10, color:m.role==="user"?"#4F46E5":"#B8934A" }}>
                      {m.role==="user"?"You":"Coach"}</b><br/>{m.content}
                  </div>
                ))}
                {chatBusy && <div style={{ fontSize:11, color:"#B8934A" }}>Coach is thinking…</div>}
              </div>
            )}
            <div style={{ display:"flex", gap:6 }}>
              <input value={chatInput} onChange={e=>setChatInput(e.target.value)}
                onKeyDown={e=>e.key==="Enter"&&sendChat()}
                placeholder="Ask your coach anything…"
                style={{ flex:1, padding:"10px 12px", border:"1px solid #E2E8F0", borderRadius:9,
                  fontSize:13, outline:"none", minHeight:40, boxSizing:"border-box" }}/>
              <button onClick={sendChat} disabled={chatBusy||!chatInput.trim()}
                style={{ padding:"10px 16px", background:chatBusy||!chatInput.trim()?"#E2E8F0":"#1C2B4A",
                  color:chatBusy||!chatInput.trim()?"#94A3B8":"white", border:"none", borderRadius:9,
                  fontSize:13, fontWeight:700, cursor:"pointer" }}>
                Send
              </button>
            </div>
          </div>

          {/* ═══ SECTION 3: Search targeting — collapsed by default ═══ */}
          <details style={{ background:"white", border:"1px solid #E8EDF4", borderRadius:16,
            padding:"14px 16px", marginBottom:14, boxShadow:"0 2px 12px rgba(15,27,51,0.05)" }}>
            <summary style={{ fontSize:14, fontWeight:800, color:"#1C2B4A", cursor:"pointer",
              listStyle:"none", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <span>🎯 Job Search Targeting</span>
              <span style={{ fontSize:11, fontWeight:500, color:"#94A3B8" }}>
                {titles.length + keywords.length > 0 ? `${titles.length} titles · ${keywords.length} domains` : "set up"}
              </span>
            </summary>
            <div style={{ marginTop:14 }}>
              {/* Target Job Titles */}
              <div style={{ background:"#F8FAFC", border:"1px solid #E2E8F0", borderRadius:10, padding:14, marginBottom:10 }}>
                <p style={{ fontSize:13, fontWeight:700, color:"#1C2B4A", margin:"0 0 4px" }}>Target Job Titles</p>
                <p style={{ fontSize:12, color:"#64748B", margin:"0 0 10px", lineHeight:1.4 }}>
                  Titles you're aiming for. These widen your search, they never limit it.
                </p>
                <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:10 }}>
                  {titles.length === 0 && <span style={{ fontSize:12, color:"#94A3B8" }}>None yet.</span>}
                  {titles.map((t,i)=>(
                    <span key={i} style={{ display:"inline-flex", alignItems:"center", gap:6, background:"#1C2B4A", color:"white", fontSize:12, padding:"4px 10px", borderRadius:14 }}>
                      {t}<span onClick={()=>removeTitle(t)} style={{ cursor:"pointer", fontWeight:700, opacity:0.8 }}>×</span>
                    </span>
                  ))}
                </div>
                <div style={{ display:"flex", gap:8 }}>
                  <input value={newTitle} onChange={e=>setNewTitle(e.target.value)} onKeyDown={e=>{ if(e.key==="Enter") addTitle(); }}
                    placeholder="e.g. Service Delivery Manager"
                    style={{ flex:1, padding:"9px 12px", border:"1px solid #CBD5E1", borderRadius:8, fontSize:13, boxSizing:"border-box" }} />
                  <button onClick={addTitle} style={{ padding:"9px 16px", background:"#1C2B4A", color:"white", border:"none", borderRadius:8, cursor:"pointer", fontSize:13, fontWeight:600 }}>Add</button>
                </div>
              </div>
              {/* Specialist Domains */}
              <div style={{ background:"#FFFBEB", border:"1px solid #FDE68A", borderRadius:10, padding:14 }}>
                <p style={{ fontSize:13, fontWeight:700, color:"#1C2B4A", margin:"0 0 4px" }}>Specialist Domains</p>
                <p style={{ fontSize:12, color:"#92400E", margin:"0 0 10px", lineHeight:1.4 }}>
                  Domains you specialise in (e.g. "Oracle"). Combined with your skillset when searching.
                </p>
                <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:10 }}>
                  {keywords.length === 0 && <span style={{ fontSize:12, color:"#A16207" }}>None yet.</span>}
                  {keywords.map((k,i)=>(
                    <span key={i} style={{ display:"inline-flex", alignItems:"center", gap:6, background:"#B8934A", color:"white", fontSize:12, padding:"4px 10px", borderRadius:14 }}>
                      {k}<span onClick={()=>removeKeyword(k)} style={{ cursor:"pointer", fontWeight:700, opacity:0.8 }}>×</span>
                    </span>
                  ))}
                </div>
                <div style={{ display:"flex", gap:8 }}>
                  <input value={newKw} onChange={e=>setNewKw(e.target.value)} onKeyDown={e=>{ if(e.key==="Enter") addKeyword(); }}
                    placeholder="e.g. Oracle, ERP, Cloud"
                    style={{ flex:1, padding:"9px 12px", border:"1px solid #FCD34D", borderRadius:8, fontSize:13, boxSizing:"border-box", background:"white" }} />
                  <button onClick={addKeyword} style={{ padding:"9px 16px", background:"#B8934A", color:"white", border:"none", borderRadius:8, cursor:"pointer", fontSize:13, fontWeight:600 }}>Add</button>
                </div>
                {kwSaving && <p style={{ fontSize:11, color:"#A16207", margin:"6px 0 0" }}>Saving...</p>}
              </div>
            </div>
          </details>

          {/* ═══ SECTION 5: Full resume details — collapsed by default ═══ */}
          <details style={{ background:"white", border:"1px solid #E8EDF4", borderRadius:16,
            padding:"14px 16px", marginBottom:14, boxShadow:"0 2px 12px rgba(15,27,51,0.05)" }}>
            <summary style={{ fontSize:14, fontWeight:800, color:"#1C2B4A", cursor:"pointer",
              listStyle:"none", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <span>📄 Full Resume Details</span>
              <span style={{ fontSize:11, fontWeight:500, color:"#94A3B8" }}>
                {(profile.experience||[]).length} roles
              </span>
            </summary>
            <div style={{ marginTop:14 }}>
              {profile.summary && (
                <div style={{ marginBottom:16 }}>
                  <p style={{ fontSize:12, fontWeight:700, color:"#1C2B4A", margin:"0 0 6px", textTransform:"uppercase", letterSpacing:1 }}>Summary</p>
                  <p style={{ fontSize:13, color:"#374151", lineHeight:1.5, margin:0 }}>{profile.summary}</p>
                </div>
              )}
              {(profile.experience||[]).length>0 && (
                <div style={{ marginBottom:16 }}>
                  <p style={{ fontSize:12, fontWeight:700, color:"#1C2B4A", margin:"0 0 8px", textTransform:"uppercase", letterSpacing:1 }}>Experience ({(profile.experience||[]).length} roles)</p>
                  {(profile.experience||[]).map((e,i)=>(
                    <div key={i} style={{ borderLeft:"3px solid #B8934A", paddingLeft:10, marginBottom:10 }}>
                      <p style={{ fontWeight:600, fontSize:13, margin:"0 0 1px" }}>{e.title||e.role||""}{(e.company||e.employer)?` — ${e.company||e.employer}`:""}</p>
                      <p style={{ fontSize:11, color:"#64748B", margin:"0 0 4px" }}>{e.dates||e.period||e.date||""}</p>
                      {(e.bullets||[]).slice(0,3).map((b,j)=>(
                        <p key={j} style={{ fontSize:12, color:"#475569", margin:"0 0 2px", paddingLeft:8 }}>• {typeof b==="string"?b:(b.text||b.bullet||"")}</p>
                      ))}
                    </div>
                  ))}
                </div>
              )}
              {(profile.certifications||[]).length>0 && (
                <div style={{ marginBottom:16 }}>
                  <p style={{ fontSize:12, fontWeight:700, color:"#1C2B4A", margin:"0 0 8px", textTransform:"uppercase", letterSpacing:1 }}>Certifications ({(profile.certifications||[]).length})</p>
                  {(profile.certifications||[]).map((c,i)=>(
                    <div key={i} style={{ fontSize:13, padding:"4px 0", borderBottom:"1px solid #F1F5F9", color:"#374151" }}>
                      {typeof c==="string"?c:`${c.name||c.title||""}${(c.issuer||c.status)?` — ${c.issuer||c.status}`:""}`}
                    </div>
                  ))}
                </div>
              )}
              {(profile.education||[]).length>0 && (
                <div style={{ marginBottom:16 }}>
                  <p style={{ fontSize:12, fontWeight:700, color:"#1C2B4A", margin:"0 0 8px", textTransform:"uppercase", letterSpacing:1 }}>Education ({(profile.education||[]).length})</p>
                  {(profile.education||[]).map((e,i)=>(
                    <div key={i} style={{ fontSize:13, padding:"4px 0", borderBottom:"1px solid #F1F5F9", color:"#374151" }}>
                      {typeof e==="string"?e:`${e.degree||e.title||e.qualification||""}${(e.institution||e.school)?` — ${e.institution||e.school}`:""}${e.year?` (${e.year})`:""}`}
                    </div>
                  ))}
                </div>
              )}
              {Object.keys(profile.technicalSkills||{}).length>0 && (
                <div style={{ marginBottom:16 }}>
                  <p style={{ fontSize:12, fontWeight:700, color:"#1C2B4A", margin:"0 0 8px", textTransform:"uppercase", letterSpacing:1 }}>Skills</p>
                  {Object.entries(profile.technicalSkills||{}).map(([cat,vals],i)=>(
                    <div key={i} style={{ fontSize:12, marginBottom:4 }}>
                      <span style={{ fontWeight:600, color:"#374151" }}>{cat}: </span>
                      <span style={{ color:"#64748B" }}>{typeof vals==="string"?vals:JSON.stringify(vals)}</span>
                    </div>
                  ))}
                </div>
              )}
              {(profile.achievements||[]).length>0 && (
                <div style={{ marginBottom:8 }}>
                  <p style={{ fontSize:12, fontWeight:700, color:"#1C2B4A", margin:"0 0 8px", textTransform:"uppercase", letterSpacing:1 }}>Key Achievements ({(profile.achievements||[]).length})</p>
                  {(profile.achievements||[]).map((a,i)=>(
                    <div key={i} style={{ background:"#F8FAFC", border:"1px solid #E2E8F0", borderRadius:6, padding:"8px 10px", marginBottom:6 }}>
                      <p style={{ fontWeight:600, fontSize:13, margin:"0 0 2px" }}>{a.title||""}</p>
                      <p style={{ fontSize:12, color:"#64748B", margin:0 }}>{a.body||a.description||""}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </details>

          {/* Upload new resume — quiet footer action */}
          <button onClick={() => { setStep("start"); setError(""); setText(""); }}
            style={{ width:"100%", padding:11, background:"none", border:"1px solid #CBD5E1", borderRadius:10, cursor:"pointer", fontSize:13, color:"#64748B" }}>
            Upload a new resume
          </button>
        </div>
      )}

      {/* ── LOADING ─────────────────────────────────────────────────────── */}
      {loadingMsg && (
        <div style={{ background:"#EFF6FF", border:"1px solid #BFDBFE", borderRadius:10, padding:20, textAlign:"center" }}>
          <p style={{ fontSize:24, margin:"0 0 8px" }}>⏳</p>
          <p style={{ fontSize:14, color:"#1D4ED8", fontWeight:600, margin:"0 0 4px" }}>{loadingMsg}</p>
          <p style={{ fontSize:12, color:"#64748B", margin:0 }}>Keep this screen open</p>
        </div>
      )}

      {/* ── EXTRACTED: show text preview ────────────────────────────────── */}
      {step === "extracted" && !loadingMsg && (
        <div>
          <div style={{ background:"#F0FDF4", border:"1px solid #BBF7D0", borderRadius:8, padding:12, marginBottom:12 }}>
            <p style={{ color:"#166534", fontWeight:600, fontSize:13, margin:"0 0 4px" }}>Resume text extracted ({text.length} characters)</p>
            <p style={{ color:"#15803D", fontSize:12, margin:0 }}>Looks good? Tap Build My Profile below.</p>
          </div>
          <VoiceSafeTextarea value={text} onChange={e=>setText(e.target.value)} readOnly={false}
            style={{ display:"block", width:"100%", height:160, fontFamily:"monospace", fontSize:11,
                     padding:10, border:"1px solid #E2E8F0", borderRadius:8,
                     boxSizing:"border-box", marginBottom:12, color:"#374151", background:"#F8FAFC" }} />
          <button onClick={convert}
            style={{ width:"100%", padding:13, background:"#1C2B4A", color:"white",
                     border:"none", borderRadius:8, cursor:"pointer", fontSize:15, fontWeight:600, marginBottom:8 }}>
            Build My Profile
          </button>
          <button onClick={() => { setStep("start"); setText(""); setError(""); }}
            style={{ width:"100%", padding:10, background:"none", border:"1px solid #CBD5E1",
                     borderRadius:8, cursor:"pointer", fontSize:13, color:"#64748B" }}>
            Try a different file
          </button>
        </div>
      )}

      {/* ── START: upload box ───────────────────────────────────────────── */}
      {step === "start" && !loadingMsg && (
        <div>
          <label style={{
            display:"flex", flexDirection:"column", alignItems:"center", gap:10,
            padding:"32px 20px", background:"#F8FAFC", border:"2px dashed #CBD5E1",
            borderRadius:12, cursor:"pointer", marginBottom:14
          }}>
            <span style={{ fontSize:40 }}>📄</span>
            <span style={{ fontWeight:700, fontSize:16, color:"#1C2B4A" }}>Upload Your Resume</span>
            <span style={{ fontSize:13, color:"#64748B" }}>PDF, Word (.docx), or text file</span>
            <span style={{ fontSize:12, color:"#94A3B8", background:"#E2E8F0", padding:"6px 16px", borderRadius:6 }}>
              Tap here to choose file
            </span>
            <input type="file" accept=".pdf,.docx,.doc,.txt" onChange={onFileChange} style={{ display:"none" }} />
          </label>
          <div style={{ textAlign:"center", color:"#94A3B8", fontSize:13, marginBottom:14 }}>or</div>
          <button onClick={() => { setStep("extracted"); setText(""); }}
            style={{ width:"100%", padding:11, background:"none", border:"1px solid #CBD5E1",
                     borderRadius:8, cursor:"pointer", fontSize:14, color:"#374151" }}>
            Paste resume text manually
          </button>
        </div>
      )}

      {/* ── PASTE mode ──────────────────────────────────────────────────── */}
      {step === "extracted" && !text && !loadingMsg && (
        <div style={{ marginTop:12 }}>
          <VoiceSafeTextarea value={text} onChange={e=>setText(e.target.value)}
            style={{ display:"block", width:"100%", height:220, fontFamily:"sans-serif", fontSize:13,
                     padding:12, border:"1px solid #d1d5db", borderRadius:8, boxSizing:"border-box" }}
            placeholder="Paste the full text from your resume here..." autoFocus />
          <button onClick={convert} disabled={text.trim().length < 100}
            style={{ width:"100%", padding:13, background:"#1C2B4A", color:"white",
                     border:"none", borderRadius:8, cursor:"pointer", fontSize:15, fontWeight:600,
                     marginTop:10, opacity: text.trim().length >= 100 ? 1 : 0.4 }}>
            Build My Profile
          </button>
        </div>
      )}

      {error && (
        <div style={{ background:"#FEF2F2", border:"1px solid #FECACA", borderRadius:8, padding:12, marginTop:12 }}>
          <p style={{ color:"#DC2626", fontSize:13, margin:0 }}>{error}</p>
        </div>
      )}
    </div>
    </div>
  );
}

function JobListRow({ job, selectMode, selected, onToggleSelect, onOpen, onArchive, onUnarchive, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const score = job.fit_score;
  const scoreColor = score>=8?"#22C55E":score>=6?"#C9A55A":score>=4?"#F59E0B":"#EF4444";
  const src = (job.source||"").toLowerCase();
  const isSeek = src.includes("seek");
  const _rawSalary = job.salary || job.salary_estimated || "";
  const salary = /not\s*applicable|^n\/?a$/i.test(_rawSalary.trim()) ? "" : _rawSalary;
  const rationale = job.rationale || job.fit_rationale || "";
  const kws = Array.isArray(job.keywords) ? job.keywords : [];
  const applied = job.apply_status === "applied";

  // ── Swipe (mobile): drag left past threshold = archive/unarchive ──
  const [dragX, setDragX] = useState(0);
  const touch = useRef({ x0: 0, y0: 0, active: false, horiz: false });
  const SWIPE_T = 72;
  const onTouchStart = (e) => {
    if (selectMode || expanded) return;
    const t = e.touches[0];
    touch.current = { x0: t.clientX, y0: t.clientY, active: true, horiz: false };
  };
  const [leaving, setLeaving] = useState(false);
  const onTouchMove = (e) => {
    if (!touch.current.active || leaving) return;
    const t = e.touches[0];
    const dx = t.clientX - touch.current.x0, dy = t.clientY - touch.current.y0;
    if (!touch.current.horiz && Math.abs(dx) < 12) return;
    if (!touch.current.horiz && Math.abs(dy) > Math.abs(dx)) { touch.current.active = false; return; }
    touch.current.horiz = true;
    const nx = Math.max(-110, Math.min(0, dx));  // left-drag only
    if (nx <= -SWIPE_T && !touch.current.buzzed) {
      touch.current.buzzed = true;
      try { navigator.vibrate && navigator.vibrate(12); } catch(err) {}
    }
    setDragX(nx);
  };
  const onTouchEnd = () => {
    if (!leaving && touch.current.horiz && dragX <= -SWIPE_T) {
      setLeaving(true);
      setDragX(-260);   // slide out, no snap-back - removal is instant (optimistic)
      job.is_archived ? onUnarchive(job.id) : onArchive(job.id);
    } else {
      setDragX(0);
    }
    touch.current = { x0: 0, y0: 0, active: false, horiz: false };
  };

  const rowClick = () => {
    if (touch.current.horiz) return;             // a swipe is not a tap
    if (selectMode) { onToggleSelect(); return; }
    setExpanded(e => !e);
  };

  const pill = (bg, col, txt) => (
    <span style={{fontSize:9.5,fontWeight:700,padding:"2px 7px",borderRadius:6,flexShrink:0,
      background:bg, color:col, whiteSpace:"nowrap"}}>{txt}</span>
  );

  return (
    <div style={{position:"relative", borderRadius:11}}>
      {dragX < -8 && (
        <div style={{position:"absolute", inset:0, borderRadius:11, display:"flex",
          alignItems:"center", justifyContent:"flex-end", paddingRight:18,
          background: dragX <= -SWIPE_T ? "rgba(184,147,74,0.35)" : "rgba(184,147,74,0.15)",
          color:"#B8934A", fontSize:12, fontWeight:800}}>
          {job.is_archived ? "↩ RESTORE" : "🗂 ARCHIVE"}
        </div>
      )}
    <div onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}
      style={{background: selected?"rgba(201,165,90,0.12)":"linear-gradient(145deg,#141D35,#111827)",
      border:`1px solid ${selected?"#C9A55A":expanded?"rgba(201,165,90,0.4)":"rgba(255,255,255,0.07)"}`,
      borderRadius:11, overflow:"hidden",
      transform:`translateX(${dragX}px)`, transition: dragX===0 ? "transform 0.18s ease" : "none"}}>

      {/* Collapsed row header (always visible) */}
      <div onClick={rowClick}
        style={{display:"flex", alignItems:"center", gap:11, padding:"10px 13px", cursor:"pointer"}}>
        {selectMode && (
          <div style={{width:20,height:20,borderRadius:5,flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",
            border:`2px solid ${selected?"#C9A55A":"#475569"}`, background: selected?"#C9A55A":"transparent",
            color:"#1A1206", fontSize:13, fontWeight:800}}>{selected?"\u2713":""}</div>
        )}
        <div style={{width:34,height:34,borderRadius:9,flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",
          fontSize:14,fontWeight:800,border:`2px solid ${scoreColor}`,color:scoreColor}}>
          {score ?? "\u2013"}
        </div>
        <div style={{flex:1, minWidth:0}}>
          <div style={{fontSize:13.5,fontWeight:700,color:"#EEF2FF",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
            {job.title || "Untitled role"}
          </div>
          <div style={{fontSize:11,color:"#8893A7",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
            {job.company_name || ""}{job.location?` \u00b7 ${job.location}`:""}{salary?` \u00b7 ${salary}`:""}
          </div>
        </div>
        {/* Compact badges */}
        <div style={{display:"flex", gap:5, alignItems:"center", flexShrink:0}}>
          {applied && pill("rgba(34,197,94,0.15)", "#4ADE80", "\u2713 Applied")}
          {job.is_archived && pill("rgba(100,116,139,0.15)", "#94A3B8", "Archived")}
          {src && pill(isSeek?"rgba(240,60,70,0.15)":"rgba(10,102,194,0.15)", isSeek?"#F87171":"#60A5FA", isSeek?"SEEK":"LinkedIn")}
          <span style={{fontSize:13, color:"#64748B", transition:"transform 0.15s", transform:expanded?"rotate(180deg)":"none"}}>{"\u25BE"}</span>
        </div>
      </div>

      {/* Expanded detail panel */}
      {expanded && !selectMode && (
        <div style={{padding:"0 13px 12px 13px", borderTop:"1px solid rgba(255,255,255,0.06)"}}>
          {rationale && (
            <div style={{fontSize:11.5, color:"#C7D2E8", lineHeight:1.5, margin:"10px 0"}}>
              <span style={{color:"#C9A55A", fontWeight:700}}>Why this score: </span>{rationale}
            </div>
          )}
          <div style={{display:"flex", flexWrap:"wrap", gap:6, margin:"8px 0"}}>
            {job.applicants_count && pill("rgba(249,115,22,0.12)", "#FB923C", `${job.applicants_count} applicants`)}
            {salary && pill("rgba(201,165,90,0.12)", "#C9A55A", salary)}
            {job.status && job.status!=="pending" && pill("rgba(59,130,246,0.12)", "#60A5FA", job.status)}
            {kws.slice(0,5).map((k,i)=>(
              <span key={i} style={{fontSize:9.5,fontWeight:600,padding:"2px 7px",borderRadius:6,
                background:"rgba(255,255,255,0.05)", color:"#8893A7"}}>{k}</span>
            ))}
          </div>
          <div style={{display:"flex", gap:7, marginTop:10, flexWrap:"wrap"}}>
            <button onClick={onOpen}
              style={{padding:"7px 14px", fontSize:12, fontWeight:700, borderRadius:8, cursor:"pointer",
                border:"none", background:"#C9A55A", color:"#1A1206"}}>
              Open full details {"\u2192"}
            </button>
            {job.is_archived
              ? <button onClick={()=>onUnarchive(job.id)}
                  style={{padding:"7px 12px", fontSize:12, fontWeight:600, borderRadius:8, cursor:"pointer",
                    border:"1px solid #334155", background:"transparent", color:"#8893A7"}}>Unarchive</button>
              : <button onClick={()=>onArchive(job.id)}
                  style={{padding:"7px 12px", fontSize:12, fontWeight:600, borderRadius:8, cursor:"pointer",
                    border:"1px solid #334155", background:"transparent", color:"#8893A7"}}>Archive</button>}
            <button onClick={()=>onDelete(job.id)}
              style={{padding:"7px 12px", fontSize:12, fontWeight:600, borderRadius:8, cursor:"pointer",
                border:"1px solid #4A2020", background:"transparent", color:"#F87171"}}>Delete</button>
          </div>
        </div>
      )}
    </div>
    </div>
  );
}

function JobCard({ job, idx, selectMode, selected, onToggleSelect, onStatusChange, onDelete, onArchive, onUnarchive, onEstimateSalary, onAutoApply, onApproveResume, onRebuildResume, onRefreshJob, pendingQuestions, onAnswerQuestion, applyUrlInput, onApplyUrlChange, onSaveApplyUrl, onReloadAll, getJobById, toast }) {
  const [showModal, setShowModal]     = useState(false);
  const [showPdf, setShowPdf]           = useState(false);
  const [template, setTemplate]       = useState(String(job.template || "4"));
  const [resumeApproving, setResumeApproving] = useState(false);
  const [rebuilding, setRebuilding] = useState(false);
  const [seniority, setSeniority] = useState(job.resume_seniority || "auto");
  const [savingSeniority, setSavingSeniority] = useState(false);
  // ── Phase 4: per-job insights ──
  const [matchRep, setMatchRep] = useState(null);
  const [matchBusy, setMatchBusy] = useState(false);
  const [prepPack, setPrepPack] = useState(null);
  const [prepBusy, setPrepBusy] = useState(false);
  const [prepErr, setPrepErr] = useState("");
  const [prepChecked, setPrepChecked] = useState(false);   // cached-lookup done?
  const [practice, setPractice] = useState(false);
  const [prIdx, setPrIdx] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [stage, setStage] = useState("role");              // role | you | questions | practice | research
  const [pfilter, setPfilter] = useState("all");
  const [researchTxt, setResearchTxt] = useState("");
  const [checkedRes, setCheckedRes] = useState({});
  const loadMatch = async () => {
    if (matchRep) { setMatchRep(null); return; }
    setMatchBusy(true);
    try { const d = await api(`/api/jobs/${job.id}/match-report`);
      if (d.ok) setMatchRep(d); } catch(e){}
    setMatchBusy(false);
  };
  const loadPrep = async (force, research) => {
    if (prepPack && !force) { setPrepPack(null); return; }
    setPrepBusy(true); setPrepErr("");
    try { const d = await api(`/api/jobs/${job.id}/prep-pack`, {method:"POST",
        body: JSON.stringify(force ? {force:true, ...(research?{research_context:research}:{})} : {})});
      if (d.ok) setPrepPack(d.pack);
      else setPrepErr(d.error || "Could not generate.");
    } catch(e){ setPrepErr("Generation failed - try again."); }
    setPrepBusy(false);
  };
  const rebuildProgress = useStagedProgress(rebuilding,
    ["Tailoring to this role", "Building the PDF", "Running quality checks", "Finalising"]);
  const [aiFeedback, setAiFeedback]   = useState("");
  const [manualApplyDate, setManualApplyDate] = useState(new Date().toISOString().split('T')[0]);
  const [clGenerating, setClGenerating] = useState(false);
  const [clMsg,        setClMsg]        = useState("");
  const [clPdfLoading, setClPdfLoading] = useState(false);
  const [clEditing,   setClEditing]   = useState(false);
  const [clEditText,  setClEditText]  = useState("");
  const [aiRefining, setAiRefining]   = useState(false);
  const refineProgress = useStagedProgress(aiRefining,
    ["Applying your feedback", "Rebuilding the PDF", "Running quality checks", "Finalising"]);
  const [aiRefineMsg, setAiRefineMsg] = useState("");
  const [gapStep,   setGapStep]   = useState("idle");
  const [gapData,   setGapData]   = useState(null);
  const [gapAns,    setGapAns]    = useState({});      // {i: answer text}
  const [gapProp,   setGapProp]   = useState(null);
  const [gapErr,    setGapErr]    = useState("");
  const [showGapDiff, setShowGapDiff] = useState(false);
  const [liveScore, setLiveScore] = useState(null);
  const preGapScore = useRef(null);
  const [gapTouched, setGapTouched] = useState(false);
  useEffect(() => {
    if (gapStep === "done" && gapTouched && liveScore != null &&
        preGapScore.current != null && liveScore !== preGapScore.current) {
      toast && toast(`🎯 Score updated ${preGapScore.current} → ${liveScore} after coaching`);
      setGapTouched(false);
    }
  }, [gapStep, liveScore]);
  const score = liveScore ?? (job.fit_score || 0);

  // ── Chat bubble styles for gap coach ──
  const coachBubble = {
    alignSelf:"flex-start", maxWidth:"90%", background:"#141F38",
    border:"1px solid #1E2A44", borderRadius:"16px 16px 16px 4px",
    padding:"11px 14px", fontSize:13, color:"#DCE4F5", lineHeight:1.55
  };
  const coachBubbleSm = {
    alignSelf:"flex-start", maxWidth:"85%", background:"#141F38",
    border:"1px solid #1E2A44", borderRadius:"14px 14px 14px 4px",
    padding:"8px 12px", fontSize:12, color:"#B8C4DC", lineHeight:1.5
  };
  const userBubble = {
    alignSelf:"flex-end", maxWidth:"90%",
    background:"linear-gradient(135deg,#C9A55A,#A8813E)", color:"white",
    borderRadius:"16px 16px 4px 16px", padding:"11px 14px", fontSize:13,
    lineHeight:1.55, whiteSpace:"pre-wrap"
  };
  const userBubbleSm = {
    alignSelf:"flex-end", maxWidth:"85%", background:"rgba(201,165,90,0.9)",
    color:"white", borderRadius:"14px 14px 4px 14px", padding:"8px 12px",
    fontSize:12, lineHeight:1.5, whiteSpace:"pre-wrap"
  };
  const coveredChip = {
    display:"inline-block", fontSize:11, color:"#4ADE80",
    background:"rgba(34,197,94,0.1)", border:"1px solid rgba(34,197,94,0.25)",
    borderRadius:12, padding:"4px 10px", margin:"2px 0"
  };
  const chipBtn = {
    fontSize:11, fontWeight:600, color:"#C9A55A", background:"rgba(201,165,90,0.1)",
    border:"1px solid rgba(201,165,90,0.25)", borderRadius:12, padding:"5px 11px",
    cursor:"pointer"
  };
  const ghostBtn = {
    fontSize:11, fontWeight:600, color:"#8893A7", background:"none",
    border:"1px solid #2A3654", borderRadius:10, padding:"7px 12px", cursor:"pointer"
  };
  const chatInput = {
    flex:1, padding:"9px 12px", border:"1px solid #2A3654", borderRadius:10,
    fontSize:12, outline:"none", background:"#0D1424", color:"#EEF2FF"
  };
  const sendBtn = {
    padding:"9px 14px", background:"linear-gradient(135deg,#C9A55A,#A8813E)",
    color:"white", border:"none", borderRadius:10, fontSize:11, fontWeight:700, cursor:"pointer"
  };
  // Phase 2 interactive gap state
  const [qState,      setQState]      = useState({});   // {i: {status:'open'|'followup'|'accepted'|'skipped'|'deeper', followup, history:[], polished, quality, encouragement}}
  const [qShowEx,     setQShowEx]     = useState({});   // {i: bool} example expanded
  const [qShowPrior,  setQShowPrior]  = useState({});   // {i: bool} prior answer expanded
  const [qEvaluating, setQEvaluating] = useState({});   // {i: bool}
  const [deepMsgs,    setDeepMsgs]    = useState({});   // {i: [{role,content}]}
  const [deepInput,   setDeepInput]   = useState("");
  const [deepSending, setDeepSending] = useState(false);
  const [cascadeMsg,  setCascadeMsg]  = useState("");
  const [gapModalOpen, setGapModalOpen] = useState(false);
  const [voiceActive, setVoiceActive] = useState(null);   // question index being dictated
  const recognitionRef = useRef(null);

  // ── Voice dictation (Web Speech API) ──
  const startVoice = (i) => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setGapErr("Voice input isn't supported in this browser. Try Chrome."); return; }
    // Stop any existing session
    if (recognitionRef.current) { try{recognitionRef.current.stop();}catch(e){} }
    const rec = new SR();
    rec.lang = "en-AU";
    rec.continuous = true;
    rec.interimResults = true;
    let finalText = gapAns[i] || "";
    rec.onresult = (ev) => {
      let interim = "";
      for (let k = ev.resultIndex; k < ev.results.length; k++) {
        const t = ev.results[k][0].transcript;
        if (ev.results[k].isFinal) finalText += (finalText && !finalText.endsWith(" ") ? " " : "") + t;
        else interim += t;
      }
      setGapAns(p => ({...p, [i]: (finalText + (interim ? " " + interim : "")).trim()}));
    };
    rec.onerror = () => { setVoiceActive(null); };
    rec.onend = () => { setVoiceActive(null); recognitionRef.current = null; };
    recognitionRef.current = rec;
    setVoiceActive(i);
    try { rec.start(); } catch(e) { setVoiceActive(null); }
  };
  const stopVoice = () => {
    if (recognitionRef.current) { try{recognitionRef.current.stop();}catch(e){} }
    setVoiceActive(null);
  };
  const gapRestored = useRef(false);

  // ── Persist gap state to backend (debounced) ──
  useEffect(() => {
    if (!gapRestored.current) return;               // don't save during restore
    if (gapStep==="idle" && !gapData) return;       // nothing to save
    const t = setTimeout(() => {
      api(`/api/jobs/${job.id}/gap-state`, { method:"PUT",
        body: JSON.stringify({ gap_state: {
          gapStep: gapStep==="processing"||gapStep==="saving" ? "answering" : gapStep,
          gapData, gapAns, qState, deepMsgs, savedAt: new Date().toISOString()
        }})
      }).catch(()=>{});
    }, 800);
    return () => clearTimeout(t);
  }, [gapStep, gapData, gapAns, qState, deepMsgs]);


  // Extra description (collapsible)
  const [showExtra,     setShowExtra]     = useState(false);
  // InMail state
  const [inmailLoading,  setInmailLoading]  = useState(false);
  const [inmailMsg,      setInmailMsg]      = useState("");
  const [showInmailEdit, setShowInmailEdit] = useState(false);
  const [inmailText,     setInmailText]     = useState(job.inmail || "");
  const [extraText,     setExtraText]     = useState(job.extra_description || "");
  const [extraLoading,  setExtraLoading]  = useState(false);
  const [extraMsg,      setExtraMsg]      = useState("");
  const [scoreLoading,  setScoreLoading]  = useState(false);
  const _rawSal = job.salary || job.salary_estimated || "";
  const sal   = /not\s*applicable|^n\/?a$/i.test(_rawSal.trim()) ? "" : _rawSal;

  const startGap = async () => {
    preGapScore.current = job.fit_score ?? null; setGapTouched(true);
    setGapStep("loading"); setGapErr("");
    try {
      const d = await api(`/api/jobs/${job.id}/gap-analysis`, { method:"POST" });
      if (d.ok) {
        setGapData(d); setGapAns({}); setQState({}); setQShowEx({});
        setGapStep((d.questions||[]).length ? "answering" : "allcovered");
      }
      else { setGapErr(d.error || "Could not analyse this role."); setGapStep("idle"); }
    } catch (e) { setGapErr("Error: " + e.message); setGapStep("idle"); }
  };

  // Evaluate one answer (adaptive: accept or 1-2 followups)
  const evaluateAnswer = async (i, force=false) => {
    const q = gapData.questions[i];
    const answer = (gapAns[i]||"").trim();
    if (!answer) return;
    setQEvaluating(p=>({...p,[i]:true}));
    const st = qState[i] || {history:[]};
    try {
      const d = await api(`/api/jobs/${job.id}/gap-answer`, { method:"POST",
        body: JSON.stringify({ question: q.question || q, answer,
          history: st.history || [], force }) });
      if (d.ok) {
        if (d.verdict === "accept") {
          setQState(p=>({...p,[i]:{...st, status:"accepted", polished:d.polished,
            quality:d.quality, encouragement:d.encouragement||""}}));
        } else {
          setQState(p=>({...p,[i]:{...st, status:"followup",
            followup:d.followup_question, encouragement:d.encouragement||"",
            history:[...(st.history||[]), {q: st.followup || (q.question||q), a: answer}]}}));
          setGapAns(p=>({...p,[i]:""}));  // clear box for the follow-up answer
        }
      } else setGapErr(d.error||"Evaluation failed");
    } catch(e){ setGapErr(e.message); }
    setQEvaluating(p=>({...p,[i]:false}));
  };

  // Skip with reason
  const skipQuestion = async (i, reason) => {
    const q = gapData.questions[i];
    await api(`/api/jobs/${job.id}/gap-skip`, { method:"POST",
      body: JSON.stringify({ question: q.question || q, reason }) });
    setQState(p=>({...p,[i]:{status:"skipped", reason}}));
  };

  // Go deeper: open coaching chat on one question
  const goDeeper = (i) => {
    const q = gapData.questions[i];
    const st = qState[i] || {};
    const seed = [{role:"user", content:`Here's my story so far: "${st.polished || gapAns[i] || "(nothing yet)"}". Ask me questions to make it stronger.`}];
    setDeepMsgs(p=>({...p,[i]:seed}));
    setQState(p=>({...p,[i]:{...st, status:"deeper"}}));
    sendDeeper(i, seed);
  };
  const sendDeeper = async (i, msgs) => {
    setDeepSending(true);
    const q = gapData.questions[i];
    try {
      const d = await api(`/api/jobs/${job.id}/gap-deeper`, { method:"POST",
        body: JSON.stringify({ question: q.question || q, messages: msgs }) });
      if (d.ok) setDeepMsgs(p=>({...p,[i]:[...msgs, {role:"assistant", content:d.reply}]}));
    } catch(e){ setGapErr(e.message); }
    setDeepSending(false);
  };
  const finishDeeper = async (i) => {
    // Synthesise the whole coaching conversation into one polished story
    const msgs = deepMsgs[i]||[];
    const q = gapData.questions[i];
    const st = qState[i]||{};
    setDeepSending(true);
    try {
      const d = await api(`/api/jobs/${job.id}/gap-deeper`, { method:"POST",
        body: JSON.stringify({ question: q.question || q,
          messages: [...msgs, {role:"user", content:
            "We're done. Write the FINAL polished version of my story as a single cohesive answer. " +
            "First person, specific, include the corrected numbers and details from our conversation. " +
            "No preamble, no 'here is', just the story itself. Max 120 words."}] }) });
      const polished = (d.ok && d.reply) ? d.reply.trim() : (st.polished || gapAns[i] || "");
      setQState(p=>({...p,[i]:{...st, status:"accepted", polished, quality:"strong"}}));
    } catch(e) {
      setQState(p=>({...p,[i]:{...st, status:"accepted",
        polished: st.polished || gapAns[i] || "", quality:"usable"}}));
    }
    setDeepSending(false);
  };

  // Commit all accepted answers -> competencies + profile merge proposal
  const commitGap = async () => {
    const qa = (gapData.questions||[]).map((q,i)=>({
      question: q.question || q,
      answer: (qState[i]?.status==="accepted" ? qState[i].polished : "")
    })).filter(x=>x.answer);
    if (!qa.length) { setGapErr("Accept at least one answer first."); return; }
    setGapStep("processing"); setGapErr("");
    try {
      const scoreBefore = job.fit_score;
      const d = await api(`/api/jobs/${job.id}/gap-commit`, { method:"POST",
        body: JSON.stringify({ qa }) });
      if (d.updated_profile) { setGapProp(d.updated_profile); setGapStep("reviewing"); }
      else { setGapErr(d.error || "Could not process."); setGapStep("answering"); }
      // PHASE 3 BRIDGE: the coaching input enriched the profile server-side. If this
      // job already had a resume, the server marked it 'stale'; fire exactly ONE
      // bounded rebuild so the new content actually lands in the tailored resume.
      if (d.resume_refreshing) {
        api(`/api/jobs/${job.id}/build-resume`, { method: "POST", body: JSON.stringify({ force: true }) }).catch(() => {});
        setCascadeMsg("✨ Applied to your profile. Refreshing this role's resume…");
      }
      // The pipeline re-score runs in a BACKGROUND THREAD server-side, so scores
      // aren't ready the instant commit returns. Show a live "updating" note, then
      // poll a few times and reload so the user actually SEES the new score + why.
      // Definitive score feedback: the server rescored THIS job synchronously and
      // returned its before/after, so we can show the exact change instead of racing
      // an 18s poll against a background cascade (which usually lost the race).
      const tjs = d.this_job_score;
      if (tjs && typeof tjs.new === "number") {
        const before = tjs.old, after = tjs.new;
        if (typeof before === "number" && after > before) {
          setCascadeMsg(`✨ Match for this role rose ${before} → ${after} (+${after - before}). ${tjs.rationale ? "Why: " + tjs.rationale : ""}`);
        } else if (typeof before === "number" && after < before) {
          setCascadeMsg(`This role's match moved ${before} → ${after}. ${tjs.rationale ? "Why: " + tjs.rationale : ""}`);
        } else {
          setCascadeMsg(`Match for this role held at ${after}. Your answer was saved and strengthens other roles too. ${tjs.rationale ? "Why: " + tjs.rationale : ""}`);
        }
        await onReloadAll();  // refresh the card so the number on screen matches
      } else {
        setCascadeMsg("✓ Answer saved. Updating your match scores across the pipeline…");
        await onReloadAll();
      }
      // Keep the message visible longer so it isn't missed.
      setTimeout(()=>setCascadeMsg(""), 20000);
    } catch (e) { setGapErr("Error: " + e.message); setGapStep("answering"); }
  };

  const approveGap = async () => {
    setGapStep("saving"); setGapErr("");
    try {
      // 1. Save the enriched profile (fast, ~1-2s). This is the only thing we WAIT for.
      await api("/api/profile/resume", { method:"POST", body: JSON.stringify({ resume_profile: gapProp }) });

      // 2. Kick off resume rebuild in the BACKGROUND (fire-and-forget, no await).
      //    The heavy Sonnet+WeasyPrint work must never block the UI.
      onRebuildResume(job.id).catch(()=>{});

      // 3. Show success IMMEDIATELY — the user is done, their profile is saved.
      setGapStep("done");
      setCascadeMsg("Re-scoring your pipeline in the background…");

      // 4. Fire the cascade WITHOUT awaiting it. When it returns, update scores quietly.
      //    This is the key fix: the UI never sits frozen waiting up to 120s.
      api(`/api/jobs/${job.id}/rescore-cascade`, { method:"POST" })
        .then(rc => {
          if (rc && rc.ok) {
            setCascadeMsg(`✨ Re-scored ${rc.rescored} jobs · ${rc.improved} improved`);
            const mine = (rc.details||[]).find(d=>d.id===job.id);
            if (mine && mine.new!=null) setLiveScore(mine.new);
            onReloadAll();   // refresh every card's score once ready
          } else {
            setCascadeMsg("Scores will update shortly.");
          }
        })
        .catch(()=>{ setCascadeMsg("Scores will update on next refresh."); });
    } catch (e) {
      setGapErr("Save failed: " + e.message); setGapStep("reviewing");
    }
  };

    const approve = () => { setShowModal(true); };

  const [showDetail, setShowDetail] = useState(window.location.hash === `#job-${job.id}`);
  // Open this card's detail whenever the hash points at it (e.g. "Open full details"
  // from the list view sets the hash; this card may already be mounted in tile view).
  useEffect(() => {
    const sync = () => {
      const h = window.location.hash;
      if (h === `#job-${job.id}`) setShowDetail(true);
      else if (h.startsWith('#job-') || h === '#dashboard') setShowDetail(false);
    };
    sync();
    window.addEventListener("hashchange", sync);
    return () => window.removeEventListener("hashchange", sync);
  }, [job.id]);
  // ── Restore gap state when detail opens; reset flag when closed ──
  useEffect(() => {
    if (!showDetail) {
      gapRestored.current = false;   // reset so next open re-fetches
      return;
    }
    if (gapRestored.current) return;
    (async () => {
      try {
        const d = await api(`/api/jobs/${job.id}/gap-state`);
        if (d.ok && d.gap_state) {
          const s = d.gap_state;
          // If processing/saving was interrupted, fall back to answering
          const step = (s.gapStep==="processing"||s.gapStep==="saving") ? "answering"
                     : (s.gapStep || "idle");
          // Only restore answering if we actually have questions
          const hasQ = s.gapData?.questions?.length > 0;
          if (step==="answering" && !hasQ) {
            setGapStep("idle");   // nothing to show, restart cleanly
          } else if (s.gapData) {
            setGapData(s.gapData);
            setGapAns(s.gapAns || {});
            setQState(s.qState || {});
            setDeepMsgs(s.deepMsgs || {});
            setGapStep(step);
            // Backfill: questions saved before the "Use this answer" feature have no
            // (or empty) prior_answer. Match the ACTUAL saved questions against stored
            // answers via a dedicated endpoint (order-preserving), so we don't depend on
            // a fresh AI run producing the same question wording.
            const qs = s.gapData?.questions || [];
            const needsBackfill = qs.length > 0 && qs.some(q => !q.prior_answer);
            if (needsBackfill) {
              try {
                const res = await api(`/api/jobs/${job.id}/gap-prior-answers`, {
                  method: "POST",
                  body: JSON.stringify({ questions: qs }),
                });
                const pas = res?.prior_answers || [];
                if (pas.length) {
                  setGapData(prev => {
                    if (!prev?.questions) return prev;
                    const merged = prev.questions.map((q, idx) => {
                      const pa = pas[idx];
                      if (pa && pa.prior_answer && !q.prior_answer) {
                        return { ...q, prior_answer: pa.prior_answer,
                                 prior_answer_source: pa.prior_answer_source };
                      }
                      return q;
                    });
                    return { ...prev, questions: merged };
                  });
                }
              } catch(e) {}
            }
          }
        }
      } catch(e) {}
      gapRestored.current = true;
    })();
  }, [showDetail]);
  const [detailTab, setDetailTab]   = useState("overview");
  useEffect(() => {
    if (!showDetail) return;
    try {
      if (sessionStorage.getItem("open_iv") === String(job.id)) {
        sessionStorage.removeItem("open_iv");
        setDetailTab("interview");
      }
    } catch(e) {}
  }, [showDetail]);
  // Interview tab opens -> load a CACHED pack automatically (GET never generates,
  // so no silent AI spend); generation stays behind an explicit button.
  useEffect(() => {
    if (detailTab !== "interview" || prepChecked) return;
    setPrepChecked(true);
    api(`/api/jobs/${job.id}/prep-pack`).then(d => {
      if (d && d.ok && d.pack) setPrepPack(d.pack);
    }).catch(()=>{});
  }, [detailTab]);

  // ── helper: copy to clipboard ───────────────────────────────────────────
  const copyText = (txt) => navigator.clipboard?.writeText(txt).catch(()=>{});

  // ── Close detail on back-swipe / ESC ────────────────────────────────────
  // (handled inline in JSX)

  return (
    <>
    {/* ═══════════════════════════════════════════════════════════
        TILE — compact list card, tapping opens full-page detail
        ═══════════════════════════════════════════════════════════ */}
    {!showDetail && (
      <div
        onClick={()=>{setShowDetail(true);window.location.hash=`job-${job.id}`;}}
        style={{
          background:"white", borderRadius:14, border:"1px solid #E8EDF4",
          borderLeft:`4px solid ${
            job.status==="approved"?"#22C55E":
            job.status==="not_suitable"?"#EF4444":
            job.status==="archived"?"#64748B":"#CBD5E1"
          }`,
          marginBottom:10, padding:"14px 14px 10px",
          cursor:"pointer", boxShadow:"0 1px 6px rgba(0,0,0,0.05)",
          WebkitTapHighlightColor:"transparent",
          transition:"box-shadow 0.15s"
        }}
      >
        {/* Row 1: Title + Score */}
        <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:4}}>
          <div style={{flex:1, paddingRight:8}}>
            <div style={{fontSize:14, fontWeight:700, color:"#1C2B4A", lineHeight:1.3,
              overflow:"hidden", display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical"}}>
              {job.title||"Unknown Role"}
            </div>
            <div style={{fontSize:10,color:"#94A3B8",fontWeight:500,marginTop:1}}>#{job.id}</div>
          </div>
          <div style={{
            minWidth:34, height:34, borderRadius:10, display:"flex", alignItems:"center",
            justifyContent:"center", fontSize:15, fontWeight:800, flexShrink:0,
            color:scoreColor(score), background:scoreBg(score), border:`1.5px solid ${scoreBorder(score)}`
          }}>{score||"?"}</div>
        </div>

        {/* Row 2: Company + Location + Source */}
        <div style={{display:"flex", alignItems:"center", gap:6, marginBottom:6, flexWrap:"wrap"}}>
          <span style={{fontSize:12, fontWeight:600, color:"#475569"}}>{job.company_name||""}</span>
          {job.location && <span style={{fontSize:11, color:"#94A3B8"}}>· {job.location.split(",")[0]}</span>}
          <span style={{fontSize:9, fontWeight:700, padding:"1px 6px", borderRadius:8,
            background:job.source==="seek"?"#16A34A":job.source==="indeed"?"#7C3AED":job.source==="other"?"#64748B":"#0A66C2",
            color:"white", letterSpacing:0.5}}>
            {job.source==="seek"?"SEEK":job.source==="indeed"?"Indeed":job.source==="other"?"Other":"LI"}
          </span>
          {job.manual&&<span style={{fontSize:9,fontWeight:700,padding:"1px 6px",borderRadius:8,
            background:"#1C2B4A",color:"#B8934A",letterSpacing:0.5}} title="Full job description attached">Full JD</span>}
          {job.listing_closed&&<span style={{fontSize:9,fontWeight:700,padding:"1px 6px",borderRadius:8,
            background:"#FEF2F2",color:"#DC2626",border:"1px solid #FECACA",letterSpacing:0.5}}>Closed</span>}
          {job.search_label && <span style={{fontSize:10,fontWeight:700,padding:"2px 7px",borderRadius:8,background:kwBadgeColor,color:"white",maxWidth:130,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>🔍 {job.search_label}</span>}
          {sal && <span style={{fontSize:11, color:"#B8934A", fontWeight:600}}>{sal}</span>}
        </div>

        {/* Row 3: Keywords + Status */}
        <div style={{display:"flex", alignItems:"center", gap:5, flexWrap:"wrap"}}>
          {(job.keywords||[]).slice(0,3).map((k,i)=>(
            <span key={i} style={{fontSize:10, background:"#F1F5F9", color:"#475569",
              padding:"2px 6px", borderRadius:8, border:"1px solid #E2E8F0"}}>{k}</span>
          ))}
          {job.resume_status==="pending_review" && <span style={{fontSize:10, background:"#FFFBEB", color:"#92400E", padding:"2px 6px", borderRadius:8, marginLeft:"auto"}}>📄 Review</span>}
          {job.resume_status==="approved" && <span style={{fontSize:10, background:"#F0FDF4", color:"#15803D", padding:"2px 6px", borderRadius:8, marginLeft:"auto"}}>✓ Resume</span>}
          {job.status==="approved" && !job.resume_status && <span style={{fontSize:10, color:"#64748B", marginLeft:"auto"}}>⚙ Building…</span>}
          {job.apply_status==="applied" && (
            <span style={{fontSize:10,fontWeight:700,background:"#F0FDF4",color:"#15803D",
              padding:"2px 8px",borderRadius:8,border:"1px solid #86EFAC",marginLeft:"auto"}}>
              ✅ Applied{job.apply_at?` ${new Date(job.apply_at).toLocaleDateString("en-AU",{day:"numeric",month:"short"})}`:""}
            </span>
          )}
        </div>

        {/* Meta + quick actions row */}
        <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap",marginTop:6}}>
          {job.created_at&&<span style={{fontSize:10,color:"#94A3B8"}}>➕ {relDate(job.created_at)}</span>}
          {job.posted_at&&<span style={{fontSize:10,color:"#94A3B8"}}>📅 {job.posted_at.length>20?relDate(job.posted_at):job.posted_at}</span>}
          {job.applicants_count&&<span style={{fontSize:10,color:"#94A3B8"}}>👥 {job.applicants_count}</span>}

          {/* Quick action buttons */}
          <div style={{display:"flex",gap:4,marginLeft:"auto"}} onClick={e=>e.stopPropagation()}>
            {job.status==="pending" && (
              <button onClick={e=>{e.stopPropagation(); window.location.hash=`job-${job.id}`; setShowDetail(true); setShowModal(true);}}
                style={{padding:"4px 10px",background:"#16A34A",color:"white",border:"none",
                  borderRadius:6,fontSize:11,fontWeight:700,cursor:"pointer"}} title="Approve">
                ✓
              </button>
            )}
            {job.status==="approved" && (
              <button onClick={e=>{e.stopPropagation();onStatusChange(job.id,"pending");}}
                style={{padding:"4px 10px",background:"#F1F5F9",color:"#64748B",
                  border:"1px solid #E2E8F0",borderRadius:6,fontSize:11,cursor:"pointer"}} title="Move back to Pending">
                ↩
              </button>
            )}
            {job.apply_status==="applied" && (
              <button onClick={e=>{e.stopPropagation();onStatusChange(job.id,"pending");}}
                style={{padding:"4px 10px",background:"#F1F5F9",color:"#64748B",
                  border:"1px solid #E2E8F0",borderRadius:6,fontSize:11,cursor:"pointer"}} title="Undo Applied, move back to Pending">
                ↩ Undo
              </button>
            )}
            <button onClick={e=>{e.stopPropagation();onArchive(job.id);}}
              style={{padding:"4px 10px",background:"#F1F5F9",color:"#64748B",
                border:"1px solid #E2E8F0",borderRadius:6,fontSize:11,cursor:"pointer"}} title="Archive">
              📁
            </button>
            <button onClick={e=>{e.stopPropagation();if(window.confirm("Delete this job?")){onDelete(job.id);}}}
              style={{padding:"4px 10px",background:"#FEF2F2",color:"#DC2626",
                border:"1px solid #FECACA",borderRadius:6,fontSize:11,cursor:"pointer"}} title="Delete">
              🗑
            </button>
          </div>
        </div>
      </div>
    )}

    {/* ═══════════════════════════════════════════════════════════
        FULL-PAGE DETAIL — entire viewport
        ═══════════════════════════════════════════════════════════ */}
    {showDetail && (
      <div style={{
        position:"fixed", inset:0, zIndex:999, background:"#F8FAFC",
        display:"flex", flexDirection:"column", overflowY:"hidden"
      }}>

        {/* ── HERO HEADER (navy) ─────────────────────────────────── */}
        <div style={{
          background:"linear-gradient(135deg,#1C2B4A 0%,#253660 100%)",
          padding:"12px 16px 14px", flexShrink:0,
          boxShadow:"0 2px 12px rgba(0,0,0,0.25)"
        }}>
          {/* Back + Title row */}
          <div style={{display:"flex", alignItems:"flex-start", gap:10, marginBottom:10}}>
            <button onClick={()=>{setShowDetail(false);if(window.location.hash===`#job-${job.id}`)window.location.hash='';}} style={{
              background:"rgba(255,255,255,0.12)", border:"1px solid rgba(255,255,255,0.2)",
              borderRadius:8, color:"white", padding:"6px 12px", cursor:"pointer",
              fontSize:13, fontWeight:600, flexShrink:0, marginTop:2
            }}>← Back</button>
            <div style={{flex:1, overflow:"hidden"}}>
              <div style={{fontSize:16, fontWeight:800, color:"white", lineHeight:1.3, marginBottom:2}}>
                {job.title||"Unknown Role"}
              </div>
              <div style={{fontSize:13, color:"#B8934A", fontWeight:600}}>{job.company_name||""}</div>
            </div>
            <div style={{
              minWidth:40, height:40, borderRadius:12, display:"flex", flexDirection:"column",
              alignItems:"center", justifyContent:"center", flexShrink:0,
              color:scoreColor(score), background:"rgba(255,255,255,0.95)",
              border:`2px solid ${scoreBorder(score)}`
            }}>
              <span style={{fontSize:16, fontWeight:900, lineHeight:1}}>{score||"?"}</span>
              <span style={{fontSize:8, color:"#94A3B8", fontWeight:500}}>FIT</span>
            </div>
          </div>

          {/* Meta pills row */}
          <div style={{display:"flex", gap:6, flexWrap:"wrap", alignItems:"center"}}>
            {job.location && (
              <span style={{fontSize:11, color:"rgba(255,255,255,0.75)", display:"flex", alignItems:"center", gap:3}}>
                📍 {job.location}
              </span>
            )}
            {sal && (
              <span style={{fontSize:11, background:"rgba(184,147,74,0.25)", color:"#FCD34D",
                padding:"2px 8px", borderRadius:8, fontWeight:600}}>
                💰 {sal}
              </span>
            )}
            {job.employment_type&&<span style={{fontSize:10,background:'rgba(255,255,255,0.1)',color:'rgba(255,255,255,0.8)',padding:'2px 7px',borderRadius:8}}>💼 {job.employment_type}</span>}
            {job.seniority_level&&<span style={{fontSize:10,background:'rgba(255,255,255,0.1)',color:'rgba(255,255,255,0.8)',padding:'2px 7px',borderRadius:8}}>📊 {job.seniority_level}</span>}
            {job.applicants_count&&<span style={{fontSize:10,background:'rgba(255,255,255,0.1)',color:'rgba(255,255,255,0.8)',padding:'2px 7px',borderRadius:8}}>👥 {job.applicants_count}</span>}
            {job.posted_at&&<span style={{fontSize:10,color:'rgba(255,255,255,0.5)'}}>📅 {job.posted_at.length>20?relDate(job.posted_at):job.posted_at}</span>}
            {job.created_at&&<span style={{fontSize:10,color:'rgba(255,255,255,0.5)'}}>➕ Added {relDate(job.created_at)}</span>}
            <span style={{fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:8,
              background:job.source==="seek"?"#16A34A":job.source==="indeed"?"#7C3AED":job.source==="other"?"#64748B":"#0A66C2",
              color:"white"}}>
              {job.source==="seek"?"SEEK":job.source==="indeed"?"Indeed":job.source==="other"?"Other":"LinkedIn"}
            </span>
            {job.manual&&<span style={{fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:8,
              background:"rgba(184,147,74,0.25)",color:"#B8934A"}} title="Full job description attached">Full JD</span>}
            {job.listing_closed&&<span style={{fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:8,
              background:"rgba(220,38,38,0.2)",color:"#F87171",border:"1px solid rgba(220,38,38,0.3)"}}>
              ⛔ Closed
            </span>}
            {job.listing_checked_at&&!job.listing_closed&&<span style={{fontSize:9,color:"rgba(255,255,255,0.35)",padding:"2px 6px"}}>
              ✓ checked {new Date(job.listing_checked_at).toLocaleDateString("en-AU",{day:"numeric",month:"short"})}
            </span>}
            <span style={{fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:8,
              background:
                job.status==="approved"?"rgba(34,197,94,0.2)":
                job.status==="not_suitable"?"rgba(239,68,68,0.2)":job.status==="archived"?"rgba(100,116,139,0.2)":"rgba(255,255,255,0.1)",
              color:
                job.status==="approved"?"#4ADE80":
                job.status==="not_suitable"?"#F87171":job.status==="archived"?"#94A3B8":"#FCD34D",
              border:`1px solid ${job.status==="approved"?"rgba(74,222,128,0.3)":job.status==="not_suitable"?"rgba(248,113,113,0.3)":"rgba(252,211,77,0.3)"}`
            }}>
              {job.status==="approved"?"✓ Approved":job.status==="interviewing"?"🎤 Interviewing":job.status==="applied"?"📨 Applied":job.status==="not_suitable"?"✗ Not Suitable":job.status==="archived"?"Archived":"⏳ Pending"}
            </span>
                {job.is_archived && job.status!=="not_suitable" && job.status!=="archived" && (
                  <span style={{fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:8,marginLeft:4,
                    background:"rgba(100,116,139,0.2)",color:"#94A3B8",border:"1px solid rgba(100,116,139,0.3)"}}>
                    📦 Archived
                  </span>
                )}
            {job.apply_status==="applied" && (
              <span style={{fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:8,
                background:"rgba(34,197,94,0.2)",color:"#4ADE80",border:"1px solid rgba(74,222,128,0.3)"}}>
                ✅ Applied{job.apply_at?` — ${new Date(job.apply_at).toLocaleDateString("en-AU",{day:"numeric",month:"short",year:"numeric"})}`:""}
              </span>
            )}
          </div>
        </div>

        {/* ── TAB BAR ────────────────────────────────────────────── */}
        <div style={{
          background:"white", borderBottom:"2px solid #E8EDF4",
          display:"flex", flexShrink:0
        }}>
          {[["overview","Overview"],["docs","📄 Docs"],["interview","🎤 Interview"]].map(([t,label])=>(
            <button key={t} onClick={()=>setDetailTab(t)} style={{
              flex:1, padding:"11px 4px", border:"none", background:"transparent",
              fontSize:12, fontWeight:detailTab===t?700:500, cursor:"pointer",
              color:detailTab===t?"#C9A55A":"#8893A7",
              borderBottom:detailTab===t?"2px solid #C9A55A":"2px solid transparent",
              marginBottom:-2, transition:"all 0.15s"
            }}>{label}</button>
          ))}
        </div>

        {/* ── SCROLLABLE CONTENT ─────────────────────────────────── */}
        <div style={{flex:1, overflowY:"auto", padding:"16px 14px", paddingBottom:90}}>

          {/* ────────── OVERVIEW TAB ────────── */}
          {detailTab==="overview" && (<>
            {/* Match quality card */}
            <div style={{background:"white", borderRadius:12, border:"1px solid #E8EDF4",
              padding:14, marginBottom:12, boxShadow:"0 1px 4px rgba(0,0,0,0.04)"}}>
              <div style={{display:"flex", alignItems:"center", gap:8, marginBottom:10}}>
                <div style={{flex:1, height:8, borderRadius:8, background:"#F1F5F9", overflow:"hidden"}}>
                  <div style={{height:"100%", borderRadius:8, width:`${Math.min(100,(score||0)*10)}%`,
                    background:score>=8?"#22C55E":score>=6?"#F59E0B":"#EF4444"}}/>
                </div>
                <span style={{fontSize:12, fontWeight:700, color:scoreColor(score)}}>{score}/10 match</span>
              </div>
              {job.rationale && <div style={{fontSize:13, color:"#475569", lineHeight:1.5}}>{job.rationale}</div>}
            </div>

            {/* Keywords */}
            {(job.keywords||[]).length>0 && (
              <div style={{background:"white", borderRadius:12, border:"1px solid #E8EDF4",
                padding:14, marginBottom:12}}>
                <div style={{fontSize:11, fontWeight:700, color:"#94A3B8", letterSpacing:0.8,
                  textTransform:"uppercase", marginBottom:8}}>Matching Keywords</div>
                <div style={{display:"flex", flexWrap:"wrap", gap:6}}>
                  {(job.keywords||[]).map((k,i)=>(
                    <span key={i} style={{background:"#EFF6FF", color:"#1D4ED8", fontSize:12,
                      padding:"4px 10px", borderRadius:20, border:"1px solid #BFDBFE", fontWeight:500}}>{k}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Job description */}
            <div style={{background:"white", borderRadius:12, border:"1px solid #E8EDF4",
              padding:14, marginBottom:12}}>
              <div style={{fontSize:11, fontWeight:700, color:"#94A3B8", letterSpacing:0.8,
                textTransform:"uppercase", marginBottom:10}}>Job Description</div>
              <div style={{fontSize:13, color:"#374151", lineHeight:1.65,
                whiteSpace:"pre-wrap", wordBreak:"break-word"}}>
                {(job.description||"No description available.")}
              </div>
            </div>

            {/* ── Extra / Extended Position Description (collapsible) ── */}
            <div style={{background:"white", borderRadius:12, marginBottom:12,
              border:`1px solid ${job.extra_description||extraText.trim()?"#86EFAC":"#E8EDF4"}`}}>
              {/* Toggle header */}
              <div onClick={()=>setShowExtra(p=>!p)}
                style={{display:"flex",alignItems:"center",gap:8,padding:"12px 14px",cursor:"pointer"}}>
                <span style={{fontSize:11,fontWeight:700,letterSpacing:0.8,textTransform:"uppercase",flex:1,
                  color:job.extra_description||extraText.trim()?"#15803D":"#94A3B8"}}>
                  📋 Extended Position Description
                  {(job.extra_description||extraText.trim()) && " ✓"}
                </span>
                <span style={{fontSize:11,color:"#94A3B8"}}>{showExtra?"▲":"▼"}</span>
              </div>

              {showExtra && (
                <div style={{padding:"0 14px 14px"}}>
                  <div style={{fontSize:12,color:"#94A3B8",marginBottom:8,lineHeight:1.5}}>
                    Paste full JD from a PDF or internal document. Used by AI for resume, cover letter, and gap analysis.
                  </div>
                  <VoiceSafeTextarea
                    value={extraText}
                    onChange={e=>setExtraText(e.target.value)}
                    placeholder="Paste extended position description, selection criteria, or any additional role information here..."
                    style={{width:"100%",height:180,padding:"10px 12px",
                      border:"1.5px solid #CBD5E1",borderRadius:10,fontSize:12,
                      lineHeight:1.65,boxSizing:"border-box",resize:"vertical",
                      color:"#374151",outline:"none",fontFamily:"inherit"}}
                  />
                  <div style={{display:"flex",gap:8,marginTop:8,alignItems:"center",flexWrap:"wrap"}}>
                    <span style={{fontSize:11,color:"#94A3B8",flex:1}}>
                      {extraText.length.toLocaleString()} chars
                      {job.extra_description ? " · Previously saved" : ""}
                    </span>

                    {/* PDF upload */}
                    <label style={{padding:"7px 12px",background:"#F8FAFC",border:"1px solid #E8EDF4",
                      borderRadius:8,cursor:"pointer",fontSize:11,fontWeight:600,color:"#475569",
                      display:"flex",alignItems:"center",gap:4}}>
                      📄 Upload PDF
                      <input type="file" accept=".pdf" style={{display:"none"}}
                        onChange={async e=>{
                          const file=e.target.files?.[0]; if(!file) return;
                          e.target.value="";  // allow re-selecting the same file after a failure
                          if(file.size > 15*1024*1024){
                            setExtraMsg("❌ PDF is over 15MB. Please use a smaller file."); return;
                          }
                          setExtraLoading(true); setExtraMsg("Uploading 0%…");
                          const form=new FormData(); form.append("pdf",file);
                          try {
                            const d = await uploadWithProgress(`/api/jobs/${job.id}/extra-description-pdf`, form,
                              (pct, phase)=>setExtraMsg(phase==="uploading" ? `Uploading ${pct}%…` : "Extracting text from PDF…"));
                            if(d && d.ok){
                              setExtraText(d.text||"");
                              job.extra_description=d.text||"";
                              setExtraMsg(`✅ Extracted ${(d.chars||0).toLocaleString()} chars. Tap Save to keep it.`);
                            } else {
                              setExtraMsg("❌ "+((d&&d.error)||"Extraction failed. Try again or paste the text instead."));
                            }
                          } catch(err){
                            setExtraMsg("❌ "+err.message);
                          }
                          setExtraLoading(false);
                        }}
                      />
                    </label>

                    {/* Save button */}
                    <button onClick={async()=>{
                      setExtraLoading(true); setExtraMsg("");
                      const r=await api(`/api/jobs/${job.id}/extra-description`,
                        {method:"PUT",body:JSON.stringify({extra_description:extraText})});
                      if(r.ok){
                        job.extra_description=extraText;
                        setExtraMsg("✅ Saved — AI will use this for all analysis");
                      } else { setExtraMsg("❌ Save failed"); }
                      setExtraLoading(false);
                    }} disabled={extraLoading}
                      style={{padding:"7px 16px",background:"#1C2B4A",color:"white",border:"none",
                        borderRadius:8,fontSize:12,fontWeight:700,cursor:"pointer",minHeight:36}}>
                      {extraLoading?"…":"Save"}
                    </button>
                  </div>
                  {extraMsg && <p style={{fontSize:12,marginTop:6,
                    color:extraMsg.startsWith("✅")?"#15803D":"#DC2626"}}>{extraMsg}</p>}
                </div>
              )}
            </div>

            {/* View original */}
            {job.link && (
              <a href={job.link} target="_blank" rel="noreferrer" style={{display:"block"}}>
                <button style={{width:"100%", padding:12, background:"#F8FAFC",
                  border:"1px solid #E8EDF4", borderRadius:12, color:"#1C2B4A", fontSize:13,
                  fontWeight:600, cursor:"pointer"}}>
                  🔗 View Original Posting
                </button>
              </a>
            )}
            {/* ── Phase 4: Match report + Interview prep ── */}
            <div style={{display:"flex", gap:8, marginBottom:12}}>
              <button onClick={loadMatch} disabled={matchBusy} style={{flex:1, padding:"10px 8px",
                background: matchRep?"rgba(184,147,74,0.18)":"#0D1424", color:"#EEF2FF",
                border:"1px solid #1E2A44", borderRadius:12, fontSize:12.5, fontWeight:700, cursor:"pointer"}}>
                {matchBusy?"Checking…":"📊 Match report"}
              </button>
              <button onClick={()=>setDetailTab("interview")} style={{flex:1, padding:"10px 8px",
                background:"#0D1424", color:"#EEF2FF",
                border:"1px solid #1E2A44", borderRadius:12, fontSize:12.5, fontWeight:700, cursor:"pointer"}}>
                🎤 Interview prep →
              </button>
            </div>
            {matchRep && (
              <div style={{background:"#0D1424", border:"1px solid #1E2A44", borderRadius:14,
                padding:"14px 16px", marginBottom:12}}>
                <div style={{display:"flex", alignItems:"center", gap:10, marginBottom:8}}>
                  <div style={{fontSize:12.5, fontWeight:800, color:"#EEF2FF"}}>Keyword coverage</div>
                  <div style={{flex:1, height:7, background:"#1E2A44", borderRadius:4, overflow:"hidden"}}>
                    <div style={{width:`${matchRep.coverage}%`, height:"100%",
                      background: matchRep.coverage>=70?"#22C55E":matchRep.coverage>=40?"#C9A55A":"#F59E0B"}}/>
                  </div>
                  <div style={{fontSize:13, fontWeight:800, color:"#B8934A"}}>{matchRep.coverage}%</div>
                </div>
                {matchRep.matched.length>0 && <div style={{marginBottom:6}}>
                  {matchRep.matched.map(k=><span key={k} style={{display:"inline-block", fontSize:10.5,
                    padding:"3px 8px", margin:"0 5px 5px 0", borderRadius:7,
                    background:"rgba(34,197,94,0.14)", color:"#4ADE80"}}>✓ {k}</span>)}
                </div>}
                {matchRep.missing.length>0 && <div style={{marginBottom:6}}>
                  {matchRep.missing.map(k=><span key={k} style={{display:"inline-block", fontSize:10.5,
                    padding:"3px 8px", margin:"0 5px 5px 0", borderRadius:7,
                    background:"rgba(245,158,11,0.13)", color:"#FBBF24"}}>○ {k}</span>)}
                </div>}
                <div style={{fontSize:11, color:"#8893A7", lineHeight:1.5}}>{matchRep.tip}</div>
              </div>
            )}
            {/* ── Gap Analysis (moved here from AI tab) ── */}
            {/* ── Skill Gap Analysis — launcher + modal ── */}
            {(job.status==="pending"||job.status==="approved") && (<>
              {/* Compact inline launcher */}
              <div style={{background:"#0D1424", borderRadius:14, border:"1px solid #1E2A44",
                padding:"14px 16px", marginBottom:12, display:"flex", alignItems:"center", gap:12}}>
                <div style={{width:36, height:36, borderRadius:11, flexShrink:0,
                  background:"linear-gradient(135deg,#C9A55A,#A8813E)", display:"flex",
                  alignItems:"center", justifyContent:"center", fontSize:18}}>🎯</div>
                <div style={{flex:1, minWidth:0}}>
                  <div style={{fontSize:13, fontWeight:700, color:"#EEF2FF"}}>Gap Coach</div>
                  <div style={{fontSize:11, color:"#6B7A99"}}>
                    {gapStep==="idle" ? "Check your fit for this role" :
                     gapStep==="done" ? "✓ Complete · tap to review" :
                     gapStep==="allcovered" ? "✓ You're fully covered" :
                     gapStep==="reviewing" ? "Review pending updates" :
                     (gapData?.questions||[]).length>0
                       ? `${Object.values(qState).filter(s=>s?.status==="accepted").length}/${(gapData.questions||[]).length} answered · in progress`
                       : "In progress"}
                  </div>
                </div>
                <button onClick={()=>{ setGapModalOpen(true); if(gapStep==="idle") startGap(); }}
                  style={{padding:"10px 18px", background:"linear-gradient(135deg,#C9A55A,#A8813E)",
                    color:"white", border:"none", borderRadius:11, cursor:"pointer", fontSize:12.5,
                    fontWeight:700, whiteSpace:"nowrap", flexShrink:0,
                    boxShadow:"0 2px 10px rgba(201,165,90,0.3)"}}>
                  {gapStep==="idle" ? "Start →" : "Open →"}
                </button>
              </div>

              {/* Full-screen modal */}
              {gapModalOpen && (
                <div onClick={()=>setGapModalOpen(false)}
                  style={{position:"fixed", inset:0, zIndex:300, background:"rgba(4,6,15,0.75)",
                    backdropFilter:"blur(6px)", WebkitBackdropFilter:"blur(6px)",
                    display:"flex", alignItems:"center", justifyContent:"center", padding:"16px"}}>
                  <div onClick={e=>e.stopPropagation()}
                    style={{width:"100%", maxWidth:680, maxHeight:"92vh", display:"flex",
                      flexDirection:"column", borderRadius:20, overflow:"hidden",
                      boxShadow:"0 20px 80px rgba(0,0,0,0.6)"}}>
                    <div style={{background:"#0D1424", flex:1, minHeight:0, display:"flex",
                flexDirection:"column", overflow:"hidden"}}>

                {/* Chat header */}
                <div style={{background:"linear-gradient(135deg,#141F38,#0D1424)", padding:"13px 16px",
                  borderBottom:"1px solid #1E2A44", display:"flex", alignItems:"center", gap:10, flexShrink:0}}>
                  <div style={{width:32, height:32, borderRadius:10,
                    background:"linear-gradient(135deg,#C9A55A,#A8813E)", display:"flex",
                    alignItems:"center", justifyContent:"center", fontSize:16, flexShrink:0}}>🎯</div>
                  <div style={{flex:1, minWidth:0}}>
                    <div style={{fontSize:13, fontWeight:700, color:"#EEF2FF"}}>Gap Coach</div>
                    <div style={{fontSize:10.5, color:"#6B7A99"}}>
                      {gapStep==="idle" ? "Ready to review this role" :
                       gapStep==="loading" ? "Analysing…" :
                       gapStep==="allcovered" ? "You're covered" :
                       gapStep==="done" ? "Complete" :
                       gapStep==="reviewing" ? "Review updates" :
                       `${Object.values(qState).filter(s=>s?.status==="accepted").length} of ${(gapData?.questions||[]).length} answered`}
                    </div>
                  </div>
                  {gapStep==="answering" && (gapData?.questions||[]).length>0 && (
                    <div style={{display:"flex", gap:3}}>
                      {(gapData.questions||[]).map((_,i)=>{
                        const s = qState[i]?.status;
                        return <div key={i} style={{width:6, height:6, borderRadius:"50%",
                          background: s==="accepted"?"#4ADE80": s==="skipped"?"#4E5A70":"#C9A55A",
                          opacity: s?1:0.35}}/>;
                      })}
                    </div>
                  )}
                </div>

                {/* Chat body */}
                <div style={{padding:"16px 18px", minHeight:0, flex:1, overflowY:"auto",
                  WebkitOverflowScrolling:"touch",
                  display:"flex", flexDirection:"column", gap:12,
                  background:"radial-gradient(ellipse at top,rgba(201,165,90,0.04),transparent 60%)"}}>

                  {/* IDLE — coach invites */}
                  {(gapStep==="idle" || (gapStep==="answering" && !gapData)) && (<>
                    <div style={coachBubble}>
                      Want me to check how your profile stacks up against this role? I'll spot what you've
                      already got covered and ask about anything worth strengthening.
                    </div>
                    <button onClick={startGap} style={{alignSelf:"flex-end", padding:"11px 20px",
                      background:"linear-gradient(135deg,#C9A55A,#A8813E)", color:"white", border:"none",
                      borderRadius:"16px 16px 4px 16px", cursor:"pointer", fontSize:13, fontWeight:700,
                      boxShadow:"0 2px 10px rgba(201,165,90,0.3)"}}>
                      Analyse this role →
                    </button>
                  </>)}

                  {/* LOADING */}
                  {gapStep==="loading" && (
                    <div style={coachBubble}>
                      <span style={{display:"inline-flex", gap:4, alignItems:"center"}}>
                        Reading the role and comparing to your profile
                        <span className="gap-dots" style={{color:"#C9A55A"}}>…</span>
                      </span>
                    </div>
                  )}

                  {/* ALL COVERED */}
                  {gapStep==="allcovered" && gapData && (<>
                    <div style={coachBubble}>
                      <div style={{fontSize:22, marginBottom:6}}>🎯</div>
                      Great news, your profile already covers what this role needs. Nothing to add right now.
                    </div>
                    {(gapData.covered||[]).length>0 && (
                      <div style={{alignSelf:"flex-start", maxWidth:"90%"}}>
                        {(gapData.covered||[]).map((c,i)=>(
                          <div key={i} style={coveredChip}>
                            ✓ {c.requirement} <span style={{opacity:0.55}}>· {c.competency}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </>)}

                  {/* ANSWERING — the conversation */}
                  {(gapStep==="answering"||gapStep==="processing") && gapData && (<>

                    {/* Coach opener: what's covered */}
                    {((gapData.matched||[]).length>0 || (gapData.covered||[]).length>0) && (
                      <div style={coachBubble}>
                        Here's what you've already got locked in for this role:
                        <div style={{marginTop:8, display:"flex", flexWrap:"wrap", gap:5}}>
                          {(gapData.matched||[]).map((m,i)=>(
                            <span key={"m"+i} style={coveredChip}>✓ {m}</span>
                          ))}
                          {(gapData.covered||[]).map((c,i)=>(
                            <span key={"c"+i} style={coveredChip}>✓ {c.requirement}</span>
                          ))}
                        </div>
                        {(gapData.questions||[]).length>0 && (
                          <div style={{marginTop:10, paddingTop:10, borderTop:"1px solid rgba(255,255,255,0.08)",
                            fontSize:12.5, color:"#B8C4DC"}}>
                            {(gapData.questions||[]).length===1
                              ? "Just one thing I'd love to hear more about:"
                              : `A few things worth adding, ${(gapData.questions||[]).length} in total. Let's take them one at a time:`}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Each question as a conversation turn */}
                    {gapData.questions.map((q,i)=>{
                      const st = qState[i] || {};
                      const isObj = typeof q === "object";
                      const qText = isObj ? q.question : q;

                      // Skipped — quiet chip
                      if (st.status==="skipped") return (
                        <div key={i} style={{alignSelf:"center", fontSize:11, color:"#4E5A70",
                          background:"rgba(255,255,255,0.03)", padding:"5px 12px", borderRadius:12,
                          border:"1px dashed #2A3654"}}>
                          ⏭ Skipped this one
                        </div>
                      );

                      // Accepted — coach's question + your answer bubble + confirmation
                      if (st.status==="accepted") return (
                        <div key={i} style={{display:"flex", flexDirection:"column", gap:8}}>
                          <div style={coachBubbleSm}>{qText}</div>
                          <div style={userBubble}>{st.polished}</div>
                          <div style={{alignSelf:"flex-end", display:"flex", alignItems:"center", gap:8}}>
                            <span style={{fontSize:11, color:"#4ADE80", fontWeight:600}}>
                              {st.quality==="strong"?"✅ Strong":"✓ Saved"}
                            </span>
                            <button onClick={()=>goDeeper(i)} style={ghostBtn}>💬 Go deeper</button>
                          </div>
                        </div>
                      );

                      // Deeper — inline coaching thread
                      if (st.status==="deeper") return (
                        <div key={i} style={{background:"rgba(201,165,90,0.06)", border:"1px solid rgba(201,165,90,0.2)",
                          borderRadius:14, padding:12}}>
                          <div style={{fontSize:10.5, fontWeight:700, color:"#C9A55A", marginBottom:8,
                            textTransform:"uppercase", letterSpacing:0.5}}>💬 Deep dive</div>
                          <div style={{display:"flex", flexDirection:"column", gap:8, maxHeight:240,
                            overflowY:"auto", marginBottom:10}}>
                            {(deepMsgs[i]||[]).map((m,mi)=>(
                              <div key={mi} style={m.role==="user"?userBubbleSm:coachBubbleSm}>{m.content}</div>
                            ))}
                            {deepSending && <div style={{...coachBubbleSm, opacity:0.7}}>
                              <span className="gap-dots">…</span></div>}
                          </div>
                          <div style={{display:"flex", gap:6}}>
                            <input value={deepInput} onChange={e=>setDeepInput(e.target.value)}
                              onKeyDown={e=>{if(e.key==="Enter"&&deepInput.trim()){
                                const msgs=[...(deepMsgs[i]||[]),{role:"user",content:deepInput.trim()}];
                                setDeepMsgs(p=>({...p,[i]:msgs})); setDeepInput(""); sendDeeper(i,msgs);
                              }}}
                              placeholder="Reply…"
                              style={chatInput}/>
                            <button onClick={()=>finishDeeper(i)} style={sendBtn}>Done</button>
                          </div>
                        </div>
                      );

                      // Active question (open or followup) — only show the FIRST unanswered
                      const firstActive = gapData.questions.findIndex((_,j)=>{
                        const s = qState[j]?.status;
                        return s!=="accepted" && s!=="skipped";
                      });
                      if (i !== firstActive) {
                        // Future questions: show as faint upcoming, not interactive
                        if (i > firstActive) return (
                          <div key={i} style={{alignSelf:"flex-start", fontSize:11.5, color:"#3A4560",
                            opacity:0.4, paddingLeft:4}}>· coming up next</div>
                        );
                        return null;
                      }

                      return (
                        <div key={i} style={{display:"flex", flexDirection:"column", gap:8}}>
                          {/* Coach's question bubble */}
                          <div style={coachBubble}>
                            {isObj && q.why && st.status!=="followup" && (
                              <div style={{fontSize:11, color:"#C9A55A", marginBottom:7, lineHeight:1.45}}>
                                💡 {q.why}
                              </div>
                            )}
                            {st.status==="followup" && st.encouragement && (
                              <div style={{fontSize:11.5, color:"#4ADE80", marginBottom:6}}>👍 {st.encouragement}</div>
                            )}
                            <div style={{fontSize:13, color:"#EEF2FF", lineHeight:1.5, fontWeight:500}}>
                              {st.status==="followup" ? st.followup : qText}
                            </div>

                            {/* Scaffolding chips (only on first ask) */}
                            {isObj && st.status!=="followup" && (
                              <div style={{marginTop:9, display:"flex", flexWrap:"wrap", gap:6}}>
                                {(q.weak_example||q.strong_example) && (
                                  <button onClick={()=>setQShowEx(p=>({...p,[i]:!p[i]}))} style={chipBtn}>
                                    {qShowEx[i]?"Hide example":"💡 See example"}
                                  </button>
                                )}
                                {q.starter && !gapAns[i] && (
                                  <button onClick={()=>setGapAns(p=>({...p,[i]:q.starter+" "}))} style={chipBtn}>
                                    ✏️ Use a starter
                                  </button>
                                )}
                              </div>
                            )}
                            {isObj && qShowEx[i] && st.status!=="followup" && (
                              <div style={{marginTop:8, fontSize:11.5, lineHeight:1.55,
                                background:"rgba(0,0,0,0.2)", borderRadius:9, padding:"9px 11px"}}>
                                <div style={{color:"#F87171", marginBottom:5}}>✗ "{q.weak_example}"</div>
                                <div style={{color:"#4ADE80"}}>✓ "{q.strong_example}"</div>
                              </div>
                            )}
                            {isObj && (q.include||[]).length>0 && st.status!=="followup" && (
                              <div style={{fontSize:10, color:"#6B7A99", marginTop:7}}>
                                Try to include: {(q.include||[]).join(" · ")}
                              </div>
                            )}
                            {/* Prior answer: you've told a relevant story before - reference + one-tap reuse */}
                            {isObj && q.prior_answer && st.status!=="followup" && (
                              <div style={{marginTop:9, background:"rgba(201,165,90,0.09)",
                                border:"1px solid rgba(201,165,90,0.35)", borderRadius:10, padding:"9px 11px"}}>
                                <div style={{fontSize:10.5, color:"#C9A55A", fontWeight:700, marginBottom:5}}>
                                  💬 You've answered something like this before{q.prior_answer_source?` (${q.prior_answer_source})`:""}:
                                </div>
                                <div style={{fontSize:11.5, lineHeight:1.5, color:"#C7D2E8",
                                  maxHeight:qShowPrior[i]?"none":72, overflow:"hidden", position:"relative"}}>
                                  {q.prior_answer}
                                </div>
                                <div style={{display:"flex", gap:7, marginTop:7, flexWrap:"wrap"}}>
                                  <button onClick={()=>setGapAns(p=>({...p,[i]:q.prior_answer}))}
                                    style={{...chipBtn, background:"#C9A55A", color:"#1A1206", border:"none", fontWeight:700}}>
                                    ✓ Use this answer
                                  </button>
                                  {q.prior_answer.length>180 && (
                                    <button onClick={()=>setQShowPrior(p=>({...p,[i]:!p[i]}))} style={chipBtn}>
                                      {qShowPrior[i]?"Show less":"Show full"}
                                    </button>
                                  )}
                                </div>
                                <div style={{fontSize:9.5, color:"#6B7A99", marginTop:6}}>
                                  Tap to reuse, then sharpen it for this specific role.
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Your reply composer */}
                          <div style={{alignSelf:"flex-end", width:"100%", maxWidth:"92%"}}>
                            <div style={{position:"relative"}}>
                              <VoiceSafeTextarea value={gapAns[i]||""} onChange={e=>setGapAns({...gapAns,[i]:e.target.value})}
                                placeholder={st.status==="followup"?"Type or speak your follow-up…":"Type or speak your answer…"}
                                style={{width:"100%", minHeight:70, padding:"11px 46px 11px 13px",
                                  border:`1px solid ${voiceActive===i?"#C9A55A":"#2A3654"}`,
                                  borderRadius:"14px 14px 4px 14px", fontSize:13,
                                  boxSizing:"border-box", background:"#141F38", color:"#EEF2FF",
                                  resize:"vertical", lineHeight:1.5, outline:"none", fontFamily:"inherit"}}/>
                              {/* Mic button */}
                              <button onClick={()=> voiceActive===i ? stopVoice() : startVoice(i)}
                                title={voiceActive===i?"Stop recording":"Speak your answer"}
                                style={{position:"absolute", right:8, top:8, width:32, height:32,
                                  borderRadius:"50%", border:"none", cursor:"pointer",
                                  background: voiceActive===i ? "#EF4444" : "rgba(201,165,90,0.15)",
                                  color: voiceActive===i ? "white" : "#C9A55A",
                                  fontSize:15, display:"flex", alignItems:"center", justifyContent:"center",
                                  animation: voiceActive===i ? "gapPulse 1.2s infinite" : "none"}}>
                                {voiceActive===i ? "⏹" : "🎤"}
                              </button>
                            </div>
                            {voiceActive===i && (
                              <div style={{fontSize:10.5, color:"#C9A55A", marginTop:4, textAlign:"right"}}>
                                🔴 Listening… tap the square to stop
                              </div>
                            )}
                            <div style={{display:"flex", gap:6, marginTop:7, justifyContent:"flex-end",
                              alignItems:"center", flexWrap:"wrap"}}>
                              {st.status!=="followup" && (
                                <select onChange={e=>{if(e.target.value)skipQuestion(i,e.target.value);}}
                                  defaultValue=""
                                  style={{padding:"9px 8px", background:"#141F38", border:"1px solid #2A3654",
                                    borderRadius:9, fontSize:11, color:"#6B7A99", cursor:"pointer", outline:"none"}}>
                                  <option value="" disabled>Skip…</option>
                                  <option value="not_relevant">Not relevant</option>
                                  <option value="later">Later</option>
                                  <option value="dont_have">Don't have this</option>
                                </select>
                              )}
                              {st.status==="followup" && (
                                <button onClick={()=>evaluateAnswer(i, true)} style={ghostBtn}>Use as-is</button>
                              )}
                              <button onClick={()=>evaluateAnswer(i)} disabled={!(gapAns[i]||"").trim()||qEvaluating[i]}
                                style={{padding:"10px 20px",
                                  background:(gapAns[i]||"").trim()&&!qEvaluating[i]?"linear-gradient(135deg,#C9A55A,#A8813E)":"#1E2A44",
                                  color:(gapAns[i]||"").trim()&&!qEvaluating[i]?"white":"#4E5A70",
                                  border:"none", borderRadius:12, fontSize:12.5, fontWeight:700,
                                  cursor:(gapAns[i]||"").trim()&&!qEvaluating[i]?"pointer":"default"}}>
                                {qEvaluating[i]?"Checking…":"Send"}
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}

                    {/* Commit CTA — appears once something's accepted */}
                    {Object.values(qState).some(s=>s?.status==="accepted") &&
                     gapData.questions.every((_,i)=>["accepted","skipped"].includes(qState[i]?.status)) && (
                      <div style={{marginTop:4}}>
                        <div style={{...coachBubble, marginBottom:10}}>
                          That's everything. Ready to add {Object.values(qState).filter(s=>s?.status==="accepted").length} answer{Object.values(qState).filter(s=>s?.status==="accepted").length>1?"s":""} to your profile? I'll rebuild your resume and re-score your pipeline.
                        </div>
                        <button onClick={commitGap} style={{width:"100%", padding:13,
                          background:"linear-gradient(135deg,#22C55E,#16A34A)", color:"white", border:"none",
                          borderRadius:12, cursor:"pointer", fontSize:13.5, fontWeight:700,
                          boxShadow:"0 2px 12px rgba(34,197,94,0.3)"}}>
                          ✓ Add to My Profile
                        </button>
                      </div>
                    )}
                    {gapStep==="processing" && (
                      <div style={{...coachBubble, alignSelf:"center", textAlign:"center"}}>
                        Adding to your profile <span className="gap-dots" style={{color:"#C9A55A"}}>…</span>
                      </div>
                    )}
                  </>)}

                  {/* REVIEWING — proposed updates */}
                  {gapStep==="reviewing" && gapProp && (<>
                    {cascadeMsg && (
                      <div style={{...coachBubble, borderColor:"rgba(201,165,90,0.3)"}}>
                        {cascadeMsg}. Your competencies are saved and scores are already updated.
                      </div>
                    )}
                    <div style={coachBubble}>
                      Here's what I'll add to your profile. Take a look before I save.
                      <div style={{marginTop:9, fontSize:12, color:"#B8C4DC"}}>
                        <b style={{color:"#EEF2FF"}}>{Object.keys(gapProp.technicalSkills||{}).length}</b> skill categories ·{" "}
                        <b style={{color:"#EEF2FF"}}>{(gapProp.capabilities||[]).length}</b> capabilities ·{" "}
                        <b style={{color:"#EEF2FF"}}>{(gapProp.achievements||[]).length}</b> achievements
                      </div>
                      <button onClick={()=>setShowGapDiff(v=>!v)} style={{...chipBtn, marginTop:9}}>
                        {showGapDiff ? "Hide details" : "See what's changing"}
                      </button>
                      {showGapDiff && (
                        <div style={{marginTop:10, fontSize:11.5, lineHeight:1.5, color:"#B8C4DC"}}>
                          {(gapProp.achievements||[]).slice(0,5).map((a,i)=>(
                            <div key={i} style={{padding:"3px 0"}}>
                              + {typeof a==="string" ? a : a.title || a.name || ""}
                            </div>
                          ))}
                          {Object.entries(gapProp.technicalSkills||{}).slice(0,4).map(([cat,items])=>(
                            <div key={cat} style={{padding:"3px 0"}}>
                              <b style={{color:"#C9A55A"}}>{cat}:</b> {Array.isArray(items)?items.join(", "):String(items)}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    {/* Choose the resume level BEFORE building. Auto-detected from the
                        role; override here if you want it pitched differently. */}
                    <div style={{marginBottom:10, background:"rgba(201,165,90,0.08)",
                      border:"1px solid rgba(201,165,90,0.3)", borderRadius:10, padding:"9px 11px"}}>
                      <div style={{fontSize:11, color:"#C9A55A", fontWeight:700, marginBottom:6}}>
                        Resume level for this build
                        {seniority==="auto" && job.auto_seniority && <span> · will build as <b style={{textTransform:"capitalize"}}>{job.auto_seniority}</b></span>}
                      </div>
                      <div style={{display:"flex", gap:5}}>
                        {["auto","junior","mid","senior"].map(lvl=>(
                          <button key={lvl}
                            onClick={async()=>{ setSeniority(lvl); setSavingSeniority(true);
                              try { await api(`/api/jobs/${job.id}/seniority`, {method:"POST", body:JSON.stringify({seniority: lvl==="auto"?null:lvl})}); } catch(e){}
                              setSavingSeniority(false); }}
                            disabled={savingSeniority}
                            style={{flex:1, padding:"6px 4px", fontSize:11, fontWeight:600, cursor:"pointer",
                              borderRadius:7, textTransform:"capitalize",
                              border: seniority===lvl?"1.5px solid #C9A55A":"1px solid #334155",
                              background: seniority===lvl?"#C9A55A":"transparent",
                              color: seniority===lvl?"#1A1206":"#94A3B8"}}>
                            {lvl}{lvl==="auto"&&job.auto_seniority&&seniority!=="auto"?` →${job.auto_seniority}`:""}
                          </button>
                        ))}
                      </div>
                      {seniority==="junior" && <div style={{fontSize:10, color:"#F59E0B", marginTop:5}}>Drops the stat matrix + senior framing, leads with hands-on work.</div>}
                      {seniority==="mid" && <div style={{fontSize:10, color:"#F59E0B", marginTop:5}}>Keeps metrics but drops the $38M figure.</div>}
                      {(seniority==="senior"||(seniority==="auto"&&job.auto_seniority==="senior")) && <div style={{fontSize:10, color:"#94A3B8", marginTop:5}}>Full framing including all metrics.</div>}
                    </div>
                    <button onClick={approveGap} style={{width:"100%", padding:13,
                      background:"linear-gradient(135deg,#22C55E,#16A34A)", color:"white", border:"none",
                      borderRadius:12, cursor:"pointer", fontSize:13, fontWeight:700}}>
                      ✓ Approve & Rebuild Resume{seniority!=="auto"?` (${seniority})`:job.auto_seniority?` (${job.auto_seniority})`:""}
                    </button>
                    <button onClick={()=>{setGapStep("answering");setGapProp(null);setShowGapDiff(false);}}
                      style={{...ghostBtn, alignSelf:"center", marginTop:2}}>
                      Back
                    </button>
                  </>)}

                  {gapStep==="saving" && (
                    <div style={{...coachBubble, alignSelf:"center"}}>
                      Saving <span className="gap-dots" style={{color:"#C9A55A"}}>…</span>
                    </div>
                  )}

                  {/* DONE — summary transcript */}
                  {gapStep==="done" && (<>
                    <div style={{...coachBubble, textAlign:"center"}}>
                      <div style={{fontSize:22, marginBottom:6}}>✅</div>
                      <div style={{fontWeight:700, color:"#4ADE80", marginBottom:4}}>All done, profile updated</div>
                      {cascadeMsg && <div style={{fontSize:11.5, color:"#C9A55A", marginTop:4}}>{cascadeMsg}</div>}
                      {liveScore!==null && (
                        <div style={{marginTop:8, fontSize:15, fontWeight:800,
                          color:liveScore>=8?"#22C55E":liveScore>=6?"#F59E0B":"#EF4444"}}>
                          This job: {liveScore}/10
                        </div>
                      )}
                    </div>
                    {gapData && (gapData.questions||[]).map((q,i)=>{
                      const st = qState[i]||{};
                      if (st.status!=="accepted") return null;
                      return (
                        <div key={i} style={{display:"flex", flexDirection:"column", gap:6}}>
                          <div style={coachBubbleSm}>{(typeof q==="object"?q.question:q)}</div>
                          <div style={userBubble}>{st.polished}</div>
                        </div>
                      );
                    })}
                    <button onClick={()=>{
                      setGapStep("idle");setGapData(null);setQState({});setCascadeMsg("");setGapAns({});setDeepMsgs({});
                      api(`/api/jobs/${job.id}/gap-state`,{method:"PUT",body:JSON.stringify({gap_state:null})}).catch(()=>{});
                    }} style={{...ghostBtn, alignSelf:"center", marginTop:4}}>
                      🔄 Re-analyse against updated profile
                    </button>
                  </>)}

                  {gapErr && (
                    <div style={{alignSelf:"center", fontSize:11.5, color:"#F87171",
                      background:"rgba(239,68,68,0.1)", padding:"7px 12px", borderRadius:10}}>
                      {gapErr}
                    </div>
                  )}
                </div>
              </div>
                    {/* Modal close bar */}
                    <div style={{background:"#0D1424", borderTop:"1px solid #1E2A44",
                      padding:"10px 16px", textAlign:"center"}}>
                      <button onClick={()=>setGapModalOpen(false)}
                        style={{fontSize:12, color:"#8893A7", background:"none", border:"none",
                          cursor:"pointer", fontWeight:600, padding:"4px 12px"}}>
                        Close coach
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>)}

          </>)}

          

                    {/* ────────── DOCS TAB ────────── */}
          {detailTab==="docs" && (<>
            {/* ── Resume ── */}
            <div style={{background:"white", borderRadius:12, border:"1px solid #E8EDF4",
              padding:14, marginBottom:12}}>
              <div style={{fontSize:11,fontWeight:700,color:"#94A3B8",letterSpacing:0.8,
                textTransform:"uppercase",marginBottom:10}}>📄 Resume</div>

              {!job.resume_url && !["building","failed","error"].includes(job.resume_status||"") && (
                <div style={{textAlign:"center",padding:"16px 0",color:"#94A3B8"}}>
                  <div style={{fontSize:28,marginBottom:8,opacity:0.5}}>📄</div>
                  <div style={{fontSize:13,fontWeight:600,color:"#1C2B4A",marginBottom:6}}>
                    No resume yet
                  </div>
                  <div style={{fontSize:12,color:"#94A3B8",marginBottom:12}}>
                    Approve this job to build a tailored resume.
                  </div>
                  <button onClick={()=>setShowModal(true)}
                    style={{padding:"10px 20px",background:"rgba(34,197,94,0.15)",color:"#15803D",
                      border:"1px solid rgba(34,197,94,0.3)",borderRadius:10,fontSize:13,
                      fontWeight:700,cursor:"pointer"}}>
                    ✓ Approve Job
                  </button>
                </div>
              )}

            {(job.resume_url || ["building","failed","error"].includes(job.resume_status||"")) && (
              <div>
                <div style={{fontSize:11, fontWeight:700, color:"#94A3B8", letterSpacing:0.8,
                  textTransform:"uppercase", marginBottom:10, display:"none"}}>Resume Status</div>

                {/* No resume yet */}
                {!job.resume_status && (
                  <div style={{textAlign:"center", padding:"20px 0", color:"#94A3B8", fontSize:13}}>
                    ⚙️ Building your tailored resume…
                  </div>
                )}

                {/* Building */}
                {job.resume_status==="building" && (
                  <div style={{textAlign:"center", padding:"20px 0"}}>
                    <div style={{fontSize:24, marginBottom:8}}>⚙️</div>
                    <div style={{fontSize:13, color:"#94A3B8"}}>Building tailored resume…</div>
                    <button onClick={()=>onRebuildResume(job.id)}
                      style={{marginTop:10, padding:"7px 16px", background:"none",
                        border:"1px solid #E2E8F0", borderRadius:8, cursor:"pointer",
                        fontSize:12, color:"#64748B"}}>Retry</button>
                  </div>
                )}

                {(job.resume_status==="failed" || job.resume_status==="error") && job.resume_url && (
                  <div style={{display:"flex", alignItems:"center", gap:10, padding:"10px 12px",
                    background:"#F0FDF4", borderRadius:10, border:"1px solid #86EFAC", marginBottom:10}}>
                    <span style={{fontSize:18}}>📄</span>
                    <div style={{flex:1}}>
                      <div style={{fontSize:12.5, fontWeight:700, color:"#15803D"}}>
                        Your last successful resume is still available
                      </div>
                      <a href={job.resume_url} target="_blank" rel="noreferrer"
                        style={{fontSize:11.5, color:"#16A34A"}}>{job.resume_filename||"View resume"}</a>
                    </div>
                    <button onClick={()=>downloadPdf(job.id, job.resume_filename||"Resume.pdf")}
                      style={{padding:"6px 12px", background:"#16A34A", color:"white",
                        border:"none", borderRadius:8, cursor:"pointer", fontSize:12}}>⬇</button>
                  </div>
                )}
                {(job.resume_status==="failed" || job.resume_status==="error") && (
                  <div style={{textAlign:"center", padding:"18px 12px", background:"#FEF2F2",
                    border:"1px solid #FECACA", borderRadius:10}}>
                    <div style={{fontSize:22, marginBottom:6}}>⚠️</div>
                    <div style={{fontSize:13, fontWeight:600, color:"#B91C1C", marginBottom:4}}>
                      Resume build didn't pass quality checks
                    </div>
                    <div style={{fontSize:12, color:"#7F1D1D", marginBottom:12, lineHeight:1.5}}>
                      The tailoring or content check failed, so no resume was published (better than handing you a broken one). Try rebuilding.
                    </div>
                    <button onClick={()=>onRebuildResume(job.id)}
                      style={{padding:"9px 20px", background:"#B91C1C", color:"white",
                        border:"none", borderRadius:8, cursor:"pointer", fontSize:13, fontWeight:600}}>
                      🔄 Rebuild resume
                    </button>
                  </div>
                )}

                {/* Ready for review */}
                {job.resume_url && job.resume_status==="pending_review" && (<>
                  <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10}}>
                    <span style={{fontSize:13, fontWeight:600, color:"#92400E"}}>
                      📄 {job.resume_filename||"Resume"}
                    </span>
                    <button onClick={()=>setShowPdf(p=>!p)} style={{
                      padding:"4px 10px", background:"none", border:"1px solid #D97706",
                      borderRadius:6, color:"#92400E", fontSize:11, cursor:"pointer"}}>
                      {showPdf?"▲ Hide":"▼ Preview"}
                    </button>
                  </div>

                  {showPdf && (() => {
                    const isMobile=/Android|webOS|iPhone|iPad|iPod/i.test(navigator.userAgent);
                    const pdfUrl=job.resume_url||`${API_URL}/api/jobs/${job.id}/resume`;
                    return isMobile ? (
                      <div style={{background:"#F8FAFC", borderRadius:10, padding:16, textAlign:"center", marginBottom:12}}>
                        <div style={{fontSize:13, color:"#64748B", marginBottom:10}}>📱 Tap to open your resume</div>
                        <a href={pdfUrl} target="_blank" rel="noreferrer" style={{display:"block", marginBottom:8}}>
                          <button style={{width:"100%", padding:12, background:"#1C2B4A", color:"white",
                            border:"none", borderRadius:10, fontSize:13, fontWeight:600, cursor:"pointer"}}>
                            📄 Open Resume PDF
                          </button>
                        </a>
                      </div>
                    ) : (
                      <div style={{marginBottom:12}}>
                        <object data={pdfUrl} type="application/pdf"
                          style={{width:"100%", height:480, border:"1px solid #E2E8F0", borderRadius:10, display:"block"}}>
                          <a href={pdfUrl} target="_blank" rel="noreferrer">
                            <button style={{padding:"10px 20px", background:"#1C2B4A", color:"white",
                              border:"none", borderRadius:8, cursor:"pointer"}}>Open PDF</button>
                          </a>
                        </object>
                      </div>
                    );
                  })()}

                  <div style={{display:"flex", gap:8, marginBottom:14}}>
                    <a href={job.resume_url} target="_blank" rel="noreferrer" style={{flex:1}}>
                      <button style={{width:"100%", padding:10, background:"#F1F5F9",
                        border:"1px solid #E2E8F0", borderRadius:10, color:"#475569",
                        fontSize:12, fontWeight:600, cursor:"pointer"}}>👁 Open</button>
                    </a>
                    <button onClick={()=>downloadPdf(job.id, job.resume_filename||"Resume.pdf")}
                      style={{flex:1, width:"100%", padding:10, background:"#1C2B4A",
                        border:"none", borderRadius:10, color:"white",
                        fontSize:12, fontWeight:600, cursor:"pointer"}}>⬇ PDF</button>
                    <button onClick={()=>downloadDocx(`/api/jobs/${job.id}/resume-docx`, `Resume.docx`)}
                      style={{flex:1, padding:10, background:"#2B579A",
                        border:"none", borderRadius:10, color:"white",
                        fontSize:12, fontWeight:600, cursor:"pointer"}}>⬇ Word</button>
                    <button style={{flex:1.5, padding:10, background:"#16A34A",
                      border:"none", borderRadius:10, color:"white",
                      fontSize:12, fontWeight:700, cursor:"pointer",
                      opacity:resumeApproving?0.6:1}}
                      disabled={resumeApproving}
                      onClick={async()=>{setResumeApproving(true);await onApproveResume(job.id);setResumeApproving(false);}}>
                      {resumeApproving?"Approving…":"✓ Approve"}
                    </button>
                  </div>
                </>)}

                {/* Approved */}
                {job.resume_url && job.resume_status==="approved" && (
                  <div style={{display:"flex", alignItems:"center", gap:10, padding:"10px 12px",
                    background:"#F0FDF4", borderRadius:10, border:"1px solid #86EFAC", marginBottom:12}}>
                    <span style={{fontSize:20}}>✅</span>
                    <div style={{flex:1}}>
                      <div style={{fontSize:13, fontWeight:600, color:"#15803D"}}>Resume Approved</div>
                      <a href={job.resume_url} target="_blank" rel="noreferrer"
                        style={{fontSize:12, color:"#16A34A"}}>{job.resume_filename||"View resume"}</a>
                    </div>
                    <button onClick={()=>downloadPdf(job.id, job.resume_filename||"Resume.pdf")}
                      style={{padding:"6px 12px", background:"#16A34A", color:"white",
                        border:"none", borderRadius:8, cursor:"pointer", fontSize:12}}>⬇</button>
                  </div>
                )}

                {/* Resume level selector - tunes framing so a senior resume doesn't scare off junior roles */}
                {(job.resume_status==="pending_review"||job.resume_status==="approved"||job.resume_status==="failed") && (
                  <div style={{marginBottom:8}}>
                    <div style={{fontSize:11, color:"#94A3B8", marginBottom:4, fontWeight:600}}>
                      Resume level
                      {seniority==="auto" && job.auto_seniority && (
                        <span style={{color:"#1C2B4A", fontWeight:700}}> · Auto → <span style={{textTransform:"capitalize"}}>{job.auto_seniority}</span></span>
                      )}
                    </div>
                    <div style={{display:"flex", gap:4}}>
                      {["auto","junior","mid","senior"].map(lvl => (
                        <button key={lvl}
                          onClick={async()=>{
                            setSeniority(lvl); setSavingSeniority(true);
                            try {
                              await api(`/api/jobs/${job.id}/seniority`, {method:"POST", body: JSON.stringify({seniority: lvl==="auto"?null:lvl})});
                            } catch(e){}
                            setSavingSeniority(false);
                          }}
                          disabled={savingSeniority}
                          style={{flex:1, padding:"6px 4px", fontSize:11, fontWeight:600, cursor:"pointer",
                            borderRadius:7, textTransform:"capitalize", position:"relative",
                            border: seniority===lvl?"1.5px solid #1C2B4A":"1px solid #E2E8F0",
                            background: seniority===lvl?"#1C2B4A":"white",
                            color: seniority===lvl?"white":"#64748B"}}>
                          {lvl}
                          {lvl==="auto" && job.auto_seniority && seniority!=="auto" && (
                            <span style={{display:"block", fontSize:8.5, fontWeight:600, opacity:0.7, textTransform:"capitalize"}}>→{job.auto_seniority}</span>
                          )}
                        </button>
                      ))}
                    </div>
                    {seniority==="auto" && job.auto_seniority_reason && (
                      <div style={{fontSize:10, color:"#94A3B8", marginTop:4, lineHeight:1.4}}>
                        Matched <b style={{color:"#64748B", textTransform:"capitalize"}}>{job.auto_seniority}</b> · {job.auto_seniority_reason}
                      </div>
                    )}
                    {seniority==="junior" && <div style={{fontSize:10.5, color:"#92400E", marginTop:4, lineHeight:1.4}}>Drops the stat matrix and senior framing, leads with hands-on work. Rebuild to apply.</div>}
                    {seniority==="mid" && <div style={{fontSize:10.5, color:"#92400E", marginTop:4, lineHeight:1.4}}>Keeps your metrics but drops the $38M figure. Rebuild to apply.</div>}
                  </div>
                )}

                {/* Rebuild button (the failed banner has its own - no duplicates) */}
                {(job.resume_status==="pending_review"||job.resume_status==="approved") && (
                  <button onClick={async()=>{setRebuilding(true);await onRebuildResume(job.id);setRebuilding(false);}}
                    disabled={rebuilding}
                    style={{width:"100%", padding:10, background:"none", border:"1px solid #E2E8F0",
                      borderRadius:10, cursor:"pointer", color:"#64748B", fontSize:12,
                      fontWeight:600, opacity:rebuilding?0.6:1}}>
                    {rebuilding?`⚙️ ${rebuildProgress.text}`:"🔄 Rebuild Resume"}
                  </button>
                )}
              </div>
            )}

              {/* ── AI Resume Refinement ── */}
              {/* AI Resume Refinement — visible whenever resume exists */}
            {job.resume_url && (job.resume_status==="pending_review"||job.resume_status==="approved") && (
              <div style={{background:"#F5F3FF", border:"1px solid #DDD6FE",
                borderRadius:12, padding:14, marginBottom:12}}>
                <div style={{fontSize:11, fontWeight:700, color:"#5B21B6", letterSpacing:0.8,
                  textTransform:"uppercase", marginBottom:6}}>🤖 AI Refinement</div>
                <p style={{fontSize:12, color:"#6D28D9", margin:"0 0 10px", lineHeight:1.4}}>
                  Tap a quick action, or tell the AI what to change. It improves the resume by intent, keeping it sharp and within 3 pages.
                </p>
                <div style={{display:"flex", flexWrap:"wrap", gap:6, marginBottom:10}}>
                  {[
                    ["Strengthen summary", "Rewrite my professional summary to be a strong, role-tailored paragraph of 3-4 full sentences for this specific job. Open by positioning me for this role's domain using its keywords, cite 2-3 of my most relevant strengths with specifics, and close on the value I bring. Make it compelling and specific, never a generic one-liner."],
                    ["Emphasise leadership", "Emphasise my leadership, people-management and transformation impact throughout, leading with the strongest examples."],
                    ["Tighten wording", "Tighten the wording across the resume: cut filler, keep whole sentences, make every line punchy and specific."],
                    ["Lead with strongest", "Reorder so my strongest, most role-relevant achievements and bullets come first in each section."],
                    ["More metrics", "Surface and emphasise quantified results and metrics where they already exist in my experience. Never invent any."],
                  ].map(([label, instruction], i)=>(
                    <button key={i} onClick={()=>setAiFeedback(f => f && f.trim() ? f.trim()+"; "+instruction : instruction)}
                      style={{fontSize:11, color:"#5B21B6", background:"white", border:"1px solid #DDD6FE",
                        borderRadius:14, padding:"5px 11px", cursor:"pointer", fontWeight:600}}>
                      {label}
                    </button>
                  ))}
                </div>
                <VoiceSafeTextarea value={aiFeedback} onChange={e=>setAiFeedback(e.target.value)}
                  placeholder="Or type your own: e.g. 'Write a full 5-sentence summary specific to this role' or 'Emphasise Oracle ERP delivery leadership' or 'Strengthen the second achievement'."
                  style={{width:"100%", height:70, padding:"9px 11px", border:"1px solid #DDD6FE",
                    borderRadius:10, fontSize:13, boxSizing:"border-box", background:"white",
                    resize:"none", lineHeight:1.5}}/>
                {aiRefineMsg && <p style={{fontSize:12, margin:"6px 0 0",
                  color:aiRefineMsg.startsWith("✅")?"#16A34A":"#DC2626"}}>{aiRefineMsg}</p>}
                <button disabled={!aiFeedback.trim()||aiRefining}
                  onClick={async()=>{
                    setAiRefining(true); setAiRefineMsg("Applying feedback and rebuilding…");
                    const d=await api(`/api/jobs/${job.id}/refine-resume`,
                      {method:"POST",body:JSON.stringify({feedback:aiFeedback})});
                    if(d.ok){
                      if(d.resume_url){ job.resume_url=d.resume_url; }
                      await onRefreshJob(job.id);
                      setShowPdf(false);
                      setAiRefineMsg("✅ Done! Resume rebuilt. Tap Preview to see the changes.");
                      setAiFeedback("");
                    }
                    else setAiRefineMsg("❌ "+(d.error||"Failed"));
                    setAiRefining(false);
                  }}
                  style={{marginTop:8, width:"100%", padding:11, border:"none", borderRadius:10,
                    fontSize:13, fontWeight:700, cursor:aiFeedback.trim()&&!aiRefining?"pointer":"default",
                    background:aiFeedback.trim()&&!aiRefining?"#6D28D9":"#E2E8F0",
                    color:aiFeedback.trim()&&!aiRefining?"white":"#94A3B8"}}>
                  {aiRefining?refineProgress.text:"Apply Feedback & Rebuild"}
                </button>
              </div>
            )}

            {/* Pending job — build prompt */}
            {job.status==="pending" && (
              <div style={{textAlign:"center", padding:"30px 20px", color:"#94A3B8"}}>
                <div style={{fontSize:32, marginBottom:8}}>📄</div>
                <div style={{fontSize:14, fontWeight:600, color:"#475569", marginBottom:4}}>No Resume Built Yet</div>
                <div style={{fontSize:13, marginBottom:16, lineHeight:1.5}}>
                  Approve this job to automatically build a tailored resume.
                </div>
              </div>
            )}

            </div>

            {/* ── Cover Letter ── */}
            {/* Cover Letter */}
            <div style={{background:"white", borderRadius:12, border:"1px solid #E8EDF4",
              padding:14, marginBottom:12}}>
              <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10, flexWrap:"wrap", gap:6}}>
                <div style={{fontSize:11, fontWeight:700, color:"#94A3B8", letterSpacing:0.8,
                  textTransform:"uppercase"}}>✉️ Cover Letter</div>
                <div style={{display:"flex", gap:5, flexWrap:"wrap"}}>
                  {job.cover_letter && (
                    <button onClick={()=>copyText(job.cover_letter)} style={{
                      padding:"4px 10px", background:"#F1F5F9", border:"1px solid #E2E8F0",
                      borderRadius:7, cursor:"pointer", fontSize:11, color:"#475569", fontWeight:600}}>
                      📋 Copy
                    </button>
                  )}
                  {job.cover_letter && (
                    <button onClick={()=>{setClEditing(e=>!e);setClEditText(job.cover_letter||"");}}
                      style={{padding:"4px 10px", background:clEditing?"#EFF6FF":"#F1F5F9",
                        border:clEditing?"1px solid #93C5FD":"1px solid #E2E8F0",
                        borderRadius:7, cursor:"pointer", fontSize:11,
                        color:clEditing?"#1D4ED8":"#475569", fontWeight:600}}>
                      ✏️ Edit
                    </button>
                  )}
                  {job.cover_letter && (
                    <button onClick={async()=>{
                      setClPdfLoading(true);
                      setClMsg("Opening print view...");
                      try {
                        // Must hit the backend directly: the relative /api path is served
                        // the SPA shell (no proxy), so it would open a blank page.
                        const resp = await fetch(`https://job-pipeline-production-8cff.up.railway.app/api/jobs/${job.id}/cover-letter-html`,
                          {headers:{"Authorization":"Bearer "+getToken()}});
                        if(!resp.ok){setClMsg("❌ Failed to open print view");setClPdfLoading(false);return;}
                        const html = await resp.text();
                        const w = window.open("", "_blank");
                        if(w){ w.document.open(); w.document.write(html); w.document.close(); }
                        else {
                          // popup blocked - fall back to a blob URL
                          const blob = new Blob([html],{type:"text/html"});
                          window.open(URL.createObjectURL(blob),"_blank");
                        }
                        setClMsg("");
                      } catch(e){setClMsg("❌ "+e.message);}
                      setClPdfLoading(false);
                    }} disabled={clPdfLoading} style={{
                      padding:"4px 10px", background:clPdfLoading?"#F1F5F9":"#6D28D9",
                      border:"none", borderRadius:7, cursor:"pointer",
                      fontSize:11, color:clPdfLoading?"#94A3B8":"white", fontWeight:600}}>
                      {clPdfLoading?"...":"🖨️ Print"}
                    </button>
                  )}
                  <button onClick={async()=>{
                    setClGenerating(true); setClMsg("Generating cover letter...");
                    const d=await api(`/api/jobs/${job.id}/cover-letter`,{method:"POST"});
                    if(d.ok){
                      job.cover_letter = d.cover_letter;
                      setClMsg("✅ Done! Tap Print to save as PDF or Copy to use the text.");
                    } else setClMsg("❌ "+(d.error||"Failed"));
                    setClGenerating(false);
                  }} disabled={clGenerating} style={{
                    padding:"4px 10px", background:clGenerating?"#F1F5F9":"#1C2B4A",
                    border:"none", borderRadius:7, cursor:"pointer",
                    fontSize:11, color:clGenerating?"#94A3B8":"white", fontWeight:600}}>
                    {clGenerating?"..." : job.cover_letter?"🔄 Regenerate":"✨ Generate"}
                  </button>
                </div>
              </div>
              {clMsg && <p style={{fontSize:12, color:clMsg.startsWith("✅")?"#16A34A":"#DC2626",
                margin:"0 0 8px"}}>{clMsg}</p>}
              {job.cover_letter ? (<>
                {/* Read-only display */}
                {!clEditing && (
                  <div style={{
                    fontSize:12, color:"#374151", lineHeight:1.65, whiteSpace:"pre-wrap",
                    wordBreak:"break-word", background:"#F8FAFC", borderRadius:10,
                    padding:"12px 14px", border:"1px solid #E8EDF4",
                    maxHeight:320, overflowY:"auto"
                  }}>
                    {job.cover_letter}
                  </div>
                )}
                {/* Editable feedback box */}
                {clEditing && (
                  <div style={{marginTop:4}}>
                    <VoiceSafeTextarea
                      value={clEditText}
                      onChange={e=>setClEditText(e.target.value)}
                      style={{width:"100%", height:260, padding:"10px 12px",
                        border:"1.5px solid #93C5FD", borderRadius:10, fontSize:12,
                        lineHeight:1.65, boxSizing:"border-box", resize:"vertical",
                        background:"white", color:"#1C2B4A", outline:"none"}}
                    />
                    <div style={{display:"flex", gap:8, marginTop:8}}>
                      <button onClick={async()=>{
                        const r=await api(`/api/jobs/${job.id}/cover-letter`,
                          {method:"PUT", body:JSON.stringify({cover_letter:clEditText})});
                        if(r.ok){
                          job.cover_letter=clEditText;
                          setClEditing(false);
                          setClMsg("✅ Cover letter saved.");
                        } else setClMsg("❌ "+(r.error||"Save failed"));
                      }} style={{flex:1, padding:"9px 0", background:"#1C2B4A", color:"white",
                        border:"none", borderRadius:9, fontSize:12, fontWeight:700, cursor:"pointer"}}>
                        Save Changes
                      </button>
                      <button onClick={()=>setClEditing(false)}
                        style={{padding:"9px 16px", background:"#F1F5F9", color:"#64748B",
                          border:"1px solid #E2E8F0", borderRadius:9, fontSize:12, cursor:"pointer"}}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </>) : (
                <div style={{textAlign:"center", padding:"20px 0", color:"#94A3B8"}}>
                  <div style={{fontSize:28, marginBottom:6}}>✉️</div>
                  <div style={{fontSize:13, marginBottom:12}}>No cover letter yet for this role.</div>
                  <div style={{fontSize:12, color:"#CBD5E1"}}>Tap Generate above to create one tailored to this job.</div>
                </div>
              )}
            </div>

            {/* ── LinkedIn InMail / Outreach Message ── */}
            <div style={{background:"white", borderRadius:12, border:"1px solid #E8EDF4",
              padding:14, marginBottom:12}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",
                marginBottom:10,flexWrap:"wrap",gap:6}}>
                <div style={{fontSize:11,fontWeight:700,color:"#94A3B8",letterSpacing:0.8,
                  textTransform:"uppercase"}}>✉️ LinkedIn InMail</div>
                <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
                  {job.inmail && (
                    <button onClick={()=>copyText(job.inmail)}
                      style={{padding:"4px 10px",background:"#F1F5F9",border:"1px solid #E2E8F0",
                        borderRadius:7,cursor:"pointer",fontSize:11,color:"#475569",fontWeight:600}}>
                      📋 Copy
                    </button>
                  )}
                  {job.inmail && (
                    <button onClick={()=>{setShowInmailEdit(p=>!p);setInmailText(job.inmail||"");}}
                      style={{padding:"4px 10px",background:showInmailEdit?"#EFF6FF":"#F1F5F9",
                        border:showInmailEdit?"1px solid #93C5FD":"1px solid #E2E8F0",
                        borderRadius:7,cursor:"pointer",fontSize:11,
                        color:showInmailEdit?"#1D4ED8":"#475569",fontWeight:600}}>
                      ✏️ Edit
                    </button>
                  )}
                  <button onClick={async()=>{
                    setInmailLoading(true); setInmailMsg("Generating…");
                    const d=await api(`/api/jobs/${job.id}/inmail`,{method:"POST"});
                    if(d.ok){job.inmail=d.inmail;setInmailText(d.inmail);setInmailMsg("");}
                    else setInmailMsg("❌ "+(d.error||"Failed"));
                    setInmailLoading(false);
                  }} disabled={inmailLoading}
                    style={{padding:"4px 10px",background:inmailLoading?"#F1F5F9":"#1C2B4A",
                      border:"none",borderRadius:7,cursor:"pointer",fontSize:11,
                      color:inmailLoading?"#94A3B8":"white",fontWeight:600}}>
                    {inmailLoading?"…":job.inmail?"🔄 Regenerate":"✨ Generate"}
                  </button>
                </div>
              </div>

              {/* InMail display / edit */}
              {job.inmail && !showInmailEdit && (
                <div style={{fontSize:12,color:"#374151",lineHeight:1.65,
                  whiteSpace:"pre-wrap",wordBreak:"break-word",
                  background:"#F8FAFC",borderRadius:10,padding:"12px 14px",
                  border:"1px solid #E8EDF4",maxHeight:260,overflowY:"auto"}}>
                  {job.inmail}
                </div>
              )}
              {job.inmail && showInmailEdit && (
                <div>
                  <VoiceSafeTextarea value={inmailText} onChange={e=>setInmailText(e.target.value)}
                    style={{width:"100%",height:200,padding:"10px 12px",
                      border:"1.5px solid #93C5FD",borderRadius:10,fontSize:12,
                      lineHeight:1.65,boxSizing:"border-box",resize:"vertical",
                      background:"white",color:"#1C2B4A",outline:"none",fontFamily:"inherit"}}
                  />
                  <div style={{display:"flex",gap:8,marginTop:8}}>
                    <button onClick={async()=>{
                      const r=await api(`/api/jobs/${job.id}/inmail`,
                        {method:"PUT",body:JSON.stringify({inmail:inmailText})});
                      if(r.ok){job.inmail=inmailText;setShowInmailEdit(false);}
                    }} style={{flex:1,padding:"9px 0",background:"#1C2B4A",color:"white",
                      border:"none",borderRadius:9,fontSize:12,fontWeight:700,cursor:"pointer"}}>
                      Save
                    </button>
                    <button onClick={()=>setShowInmailEdit(false)}
                      style={{padding:"9px 16px",background:"#F1F5F9",color:"#64748B",
                        border:"1px solid #E2E8F0",borderRadius:9,fontSize:12,cursor:"pointer"}}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
              {!job.inmail && (
                <div style={{textAlign:"center",padding:"16px 0",color:"#94A3B8"}}>
                  <div style={{fontSize:28,marginBottom:6}}>✉️</div>
                  <div style={{fontSize:13,marginBottom:4}}>No InMail drafted yet.</div>
                  <div style={{fontSize:12,color:"#CBD5E1"}}>
                    Tap Generate to create a personalised 180-word LinkedIn message.
                  </div>
                </div>
              )}
              {inmailMsg && <p style={{fontSize:12,marginTop:6,color:"#DC2626"}}>{inmailMsg}</p>}
            </div>
          </>)}

        {detailTab==="interview" && (<>
          <div style={{padding:"16px 14px 22px", background:"#0D1225",
            borderRadius:16, margin:"4px 0 18px",
            border:"1px solid rgba(255,255,255,0.06)"}}>
            {!prepPack && !prepBusy && !prepChecked && (
              <div style={{textAlign:"center", padding:"30px 0", color:"#8893A7", fontSize:12.5}}>
                ⏳ Checking for your saved pack…
              </div>
            )}
            {!prepPack && !prepBusy && prepChecked && (
              <div style={{textAlign:"center", padding:"26px 14px", background:"#0D1424",
                border:"1px solid #1E2A44", borderRadius:14}}>
                <div style={{fontSize:30, marginBottom:8}}>🎤</div>
                <div style={{fontSize:14.5, fontWeight:800, color:"#EEF2FF", marginBottom:6}}>
                  Interview Prep Pack
                </div>
                <div style={{fontSize:12, color:"#8893A7", lineHeight:1.6, maxWidth:420, margin:"0 auto 14px"}}>
                  Your complete interview bible for this role: the job decoded in plain
                  English, your strongest cards from your Competency Capital, a 20+ question
                  bank (priority, behavioural, leadership, technical, curveballs) with STAR
                  answers drawn from YOUR real evidence, flashcard practice mode, and company
                  research with one-tap links. Generated once, cached for this job.
                </div>
                <button onClick={()=>loadPrep(false)} style={{padding:"11px 20px",
                  background:"#B8934A", color:"#0D1225", border:"none", borderRadius:11,
                  fontSize:13, fontWeight:800, cursor:"pointer"}}>
                  ✨ Generate my prep pack
                </button>
                <div style={{fontSize:10.5, color:"#64748B", marginTop:8}}>Uses AI once · instant afterwards</div>
              </div>
            )}
            {prepBusy && (
              <div style={{textAlign:"center", padding:"30px 0", color:"#8893A7", fontSize:13}}>
                ⚙️ Building your pack from your evidence…
              </div>
            )}
            {prepErr && <div style={{fontSize:12, color:"#F87171", marginBottom:10}}>{prepErr}</div>}
            {prepPack && (() => {
              const P = prepPack;
              const legacy = !P.role_brief;
              const bank = [
                ...((P.priority_questions||P.likely_questions||[]).map(x=>({...x, cat:"priority"}))),
                ...((P.behavioral||[]).map(x=>({...x, cat:"behavioral"}))),
                ...((P.leadership||[]).map(x=>({...x, cat:"leadership"}))),
              ];
              const pbank = pfilter==="all" ? bank : bank.filter(x=>x.cat===pfilter);
              const CAT = {priority:"⭐ Priority", behavioral:"🤝 Behavioural", leadership:"🧭 Leadership"};
              const card = {background:"#0D1424", border:"1px solid #1E2A44", borderRadius:13,
                padding:"12px 14px", marginBottom:8};
              const secH = (t)=>(<div style={{fontSize:11.5, fontWeight:800, color:"#E5C77E",
                margin:"14px 0 8px", letterSpacing:0.4}}>{t}</div>);
              const compChip = (name)=> name ? (
                <span onClick={()=>{window.location.hash="capital";}}
                  style={{display:"inline-block", fontSize:9.5, fontWeight:800, cursor:"pointer",
                    padding:"2px 8px", borderRadius:7, marginLeft:6,
                    background:"rgba(184,147,74,0.16)", color:"#B8934A"}}>↳ {name}</span>) : null;
              const starBlock = (s)=> s ? (
                <div style={{fontSize:11.5, color:"#B8C2D8", lineHeight:1.6, marginTop:4}}>
                  <b style={{color:"#B8934A"}}>S</b> {s.situation}{" "}
                  <b style={{color:"#B8934A"}}>T</b> {s.task}{" "}
                  <b style={{color:"#B8934A"}}>A</b> {s.action}{" "}
                  <b style={{color:"#B8934A"}}>R</b> {s.result}
                </div>) : null;
              const copyAll = () => {
                const L=[`INTERVIEW BIBLE — ${job.title} @ ${job.company_name||""}`];
                if(P.role_brief){L.push(`\nTHE ROLE, PLAINLY\n${P.role_brief.what_it_is||""}`,
                  `\nDAY TO DAY`, ...(P.role_brief.day_to_day||[]).map(x=>`• ${x}`),
                  `\nREALLY HIRING FOR`, ...(P.role_brief.really_hiring_for||[]).map(x=>`• ${x}`));}
                if(P.elevator_pitch) L.push(`\nYOUR 30-SECOND OPENER\n${P.elevator_pitch}`);
                if((P.your_strengths||[]).length) L.push(`\nYOUR STRONGEST CARDS`,
                  ...(P.your_strengths||[]).map(s=>`• ${s.competency}: ${s.why_it_lands}`));
                const qsec=(t,a)=>{ if(!(a||[]).length) return;
                  L.push(`\n${t}`); (a||[]).forEach((x,i)=>{L.push(`${i+1}. ${x.q}`);
                    if(x.competency)L.push(`   [${x.competency}]`);
                    if(x.why)L.push(`   Why: ${x.why}`);
                    if(x.star)L.push(`   S:${x.star.situation} T:${x.star.task} A:${x.star.action} R:${x.star.result}`);
                    if(x.angle)L.push(`   Probing: ${x.angle}`);
                    (x.talking_points||[]).forEach(tp=>L.push(`   • ${tp}`));
                    if(x.approach)L.push(`   Approach: ${x.approach}`);});};
                qsec("PRIORITY QUESTIONS", P.priority_questions||P.likely_questions);
                qsec("BEHAVIOURAL", P.behavioral); qsec("LEADERSHIP", P.leadership);
                qsec("TECHNICAL", P.technical); qsec("CURVEBALLS", P.curveballs);
                L.push(`\nTALKING POINTS`, ...(P.talking_points||[]).map(t=>`• ${t}`));
                L.push(`\nASK THE PANEL`, ...(P.questions_to_ask||[]).map(q=>`• ${q}`));
                if(P.company_angle){L.push(`\nCOMPANY READ (from the ad)`,
                  ...((P.company_angle.from_the_jd)||[]).map(x=>`• ${x}`),
                  `\nRESEARCH CHECKLIST`, ...((P.company_angle.research_checklist)||[]).map(x=>`□ ${x}`));}
                navigator.clipboard.writeText(L.join("\n"))
                  .then(()=>toast && toast("Interview bible copied — paste anywhere")).catch(()=>{});
              };
              return (<>
              <div style={{display:"flex", alignItems:"center", gap:8, marginBottom:10, flexWrap:"wrap"}}>
                <div style={{flex:1, fontSize:13.5, fontWeight:800, color:"#EEF2FF"}}>
                  🎤 Interview Coach
                  {P.generated_at && <span style={{fontSize:10, color:"#64748B", fontWeight:500}}>
                    {"  ·  "}{String(P.generated_at).substring(0,10)}</span>}
                </div>
                <button onClick={copyAll} style={{background:"none", border:"1px solid #1E2A44",
                  color:"#8893A7", borderRadius:8, padding:"5px 10px", fontSize:11,
                  cursor:"pointer"}}>📋 Copy all</button>
                <button onClick={()=>loadPrep(true)} disabled={prepBusy}
                  style={{background:"none", border:"1px solid #1E2A44", color:"#8893A7",
                    borderRadius:8, padding:"5px 10px", fontSize:11, cursor:"pointer"}}>↻</button>
              </div>

              {legacy ? (<>
                <div style={{...card, borderColor:"rgba(184,147,74,0.4)"}}>
                  <div style={{fontSize:11.5, color:"#E5C77E", lineHeight:1.5}}>
                    This pack was generated before the full Interview Coach existed. Tap ↻ above
                    to regenerate the complete experience: the role decoded, your strongest cards,
                    the full question bank, and company research.
                  </div>
                </div>
                {(P.likely_questions||[]).map((x,i)=>(
                  <div key={i} style={card}>
                    <div style={{fontSize:12.5, fontWeight:700, color:"#EEF2FF"}}>{i+1}. {x.q}</div>
                    {starBlock(x.star)}
                  </div>
                ))}
              </>) : (<>

              {/* ── Stage nav ── */}
              <div style={{display:"flex", gap:6, marginBottom:14, flexWrap:"wrap"}}>
                {[["role","🧭 The role"],["you","🧠 You"],["questions","❓ Questions"],
                  ["practice","🃏 Practice"],["research","🔎 Research"]].map(([k,l])=>(
                  <button key={k} onClick={()=>setStage(k)}
                    style={{padding:"7px 12px", borderRadius:9, fontSize:11.5, fontWeight:700,
                      cursor:"pointer",
                      background: stage===k ? "#B8934A" : "rgba(255,255,255,0.05)",
                      color: stage===k ? "#0D1225" : "#8893A7",
                      border: stage===k ? "none" : "1px solid #1E2A44"}}>{l}</button>
                ))}
              </div>

              {stage==="role" && P.role_brief && (<>
                <div style={{...card, background:"linear-gradient(145deg,#141D35,#0D1424)"}}>
                  <div style={{fontSize:11, fontWeight:800, color:"#E5C77E", marginBottom:6}}>
                    WHAT THIS JOB ACTUALLY IS
                  </div>
                  <div style={{fontSize:13, color:"#EEF2FF", lineHeight:1.65}}>{P.role_brief.what_it_is}</div>
                  {P.role_brief.level_read && <div style={{fontSize:11, color:"#8893A7", marginTop:8}}>
                    📐 {P.role_brief.level_read}</div>}
                </div>
                {secH("📅 A NORMAL WEEK — WHAT YOU'D ACTUALLY DO")}
                {(P.role_brief.day_to_day||[]).map((d,i)=>(
                  <div key={i} style={{display:"flex", gap:10, alignItems:"flex-start",
                    background:"#0D1424", border:"1px solid #1E2A44", borderRadius:11,
                    padding:"10px 12px", marginBottom:6}}>
                    <span style={{fontSize:16}}>{["🌅","📊","🤝","🛠","🧯"][i%5]}</span>
                    <span style={{fontSize:12, color:"#B8C2D8", lineHeight:1.55}}>{d}</span>
                  </div>
                ))}
                {secH("🎯 WHAT THEY'RE REALLY HIRING FOR")}
                <div>
                  {(P.role_brief.really_hiring_for||[]).map((r,i)=>(
                    <span key={i} style={{display:"inline-block", fontSize:11.5, fontWeight:700,
                      padding:"7px 12px", margin:"0 6px 6px 0", borderRadius:9,
                      background:"rgba(184,147,74,0.12)", color:"#E5C77E",
                      border:"1px solid rgba(184,147,74,0.3)"}}>{r}</span>
                  ))}
                </div>
              </>)}

              {stage==="you" && (<>
                {P.elevator_pitch && (
                  <div style={{background:"rgba(184,147,74,0.1)", border:"1px solid rgba(184,147,74,0.35)",
                    borderRadius:13, padding:"13px 15px", marginBottom:12}}>
                    <div style={{fontSize:11.5, fontWeight:800, color:"#E5C77E", marginBottom:5}}>
                      👋 Your 30-second opener
                    </div>
                    <div style={{fontSize:12.5, color:"#EEF2FF", lineHeight:1.65}}>{P.elevator_pitch}</div>
                  </div>
                )}
                {(P.your_strengths||[]).length>0 && <>
                  {secH("🏆 YOUR STRONGEST CARDS FOR THIS ROLE")}
                  {(P.your_strengths||[]).map((s,i)=>(
                    <div key={i} style={card}>
                      <div style={{fontSize:12.5, fontWeight:800, color:"#EEF2FF"}}>
                        {s.competency}{compChip("view evidence")}
                      </div>
                      <div style={{fontSize:11.5, color:"#B8C2D8", marginTop:3, lineHeight:1.55}}>{s.why_it_lands}</div>
                    </div>
                  ))}
                  <div style={{fontSize:10.5, color:"#64748B", marginTop:4}}>
                    Tap "view evidence" to open your Competency Capital and rehearse the proof behind each card.
                  </div>
                </>}
                {(P.talking_points||[]).length>0 && <>
                  {secH("💬 NUMBERS TO LAND")}
                  <div style={card}>
                    {(P.talking_points||[]).map((tp,i)=>
                      <div key={i} style={{fontSize:11.5, color:"#B8C2D8", marginBottom:4, lineHeight:1.5}}>• {tp}</div>)}
                  </div>
                </>}
              </>)}

              {stage==="questions" && (<>
                {secH("⭐ PRIORITY — MOST LIKELY FIRST")}
                {(P.priority_questions||[]).map((x,i)=>(
                  <div key={i} style={{...card, borderColor:"rgba(184,147,74,0.35)"}}>
                    <div style={{fontSize:12.5, fontWeight:700, color:"#EEF2FF"}}>{i+1}. {x.q}</div>
                    {x.why && <div style={{fontSize:10.5, color:"#6B7A99", margin:"2px 0 2px"}}>{x.why}</div>}
                    {starBlock(x.star)}
                  </div>
                ))}
                {secH("🤝 BEHAVIOURAL")}
                {(P.behavioral||[]).map((x,i)=>(
                  <div key={i} style={card}>
                    <div style={{fontSize:12.5, fontWeight:700, color:"#EEF2FF"}}>{x.q}{compChip(x.competency)}</div>
                    {starBlock(x.star)}
                  </div>
                ))}
                {secH("🧭 LEADERSHIP")}
                {(P.leadership||[]).map((x,i)=>(
                  <div key={i} style={card}>
                    <div style={{fontSize:12.5, fontWeight:700, color:"#EEF2FF"}}>{x.q}{compChip(x.competency)}</div>
                    {starBlock(x.star)}
                  </div>
                ))}
                {(P.technical||[]).length>0 && <>{secH("🛠 TECHNICAL & DOMAIN")}
                {(P.technical||[]).map((x,i)=>(
                  <div key={i} style={card}>
                    <div style={{fontSize:12.5, fontWeight:700, color:"#EEF2FF"}}>{x.q}</div>
                    {x.angle && <div style={{fontSize:10.5, color:"#6B7A99", margin:"2px 0 4px"}}>Probing: {x.angle}</div>}
                    {(x.talking_points||[]).map((tp,j)=>
                      <div key={j} style={{fontSize:11.5, color:"#B8C2D8", lineHeight:1.5}}>• {tp}</div>)}
                  </div>
                ))}</>}
                {(P.curveballs||[]).length>0 && <>{secH("🥎 CURVEBALLS")}
                {(P.curveballs||[]).map((x,i)=>(
                  <div key={i} style={card}>
                    <div style={{fontSize:12.5, fontWeight:700, color:"#EEF2FF"}}>{x.q}</div>
                    <div style={{fontSize:11.5, color:"#B8C2D8", marginTop:3, lineHeight:1.6}}>{x.approach}</div>
                  </div>
                ))}</>}
                {(P.questions_to_ask||[]).length>0 && <>{secH("🙋 ASK THE PANEL")}
                <div style={card}>
                  {(P.questions_to_ask||[]).map((qq,i)=>
                    <div key={i} style={{fontSize:11.5, color:"#B8C2D8", marginBottom:4, lineHeight:1.5}}>• {qq}</div>)}
                </div></>}
              </>)}

              {stage==="practice" && (() => {
                const x = pbank[Math.min(prIdx, Math.max(0,pbank.length-1))] || {};
                return (<>
                  <div style={{display:"flex", gap:6, marginBottom:12, flexWrap:"wrap"}}>
                    {[["all","All"],["priority","⭐ Priority"],["behavioral","🤝 Behavioural"],["leadership","🧭 Leadership"]].map(([k,l])=>(
                      <button key={k} onClick={()=>{setPfilter(k); setPrIdx(0); setRevealed(false);}}
                        style={{padding:"5px 11px", borderRadius:8, fontSize:10.5, fontWeight:700, cursor:"pointer",
                          background: pfilter===k ? "rgba(184,147,74,0.2)" : "none",
                          color: pfilter===k ? "#E5C77E" : "#64748B",
                          border:"1px solid " + (pfilter===k ? "rgba(184,147,74,0.4)" : "#1E2A44")}}>{l}</button>
                    ))}
                  </div>
                  {pbank.length===0 ? <div style={{color:"#8893A7", fontSize:12}}>No questions in this category.</div> : (
                  <div style={{background:"#0D1424", border:"1px solid #1E2A44", borderRadius:14,
                    padding:"18px 16px", textAlign:"center"}}>
                    <div style={{fontSize:10.5, color:"#64748B", marginBottom:4}}>
                      {CAT[x.cat]||""} · Question {Math.min(prIdx,pbank.length-1)+1} of {pbank.length}
                    </div>
                    <div style={{fontSize:14.5, fontWeight:700, color:"#EEF2FF", lineHeight:1.5, marginBottom:6}}>{x.q}</div>
                    {x.competency && <div style={{marginBottom:10}}>{compChip(x.competency)}</div>}
                    {!revealed ? (
                      <button onClick={()=>setRevealed(true)} style={{padding:"10px 18px",
                        background:"#B8934A", color:"#0D1225", border:"none", borderRadius:10,
                        fontSize:12.5, fontWeight:800, cursor:"pointer"}}>
                        Reveal your STAR answer
                      </button>
                    ) : (
                      x.star && <div style={{textAlign:"left", fontSize:12, color:"#B8C2D8",
                        lineHeight:1.7, background:"rgba(0,0,0,0.25)", borderRadius:10,
                        padding:"12px 13px", borderLeft:"2px solid #B8934A"}}>
                        <b style={{color:"#B8934A"}}>S</b> {x.star.situation}<br/>
                        <b style={{color:"#B8934A"}}>T</b> {x.star.task}<br/>
                        <b style={{color:"#B8934A"}}>A</b> {x.star.action}<br/>
                        <b style={{color:"#B8934A"}}>R</b> {x.star.result}
                      </div>
                    )}
                    <div style={{display:"flex", gap:8, justifyContent:"center", marginTop:16}}>
                      <button disabled={prIdx===0}
                        onClick={()=>{ setPrIdx(i=>Math.max(0,i-1)); setRevealed(false); }}
                        style={{padding:"8px 16px", background:"none", border:"1px solid #1E2A44",
                          color:"#8893A7", borderRadius:9, fontSize:12, cursor:"pointer",
                          opacity: prIdx===0?0.4:1}}>← Prev</button>
                      <button disabled={prIdx>=pbank.length-1}
                        onClick={()=>{ setPrIdx(i=>i+1); setRevealed(false); }}
                        style={{padding:"8px 16px", background:"#1E2A44", border:"none",
                          color:"#EEF2FF", borderRadius:9, fontSize:12, fontWeight:700,
                          cursor:"pointer", opacity: prIdx>=pbank.length-1?0.4:1}}>Next →</button>
                    </div>
                  </div>)}
                </>);
              })()}

              {stage==="research" && (<>
                {P.company_angle && (P.company_angle.from_the_jd||[]).length>0 && <>
                  {secH("🕵️ READING BETWEEN THE LINES OF THE AD")}
                  {(P.company_angle.from_the_jd||[]).map((f,i)=>(
                    <div key={i} style={card}>
                      <div style={{fontSize:12, color:"#B8C2D8", lineHeight:1.6}}>{f}</div>
                    </div>
                  ))}
                </>}
                {secH("🔗 GO LOOK — ONE TAP EACH")}
                <div style={{display:"flex", gap:8, flexWrap:"wrap", marginBottom:6}}>
                  {[
                    ["📰 Recent news", `https://www.google.com/search?q=%22${encodeURIComponent(job.company_name||"")}%22+news&tbs=qdr:m`],
                    ["🏢 LinkedIn company", `https://www.linkedin.com/search/results/companies/?keywords=${encodeURIComponent(job.company_name||"")}`],
                    ["👥 Find your interviewers", `https://www.linkedin.com/search/results/people/?keywords=${encodeURIComponent((job.company_name||"")+" "+(job.title||"").split(" ").slice(0,2).join(" "))}`],
                    ["⭐ Glassdoor", `https://www.google.com/search?q=${encodeURIComponent((job.company_name||"")+" glassdoor reviews")}`],
                  ].map(([l,u])=>(
                    <a key={l} href={u} target="_blank" rel="noreferrer"
                      style={{padding:"8px 13px", background:"#0D1424", border:"1px solid #1E2A44",
                        borderRadius:10, fontSize:11.5, fontWeight:700, color:"#EEF2FF",
                        textDecoration:"none"}}>{l}</a>
                  ))}
                </div>
                {P.company_angle && (P.company_angle.research_checklist||[]).length>0 && <>
                  {secH("✅ BEFORE THE INTERVIEW, KNOW:")}
                  {(P.company_angle.research_checklist||[]).map((it,i)=>(
                    <div key={i} onClick={()=>setCheckedRes(m=>({...m,[i]:!m[i]}))}
                      style={{display:"flex", gap:10, alignItems:"flex-start", cursor:"pointer",
                        background:"#0D1424", border:"1px solid #1E2A44", borderRadius:11,
                        padding:"10px 12px", marginBottom:6, opacity: checkedRes[i]?0.55:1}}>
                      <span style={{fontSize:14}}>{checkedRes[i]?"✅":"⬜"}</span>
                      <span style={{fontSize:12, color:"#B8C2D8", lineHeight:1.55,
                        textDecoration: checkedRes[i]?"line-through":"none"}}>{it}</span>
                    </div>
                  ))}
                </>}
                {secH("🧵 WEAVE YOUR FINDINGS IN")}
                <div style={card}>
                  <div style={{fontSize:11, color:"#8893A7", marginBottom:8, lineHeight:1.5}}>
                    Paste anything you learned - news, the interviewer's background, values from
                    their site - and the whole pack regenerates with it woven through your answers
                    and panel questions.
                  </div>
                  <VoiceSafeTextarea value={researchTxt} onChange={e=>setResearchTxt(e.target.value)}
                    rows={4} placeholder="e.g. Interviewer Jane Smith led their cloud migration; company just acquired X; values page emphasises customer obsession…"
                    style={{width:"100%", boxSizing:"border-box", padding:"9px 11px", borderRadius:9,
                      border:"1px solid #1E2A44", background:"rgba(255,255,255,0.04)",
                      color:"#EEF2FF", fontSize:12, lineHeight:1.5}}/>
                  <button onClick={()=>{ if(researchTxt.trim()) loadPrep(true, researchTxt.trim()); }}
                    disabled={prepBusy || !researchTxt.trim()}
                    style={{marginTop:8, padding:"10px 16px", background:"#B8934A", color:"#0D1225",
                      border:"none", borderRadius:10, fontSize:12.5, fontWeight:800,
                      cursor:"pointer", opacity:(prepBusy||!researchTxt.trim())?0.5:1}}>
                    {prepBusy?"Weaving…":"✨ Weave into my pack"}
                  </button>
                </div>
              </>)}
              </>)}
              </>);
            })()}
          </div>
        </>)}

        </div>

        {/* ── STICKY FOOTER ACTIONS ──────────────────────────────── */}
        <div style={{
          position:"absolute", bottom:0, left:0, right:0,
          background:"white", borderTop:"1px solid #E8EDF4",
          padding:"10px 14px", display:"flex", gap:8
        }}>
          {job.status==="pending" && (<>
            <button onClick={approve} style={{
              flex:3, padding:13, background:"#16A34A", color:"white", border:"none",
              borderRadius:12, fontSize:14, fontWeight:800, cursor:"pointer",
              boxShadow:"0 2px 8px rgba(22,163,74,0.35)"}}>✓ Approve</button>
            <button onClick={()=>{onStatusChange(job.id,"not_suitable");setShowDetail(false);window.location.hash='';}} style={{
              flex:2, padding:13, background:"#EF4444", color:"white", border:"none",
              borderRadius:12, fontSize:14, fontWeight:800, cursor:"pointer"}}
              title="Marks the job Not Suitable and archives it in one step">✗ Not Suitable</button>
            <button onClick={()=>{onArchive(job.id);setShowDetail(false);window.location.hash='';}} style={{
              flex:1, padding:13, background:"#F1F5F9", border:"1px solid #E2E8F0",
              borderRadius:12, fontSize:16, cursor:"pointer"}}>📁</button>
            <button onClick={()=>{if(window.confirm('Delete?')){onDelete(job.id);setShowDetail(false);window.location.hash='';}}} style={{flex:1,padding:13,background:'#FEF2F2',border:'1px solid #FECACA',borderRadius:12,fontSize:16,cursor:'pointer'}}>🗑</button>
          </>)}
          {["approved","applied","interviewing"].includes(job.status) && (<>
            {job.resume_status==="approved" && job.apply_url && !job.apply_status && (
              <button onClick={()=>onAutoApply(job.id,job.apply_url)} style={{
                flex:3, padding:13, background:"#B8934A", color:"white", border:"none",
                borderRadius:12, fontSize:14, fontWeight:800, cursor:"pointer"}}>⚡ Auto Apply</button>
            )}
            {!["applied","applying","queued","already_applied"].includes(job.apply_status) && (
              <button onClick={async()=>{
                const r=await api(`/api/jobs/${job.id}/apply-status`,{method:"POST",body:JSON.stringify({apply_status:"applied",apply_at:new Date().toISOString()})});
                if(r.ok!==false){await onRefreshJob(job.id);}
              }} style={{flex:3,padding:13,background:"#16A34A",color:"white",border:"none",
                borderRadius:12,fontSize:13,fontWeight:700,cursor:"pointer"}}>
                ✓ Mark Applied
              </button>
            )}
            {job.apply_status==="applied" && job.status!=="interviewing" && (
              <div style={{flex:3, padding:13, textAlign:"center", fontSize:13,
                fontWeight:700, color:"#15803D"}}>
                ✅ Applied{job.apply_at ? ` — ${new Date(job.apply_at).toLocaleDateString("en-AU",{day:"numeric",month:"short",year:"numeric"})}` : "!"}
              </div>
            )}
            {job.apply_status==="applied" && job.status!=="interviewing" && (
              <button onClick={()=>onStatusChange(job.id,"interviewing")} style={{
                flex:3, padding:13, background:"rgba(168,85,247,0.12)", color:"#9333EA",
                border:"1px solid rgba(168,85,247,0.35)", borderRadius:12, fontSize:13,
                fontWeight:800, cursor:"pointer"}}>
                🎤 They called? → Interviewing
              </button>
            )}
            {job.status==="interviewing" && (
              <div style={{flex:4, padding:13, textAlign:"center", fontSize:13,
                fontWeight:800, color:"#9333EA", background:"rgba(168,85,247,0.08)",
                border:"1px solid rgba(168,85,247,0.3)", borderRadius:12}}>
                🎤 Interviewing
                <span onClick={()=>onStatusChange(job.id,"applied")}
                  style={{marginLeft:10, fontSize:10.5, fontWeight:600, color:"#8893A7",
                    cursor:"pointer", textDecoration:"underline dotted"}}>↩ back to applied</span>
              </div>
            )}
            <button onClick={()=>onStatusChange(job.id,"pending")} style={{
              flex:1, padding:13, background:"#F1F5F9", border:"1px solid #E2E8F0",
              borderRadius:12, fontSize:12, fontWeight:600, cursor:"pointer", color:"#64748B"}}>
              Reopen to Pending
            </button>
            <button onClick={()=>{onDelete(job.id);setShowDetail(false);window.location.hash='';}} style={{
              flex:1, padding:13, background:"#FEF2F2", border:"1px solid #FECACA",
              borderRadius:12, fontSize:16, cursor:"pointer"}}>🗑</button>
          </>)}
          {(job.status==="not_suitable" || job.status==="archived") && (<>
            <button onClick={async ()=>{ await onUnarchive(job.id); onStatusChange(job.id,"pending"); }} style={{
              flex:2, padding:13, background:"#F1F5F9", border:"1px solid #E2E8F0",
              borderRadius:12, fontSize:13, fontWeight:600, cursor:"pointer", color:"#64748B"}}>
              ↩ Reopen to Pending
            </button>
            <button onClick={()=>{if(window.confirm('Delete?')){onDelete(job.id);setShowDetail(false);window.location.hash='';}}} style={{flex:1,padding:13,background:'#FEF2F2',border:'1px solid #FECACA',borderRadius:12,fontSize:16,cursor:'pointer'}}>🗑</button>
          </>)}


        </div>

        {/* ── TEMPLATE PICKER MODAL (portal bottom sheet - immune to ancestor
             transforms, viewport-fixed, safe-area aware, backdrop dismisses) ── */}
        {showModal && createPortal(
          <div onClick={()=>setShowModal(false)} style={{position:"fixed", inset:0, zIndex:1150,
            background:"rgba(5,8,20,0.55)", display:"flex",
            alignItems:"flex-end", justifyContent:"center"}}>
            <div onClick={e=>e.stopPropagation()}
              style={{background:"white", borderRadius:"20px 20px 0 0",
              padding:"24px 20px calc(24px + env(safe-area-inset-bottom))",
              width:"100%", maxWidth:480, boxSizing:"border-box",
              maxHeight:"88vh", overflowY:"auto"}}>
              <div style={{width:36, height:4, background:"#E2E8F0", borderRadius:2,
                margin:"0 auto 20px"}}/>
              <h3 style={{fontSize:18, fontWeight:800, color:"#1C2B4A",
                margin:"0 0 4px"}}>Approve & Build Resume</h3>
              <p style={{fontSize:13, color:"#64748B", margin:"0 0 14px"}}>
                Confirm the resume level and style, then build.
              </p>

              {/* Resume level - auto-detected, override before the build runs */}
              <div style={{marginBottom:16}}>
                <div style={{fontSize:11, fontWeight:700, color:"#64748B", marginBottom:6,
                  textTransform:"uppercase", letterSpacing:0.5}}>
                  Resume level
                  {seniority==="auto" && job.auto_seniority &&
                    <span style={{color:"#B8934A", textTransform:"none", letterSpacing:0}}> · auto: <b style={{textTransform:"capitalize"}}>{job.auto_seniority}</b></span>}
                </div>
                <div style={{display:"flex", gap:6}}>
                  {["auto","junior","mid","senior"].map(lvl => (
                    <button key={lvl} disabled={savingSeniority}
                      onClick={async()=>{
                        setSeniority(lvl); setSavingSeniority(true);
                        try { await api(`/api/jobs/${job.id}/seniority`, {method:"POST",
                          body: JSON.stringify({seniority: lvl==="auto"?null:lvl})}); } catch(e){}
                        setSavingSeniority(false);
                      }}
                      style={{flex:1, padding:"9px 4px", fontSize:12, fontWeight:600, cursor:"pointer",
                        borderRadius:8, textTransform:"capitalize",
                        border: seniority===lvl?"2px solid #B8934A":"1px solid #E2E8F0",
                        background: seniority===lvl?"#FFFBEB":"white",
                        color: seniority===lvl?"#1C2B4A":"#64748B"}}>
                      {lvl}{lvl==="auto"&&job.auto_seniority&&seniority!=="auto"?` →${job.auto_seniority}`:""}
                    </button>
                  ))}
                </div>
                {seniority==="junior" && <div style={{fontSize:10.5, color:"#92400E", marginTop:5}}>Leads with hands-on delivery, lighter executive framing.</div>}
                {seniority==="mid" && <div style={{fontSize:10.5, color:"#92400E", marginTop:5}}>Balanced framing: your metrics without heavy executive weight.</div>}
                {(seniority==="senior"||(seniority==="auto"&&job.auto_seniority==="senior")) && <div style={{fontSize:10.5, color:"#94A3B8", marginTop:5}}>Full executive framing and metrics.</div>}
              </div>

              <div style={{fontSize:11, fontWeight:700, color:"#64748B", marginBottom:6,
                textTransform:"uppercase", letterSpacing:0.5}}>Template</div>
              <div style={{display:"flex", flexWrap:"wrap", gap:10, marginBottom:20}}>
                {[["4","Corporate Premium","Navy & gold executive","linear-gradient(90deg,#1C2B4A,#B8934A)"],
                  ["1","Executive Serif","Classic serif, navy accents","#1C2B4A"],
                  ["3","Clean Sans","Light header, minimal","linear-gradient(90deg,#FFFFFF,#B8934A)"],
                  ["2","ATS Compact","Plain text, parser-first","#111111"]].map(([t,name,desc,sw])=>(
                  <div key={t} onClick={()=>setTemplate(t)} style={{
                    flexBasis:"calc(50% - 5px)", padding:12, borderRadius:12,
                    border:`2px solid ${template===t?"#B8934A":"#E2E8F0"}`,
                    background:template===t?"#FFFBEB":"#F8FAFC", cursor:"pointer", boxSizing:"border-box"}}>
                    <div style={{height:6, borderRadius:4, marginBottom:8, background:sw,
                      border: t==="3"?"1px solid #E2E8F0":"none"}}/>
                    <div style={{fontSize:12.5, fontWeight:700, color:"#1C2B4A", marginBottom:2}}>{name}</div>
                    <div style={{fontSize:10.5, color:"#64748B"}}>{desc}</div>
                  </div>
                ))}
              </div>
              <div style={{display:"flex", gap:10}}>
                <button onClick={()=>setShowModal(false)} style={{
                  flex:1, padding:13, background:"#F1F5F9", border:"1px solid #E2E8F0",
                  borderRadius:12, cursor:"pointer", fontSize:13, color:"#64748B", fontWeight:600}}>
                  Cancel
                </button>
                <button onClick={()=>{onStatusChange(job.id,"approved",template);setShowModal(false);setShowDetail(false);}} style={{
                  flex:2, padding:13, background:"#16A34A", color:"white", border:"none",
                  borderRadius:12, cursor:"pointer", fontSize:14, fontWeight:800,
                  boxShadow:"0 2px 8px rgba(22,163,74,0.35)"}}>
                  ✓ Approve & Build{seniority!=="auto"?` (${seniority})`:job.auto_seniority?` (${job.auto_seniority})`:""}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
      </div>
    )}
    </>
  );
}

// ── Main App ──────────────────────────────────────────────────────────────

function BookmarkletPage({ onBack }) {
  const [token,setToken]=useState(null);
  const [loading,setLoading]=useState(true);
  const [copied,setCopied]=useState(false);
  useEffect(()=>{ api("/api/bookmarklet-token",{method:"POST"}).then(d=>{if(d.ok)setToken(d.token);}).finally(()=>setLoading(false)); },[]);
  // Short loader URL (~250 chars) — pulls the full script from Railway so mobile browsers don't truncate it
  // eslint-disable-next-line no-script-url
  const href = token ? "javascript:(function(){var s=document.createElement('script');s.src='https://job-pipeline-production-8cff.up.railway.app/bookmarklet.js?t='+encodeURIComponent('"+token+"')+'&_='+Date.now();document.head.appendChild(s);})();" : "";
  const copy=()=>navigator.clipboard?.writeText(href).then(()=>{setCopied(true);setTimeout(()=>setCopied(false),2500);});
  const [copiedTok,setCopiedTok]=useState(false);
  const copyTok=()=>navigator.clipboard?.writeText(token||"").then(()=>{setCopiedTok(true);setTimeout(()=>setCopiedTok(false),2500);});
  const feats=[["📋","Reads title, company, location, full description & salary"],["🤖","Scores against your profile instantly"],["✅","Adds to pipeline — no copy-paste"],["🌐","Works on Seek, LinkedIn, any job site — desktop & mobile"]];
  const notes=["Token valid 90 days — revisit to refresh.","Seek: all details auto-extracted.","LinkedIn: open job in browser (not app), tap bookmark.","Tap bookmark twice to close without adding."];
  return (<div style={{background:"#F8FAFC",minHeight:"100vh"}}>
    <div style={{background:"#1C2B4A",padding:"12px 16px",display:"flex",alignItems:"center",gap:12,borderBottom:"3px solid #B8934A",position:"sticky",top:0,zIndex:100}}>
      <button onClick={onBack} style={{background:"rgba(255,255,255,0.12)",border:"1px solid rgba(255,255,255,0.2)",borderRadius:8,color:"white",padding:"7px 14px",cursor:"pointer",fontSize:13,fontWeight:600}}>← Back</button>
      <div><div style={{fontSize:15,fontWeight:700,color:"white"}}>📎 Bookmarklet</div><div style={{fontSize:11,color:"#94A3B8"}}>Add any job to your pipeline in one tap</div></div>
    </div>
    <div style={{padding:"16px 14px",maxWidth:520,margin:"0 auto"}}>
      <div style={{background:"white",borderRadius:14,border:"1px solid #E8EDF4",padding:16,marginBottom:12}}>
        <div style={{fontSize:13,fontWeight:700,color:"#1C2B4A",marginBottom:10}}>What it does</div>
        {feats.map(([icon,text],i)=>(<div key={i} style={{display:"flex",gap:10,marginBottom:8}}><span style={{fontSize:16,flexShrink:0}}>{icon}</span><span style={{fontSize:13,color:"#475569",lineHeight:1.4}}>{text}</span></div>))}
      </div>
      <div style={{background:"white",borderRadius:14,border:"1px solid #E8EDF4",padding:16,marginBottom:12}}>
        <div style={{fontSize:13,fontWeight:700,color:"#1C2B4A",marginBottom:12}}>🖥 Desktop — drag to bookmark bar</div>
        {loading?<div style={{color:"#94A3B8",fontSize:13}}>Generating your personalised bookmarklet…</div>:token?(<>
          <div style={{background:"#EFF6FF",border:"1px solid #BFDBFE",borderRadius:10,padding:"11px 13px",marginBottom:12,fontSize:12,color:"#1D4ED8",lineHeight:1.6}}>
            1. Show bookmark bar (View → Show Bookmarks Bar)<br/>2. Drag the button below onto the bar<br/>3. On any job page, click it → tap <strong>Add to Pipeline</strong>
          </div>
          <div style={{textAlign:"center",marginBottom:6}}>
            <a href={href} onClick={e=>e.preventDefault()} style={{display:"inline-block",padding:"12px 28px",background:"linear-gradient(135deg,#1C2B4A,#2D3F6B)",color:"white",borderRadius:12,fontSize:14,fontWeight:700,textDecoration:"none",boxShadow:"0 4px 16px rgba(28,43,74,0.3)",cursor:"grab",userSelect:"none"}}>📎 Add to Pipeline</a>
          </div>
          <div style={{fontSize:11,color:"#94A3B8",textAlign:"center"}}>← Drag this button to your bookmarks bar</div>
        </>):<div style={{color:"#DC2626",fontSize:13}}>Could not generate token. Try logging out and back in.</div>}
      </div>
      <div style={{background:"white",borderRadius:14,border:"1px solid #E8EDF4",padding:16,marginBottom:12}}>
        <div style={{fontSize:13,fontWeight:700,color:"#1C2B4A",marginBottom:12}}>📱 Mobile — save as bookmark</div>
        {loading?<div style={{color:"#94A3B8",fontSize:13}}>Generating…</div>:token?(<>
          <div style={{background:"#F0FDF4",border:"1px solid #86EFAC",borderRadius:10,padding:"11px 13px",marginBottom:12,fontSize:12,color:"#374151",lineHeight:1.65}}>
            <strong>iOS Safari:</strong><br/>1. Tap Copy below<br/>2. Bookmark any page (Share → Add Bookmark)<br/>3. Bookmarks → Edit that bookmark → paste the code as the URL<br/><br/>
            <strong>Android Chrome:</strong><br/>1. Tap Copy<br/>2. Star any page<br/>3. Menu → Bookmarks → find it → Edit → paste as the URL
          </div>
          <button onClick={copy} style={{width:"100%",padding:12,background:copied?"#16A34A":"#1C2B4A",color:"white",border:"none",borderRadius:10,fontSize:14,fontWeight:700,cursor:"pointer"}}>{copied?"✓ Copied!":"📋 Copy Bookmarklet Code"}</button>
          <div style={{marginTop:8,background:"#F8FAFC",borderRadius:8,padding:"7px 10px",fontSize:10,color:"#94A3B8",wordBreak:"break-all",maxHeight:52,overflow:"hidden",fontFamily:"monospace"}}>{href.substring(0,120)}…</div>
        </>):<div style={{color:"#DC2626",fontSize:13}}>Could not generate token.</div>}
      </div>
      <div style={{background:"#F5F3FF",border:"1px solid #DDD6FE",borderRadius:14,padding:16,marginBottom:12}}>
        <div style={{fontSize:13,fontWeight:700,color:"#6D28D9",marginBottom:4,display:"flex",alignItems:"center",gap:8}}>
          Chrome Extension
          <span style={{fontSize:10,fontWeight:700,background:"#6D28D9",color:"white",padding:"2px 7px",borderRadius:20}}>v1.5.1</span>
        </div>
        <div style={{fontSize:12,color:"#5B21B6",lineHeight:1.6,marginBottom:10}}>
          Add jobs from LinkedIn, Seek, Indeed, or any career page with one click. Also automates Easy Apply on LinkedIn. Download, unzip, load in Chrome via Developer mode, then paste your token.
        </div>
        <a href="/add-to-pipeline.zip" download style={{display:"block",textAlign:"center",width:"100%",boxSizing:"border-box",padding:12,background:"#6D28D9",color:"white",borderRadius:8,fontWeight:700,fontSize:14,textDecoration:"none",marginBottom:8}}>Download Extension (.zip)</a>
        {loading?<div style={{color:"#94A3B8",fontSize:13}}>Generating...</div>:token?(
          <button onClick={copyTok} style={{width:"100%",padding:12,background:copiedTok?"#16A34A":"#7C3AED",color:"white",border:"none",borderRadius:8,fontWeight:700,fontSize:14,cursor:"pointer"}}>{copiedTok?"Token Copied":"Copy Extension Token"}</button>
        ):<div style={{color:"#DC2626",fontSize:13}}>Could not generate token.</div>}
        <div style={{marginTop:8,background:"#FAFAFA",borderRadius:8,padding:"7px 10px",fontSize:10,color:"#6B7280",lineHeight:1.5}}>After loading, click the extension icon, paste your token, and Save. Then open any job on LinkedIn or Seek and click Add to Pipeline.</div>
      </div>
            <div style={{background:"#FFFBEB",border:"1px solid #FDE68A",borderRadius:14,padding:16,marginBottom:20}}>
        <div style={{fontSize:13,fontWeight:700,color:"#92400E",marginBottom:8}}>ℹ️ Notes</div>
        {notes.map((t,i)=>(<div key={i} style={{fontSize:12,color:"#78350F",marginBottom:6,lineHeight:1.4}}>• {t}</div>))}
      </div>
    </div>
  </div>);
}

function CareerCoachPage({ onBack }) {
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi! I'm your career coach. I can help you sharpen your resume, spot skill gaps, and think through career moves into parallel domains that fit your background. I've got your profile in front of me. What would you like to work on?" }
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [proposal, setProposal] = useState(null);
  const [applying, setApplying] = useState(false);
  const [pendingProfile, setPendingProfile] = useState(null);
  const [building, setBuilding] = useState(false);
  const buildProgress = useStagedProgress(building,
    ["Reading your profile", "Sharpening your resume with AI", "Building the PDF", "Finalising"]);
  const [masterUrl, setMasterUrl] = useState(null);
  const [masterErr, setMasterErr] = useState("");
  const [previousUrl, setPreviousUrl] = useState(null);
  const [previewHtml, setPreviewHtml] = useState("");
  const [showPreview, setShowPreview] = useState(true);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [resumeHistory, setResumeHistory] = useState([]);
  const [captureBusy, setCaptureBusy] = useState(false);
  const [capturedFacts, setCapturedFacts] = useState(null);
  const [captureMsg, setCaptureMsg] = useState("");
  const scrollRef = useRef(null);

  const [masterTpl, setMasterTpl] = useState(localStorage.getItem("masterTemplate") || "4");
  const loadPreview = async (profileOverride) => {
    setPreviewLoading(true);
    try {
      const body = JSON.stringify({ profile: profileOverride || undefined, template: masterTpl });
      const d = await api("/api/master-resume/preview", { method: "POST", body });
      if (d.ok && d.html) setPreviewHtml(d.html);
    } catch (e) { /* preview is best-effort */ }
    setPreviewLoading(false);
  };
  useEffect(() => { loadPreview(); }, [masterTpl]);
  // Load the last-built master resume + history so the download buttons and the
  // history list survive a page refresh (they used to vanish on reload).
  useEffect(() => {
    (async () => {
      try {
        const d = await api("/api/master-resume/latest");
        if (d && d.ok) {
          if (d.resume_url) setMasterUrl(d.resume_url);
          if (Array.isArray(d.history)) setResumeHistory(d.history);
        }
      } catch (e) { /* best-effort */ }
    })();
  }, []);

  // Load persisted coach chat history so the conversation continues across sessions
  useEffect(() => {
    api("/api/coach/history").then(d => {
      if (d.ok && Array.isArray(d.history) && d.history.length > 0) {
        setMessages(d.history);
      }
      setHistoryLoaded(true);
    }).catch(() => setHistoryLoaded(true));
  }, []);

  // Persist chat history whenever it changes (after the initial load, and only
  // once there's real conversation beyond the opening greeting)
  useEffect(() => {
    if (!historyLoaded) return;
    if (messages.length <= 1) return;
    const t = setTimeout(() => {
      api("/api/coach/history", { method: "POST", body: JSON.stringify({ messages }) }).catch(() => {});
    }, 800);
    return () => clearTimeout(t);
  }, [messages, historyLoaded]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    const next = [...messages, { role: "user", content: text }];
    setMessages(next);
    setInput("");
    setSending(true);
    setProposal(null);
    try {
      const d = await api("/api/coach/chat", {
        method: "POST",
        body: JSON.stringify({ message: text, messages: next.slice(-12) })
      });
      if (d.ok) {
        setMessages(m => [...m, { role: "assistant", content: d.reply }]);
        if (d.proposal) setProposal(d.proposal);
      } else {
        setMessages(m => [...m, { role: "assistant", content: "Sorry, I hit an error: " + (d.error || "unknown") + ". Try again?" }]);
      }
    } catch (e) {
      setMessages(m => [...m, { role: "assistant", content: "Connection error. Please try again." }]);
    }
    setSending(false);
  };

  // Scan the conversation for concrete facts worth adding to the profile
  const captureFacts = async () => {
    if (captureBusy) return;
    setCaptureBusy(true);
    setCaptureMsg("");
    setCapturedFacts(null);
    try {
      const d = await api("/api/coach/capture-facts", { method: "POST", body: JSON.stringify({ messages }) });
      if (d.ok && d.facts && d.facts.length > 0) {
        setCapturedFacts(d.facts);
      } else if (d.ok) {
        setCaptureMsg("Nothing new to add yet - mention specific skills, tools, or achievements in the chat and try again.");
      } else {
        setCaptureMsg(d.error || "Couldn't scan the conversation. Try again.");
      }
    } catch (e) {
      setCaptureMsg("Couldn't reach the server. Try again.");
    }
    setCaptureBusy(false);
  };

  // Turn approved facts into a coach/apply change so they flow through the normal
  // review + save path (and land in resume-rendered fields)
  const applyCapturedFacts = async () => {
    if (!capturedFacts || applying) return;
    const changeText = "Add these facts the candidate confirmed about themselves, placing each in the most appropriate resume-rendered field: "
      + capturedFacts.map(f => `${f.fact} (suggested location: ${f.target})`).join("; ");
    setProposal(changeText);
    setCapturedFacts(null);
    setCaptureMsg("Review the proposed change below, then Apply and Save as usual.");
  };

  const clearHistory = async () => {
    if (!window.confirm("Clear this conversation? Your profile and resume aren't affected.")) return;
    const fresh = [{ role: "assistant", content: "Hi! I'm your career coach. I can help you sharpen your resume, spot skill gaps, and think through career moves into parallel domains that fit your background. I've got your profile in front of me. What would you like to work on?" }];
    setMessages(fresh);
    setProposal(null); setCapturedFacts(null); setCaptureMsg("");
    try { await api("/api/coach/history", { method: "POST", body: JSON.stringify({ messages: fresh }) }); } catch (e) {}
  };

  const applyProposal = async () => {
    if (!proposal || applying) return;
    setApplying(true);
    try {
      const d = await api("/api/coach/apply", { method: "POST", body: JSON.stringify({ change: proposal }) });
      if (d.ok && d.updated_profile) {
        setPendingProfile(d.updated_profile);
        loadPreview(d.updated_profile);
      } else {
        setMessages(m => [...m, { role: "assistant", content: "Couldn't prepare that change: " + (d.error || "error") }]);
        setProposal(null);
      }
    } catch (e) {
      setProposal(null);
    }
    setApplying(false);
  };

  const confirmSave = async () => {
    if (!pendingProfile) return;
    try {
      await api("/api/profile/resume", { method: "POST", body: JSON.stringify({ resume_profile: pendingProfile }) });
      // PHASE 3 BRIDGE: the change is now in the profile - auto-rebuild the master
      // resume so it actually LANDS in the PDF without the user doing it manually.
      setMessages(m => [...m, { role: "assistant", content: "Done. I've updated your master profile and I'm refreshing your master resume now so the change lands in the PDF." }]);
      setMasterUrl(null);
      loadPreview(pendingProfile);
      setPendingProfile(null);
      setProposal(null);
      buildMaster();   // one bounded rebuild, no manual step
      return;
    } catch (e) {
      setMessages(m => [...m, { role: "assistant", content: "Save failed. Please try again." }]);
    }
    setPendingProfile(null);
    setProposal(null);
  };

  const buildMaster = async () => {
    setBuilding(true);
    setMasterErr("");
    setMasterUrl(null);
    try {
      const d = await api("/api/master-resume/build", { method: "POST",
        body: JSON.stringify({ template: masterTpl }) });
      if (d.ok && d.resume_url) {
        setMasterUrl(d.resume_url);
        if (d.previous_url) setPreviousUrl(d.previous_url);
        // refresh the download history list
        try {
          const latest = await api("/api/master-resume/latest");
          if (latest && Array.isArray(latest.history)) setResumeHistory(latest.history);
        } catch (e) {}
      }
      else setMasterErr(d.error || "Could not build resume.");
    } catch (e) {
      setMasterErr("Build failed. Please try again.");
    }
    setBuilding(false);
  };

  return (
    <div style={{ background: "#F8FAFC", minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <div style={{ background: "#1C2B4A", padding: "12px 16px", display: "flex", alignItems: "center", gap: 12, borderBottom: "3px solid #B8934A" }}>
        <button onClick={onBack} style={{ background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.2)", color: "white", borderRadius: 8, padding: "6px 12px", cursor: "pointer", fontSize: 13 }}>← Back</button>
        <div style={{ flex: 1 }}><div style={{ fontSize: 15, fontWeight: 700, color: "white" }}>📄 Resume Studio</div><div style={{ fontSize: 11, color: "#94A3B8" }}>Preview, build, and refine your resume</div></div>
        {messages.length > 1 && (
          <button onClick={clearHistory} title="Clear conversation"
            style={{ background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.2)", color: "rgba(255,255,255,0.8)", borderRadius: 8, padding: "6px 10px", cursor: "pointer", fontSize: 11 }}>
            Clear chat
          </button>
        )}
      </div>

      <div style={{ maxWidth: 640, width: "100%", margin: "0 auto", padding: "14px 14px 0", boxSizing: "border-box", flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
        {/* Live preview — now at the TOP */}
        <div style={{ background: "white", borderRadius: 14, border: "1px solid #E8EDF4", padding: 14, marginBottom: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: showPreview ? 10 : 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#1C2B4A" }}>👁 Resume Preview {previewLoading && <span style={{ fontSize: 11, color: "#94A3B8", fontWeight: 400 }}>updating…</span>}</div>
            <button onClick={() => setShowPreview(s => !s)} style={{ background: "none", border: "1px solid #CBD5E1", borderRadius: 6, padding: "4px 10px", fontSize: 12, color: "#64748B", cursor: "pointer" }}>{showPreview ? "Hide" : "Show"}</button>
          </div>
          {showPreview && (
            <div style={{ border: "1px solid #E2E8F0", borderRadius: 8, overflow: "hidden", background: "#F1F5F9" }}>
              {previewHtml
                ? <iframe title="resume-preview" srcDoc={previewHtml} style={{ width: "100%", height: 520, border: "none", background: "white" }} />
                : <div style={{ padding: 30, textAlign: "center", color: "#94A3B8", fontSize: 13 }}>{previewLoading ? "Loading preview…" : "Preview will appear here."}</div>}
            </div>
          )}
          <div style={{ fontSize: 11, color: "#94A3B8", marginTop: 8 }}>This is a close on-screen preview. The downloaded PDF is the exact, print-ready version.</div>
        </div>

        {/* Master resume build/download card — below the preview */}
        <div style={{ background: "white", borderRadius: 14, border: "1px solid #E8EDF4", padding: 14, marginBottom: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#1C2B4A", marginBottom: 4 }}>📄 Your Master Resume</div>
          <div style={{ fontSize: 12, color: "#64748B", marginBottom: 10, lineHeight: 1.5 }}>A polished, AI-enhanced general resume built from your profile. Best version, not tied to any job. 3 pages, ready to send.</div>
          <button onClick={() => { window.location.hash = "mydata"; }}
            style={{ width: "100%", padding: "9px 11px", background: "#FEF9F0", color: "#92400E", border: "1px solid #F0D9A8", borderRadius: 8, fontWeight: 600, fontSize: 12.5, cursor: "pointer", marginBottom: 10, textAlign: "left" }}>
            ✏️ Edit facts &amp; figures (summary, achievements, skills, bullets) →
          </button>
          <div style={{fontSize:11, fontWeight:700, color:"#64748B", margin:"2px 0 6px", textTransform:"uppercase", letterSpacing:0.5}}>Template</div>
          <div style={{display:"flex", gap:6, marginBottom:10}}>
            {[["4","Corporate"],["1","Serif"],["3","Clean"],["2","ATS"]].map(([t,label])=>(
              <button key={t} onClick={()=>{setMasterTpl(t); localStorage.setItem("masterTemplate", t);}}
                style={{flex:1, padding:"7px 4px", fontSize:11.5, fontWeight:700, cursor:"pointer",
                  borderRadius:8, border: masterTpl===t?"2px solid #B8934A":"1px solid #E2E8F0",
                  background: masterTpl===t?"#FFFBEB":"white", color: masterTpl===t?"#1C2B4A":"#64748B"}}>
                {label}
              </button>
            ))}
          </div>
          {!masterUrl ? (
            <button onClick={buildMaster} disabled={building} style={{ width: "100%", padding: 11, background: building ? "#94A3B8" : "#1C2B4A", color: "white", border: "none", borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: building ? "not-allowed" : "pointer" }}>
              {building ? buildProgress.text : "✨ Build My Master Resume"}
            </button>
          ) : (
            <div style={{ display: "flex", gap: 8 }}>
              <a href={masterUrl} target="_blank" rel="noreferrer" style={{ flex: 1, textAlign: "center", padding: 11, background: "#16A34A", color: "white", borderRadius: 8, fontWeight: 700, fontSize: 13, textDecoration: "none" }}>⬇️ PDF</a>
              <button onClick={()=>downloadDocx("/api/master-resume/docx", "Resume.docx")} style={{ flex: 1, padding: 11, background: "#2B579A", color: "white", border: "none", borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: "pointer" }}>⬇️ Word</button>
              <button onClick={buildMaster} disabled={building} style={{ padding: "11px 14px", background: "white", color: "#1C2B4A", border: "1.5px solid #1C2B4A", borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: "pointer" }}>{building ? "…" : "Rebuild"}</button>
            </div>
          )}
          {masterErr && <div style={{ color: "#DC2626", fontSize: 12, marginTop: 8 }}>{masterErr}</div>}
          {previousUrl && <a href={previousUrl} target="_blank" rel="noreferrer" style={{ display: "inline-block", marginTop: 8, fontSize: 12, color: "#64748B", textDecoration: "underline" }}>↩ Download previous version</a>}
          {resumeHistory.length > 0 && (
            <div style={{ marginTop: 14, borderTop: "1px solid #EEF2F7", paddingTop: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#1C2B4A", marginBottom: 8 }}>🕑 Recent resumes (last {resumeHistory.length})</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {resumeHistory.map((h, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#475569" }}>
                    <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {i === 0 ? "★ " : ""}{(() => { try { return new Date(h.built_at).toLocaleString([], {month:"short", day:"numeric", hour:"2-digit", minute:"2-digit"}); } catch(e){ return h.built_at || "Resume"; } })()}
                    </span>
                    <a href={h.url} target="_blank" rel="noreferrer" style={{ color: "#16A34A", fontWeight: 600, textDecoration: "none" }}>PDF</a>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Resume refinement chat — below build */}
        <div style={{ fontSize: 12, fontWeight: 700, color: "#1C2B4A", margin: "2px 0 8px" }}>💬 Refine your resume with AI</div>
        <div ref={scrollRef} style={{ flex: 1, overflowY: "auto", background: "white", borderRadius: 14, border: "1px solid #E8EDF4", padding: 14, marginBottom: 10, minHeight: 200 }}>
          {messages.map((m, i) => (
            <div key={i} style={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start", marginBottom: 10 }}>
              <div style={{ maxWidth: "82%", padding: "9px 13px", borderRadius: 12, fontSize: 13, lineHeight: 1.5, whiteSpace: "pre-wrap", background: m.role === "user" ? "#1C2B4A" : "#F1F5F9", color: m.role === "user" ? "white" : "#1C2B4A" }}>{m.content}</div>
            </div>
          ))}
          {sending && <div style={{ display: "flex", justifyContent: "flex-start" }}><div style={{ padding: "9px 13px", borderRadius: 12, fontSize: 13, background: "#F1F5F9", color: "#94A3B8" }}>Thinking…</div></div>}

          {proposal && !pendingProfile && (
            <div style={{ background: "#F5F3FF", border: "1px solid #DDD6FE", borderRadius: 10, padding: 12, marginTop: 4 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#6D28D9", marginBottom: 4 }}>Suggested resume change</div>
              <div style={{ fontSize: 12, color: "#5B21B6", marginBottom: 10, lineHeight: 1.5 }}>{proposal}</div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={applyProposal} disabled={applying} style={{ flex: 1, padding: 9, background: "#6D28D9", color: "white", border: "none", borderRadius: 7, fontWeight: 700, fontSize: 13, cursor: "pointer" }}>{applying ? "Preparing…" : "Apply to my resume"}</button>
                <button onClick={() => setProposal(null)} style={{ padding: "9px 12px", background: "white", color: "#6D28D9", border: "1.5px solid #6D28D9", borderRadius: 7, fontWeight: 600, fontSize: 13, cursor: "pointer" }}>Dismiss</button>
              </div>
            </div>
          )}

          {pendingProfile && (
            <div style={{ background: "#F0FDF4", border: "1px solid #86EFAC", borderRadius: 10, padding: 12, marginTop: 4 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#15803D", marginBottom: 4 }}>Confirm change</div>
              <div style={{ fontSize: 12, color: "#166534", marginBottom: 10, lineHeight: 1.5 }}>This will update your master profile. Your master resume and future tailored resumes will use the updated version.</div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={confirmSave} style={{ flex: 1, padding: 9, background: "#16A34A", color: "white", border: "none", borderRadius: 7, fontWeight: 700, fontSize: 13, cursor: "pointer" }}>Save it</button>
                <button onClick={() => { setPendingProfile(null); setProposal(null); }} style={{ padding: "9px 12px", background: "white", color: "#16A34A", border: "1.5px solid #16A34A", borderRadius: 7, fontWeight: 600, fontSize: 13, cursor: "pointer" }}>Cancel</button>
              </div>
            </div>
          )}

          {/* Capture facts from chat into profile */}
          {messages.length > 2 && !proposal && !pendingProfile && (
            <div style={{ marginTop: 4 }}>
              {captureMsg && (
                <div style={{ fontSize: 11.5, color: "#64748B", background: "#F8FAFC",
                  border: "1px solid #E2E8F0", borderRadius: 8, padding: "8px 11px", marginBottom: 8, lineHeight: 1.5 }}>
                  {captureMsg}
                </div>
              )}
              {capturedFacts && capturedFacts.length > 0 && (
                <div style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 10, padding: 12, marginBottom: 8 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#15803D", marginBottom: 8 }}>
                    Found {capturedFacts.length} thing{capturedFacts.length !== 1 ? "s" : ""} worth adding to your profile
                  </div>
                  {capturedFacts.map((f, i) => (
                    <div key={i} style={{ fontSize: 12, color: "#374151", marginBottom: 6, paddingLeft: 8, borderLeft: "2px solid #86EFAC", lineHeight: 1.45 }}>
                      {f.fact}
                      <span style={{ fontSize: 10.5, color: "#94A3B8" }}> → {f.target}</span>
                    </div>
                  ))}
                  <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                    <button onClick={applyCapturedFacts} style={{ flex: 1, padding: 9, background: "#16A34A", color: "white", border: "none", borderRadius: 7, fontWeight: 700, fontSize: 12.5, cursor: "pointer" }}>
                      Add to profile
                    </button>
                    <button onClick={() => setCapturedFacts(null)} style={{ padding: "9px 14px", background: "white", color: "#64748B", border: "1px solid #E2E8F0", borderRadius: 7, fontSize: 12.5, cursor: "pointer" }}>
                      Dismiss
                    </button>
                  </div>
                </div>
              )}
              {!capturedFacts && (
                <button onClick={captureFacts} disabled={captureBusy}
                  style={{ fontSize: 11.5, color: "#B8934A", background: "none",
                    border: "1px solid rgba(184,147,74,0.35)", borderRadius: 8,
                    padding: "7px 12px", cursor: "pointer", fontWeight: 600, width: "100%" }}>
                  {captureBusy ? "Scanning conversation…" : "✨ Add what I mentioned to my profile"}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Input */}
        <div style={{ display: "flex", gap: 8, paddingBottom: 14 }}>
          <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} placeholder="e.g. 'Make my summary punchier' or 'Add cloud migration to my skills'" style={{ flex: 1, padding: "11px 14px", border: "1px solid #CBD5E1", borderRadius: 22, fontSize: 13, outline: "none" }} />
          <button onClick={send} disabled={sending || !input.trim()} style={{ padding: "11px 18px", background: (sending || !input.trim()) ? "#94A3B8" : "#1C2B4A", color: "white", border: "none", borderRadius: 22, fontWeight: 700, fontSize: 14, cursor: (sending || !input.trim()) ? "not-allowed" : "pointer" }}>Send</button>
        </div>
      </div>
    </div>
  );
}


function ProfileDataPage({ onBack }) {
  const [pools, setPools] = useState(null);
  const [summary, setSummary] = useState("");
  const [summaryDirty, setSummaryDirty] = useState(false);
  const [rebuilding, setRebuilding] = useState(false);
  const [rebuildMsg, setRebuildMsg] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [dirty, setDirty] = useState(false);
  const [savedMsg, setSavedMsg] = useState("");
  const [openRole, setOpenRole] = useState(null);
  const [compCount, setCompCount] = useState(0);

  useEffect(() => {
    api("/api/profile/pools").then(d => {
      if (d.ok && d.pools) { setPools(d.pools); setCompCount(d.competency_count || 0); }
      else setErr(d.error || "Could not load your profile data.");
      setLoading(false);
    }).catch(() => { setErr("Could not reach the server."); setLoading(false); });
    // Load the summary (it lives in resume_profile, not pools) so it's editable too.
    api("/api/profile/resume").then(d => {
      if (d && d.resume_profile) setSummary(d.resume_profile.summary || "");
    }).catch(() => {});
  }, []);

  const markDirty = () => { setDirty(true); setSavedMsg(""); };

  // ── Add a NEW role (job) to the resume ──
  const [addingRole, setAddingRole] = useState(false);
  const [nrTitle, setNrTitle] = useState("");   const [nrCompany, setNrCompany] = useState("");
  const [nrDates, setNrDates] = useState("");   const [nrLocation, setNrLocation] = useState("");
  const [nrBullet, setNrBullet] = useState("");
  const addRole = () => {
    if (!nrTitle.trim() || !nrCompany.trim()) return;
    const now = new Date().toISOString();
    const role = {
      id: `role_new_${Date.now()}`,
      title: nrTitle.trim(), company: nrCompany.trim(),
      dates: nrDates.trim(), location: nrLocation.trim(),
      bullet_pool: nrBullet.trim()
        ? [{ id:`role_new_${Date.now()}_b1`, text:nrBullet.trim(), tags:[], strength:"core", source:"manual", added_at:now }]
        : [],
    };
    setPools(p => ({ ...p, roles: [role, ...(p.roles || [])] }));
    setNrTitle(""); setNrCompany(""); setNrDates(""); setNrLocation(""); setNrBullet("");
    setAddingRole(false); markDirty();
  };

  const save = async () => {
    if ((!pools || saving) && !summaryDirty) return;
    setSaving(true); setErr("");
    try {
      let ok = true;
      if (pools) {
        const d = await api("/api/profile/pools", { method: "POST", body: JSON.stringify({ pools }) });
        ok = ok && d.ok;
        if (!d.ok) setErr(d.error || "Save failed.");
      }
      // Persist the summary edit (lives in resume_profile). Fetch, patch, save.
      if (summaryDirty) {
        const cur = await api("/api/profile/resume");
        const prof = (cur && cur.resume_profile) ? cur.resume_profile : {};
        prof.summary = summary;
        const d2 = await api("/api/profile/resume", { method: "POST", body: JSON.stringify({ resume_profile: prof }) });
        ok = ok && (d2.ok !== false);
      }
      if (ok) { setDirty(false); setSummaryDirty(false); setSavedMsg("Saved. Your resume builder will use this."); setTimeout(()=>setSavedMsg(""), 6000); }
    } catch (e) { setErr("Save failed. Try again."); }
    setSaving(false);
  };

  const saveAndRebuild = async () => {
    await save();
    setRebuilding(true); setRebuildMsg("Rebuilding your resume with the changes…");
    try {
      const d = await api("/api/master-resume/build", { method: "POST",
        body: JSON.stringify({ template: localStorage.getItem("masterTemplate") || "4" }) });
      if (d.ok && d.resume_url) setRebuildMsg("Done. Your master resume is updated. Open Resume Studio to download it.");
      else setRebuildMsg(d.error || "Rebuild could not complete. Try from Resume Studio.");
    } catch (e) { setRebuildMsg("Rebuild failed. Try from Resume Studio."); }
    setRebuilding(false);
    setTimeout(()=>setRebuildMsg(""), 10000);
  };

  // ── Profile Strength: a readout of how full/strong the pools are ──
  const strength = (() => {
    if (!pools) return { score: 0, label: "—", parts: [] };
    const caps = (pools.capabilities || []).filter(c => c.strength !== "archived").length;
    const achs = (pools.achievements || []).filter(a => a.strength !== "archived").length;
    const roles = (pools.roles || []);
    const bullets = roles.reduce((n, r) => n + (r.bullet_pool || []).filter(b => b.strength !== "archived").length, 0);
    const skillVals = Object.values(pools.skills || {}).reduce((n, v) => n + v.length, 0);
    const thinRoles = roles.filter(r => (r.bullet_pool || []).filter(b => b.strength !== "archived").length < 3).length;
    // Simple weighted score out of 100
    let score = 0;
    score += Math.min(caps, 12) / 12 * 20;
    score += Math.min(achs, 6) / 6 * 20;
    score += Math.min(bullets, 25) / 25 * 25;
    score += Math.min(skillVals, 30) / 30 * 15;
    score += compCount > 0 ? Math.min(compCount, 15) / 15 * 20 : 0;
    score = Math.round(score);
    const label = score >= 80 ? "Strong" : score >= 55 ? "Solid" : score >= 30 ? "Developing" : "Thin";
    const parts = [
      { k: "Capabilities", v: caps, hint: caps < 6 ? "add a few more" : "" },
      { k: "Achievements", v: achs, hint: achs < 4 ? "aim for 4+" : "" },
      { k: "Experience bullets", v: bullets, hint: thinRoles > 0 ? `${thinRoles} role(s) look thin` : "" },
      { k: "Skills", v: skillVals, hint: "" },
      { k: "Competencies", v: compCount, hint: compCount === 0 ? "build via Career Coach" : "" },
    ];
    return { score, label, parts };
  })();

  const strengthColor = strength.score >= 80 ? "#16A34A" : strength.score >= 55 ? "#B8934A" : strength.score >= 30 ? "#D97706" : "#DC2626";

  // ── Mutators (all mark dirty; nothing saves until Save) ──
  const setCap = (i, patch) => { const c = [...pools.capabilities]; c[i] = { ...c[i], ...patch }; setPools({ ...pools, capabilities: c }); markDirty(); };
  const addCap = () => { setPools({ ...pools, capabilities: [...(pools.capabilities||[]), { id: "cap_new_" + Date.now(), name: "", tags: [], strength: "core", source: "manual" }] }); markDirty(); };
  const setAch = (i, patch) => { const a = [...pools.achievements]; a[i] = { ...a[i], ...patch }; setPools({ ...pools, achievements: a }); markDirty(); };
  const setSkillCat = (cat, valStr) => { const s = { ...pools.skills }; s[cat] = valStr.split(",").map(x => x.trim()).filter(Boolean); setPools({ ...pools, skills: s }); markDirty(); };
  const setBullet = (ri, bi, patch) => { const roles = [...pools.roles]; const bp = [...roles[ri].bullet_pool]; bp[bi] = { ...bp[bi], ...patch }; roles[ri] = { ...roles[ri], bullet_pool: bp }; setPools({ ...pools, roles }); markDirty(); };
  const setRole = (ri, patch) => { const roles = [...pools.roles]; roles[ri] = { ...roles[ri], ...patch }; setPools({ ...pools, roles }); markDirty(); };
  const removeRole = (ri) => { if(!window.confirm("Remove this role and all its bullets from your resume?")) return; setPools(p => ({ ...p, roles: p.roles.filter((_, i) => i !== ri) })); markDirty(); };
  const addBullet = (ri) => { const roles = [...pools.roles]; roles[ri] = { ...roles[ri], bullet_pool: [...(roles[ri].bullet_pool||[]), { id: "b_new_" + Date.now(), text: "", tags: [], strength: "core", source: "manual" }] }; setPools({ ...pools, roles }); markDirty(); };

  const strengthPill = (val, onCycle) => {
    const map = { core: ["#065F46", "#D1FAE5", "Core"], supporting: ["#92400E", "#FEF3C7", "Support"], archived: ["#6B7280", "#F3F4F6", "Archived"] };
    const [fg, bg, label] = map[val] || map.core;
    return <button onClick={onCycle} title="Tap to change: Core / Support / Archived"
      style={{ fontSize: 10, fontWeight: 700, color: fg, background: bg, border: "none", borderRadius: 6, padding: "3px 8px", cursor: "pointer" }}>{label}</button>;
  };
  const cycleStrength = (cur) => cur === "core" ? "supporting" : cur === "supporting" ? "archived" : "core";

  const cardStyle = { background: "white", border: "1px solid #E8EDF4", borderRadius: 14, padding: 16, marginBottom: 14 };
  const secHead = { fontSize: 14, fontWeight: 800, color: "#1C2B4A", marginBottom: 2 };
  const secSub = { fontSize: 11.5, color: "#94A3B8", marginBottom: 12, lineHeight: 1.5 };

  return (
    <div style={{ minHeight: "100vh", background: "#F4F6FA", display: "flex", flexDirection: "column" }}>
      <div style={{ background: "#1C2B4A", padding: "12px 16px", display: "flex", alignItems: "center", gap: 12, borderBottom: "3px solid #B8934A", position: "sticky", top: 0, zIndex: 10 }}>
        <button onClick={onBack} style={{ background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.2)", color: "white", borderRadius: 8, padding: "6px 12px", cursor: "pointer", fontSize: 13 }}>← Back</button>
        <div style={{ flex: 1 }}><div style={{ fontSize: 15, fontWeight: 700, color: "white" }}>👤 Profile</div><div style={{ fontSize: 11, color: "#94A3B8" }}>Your career content, organized. Your resume is built from this.</div></div>
        <button onClick={save} disabled={(!dirty && !summaryDirty) || saving}
          style={{ background: (dirty||summaryDirty) ? "#B8934A" : "rgba(255,255,255,0.1)", border: "none", color: (dirty||summaryDirty) ? "#1C2B4A" : "#8893A7", borderRadius: 8, padding: "7px 16px", cursor: (dirty||summaryDirty) ? "pointer" : "default", fontSize: 13, fontWeight: 700 }}>
          {saving ? "Saving…" : (dirty||summaryDirty) ? "Save changes" : "Saved"}
        </button>
      </div>

      <div style={{ maxWidth: 700, width: "100%", margin: "0 auto", padding: 14, boxSizing: "border-box" }}>
        {loading && <div style={{ textAlign: "center", color: "#94A3B8", padding: 40 }}>Loading your profile…</div>}
        {err && <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", color: "#B91C1C", borderRadius: 10, padding: 12, marginBottom: 12, fontSize: 13 }}>{err}</div>}
        {savedMsg && <div style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", color: "#15803D", borderRadius: 10, padding: 12, marginBottom: 12, fontSize: 13 }}>{savedMsg}</div>}

        {pools && (<>
          {/* ── Profile Strength ── */}
          <div style={{ ...cardStyle, background: "linear-gradient(135deg,#1C2B4A,#2A3B5E)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "white" }}>Profile Strength</div>
              <div style={{ fontSize: 13, fontWeight: 800, color: strengthColor }}>{strength.label} · {strength.score}%</div>
            </div>
            <div style={{ height: 8, background: "rgba(255,255,255,0.12)", borderRadius: 6, overflow: "hidden", marginBottom: 12 }}>
              <div style={{ width: strength.score + "%", height: "100%", background: strengthColor, transition: "width .4s" }} />
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {strength.parts.map((p, i) => (
                <div key={i} style={{ flex: "1 1 30%", minWidth: 90, background: "rgba(255,255,255,0.06)", borderRadius: 8, padding: "7px 9px" }}>
                  <div style={{ fontSize: 16, fontWeight: 800, color: "white" }}>{p.v}</div>
                  <div style={{ fontSize: 10, color: "#B8C4D8" }}>{p.k}</div>
                  {p.hint && <div style={{ fontSize: 9.5, color: "#F59E0B", marginTop: 2 }}>{p.hint}</div>}
                </div>
              ))}
            </div>
          </div>

          {/* ── Professional Summary (editable) ── */}
          <div style={cardStyle}>
            <div style={secHead}>Professional Summary</div>
            <div style={secSub}>The opening paragraph of your resume. Edit the wording and figures directly. Keep it to 3-4 short sentences.</div>
            <VoiceSafeTextarea value={summary}
              onChange={e => { setSummary(e.target.value); setSummaryDirty(true); setSavedMsg(""); }}
              placeholder="e.g. IT service management leader with 15 years in healthcare technology…"
              rows={5}
              style={{ width: "100%", padding: "10px 12px", border: "1px solid #E2E8F0", borderRadius: 8, fontSize: 13, lineHeight: 1.5, resize: "vertical", boxSizing: "border-box", fontFamily: "inherit" }} />
            <div style={{ fontSize: 11, color: summary.length > 600 ? "#DC2626" : "#94A3B8", marginTop: 4 }}>{summary.length} characters {summary.length > 600 ? "(a bit long, aim for under 600)" : ""}</div>
          </div>

          {/* ── Capabilities ── */}
          <div style={cardStyle}>
            <div style={secHead}>Capabilities</div>
            <div style={secSub}>High-level strengths. Tap the pill to mark Core, Support, or Archived (archived won't appear on resumes).</div>
            {(pools.capabilities || []).map((c, i) => (
              <div key={c.id || i} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 7, opacity: c.strength === "archived" ? 0.5 : 1 }}>
                <input value={c.name} onChange={e => setCap(i, { name: e.target.value })} placeholder="Capability name"
                  style={{ flex: 1, padding: "8px 10px", border: "1px solid #E2E8F0", borderRadius: 8, fontSize: 13 }} />
                {strengthPill(c.strength || "core", () => setCap(i, { strength: cycleStrength(c.strength || "core") }))}
              </div>
            ))}
            <button onClick={addCap} style={{ fontSize: 12, color: "#B8934A", background: "none", border: "1px dashed #D6BC85", borderRadius: 8, padding: "7px 12px", cursor: "pointer", fontWeight: 600, width: "100%", marginTop: 4 }}>+ Add capability</button>
          </div>

          {/* ── Technical Skills ── */}
          <div style={cardStyle}>
            <div style={secHead}>Technical Skills</div>
            <div style={secSub}>Real, named tools grouped by category. Comma-separated. No filler words.</div>
            {Object.entries(pools.skills || {}).map(([cat, vals]) => (
              <div key={cat} style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 11.5, fontWeight: 700, color: "#475569", marginBottom: 3 }}>{cat}</div>
                <input value={vals.join(", ")} onChange={e => setSkillCat(cat, e.target.value)}
                  style={{ width: "100%", padding: "8px 10px", border: "1px solid #E2E8F0", borderRadius: 8, fontSize: 12.5, boxSizing: "border-box" }} />
              </div>
            ))}
          </div>

          {/* ── Achievements ── */}
          <div style={cardStyle}>
            <div style={secHead}>Achievements</div>
            <div style={secSub}>Your strongest, quantified wins. The resume uses up to 4.</div>
            {(pools.achievements || []).map((a, i) => (
              <div key={a.id || i} style={{ border: "1px solid #EEF2F7", borderRadius: 10, padding: 10, marginBottom: 8, opacity: a.strength === "archived" ? 0.5 : 1 }}>
                <div style={{ display: "flex", gap: 8, marginBottom: 6 }}>
                  <input value={a.title} onChange={e => setAch(i, { title: e.target.value })} placeholder="Achievement title"
                    style={{ flex: 1, padding: "7px 9px", border: "1px solid #E2E8F0", borderRadius: 7, fontSize: 12.5, fontWeight: 600 }} />
                  {strengthPill(a.strength || "core", () => setAch(i, { strength: cycleStrength(a.strength || "core") }))}
                </div>
                <VoiceSafeTextarea value={a.body} onChange={e => setAch(i, { body: e.target.value })} placeholder="Full-sentence description with metrics"
                  style={{ width: "100%", height: 60, padding: "7px 9px", border: "1px solid #E2E8F0", borderRadius: 7, fontSize: 12, boxSizing: "border-box", resize: "vertical" }} />
              </div>
            ))}
          </div>

          {/* ── Experience (bullet pools per role) ── */}
          <div style={cardStyle}>
            <div style={secHead}>Experience</div>
            <div style={secSub}>Each role holds a pool of bullets. Add as many as you like: the resume selects the strongest that fit. This is where gap-coach content lands, filed under the right role.</div>

            {!addingRole ? (
              <button onClick={()=>setAddingRole(true)} style={{margin:"8px 0 12px", padding:"9px 14px",
                background:"#1C2B4A", color:"white", border:"none", borderRadius:9,
                fontSize:12.5, fontWeight:700, cursor:"pointer"}}>
                ＋ Add role (new job)
              </button>
            ) : (
              <div style={{background:"#F8FAFC", border:"1px solid #E2E8F0", borderRadius:12,
                padding:"12px 14px", margin:"8px 0 14px"}}>
                <div style={{fontSize:12.5, fontWeight:800, color:"#1C2B4A", marginBottom:8}}>New role</div>
                <div style={{display:"flex", gap:8, marginBottom:8}}>
                  <input value={nrTitle} onChange={e=>setNrTitle(e.target.value)} placeholder="Job title *"
                    style={{flex:1, padding:"9px 10px", borderRadius:8, border:"1px solid #E2E8F0", fontSize:12.5}}/>
                  <input value={nrCompany} onChange={e=>setNrCompany(e.target.value)} placeholder="Company *"
                    style={{flex:1, padding:"9px 10px", borderRadius:8, border:"1px solid #E2E8F0", fontSize:12.5}}/>
                </div>
                <div style={{display:"flex", gap:8, marginBottom:8}}>
                  <input value={nrDates} onChange={e=>setNrDates(e.target.value)} placeholder="Dates (e.g. 03/2026 - Present)"
                    style={{flex:1, padding:"9px 10px", borderRadius:8, border:"1px solid #E2E8F0", fontSize:12.5}}/>
                  <input value={nrLocation} onChange={e=>setNrLocation(e.target.value)} placeholder="Location"
                    style={{flex:1, padding:"9px 10px", borderRadius:8, border:"1px solid #E2E8F0", fontSize:12.5}}/>
                </div>
                <input value={nrBullet} onChange={e=>setNrBullet(e.target.value)}
                  placeholder="First achievement bullet (optional - add more after)"
                  style={{width:"100%", boxSizing:"border-box", padding:"9px 10px", borderRadius:8,
                    border:"1px solid #E2E8F0", fontSize:12.5, marginBottom:10}}/>
                <div style={{display:"flex", gap:8}}>
                  <button onClick={addRole} disabled={!nrTitle.trim()||!nrCompany.trim()}
                    style={{padding:"9px 16px", background:"#16A34A", color:"white", border:"none",
                      borderRadius:8, fontSize:12.5, fontWeight:700, cursor:"pointer",
                      opacity:(!nrTitle.trim()||!nrCompany.trim())?0.5:1}}>
                    Add role
                  </button>
                  <button onClick={()=>setAddingRole(false)} style={{padding:"9px 14px", background:"white",
                    color:"#64748B", border:"1px solid #E2E8F0", borderRadius:8, fontSize:12.5, cursor:"pointer"}}>
                    Cancel
                  </button>
                </div>
                <div style={{fontSize:10.5, color:"#94A3B8", marginTop:8}}>
                  The new role appears at the top. Add bullets to it below, then <b>Save</b> - it flows straight into every future resume build.
                </div>
              </div>
            )}
            {(pools.roles || []).map((r, ri) => {
              const active = (r.bullet_pool || []).filter(b => b.strength !== "archived").length;
              const open = openRole === ri;
              return (
                <div key={r.id || ri} style={{ border: "1px solid #EEF2F7", borderRadius: 10, marginBottom: 8, overflow: "hidden" }}>
                  <button onClick={() => setOpenRole(open ? null : ri)}
                    style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "11px 12px", background: open ? "#F8FAFC" : "white", border: "none", cursor: "pointer", textAlign: "left" }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#1C2B4A" }}>{r.company || "Role"}</div>
                      <div style={{ fontSize: 11, color: "#94A3B8" }}>{r.title} · {r.dates}</div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 10.5, fontWeight: 700, color: active < 3 ? "#DC2626" : "#64748B", background: active < 3 ? "#FEF2F2" : "#F1F5F9", borderRadius: 10, padding: "2px 8px" }}>{active} bullet{active !== 1 ? "s" : ""}{active < 3 ? " · thin" : ""}</span>
                      <span style={{ color: "#94A3B8", fontSize: 12 }}>{open ? "▲" : "▼"}</span>
                    </div>
                  </button>
                  {open && (
                    <div style={{ padding: "4px 12px 12px" }}>
                      {/* Editable role header */}
                      <div style={{ display:"flex", gap:8, marginBottom:8 }}>
                        <input value={r.title||""} onChange={e=>setRole(ri,{title:e.target.value})} placeholder="Job title"
                          style={{ flex:1, padding:"8px 10px", border:"1px solid #E2E8F0", borderRadius:7, fontSize:12.5, fontWeight:600 }} />
                        <input value={r.company||""} onChange={e=>setRole(ri,{company:e.target.value})} placeholder="Company"
                          style={{ flex:1, padding:"8px 10px", border:"1px solid #E2E8F0", borderRadius:7, fontSize:12.5, fontWeight:600 }} />
                      </div>
                      <div style={{ display:"flex", gap:8, marginBottom:6, alignItems:"center" }}>
                        <input value={r.dates||""} onChange={e=>setRole(ri,{dates:e.target.value})} placeholder="Dates (e.g. 08/2016 - 11/2024)"
                          style={{ flex:1, padding:"8px 10px", border:"1px solid #E2E8F0", borderRadius:7, fontSize:12.5 }} />
                        <input value={r.location||""} onChange={e=>setRole(ri,{location:e.target.value})} placeholder="Location"
                          style={{ flex:1, padding:"8px 10px", border:"1px solid #E2E8F0", borderRadius:7, fontSize:12.5 }} />
                        <button onClick={()=>removeRole(ri)} title="Remove this role"
                          style={{ padding:"7px 10px", background:"#FEF2F2", color:"#B91C1C",
                            border:"1px solid #FECACA", borderRadius:7, fontSize:11.5, cursor:"pointer", whiteSpace:"nowrap" }}>
                          ✕ Remove
                        </button>
                      </div>
                      <div style={{ fontSize:10, color:"#94A3B8", marginBottom:8 }}>Edits here update the role heading on every future build. Remember to Save.</div>
                      {(r.bullet_pool || []).map((b, bi) => (
                        <div key={b.id || bi} style={{ display: "flex", gap: 8, marginBottom: 7, alignItems: "flex-start", opacity: b.strength === "archived" ? 0.5 : 1 }}>
                          <VoiceSafeTextarea value={b.text} onChange={e => setBullet(ri, bi, { text: e.target.value })} placeholder="Bullet: led with an action verb, include a metric"
                            style={{ flex: 1, height: 44, padding: "7px 9px", border: "1px solid #E2E8F0", borderRadius: 7, fontSize: 12, boxSizing: "border-box", resize: "vertical" }} />
                          {strengthPill(b.strength || "core", () => setBullet(ri, bi, { strength: cycleStrength(b.strength || "core") }))}
                        </div>
                      ))}
                      <button onClick={() => addBullet(ri)} style={{ fontSize: 12, color: "#B8934A", background: "none", border: "1px dashed #D6BC85", borderRadius: 8, padding: "6px 12px", cursor: "pointer", fontWeight: 600, width: "100%", marginTop: 2 }}>+ Add bullet to {r.company || "this role"}</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* ── Competencies (read-only pointer to Coach) ── */}
          <div style={{ ...cardStyle, background: "#F8FAFC" }}>
            <div style={secHead}>Competencies</div>
            <div style={secSub}>You have {compCount} competencies with evidence. These are built and strengthened through conversation in Career Coach, then feed into your resume here.</div>
            <button onClick={() => { window.location.hash = "profile"; }}
              style={{ fontSize: 12, color: "#5B21B6", background: "white", border: "1px solid #DDD6FE", borderRadius: 8, padding: "8px 14px", cursor: "pointer", fontWeight: 600 }}>
              Open Career Coach to build competencies →
            </button>
          </div>

          <div style={{ height: 30 }} />
        </>)}
      </div>

      {(dirty || summaryDirty || rebuildMsg || rebuilding) && (
        <div style={{ position: "sticky", bottom: 0, background: "white", borderTop: "1px solid #E2E8F0", padding: 12, display: "flex", flexDirection: "column", gap: 8, alignItems: "center", boxShadow: "0 -4px 12px rgba(0,0,0,0.05)" }}>
          {rebuildMsg && <div style={{ fontSize: 12.5, color: "#15803D", textAlign: "center" }}>{rebuildMsg}</div>}
          {(dirty || summaryDirty) && (
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={save} disabled={saving || rebuilding}
                style={{ background: "white", border: "1.5px solid #B8934A", color: "#92400E", borderRadius: 10, padding: "11px 20px", cursor: "pointer", fontSize: 14, fontWeight: 700 }}>
                {saving ? "Saving…" : "Save"}
              </button>
              <button onClick={saveAndRebuild} disabled={saving || rebuilding}
                style={{ background: "#1C2B4A", border: "none", color: "white", borderRadius: 10, padding: "11px 24px", cursor: "pointer", fontSize: 14, fontWeight: 700 }}>
                {rebuilding ? "Rebuilding…" : "Save & Rebuild Resume"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function AppMain() {
  const [authed, setAuthed] = useState(!!getToken());
  const [currentUser,  setCurrentUser]  = useState(localStorage.getItem("username") || "");
  const [showAdmin,    setShowAdmin]    = useState(window.location.hash === '#admin');
  const [pendingCount, setPendingCount] = useState(0);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [checkStatusMsg, setCheckStatusMsg] = useState("");
  const isAdmin = currentUser === "aowias";
  const [showProfile, setShowProfile] = useState(window.location.hash === '#profile');
  const [updateReady, setUpdateReady] = useState(false);
  const [resumeOnly, setResumeOnly] = useState(false);
  const [showOnboard, setShowOnboard] = useState(false);
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" && window.innerWidth < 720);
  const [toasts, setToasts] = useState([]);
  const [schedule, setSchedule] = useState({scrape_enabled:true, scrape_hour:10, scrape_minute:0});
  const toast = (msg, kind="ok", onClick=null) => {
    const id = Date.now() + Math.random();
    setToasts(t => [...t.slice(-2), { id, msg, kind, onClick }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), onClick ? 6000 : 3200);
  };


  useEffect(() => {
    const onR = () => setIsMobile(window.innerWidth < 720);
    window.addEventListener("resize", onR);
    return () => window.removeEventListener("resize", onR);
  }, []);

  // "Next scan" label for the daily-hunt banner (10:00 AM Melbourne, once daily)
  const nextScanLabel = (() => {
    if (schedule.next_label && !["paused"].includes(schedule.next_label)) return schedule.next_label;
    try {
      const H = schedule.scrape_hour ?? 10, M = schedule.scrape_minute ?? 0;
      const h12 = ((H + 11) % 12) + 1, ap = H < 12 ? "AM" : "PM";
      const tstr = `${h12}:${String(M).padStart(2,"0")} ${ap}`;
      const mel = new Date(new Date().toLocaleString("en-US", {timeZone:"Australia/Melbourne"}));
      const scan = new Date(mel); scan.setHours(H, M, 0, 0);
      return (mel < scan ? "today " : "tomorrow ") + tstr;
    } catch(e){ return "10:00 AM daily"; }
  })();

  // Load the current user's mode flags so we can adapt the UI (hide scraping when
  // the account is in resume-builder-only mode), and decide whether the new-user
  // onboarding wizard should mount (never onboarded AND no profile yet).
  useEffect(() => {
    if (!getToken()) return;
    api("/api/me").then(async d => {
      // FAIL CLOSED: only act on a payload that is provably a real /api/me
      // response (username always present on success). During a deploy restart
      // or on flaky wifi, error bodies used to slip through and - lacking
      // onboarded_at - made an existing account look brand new, mounting the
      // onboarding wizard over a fully built profile.
      if (!d || !d.username) return;
      if (typeof d.resume_only_mode === "boolean") setResumeOnly(d.resume_only_mode);
      if (d.schedule) setSchedule(d.schedule);
      try {
        if (window.location.hash === "#onboard") { setShowOnboard(true); }   // test hook
        else if (!d.onboarded_at && !d.resume_only_mode) {
          const p = await api("/api/profile/resume");
          // Mount ONLY on the server's affirmative "no profile saved yet"
          // (key present, value null). A missing key or an error body means
          // the fetch failed - never treat that as a new user.
          if (p && !p.error &&
              Object.prototype.hasOwnProperty.call(p, "resume_profile") &&
              !p.resume_profile) setShowOnboard(true);
        }
      } catch(e) {}
      // The toolbar used to render localStorage."username" directly. That value
      // survives an unclean session end (token expiry, cache clear, account switch
      // without logout), so it could show a previous account's name alongside the
      // current user's data. /api/me is derived from the JWT server-side, so treat
      // it as authoritative and reconcile the cached copy.
      if (d.username) {
        setCurrentUser(d.username);
        localStorage.setItem("username", d.username);
        sessionStorage.setItem("username", d.username);
      }
    }).catch(()=>{});
  }, [authed]);

  // ── Auto-update: a service worker can serve a stale bundle even after a hard
  // refresh (this is why devices kept showing an old version %). On load, compare
  // the running APP_VERSION to the freshly-fetched deployed version.json; if they
  // differ, purge the service worker + caches and reload with the new bundle.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`/version.json?ts=${Date.now()}`, { cache: "no-store" });
        if (!r.ok) return;
        const { version: deployed } = await r.json();
        if (cancelled || !deployed || deployed === APP_VERSION) return;
        // A newer version is live but this device is running an older cached bundle.
        try {
          if ("serviceWorker" in navigator) {
            const regs = await navigator.serviceWorker.getRegistrations();
            await Promise.all(regs.map(reg => reg.unregister()));
          }
          if (window.caches) {
            const keys = await caches.keys();
            await Promise.all(keys.map(k => caches.delete(k)));
          }
        } catch (e) { /* best effort */ }
        // Reload once to pick up the new bundle. Guard against a reload loop with a
        // short-lived sessionStorage flag.
        const key = `reloaded_for_${deployed}`;
        if (!sessionStorage.getItem(key)) {
          sessionStorage.setItem(key, "1");
          window.location.reload();
        } else {
          // Already reloaded once for this version but still stale (rare) - just
          // surface a manual prompt instead of looping.
          setUpdateReady(true);
        }
      } catch (e) { /* offline or blocked - ignore */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const [showBookmarklet, setShowBookmarklet] = useState(window.location.hash === '#bookmarklet');
  const [showCoach, setShowCoach] = useState(window.location.hash === '#coach');
  const [showDocs, setShowDocs] = useState(window.location.hash === '#documents');
  const [showCapital, setShowCapital] = useState(window.location.hash === '#capital');
  const [showInterviews, setShowInterviews] = useState(window.location.hash === '#interviews');
  useEffect(() => {
    const syncDocs = () => {
      const h = window.location.hash;
      if (h === '#documents')  { setShowDocs(true); setShowCapital(false); setShowInterviews(false); return; }
      if (h === '#capital')    { setShowCapital(true); setShowDocs(false); setShowInterviews(false); return; }
      if (h === '#interviews') { setShowInterviews(true); setShowDocs(false); setShowCapital(false); return; }
      if (h === '#dashboard' || h === '' || h.startsWith('#job-')) {
        setShowDocs(false); setShowCapital(false); setShowInterviews(false);
      }
    };
    window.addEventListener('hashchange', syncDocs);
    return () => window.removeEventListener('hashchange', syncDocs);
  }, []);
  const [showData, setShowData] = useState(window.location.hash === '#mydata');
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  useEffect(() => {
    const onHash = () => {
      const h = window.location.hash;
      setShowCoach(h === '#coach');
      setShowProfile(h === '#profile');
      setShowAdmin(h === '#admin');
      setShowBookmarklet(h === '#bookmarklet');
      setShowData(h === '#mydata');
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(window.matchMedia && window.matchMedia("(display-mode: standalone)").matches);
  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setInstallPrompt(e); };
    const onInstalled = () => { setIsInstalled(true); setInstallPrompt(null); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);
  const [showInstallHelp, setShowInstallHelp] = useState(false);
  const doInstall = async () => {
    if (!installPrompt) {
      // No native prompt available (iOS, in-app browsers, or Chrome not warmed
      // up) - show real platform instructions instead of dumping users on the
      // bookmarklet page.
      setShowInstallHelp(true);
      return;
    }
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === "accepted") setIsInstalled(true);
    setInstallPrompt(null);
  };
  const [jobs, setJobs]     = useState([]);
  const [jobsLoaded, setJobsLoaded] = useState(false);   // first successful fetch

  // ── Build-ready watcher: while anything is building, poll and announce ──
  // Closes the core loop's last silence: approve -> 1-2 min of nothing -> the
  // PDF quietly exists. Now a tappable toast lands the moment a build finishes
  // (or fails), and the jobs list refreshes itself.
  useEffect(() => {
    if (!jobs.some(j => j.resume_status === "building")) return;
    const iv = setInterval(async () => {
      try {
        const d = await api("/api/jobs");
        const fresh = (d && (d.jobs || d)) || [];
        if (!Array.isArray(fresh) || !fresh.length) return;
        const prev = {}; jobs.forEach(j => { prev[j.id] = j.resume_status; });
        fresh.forEach(j => {
          if (prev[j.id] === "building" && j.resume_status === "pending_review")
            toast(`📄 Resume ready · ${j.title} — tap to review`, "ok", () => goTab("docs"));
          if (prev[j.id] === "building" && j.resume_status === "failed")
            toast(`Resume build failed · ${j.title} — open the job to retry`, "err");
        });
        setJobs(fresh);
      } catch (e) {}
    }, 20000);
    return () => clearInterval(iv);
  }, [jobs]);
  const jobsRef = useRef([]);
  useEffect(() => { jobsRef.current = jobs; }, [jobs]);
  // Persist filter across refresh; open to "all" if a job hash is present
  const _initHash   = window.location.hash;
  const _initJobId  = _initHash.match(/#job-(\d+)/)?.[1] ? parseInt(_initHash.match(/#job-(\d+)/)[1]) : null;
  const [filter, setFilter] = useState(
    _initJobId ? "all" : (localStorage.getItem("aiboost_filter") || "pending")
  );
  // Continuity pins: when a job's status changes (approve / not suitable), it
  // used to vanish instantly from the current filter - disorienting. Changed
  // jobs now stay visible in place (with their new badge) until the user
  // switches filter or the list refreshes.
  const [pinned, setPinned] = useState({});
  const [sortBy, setSortBy] = useState("added_desc");
  // Minimum fit score filter. Independent of sortBy, so "newest first" and
  // "score 8+" can be active at the same time. 0 = off. Jobs with a null score
  // (scoring thread died mid-scrape) fall to 0 and are excluded whenever a
  // minimum is set, which is the desired behaviour.
  const [minScore, setMinScore] = useState(0);
  const [viewMode, setViewMode] = useState("tile");     // "tile" | "list"
  const [selectMode, setSelectMode] = useState(false);  // bulk-select active
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [urlInput,   setUrl]   = useState("");
  const [manualDesc, setDesc]  = useState("");
  const [manualTitle, setMTitle]= useState("");
  const [manualCo,   setMCo]   = useState("");
  const [showManual, setShowM] = useState(false);
  const [adding, setAdding] = useState(false);
  const [scraping, setScraping] = useState(false);
  const [kwOpen, setKwOpen] = useState(false);
  const [kwChips, setKwChips] = useState([]);
  const [kwInput, setKwInput] = useState("");
  const [kwRunning, setKwRunning] = useState(false);
  const [scrapeProg, setScrapeProg] = useState(null);
  const [pendingQuestions, setPendingQuestions] = useState({});
  const [applyUrlInputs, setApplyUrlInputs] = useState({});

  const _jobsRetry = useRef(0);
  const loadJobs = useCallback(async () => {
    try {
      const data = await api("/api/jobs");
      // Only replace the list if we actually got an array back. If the server is
      // briefly saturated and returns nothing/error, KEEP the jobs already on
      // screen rather than blanking the page (fixes the "empty on refresh" flash).
      if (Array.isArray(data)) { setJobs(data); setJobsLoaded(true); setStatus(""); _jobsRetry.current = 0; }
      else if (data && data.error) {
        setStatus("Loading issue: " + String(data.error).slice(0, 140) + " (retrying)");
        if (_jobsRetry.current < 3) { _jobsRetry.current += 1; setTimeout(loadJobs, 2500 * _jobsRetry.current); }
      }
    } catch(e) {
      setStatus("Reconnecting…");
      if (_jobsRetry.current < 3) { _jobsRetry.current += 1; setTimeout(loadJobs, 2500 * _jobsRetry.current); }
    }
  }, []);

  useEffect(() => {
    if (!authed || !isAdmin) return;
    const check = () => api("/api/admin/pending-count").then(d => { if (typeof d.count==="number") setPendingCount(d.count); });
    check();
    const t = setInterval(check, 30000);
    return () => clearInterval(t);
  }, [authed, isAdmin]);

  // Persist filter in localStorage so refresh restores it
  useEffect(() => { localStorage.setItem("aiboost_filter", filter); setPinned({}); }, [filter]);

  useEffect(() => { if (authed) loadJobs(); }, [authed, loadJobs]);

  // On page load: if a scrape is already running on the backend, resume the progress display
  useEffect(() => {
    if (!authed) return;
    api("/api/scrape/progress").then(p => {
      if (p && p.running) {
        setScrapeProg(p);
        startScrapePolling();
      }
    }).catch(() => {});
  }, [authed]);

  // Poll pending questions for waiting_input jobs every 10s
  useEffect(() => {
    if (!authed) return;
    const waitingJobs = jobs.filter(j => j.apply_status === "waiting_input");
    if (waitingJobs.length === 0) return;
    const poll = setInterval(async () => {
      for (const j of waitingJobs) {
        try {
          const qs = await api(`/api/jobs/${j.id}/pending-questions`);
          if (Array.isArray(qs)) {
            setPendingQuestions(prev => ({ ...prev, [j.id]: qs }));
          }
        } catch(e) {}
      }
    }, 10000);
    // Initial fetch immediately
    waitingJobs.forEach(async j => {
      try {
        const qs = await api(`/api/jobs/${j.id}/pending-questions`);
        if (Array.isArray(qs)) setPendingQuestions(prev => ({ ...prev, [j.id]: qs }));
      } catch(e) {}
    });
    return () => clearInterval(poll);
  }, [authed, jobs]);

  // Auto-refresh while something is building/applying. Widened from 5s/10s to
  // 20s/30s - the old interval re-fetched the FULL job list (every column,
  // every row) up to 12x/minute per active user, which was a major
  // contributor to egress overage. Builds now run in the background and
  // return near-instantly (see build-resume backgrounding), so the user
  // isn't anxiously watching a frozen spinner - a slightly slower refresh
  // is imperceptible in practice but cuts this call volume by ~3x.
  useEffect(() => {
    if (!authed) return;
    const resumeBuilding = jobs.some(j => j.resume_status === "building");
    const hasBuilding = resumeBuilding || jobs.some(j => (j.status === "approved" && !j.resume_url) || j.apply_status === "applying" || j.apply_status === "waiting_input");
    if (!hasBuilding) return;
    const interval = resumeBuilding ? 20000 : 30000;
    const t = setInterval(() => { loadJobs(); }, interval);
    return () => clearInterval(t);
  }, [authed, jobs, loadJobs]);

  const counts = {
    // "All" mirrors exactly what filter==="all" displays (active, non-archived jobs)
    all:      jobs.filter(j => !j.is_archived).length,
    // Active pipeline (not archived)
    pending:  jobs.filter(j => j.status === "pending"  && !j.is_archived).length,
    approved: jobs.filter(j => j.status === "approved" && !j.is_archived).length,
    applied:  jobs.filter(j => j.apply_status === "applied" && !j.is_archived).length,
    interviewing: jobs.filter(j => j.status === "interviewing" && !j.is_archived).length,
    // Not Suitable = declined (always archived by construction)
    notSuitable: jobs.filter(j => j.status === "not_suitable").length,
    // Archived = shelved for other reasons (not the declined ones)
    archived: jobs.filter(j => j.is_archived && j.status !== "not_suitable").length,
  };

  const filtered = jobs.filter(j =>
    (pinned[j.id] === filter && !j.is_archived) ||
    ( filter === "all"          ? !j.is_archived
    : filter === "applied"      ? (j.apply_status === "applied" && !j.is_archived)
    : filter === "interviewing" ? (j.status === "interviewing" && !j.is_archived)
    : filter === "not_suitable" ? j.status === "not_suitable"
    : filter === "archived"     ? (j.is_archived && j.status !== "not_suitable")
    : (j.status === filter && !j.is_archived) )
  ).filter(j => minScore === 0 || (j.fit_score || 0) >= minScore
  ).sort((a, b) => {
    const da = a.created_at ? new Date(a.created_at).getTime() : 0;
    const db = b.created_at ? new Date(b.created_at).getTime() : 0;
    // posted_at is a relative string ("1 hour ago", "2 weeks ago") so we sort by created_at for dates
    if (sortBy === "added_desc")   return db - da;
    if (sortBy === "added_asc")    return da - db;
    if (sortBy === "score_desc")   return (b.fit_score||0) - (a.fit_score||0);
    if (sortBy === "score_asc")    return (a.fit_score||0) - (b.fit_score||0);
    return db - da;
  });

  const onStatusChange = async (id, newStatus, tmpl) => {
    await api(`/api/jobs/${id}/status`, {
      method: "POST",
      body: JSON.stringify({ status: newStatus, template: tmpl || "4" }),
    });
    // Moving a job back to pending/approved means it's no longer "applied" -
    // clear the apply flag too, or it would stay stuck in the Applied pile.
    const clearApply = (newStatus === "pending" || newStatus === "approved");
    if (clearApply) {
      await api(`/api/jobs/${id}/apply-status`, {
        method: "POST", body: JSON.stringify({ apply_status: null }),
      }).catch(() => {});
    }
    setJobs(prev => prev.map(j => j.id === id ? {...j, status: newStatus,
      is_archived: newStatus === "not_suitable" ? true : j.is_archived,
      apply_status: clearApply ? null : j.apply_status,
      template: tmpl || j.template} : j));
    setPinned(p => ({ ...p, [id]: filter }));   // keep it visible where the user is
    // Auto-trigger cloud resume build when approving
    if (newStatus === "approved") {
      toast("Approved — building your tailored resume. It stays here until you switch filters.");
      api(`/api/jobs/${id}/build-resume`, { method: "POST" })
        .catch(e => console.log("Resume build queued:", e));
    }
    if (newStatus === "not_suitable") toast("Marked not suitable");
    if (newStatus === "interviewing") toast("🎤 Moved to Interviewing — the coach is one tab away. Good luck!");
  };

  const onApproveResume = async (id) => {
    await api(`/api/jobs/${id}/approve-resume`, { method: "POST", body: JSON.stringify({}) });
    setJobs(prev => prev.map(j => j.id === id ? {...j, resume_status: "approved"} : j));
    setStatus("Resume approved. You can now queue this job for Easy Apply.");
  };

  const onAutoApply = async (id, applyUrl) => {
    setJobs(prev => prev.map(j => j.id === id ? {...j, apply_status: "applying"} : j));
    setStatus("Auto-apply started. The cloud will fill the form now.");
    const body = applyUrl ? { apply_url: applyUrl } : {};
    const d = await api(`/api/jobs/${id}/auto-apply`, { method: "POST", body: JSON.stringify(body) });
    if (d && d.error) {
      setStatus("Apply error: " + d.error);
      setJobs(prev => prev.map(j => j.id === id ? {...j, apply_status: null} : j));
    }
  };

  const onSaveApplyUrl = async (id, url) => {
    await api(`/api/jobs/${id}/apply-url`, { method: "POST", body: JSON.stringify({ apply_url: url }) });
    setJobs(prev => prev.map(j => j.id === id ? {...j, apply_url: url} : j));
    setApplyUrlInputs(prev => ({ ...prev, [id]: "" }));
  };

  const onAnswerQuestion = async (jobId, qId, answer) => {
    await api(`/api/jobs/${jobId}/answer-question/${qId}`, { method: "POST", body: JSON.stringify({ answer }) });
    setPendingQuestions(prev => ({
      ...prev,
      [jobId]: (prev[jobId] || []).filter(q => q.id !== qId)
    }));
    setStatus("Answer sent. The cloud will continue filling the form.");
  };

  const onRebuildResume = async (id) => {
    setJobs(prev => prev.map(j => j.id === id ? {...j, resume_status: "building", resume_url: null} : j));
    await api(`/api/jobs/${id}/retry-resume`, { method: "POST" });
    api(`/api/jobs/${id}/build-resume`, { method: "POST" });  // fire and forget
  };

  // Refresh one job from server after AI refinement (new resume_url has a fresh
  // cache-busting timestamp, so the old in-memory URL must be replaced)
  const onRefreshJob = async (id) => {
    try {
      const fresh = await api(`/api/jobs/${id}`);
      if (fresh && fresh.id) setJobs(prev => prev.map(j => j.id === id ? {...j, ...fresh} : j));
      else loadJobs();
    } catch { loadJobs(); }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this job?")) return;
    await api(`/api/jobs/${id}/delete`, { method: "POST" });
    setJobs(prev => prev.filter(j => j.id !== id));
    setStatus("Job deleted.");
      toast("Job deleted");
  };

  // ── Bulk selection helpers ──
  const toggleSelect = (id) => setSelectedIds(prev => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const selectAllVisible = (visibleJobs) => setSelectedIds(prev => {
    if (prev.size === visibleJobs.length) return new Set();  // toggle off if all selected
    return new Set(visibleJobs.map(j => j.id));
  });
  const bulkArchive = async () => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    if (!window.confirm(`Archive ${ids.length} job${ids.length>1?"s":""}?`)) return;
    setBulkBusy(true); setStatus(`Archiving ${ids.length}…`);
    for (const id of ids) { try { await api(`/api/jobs/${id}/archive`, { method:"POST" }); } catch(e){} }
    setJobs(prev => prev.map(j => selectedIds.has(j.id) ? {...j, is_archived:true} : j));
    setSelectedIds(new Set()); setSelectMode(false); setBulkBusy(false);
    setStatus(`Archived ${ids.length} job${ids.length>1?"s":""}.`);
  };
  const bulkDelete = async () => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    if (!window.confirm(`Permanently delete ${ids.length} job${ids.length>1?"s":""}? This cannot be undone.`)) return;
    setBulkBusy(true); setStatus(`Deleting ${ids.length}…`);
    for (const id of ids) { try { await api(`/api/jobs/${id}/delete`, { method:"POST" }); } catch(e){} }
    setJobs(prev => prev.filter(j => !selectedIds.has(j.id)));
    setSelectedIds(new Set()); setSelectMode(false); setBulkBusy(false);
    setStatus(`Deleted ${ids.length} job${ids.length>1?"s":""}.`);
  };

  const onArchive = (id) => {
    // OPTIMISTIC: the row leaves the screen instantly; the network call runs in
    // the background and reverts (with an error toast) only if it fails. The old
    // await-first version made swipes feel dead on mobile latency, causing
    // accidental repeat-swipes onto neighbouring rows.
    setJobs(prev => prev.map(j => j.id === id ? {...j, is_archived: true} : j));
    toast("Archived — find it under the ARCHIVED filter");
    api(`/api/jobs/${id}/archive`, { method: "POST" }).catch(() => {
      setJobs(prev => prev.map(j => j.id === id ? {...j, is_archived: false} : j));
      toast("Archive failed — restored", "err");
    });
  };

  const onUnarchive = (id) => {
    setJobs(prev => prev.map(j => j.id === id ? {...j, is_archived: false} : j));
    toast("Restored to your pipeline");
    api(`/api/jobs/${id}/unarchive`, { method: "POST" }).catch(() => {
      setJobs(prev => prev.map(j => j.id === id ? {...j, is_archived: true} : j));
      toast("Restore failed", "err");
    });
  };

  const onEstimateSalary = async (id) => {
    setStatus("Estimating salary...");
    const d = await api(`/api/estimate-salary/${id}`);
    if (d.salary) {
      setJobs(prev => prev.map(j => j.id === id ? {...j, salary_estimated: d.salary} : j));
      setStatus("Salary estimate added.");
    }
  };

  const [showMobileHelp, setShowMobileHelp] = useState(false);

  const addJob = async () => {
    const hasUrl = !!urlInput.trim();
    const hasManualInfo = manualTitle.trim() && manualDesc.trim();
    // Need EITHER a URL, OR enough manual info to build a job without one
    // (e.g. a referral or emailed role with no public listing link).
    if (!hasUrl && !hasManualInfo) {
      setStatus("Enter a job URL, or tap ✏️ and fill in at least a title + description.");
      return;
    }
    setAdding(true);
    setStatus(hasUrl ? "Fetching the posting… usually 5-40 seconds." : "Adding job…");
    const d = await api("/api/add-job", {
      method: "POST",
      body: JSON.stringify({
        url: urlInput.trim(),
        description: manualDesc.trim(),
        title: manualTitle.trim(),
        company: manualCo.trim(),
      }),
    });
    if (d.ok) {
      setUrl(""); setDesc(""); setMTitle(""); setMCo(""); setShowM(false);
      await loadJobs();
      setStatus(d.job?.scrape_failed
        ? `⚠️ Added, but couldn't auto-fetch details. Open the card to see why.`
        : `✅ Added: "${d.job?.title}" at ${d.job?.company_name} (Score: ${d.job?.fit_score}/10)`);
    } else if (d.need_manual) {
      // Both auto-fetch paths failed - open the manual fields with the URL kept,
      // so the user pastes the description and resubmits (no husk inserted).
      setShowM(true);
      setStatus("✏️ " + d.error);
    } else {
      setStatus(d.error === "duplicate" ? "Already in your list." : "Error: " + (d.error || "unknown"));
    }
    setAdding(false);
  };

  // Start polling scrape progress — called both when triggering AND on page load resume
  const startScrapePolling = () => {
    setScraping(true);
    const poll = setInterval(async () => {
      try {
        const p = await api("/api/scrape/progress");
        setScrapeProg(p);
        if (!p.running) {
          clearInterval(poll);
          setScraping(false);
          if (p.phase === "done") {
            await loadJobs();
            setTimeout(() => setScrapeProg(null), 6000);
          } else {
            setScrapeProg(null);
          }
        }
      } catch (e) {
        clearInterval(poll);
        setScraping(false);
        setScrapeProg(null);
      }
    }, 2000);
  };

  const addKwChip = (val) => {
    const v = (val||"").trim().replace(/,$/,"").trim();
    if (!v) return;
    if (kwChips.length >= 5) { alert("Maximum 5 keywords per search."); return; }
    if (!kwChips.map(c=>c.toLowerCase()).includes(v.toLowerCase()))
      setKwChips(prev => [...prev, v]);
    setKwInput("");
  };
  const removeKwChip = (idx) => setKwChips(prev => prev.filter((_,i)=>i!==idx));
  const runKeywordSearch = async () => {
    if (!kwChips.length) return;
    setKwRunning(true);
    setScraping(true);
    try {
      const d = await api("/api/keyword-search/start", {
        method:"POST", body: JSON.stringify({ keywords: kwChips })
      });
      if (!d.ok) { alert(d.error || "Could not start search."); setKwRunning(false); setScraping(false); return; }
      const poll = setInterval(async () => {
        const p = await api("/api/scrape/progress").catch(()=>null);
        if (!p) return;
        setScrapeProg(p);
        if (!p.running) {
          clearInterval(poll);
          setKwRunning(false);
          setScraping(false);
          setKwOpen(false);
          setKwChips([]);
          setKwInput("");
          await loadJobs();
        }
      }, 2500);
    } catch(e) { setKwRunning(false); setScraping(false); }
  };
  const triggerScrape = async () => {
    setScraping(true);
    setScrapeProg({ phase: "starting", found: 0, scored: 0, matched: 0, added: 0,
                    searches_done: 0, searches_total: 0, current_search: "" });
    await api("/api/scrape", { method: "POST" });
    startScrapePolling();
  };

  const generateDocs = async () => {
    const n = jobs.filter(j => j.status === "approved").length;
    if (!n) { setStatus("Approve at least one job first."); return; }
    await api("/api/generate", { method: "POST" });
    setStatus(`Generating documents for ${n} approved jobs...`);
  };

  const goTab = (tab) => {
    setShowAdmin(false); setShowProfile(false); setShowCoach(false);
    setShowData(false); setShowDocs(false); setShowBookmarklet(false); setShowCapital(false); setShowInterviews(false);
    if (tab === "jobs")      { window.location.hash = "dashboard"; }
    if (tab === "docs")      { setShowDocs(true);    window.location.hash = "documents"; }
    if (tab === "coach")     { setShowProfile(true); window.location.hash = "profile"; }
    if (tab === "studio")    { setShowCoach(true);   window.location.hash = "coach"; }
    if (tab === "capital")   { setShowCapital(true);  window.location.hash = "capital"; }
    if (tab === "prep")      { setShowInterviews(true); window.location.hash = "interviews"; }
  };
  const activeTab = showDocs ? "docs" : showCapital ? "capital" : showInterviews ? "prep" : showProfile ? "coach" : showCoach ? "studio"
    : (showAdmin || showData || showBookmarklet) ? "" : "jobs";
  const bottomBar = (isMobile && authed && !showOnboard) ? (
    <div style={{position:"fixed", left:0, right:0, bottom:0, zIndex:900,
      background:"#0D1225", borderTop:"1px solid rgba(255,255,255,0.1)",
      display:"flex", padding:"6px 4px calc(6px + env(safe-area-inset-bottom))"}}>
      {[["jobs","💼","Jobs"],["docs","📄","Docs"],["prep","🎤","Prep"],["capital","🧠","Capital"],["coach","🎯","Coach"],["studio","🛠","Studio"]].map(([k,ico,label])=>(
        <button key={k} onClick={()=>goTab(k)} style={{flex:1, background:"none", border:"none",
          cursor:"pointer", padding:"5px 0", color: activeTab===k ? "#B8934A" : "#8893A7"}}>
          <div style={{fontSize:17, lineHeight:1}}>{ico}</div>
          <div style={{fontSize:9.5, fontWeight:700, marginTop:2}}>{label}</div>
        </button>
      ))}
    </div>
  ) : null;
  const toastStack = toasts.length ? (
    <div style={{position:"fixed", top:12, left:"50%", transform:"translateX(-50%)", zIndex:950,
      display:"flex", flexDirection:"column", gap:6, alignItems:"center", pointerEvents:"none",

      width:"min(92vw, 420px)"}}>
      {toasts.map(t => (
        <div key={t.id}
          onClick={() => { if (t.onClick) { t.onClick(); } setToasts(x => x.filter(y => y.id !== t.id)); }}
          style={{background: t.kind==="err" ? "#7F1D1D" : "#123B27",
          border:`1px solid ${t.kind==="err" ? "#F87171" : "#4ADE80"}`, color:"#EEF2FF",
          borderRadius:10, padding:"9px 14px", fontSize:12.5, fontWeight:600,
          boxShadow:"0 8px 30px rgba(0,0,0,0.45)", maxWidth:"100%",
          pointerEvents:"auto", cursor: t.onClick ? "pointer" : "default"}}>
          {t.msg}
        </div>
      ))}
    </div>
  ) : null;
  const installHelp = showInstallHelp ? createPortal(
    <>
      <div onClick={()=>setShowInstallHelp(false)}
        style={{position:"fixed", inset:0, zIndex:1200, background:"rgba(5,8,20,0.6)"}}/>
      <div style={{position:"fixed", left:"50%", top:"50%", transform:"translate(-50%,-50%)",
        zIndex:1201, width:"min(92vw, 360px)", background:"#141A33", borderRadius:18,
        border:"1px solid rgba(255,255,255,0.1)", padding:"20px 18px",
        boxShadow:"0 24px 80px rgba(0,0,0,0.6)"}}>
        <div style={{fontSize:15, fontWeight:800, color:"#EEF2FF", marginBottom:10}}>
          📲 Install aiBoost on this phone
        </div>
        {/iPad|iPhone|iPod/.test(navigator.userAgent) ? (
          <div style={{fontSize:12.5, color:"#B8C2D8", lineHeight:1.7}}>
            1. Open <b style={{color:"#B8934A"}}>aiboostmycareer.com</b> in <b>Safari</b><br/>
            2. Tap the <b>Share</b> button (square with ↑)<br/>
            3. Tap <b>Add to Home Screen</b> → <b>Add</b>
          </div>
        ) : (
          <div style={{fontSize:12.5, color:"#B8C2D8", lineHeight:1.7}}>
            1. Open <b style={{color:"#B8934A"}}>aiboostmycareer.com</b> in <b>Chrome</b><br/>
            <span style={{color:"#8893A7"}}>(if you opened this from WhatsApp / Instagram / LinkedIn,
            tap ⋮ and choose <b>Open in Chrome</b> first - installing is not possible inside chat-app browsers)</span><br/>
            2. Tap <b>⋮</b> (top right)<br/>
            3. Tap <b>Add to Home screen</b> → <b>Install</b>
          </div>
        )}
        <button onClick={()=>setShowInstallHelp(false)} style={{marginTop:14, width:"100%",
          padding:"11px", background:"#B8934A", color:"#0D1225", border:"none",
          borderRadius:10, fontSize:13, fontWeight:800, cursor:"pointer"}}>
          Got it
        </button>
      </div>

    </>, document.body) : null;

  const withNav = (page) => (
    <div style={{background:"#0D1225", minHeight:"100vh", maxWidth:"100vw", overflowX:"hidden"}}>
      {page}{toastStack}{installHelp}<div style={{height: isMobile ? 62 : 0}}/>{bottomBar}
    </div>
  );

  if (!authed) return <LoginScreen onLogin={(uname) => { setAuthed(true); setCurrentUser(uname); localStorage.setItem("username", uname); sessionStorage.setItem("username", uname); }} />;
  if (showOnboard) return <OnboardingWizard onDone={() => { setShowOnboard(false); if(window.location.hash==="#onboard") window.location.hash=""; loadJobs(); }} />;
  if (showAdmin)   return withNav(<AdminHubPage setSchedule={setSchedule} toast={toast}
    onBack={()=>{setShowAdmin(false);window.location.hash='';api('/api/admin/pending-count').then(d=>setPendingCount(d.count||0));}} />);
  if (showProfile) return withNav(<ProfilePage onBack={()=>{setShowProfile(false);window.location.hash='';}} />);
  if (showBookmarklet) return withNav(<BookmarkletPage onBack={() => {setShowBookmarklet(false);window.location.hash='';}} />);
  if (showCoach) return withNav(<CareerCoachPage onBack={() => {setShowCoach(false);window.location.hash='';}} />);
  if (showData) return withNav(<ProfileDataPage onBack={() => {setShowData(false);window.location.hash='';}} />);
  if (showDocs) return withNav(<DocumentsPage onBack={() => {setShowDocs(false);window.location.hash='';}} />);
  if (showCapital) return withNav(<CapitalPage onBack={() => {setShowCapital(false);window.location.hash='';}} />);
  if (showInterviews) return withNav(<InterviewsHubPage jobs={jobs}
    onOpen={(id)=>{ try{sessionStorage.setItem("open_iv", String(id));}catch(e){}
      setShowInterviews(false); window.location.hash = "job-"+id; }}
    onBack={() => {setShowInterviews(false); window.location.hash='';}} />);

  const acctMenuBtn = {display:"flex",justifyContent:"space-between",alignItems:"center",width:"100%",
    padding:"10px 14px",background:"none",border:"none",borderBottom:"1px solid #F1F5F9",
    cursor:"pointer",fontSize:13,color:"#374151",textAlign:"left"};

  return withNav(
    <div style={styles.app}>
      {/* ── Toolbar ── */}
      <div style={styles.toolbar}>
        {/* Brand */}
        <div style={styles.toolbarBrand}>
          <div style={styles.toolbarTitle}>aiBoost</div>
          <div style={styles.toolbarSub}>{currentUser} · {APP_VERSION}</div>
        </div>

        {/* Right actions — 3 primary tabs + account menu */}
        <div style={{display:"flex",alignItems:"center",gap:6,marginLeft:"auto",position:"relative"}}>
          {isAdmin && pendingCount>0 && (
            <span style={{background:"rgba(239,68,68,0.15)",color:"#F87171",border:"1px solid rgba(239,68,68,0.3)",
              borderRadius:10,padding:"2px 8px",fontSize:10,fontWeight:700}}>
              {pendingCount} new
            </span>
          )}

          {/* ── TAB 1: Jobs ── */}
          <button onClick={()=>{setShowProfile(false);setShowCoach(false);setShowAdmin(false);setShowBookmarklet(false);setShowData(false);window.location.hash='';}}
            style={{background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.1)",
              color:"#EEF2FF",borderRadius:8,padding:"6px 12px",cursor:"pointer",fontSize:12,fontWeight:700,
              display: isMobile ? "none" : "inline-block"}}>
            Jobs
          </button>

          {/* ── TAB 2: Profile ── */}
          <button onClick={()=>{setShowData(true);setShowProfile(false);setShowCoach(false);setShowAdmin(false);setShowBookmarklet(false);window.location.hash='mydata';}}
            style={{background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.1)",
              color:"#EEF2FF",borderRadius:8,padding:"6px 12px",cursor:"pointer",fontSize:12,fontWeight:700,
              display: isMobile ? "none" : "inline-block"}}>
            Profile
          </button>

          {/* ── TAB 3: Career Coach ── */}
          <button onClick={()=>{setShowProfile(true);setShowCoach(false);setShowData(false);setShowDocs(false);setShowCapital(false);window.location.hash='profile';}}
            style={{background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.1)",
              color:"#EEF2FF",borderRadius:8,padding:"6px 12px",cursor:"pointer",fontSize:12,fontWeight:700,
              display: isMobile ? "none" : "inline-block"}}>
            Career Coach
          </button>

          {/* ── TAB: Documents ── */}
          <button onClick={()=>{setShowDocs(true);setShowCoach(false);setShowProfile(false);setShowData(false);setShowCapital(false);window.location.hash='documents';}}
            style={{background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.1)",
              color:"#EEF2FF",borderRadius:8,padding:"6px 12px",cursor:"pointer",fontSize:12,fontWeight:700,
              display: isMobile ? "none" : "inline-block"}}>
            Documents
          </button>

          {/* ── TAB: Competency Capital ── */}
          <button onClick={()=>{setShowCapital(true);setShowDocs(false);setShowCoach(false);setShowProfile(false);setShowData(false);window.location.hash='capital';}}
            style={{background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.1)",
              color:"#EEF2FF",borderRadius:8,padding:"6px 12px",cursor:"pointer",fontSize:12,fontWeight:700,
              display: isMobile ? "none" : "inline-block"}}>
            🧠 Capital
          </button>
          <button onClick={()=>{setShowInterviews(true);setShowCapital(false);setShowDocs(false);setShowCoach(false);setShowProfile(false);setShowData(false);window.location.hash='interviews';}}
            style={{background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.1)",
              color:"#EEF2FF",borderRadius:8,padding:"6px 12px",cursor:"pointer",fontSize:12,fontWeight:700,
              display: isMobile ? "none" : "inline-block"}}>
            🎤 Interviews
          </button>

          {/* ── TAB 4: Resume Studio ── */}
          <button onClick={()=>{setShowCoach(true);setShowProfile(false);setShowData(false);setShowDocs(false);setShowCapital(false);window.location.hash='coach';}}
            style={{background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.1)",
              color:"#EEF2FF",borderRadius:8,padding:"6px 12px",cursor:"pointer",fontSize:12,fontWeight:700,
              display: isMobile ? "none" : "inline-block"}}>
            Resume Studio
          </button>

          {/* ── Account menu (avatar) ── */}
          <button onClick={()=>setShowAccountMenu(s=>!s)} title="Account & settings"
            style={{width:36, height:36, borderRadius:"50%", flexShrink:0,
              background: showAccountMenu ? "#B8934A" : "linear-gradient(135deg,#B8934A,#8F6F33)",
              border:"1px solid rgba(255,255,255,0.18)", color:"#0D1225",
              fontSize:15, fontWeight:800, cursor:"pointer",
              display:"flex", alignItems:"center", justifyContent:"center"}}>
            {(currentUser||"?").charAt(0).toUpperCase()}
          </button>

          {showAccountMenu && createPortal(
            <>
              {/* dim backdrop */}
              <div onClick={()=>setShowAccountMenu(false)}
                style={{position:"fixed", inset:0, zIndex:998, background:"rgba(5,8,20,0.55)"}}/>
              {/* bottom sheet on mobile · anchored card on desktop */}
              <div style={ isMobile
                ? {position:"fixed", left:0, right:0, bottom:0, zIndex:999,
                   background:"#141A33", borderRadius:"20px 20px 0 0",
                   border:"1px solid rgba(255,255,255,0.08)", borderBottom:"none",
                   padding:"10px 14px calc(14px + env(safe-area-inset-bottom))",
                   boxShadow:"0 -18px 60px rgba(0,0,0,0.55)"}
                : {position:"fixed", top:74, right:16, zIndex:999,
                   width:272, background:"#141A33", borderRadius:16,
                   border:"1px solid rgba(255,255,255,0.08)", padding:"6px 10px 10px",
                   boxShadow:"0 24px 70px rgba(0,0,0,0.55)"}}>
                {isMobile && <div style={{width:38, height:4, borderRadius:2,
                  background:"rgba(255,255,255,0.18)", margin:"2px auto 10px"}}/>}

                {/* identity header */}
                <div style={{display:"flex", alignItems:"center", gap:11,
                  padding:"8px 6px 12px", borderBottom:"1px solid rgba(255,255,255,0.07)", marginBottom:6}}>
                  <div style={{width:40, height:40, borderRadius:"50%", flexShrink:0,
                    background:"linear-gradient(135deg,#B8934A,#8F6F33)", color:"#0D1225",
                    fontSize:17, fontWeight:800, display:"flex", alignItems:"center",
                    justifyContent:"center"}}>{(currentUser||"?").charAt(0).toUpperCase()}</div>
                  <div style={{minWidth:0}}>
                    <div style={{fontSize:14, fontWeight:800, color:"#EEF2FF"}}>{currentUser}</div>
                    <div style={{fontSize:10.5, color:"#8893A7"}}>aiBoost · {APP_VERSION}</div>
                  </div>
                </div>

                {(() => {
                  const row = {display:"flex", alignItems:"center", gap:12, width:"100%",
                    padding:"12px 8px", background:"none", border:"none", cursor:"pointer",
                    borderRadius:12, textAlign:"left"};
                  const ic = {width:32, height:32, borderRadius:9, flexShrink:0, fontSize:15,
                    background:"rgba(255,255,255,0.06)", display:"flex",
                    alignItems:"center", justifyContent:"center"};
                  const lb = {fontSize:13.5, fontWeight:600, color:"#EEF2FF", flex:1};
                  const chev = <span style={{color:"#3D4A66", fontSize:13}}>›</span>;
                  return (<>
                    <button style={row}
                      onClick={()=>{setShowData(true);setShowProfile(false);setShowCoach(false);setShowAccountMenu(false);window.location.hash='mydata';}}>
                      <span style={ic}>👤</span><span style={lb}>My Profile &amp; Details</span>{chev}
                    </button>
                    
                    <button style={row}
                      onClick={()=>{setShowBookmarklet(true);setShowAccountMenu(false);window.location.hash='bookmarklet';}}>
                      <span style={ic}>📎</span><span style={lb}>Chrome Extension</span>{chev}
                    </button>
                    <button style={row} onClick={async()=>{
                        setShowAccountMenu(false);
                        setCheckingStatus(true); setCheckStatusMsg("Checking...");
                        const r = await api("/api/jobs/check-listing-status",{method:"POST",body:JSON.stringify({})});
                        if(r.ok){
                          setCheckStatusMsg(`${r.closed} closed, ${r.open} active of ${r.checked} checked`);
                          const fresh=await api("/api/jobs");
                          if(Array.isArray(fresh)) setJobs(fresh);
                        } else { setCheckStatusMsg("Check failed"); }
                        setCheckingStatus(false);
                        setTimeout(()=>setCheckStatusMsg(""),6000);
                      }}>
                      <span style={ic}>🔍</span><span style={lb}>Check Listing Status</span>{chev}
                    </button>
                    {!isInstalled && (
                      <button style={row} onClick={()=>{doInstall();setShowAccountMenu(false);}}>
                        <span style={ic}>⬇</span><span style={lb}>Install App</span>{chev}
                      </button>
                    )}
                    {isAdmin && (
                      <button style={row}
                        onClick={()=>{ setShowAccountMenu(false); setShowAdmin(true); window.location.hash='admin'; }}>
                        <span style={ic}>🛠</span><span style={lb}>Admin</span>
                        {pendingCount>0 && <span style={{background:"#7F1D1D", color:"#FCA5A5",
                          fontSize:10.5, fontWeight:800, borderRadius:9, padding:"2px 8px"}}>{pendingCount}</span>}
                        {chev}
                      </button>
                    )}
                    <div style={{height:1, background:"rgba(255,255,255,0.07)", margin:"6px 4px"}}/>
                    <button style={row}
                      onClick={()=>{ clearToken(); localStorage.removeItem("username"); sessionStorage.removeItem("username"); setAuthed(false); setCurrentUser(""); setShowAccountMenu(false); }}>
                      <span style={{...ic, background:"rgba(220,38,38,0.14)"}}>🚪</span>
                      <span style={{...lb, color:"#F87171"}}>Sign Out</span>
                    </button>
                  </>);
                })()}
              </div>
            </>,
            document.body
          )}

        </div>
      </div>

      {(() => {
        const _now = Date.now();
        const newToday = jobs.filter(j => j.created_at && (_now - new Date(j.created_at).getTime()) < 86400000).length;
        const toReview = jobs.filter(j => j.resume_status === "pending_review").length;
        const buildingN = jobs.filter(j => j.resume_status === "building").length;
        if (resumeOnly || (newToday===0 && toReview===0 && buildingN===0)) return null;
        return (
          <div style={{background:"#0D1225", padding:"8px 14px 2px", fontSize:11.5,
            color:"#8893A7", lineHeight:1.5}}>
            <b style={{color:"#EEF2FF"}}>Today:</b>
            {newToday>0 && <span> {newToday} new match{newToday>1?"es":""}</span>}
            {toReview>0 && <span onClick={()=>goTab("docs")} style={{cursor:"pointer",
              textDecoration:"underline dotted", textUnderlineOffset:3}}> · <b style={{color:"#B8934A"}}>{toReview}</b> resume{toReview>1?"s":""} to review</span>}
            {buildingN>0 && <span> · {buildingN} building</span>}
          </div>
        );
      })()}

      {/* ── Status strip: ONE selector (counts + filter + sort) ── */}
      <div style={{background:"#0D1225",borderBottom:"1px solid rgba(255,255,255,0.05)",
        padding:"8px 14px",display:"flex",gap:6,alignItems:"center",overflowX:"auto",
        WebkitOverflowScrolling:"touch",scrollbarWidth:"none"}}>
        {[["ALL", counts.all, "#C9A55A", ()=>setFilter("all"), "all"],
          ["PENDING", counts.pending, "#F59E0B", ()=>setFilter("pending"), "pending"],
          ["APPROVED", counts.approved, "#22C55E", ()=>setFilter("approved"), "approved"],
          ["APPLIED", counts.applied, "#3B82F6", ()=>setFilter("applied"), "applied"],
          ["INTERVIEWING", counts.interviewing, "#A855F7", ()=>setFilter("interviewing"), "interviewing"],
          ["NOT SUITABLE", counts.notSuitable, "#EF4444", ()=>setFilter("not_suitable"), "not_suitable"],
          ["ARCHIVED", counts.archived, "#64748B", ()=>setFilter("archived"), "archived"]].map(([l, n, col, onClick, fkey]) => (
          <button key={l} onClick={onClick}
            style={{background:filter===fkey?`rgba(${col==="#F59E0B"?"245,158,11":col==="#22C55E"?"34,197,94":col==="#3B82F6"?"59,130,246":col==="#A855F7"?"168,85,247":col==="#64748B"?"100,116,139":col==="#C9A55A"?"201,165,90":"239,68,68"},0.12)`:
              "rgba(255,255,255,0.03)",
              border:`1px solid ${filter===fkey?col+"44":"rgba(255,255,255,0.06)"}`,
              borderRadius:10,padding:"5px 12px",cursor:"pointer",
              display:"flex",alignItems:"center",gap:7,flexShrink:0,transition:"all 0.15s"}}>
            <span style={{fontSize:16,fontWeight:800,color:col,lineHeight:1}}>{n}</span>
            <span style={{fontSize:9,color:"#4E5A70",fontWeight:600,letterSpacing:"0.6px",textTransform:"uppercase"}}>{l}</span>
          </button>
        ))}
      </div>

      {/* ── Filter & view controls: always visible, no side-scroll needed ── */}
      <div style={{background:"#0D1225", borderBottom:"1px solid rgba(255,255,255,0.05)",
        padding:"7px 14px", display:"flex", gap:6, alignItems:"center", flexWrap:"wrap"}}>
        <select
          value={minScore}
          onChange={e => setMinScore(Number(e.target.value))}
          title="Only show jobs at or above this fit score"
          style={{flexShrink:0, padding:"5px 9px", borderRadius:8,
                  border:`1px solid ${minScore>0?"rgba(201,165,90,0.45)":"rgba(255,255,255,0.1)"}`,
                  fontSize:11, color:minScore>0?"#C9A55A":"#8893A7",
                  fontWeight:minScore>0?700:400,
                  background:"#141F38", cursor:"pointer", outline:"none"}}
        >
          <option value={0}>Any score</option>
          <option value={9}>Score 9+</option>
          <option value={8}>Score 8+</option>
          <option value={7}>Score 7+</option>
          <option value={6}>Score 6+</option>
        </select>
        <select
          value={sortBy}
          onChange={e => setSortBy(e.target.value)}
          style={{flexShrink:0, padding:"5px 9px", borderRadius:8,
                  border:"1px solid rgba(255,255,255,0.1)", fontSize:11, color:"#8893A7",
                  background:"#141F38", cursor:"pointer", outline:"none"}}
        >
          <option value="added_desc">Newest added first</option>
          <option value="added_asc">Oldest added first</option>
          <option value="score_desc">Highest score first</option>
          <option value="score_asc">Lowest score first</option>
        </select>
        {/* Tile / List view toggle */}
        <div style={{display:"flex", flexShrink:0, borderRadius:8, overflow:"hidden", border:"1px solid rgba(255,255,255,0.1)"}}>
          {[["tile","▦"],["list","☰"]].map(([m,ic])=>(
            <button key={m} onClick={()=>setViewMode(m)} title={m==="tile"?"Card view":"List view"}
              style={{padding:"5px 10px", fontSize:13, cursor:"pointer", border:"none",
                background: viewMode===m?"#C9A55A":"#141F38", color: viewMode===m?"#1A1206":"#8893A7"}}>
              {ic}
            </button>
          ))}
        </div>
        {/* Select (bulk actions) */}
        <button onClick={()=>{ setSelectMode(s=>!s); setSelectedIds(new Set()); }}
          style={{flexShrink:0, padding:"5px 11px", borderRadius:8, fontSize:11, fontWeight:600, cursor:"pointer",
            border:`1px solid ${selectMode?"#C9A55A":"rgba(255,255,255,0.1)"}`,
            background: selectMode?"rgba(201,165,90,0.15)":"#141F38", color: selectMode?"#C9A55A":"#8893A7"}}>
          {selectMode?"Cancel":"Select"}
        </button>
        {checkStatusMsg && (
          <span style={{fontSize:10,color:"#4E5A70",fontWeight:500,whiteSpace:"nowrap",flexShrink:0}}>
            🔍 {checkStatusMsg}
          </span>
        )}
      </div>


      {/* Advanced Keyword Search Panel */}
      {kwOpen && (
        <div style={{background:"rgba(139,92,246,0.07)",border:"1px solid rgba(139,92,246,0.2)",
          borderRadius:14,padding:"14px 14px 12px",marginBottom:10,marginTop:4}}>
          <div style={{fontSize:12,fontWeight:700,color:"#A78BFA",marginBottom:5,letterSpacing:"-0.1px"}}>
            🔍 Keyword Search — LinkedIn + Seek
          </div>
          <div style={{fontSize:11,color:"#8893A7",marginBottom:10,lineHeight:1.55}}>
            Add up to 5 keywords. Each runs as its own targeted search.
          </div>
          <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:10,alignItems:"center"}}>
            {kwChips.map((c,idx)=>(
              <span key={idx} style={{display:"inline-flex",alignItems:"center",gap:5,
                background:"rgba(139,92,246,0.2)",color:"#A78BFA",
                border:"1px solid rgba(139,92,246,0.3)",
                padding:"4px 10px",borderRadius:16,fontSize:12,fontWeight:600}}>
                🔍 {c}<span onClick={()=>removeKwChip(idx)}
                  style={{cursor:"pointer",fontWeight:800,fontSize:14,lineHeight:1,marginLeft:2,color:"#C4B5FD"}}>×</span>
              </span>
            ))}
            {kwChips.length < 5 && (
              <input value={kwInput}
                onChange={e=>{ const v=e.target.value; if(v.endsWith(",")){ addKwChip(v); } else setKwInput(v); }}
                onKeyDown={e=>{ if(e.key==="Enter"){ e.preventDefault(); addKwChip(kwInput); }}}
                placeholder={kwChips.length===0?"e.g. ITSM Lead, Oracle Fusion, ERP...":"Add keyword..."}
                style={{flex:1,minWidth:140,padding:"7px 12px",
                  border:"1px solid rgba(139,92,246,0.25)",borderRadius:20,fontSize:12,outline:"none",
                  background:"rgba(255,255,255,0.04)",color:"#EEF2FF"}}
              />
            )}
          </div>
          <div style={{display:"flex",gap:8}}>
            <button onClick={runKeywordSearch} disabled={!kwChips.length||kwRunning}
              style={{flex:1,padding:"11px",
                background:(!kwChips.length||kwRunning)?"rgba(139,92,246,0.1)":"rgba(139,92,246,0.2)",
                color:(!kwChips.length||kwRunning)?"#4E5A70":"#A78BFA",
                border:"1px solid rgba(139,92,246,0.25)",borderRadius:10,fontWeight:700,
                fontSize:13,cursor:(!kwChips.length||kwRunning)?"not-allowed":"pointer",
                transition:"all 0.15s"}}>
              {kwRunning?`Searching ${scrapeProg?.current_search||"…"}…`:"Search LinkedIn + Seek"}
            </button>
            <button onClick={()=>{setKwOpen(false);setKwChips([]);setKwInput("");}}
              style={{padding:"11px 14px",background:"rgba(255,255,255,0.04)",color:"#8893A7",
                border:"1px solid rgba(255,255,255,0.08)",borderRadius:10,fontWeight:600,
                fontSize:13,cursor:"pointer"}}>
              Cancel
            </button>
          </div>
          {kwRunning && scrapeProg && (
            <div style={{marginTop:8,fontSize:11,color:"#A78BFA",fontWeight:600}}>
              {scrapeProg.current_search ? `🔍 ${scrapeProg.current_search}` : "Starting…"}
              {scrapeProg.searches_total>0 && ` — ${scrapeProg.searches_done||0}/${scrapeProg.searches_total}`}
              {scrapeProg.added>0 && ` · ${scrapeProg.added} found`}
            </div>
          )}
        </div>
      )}
      {/* Action bar */}
      <div style={styles.actionBar}>
        {scraping && scrapeProg && scrapeProg.phase !== "done" && (
          <button onClick={async()=>{ await api("/api/scrape/reset",{method:"POST"}); setScrapeProg(null); setScraping(false); }} style={{padding:"9px 14px",background:"rgba(239,68,68,0.15)",color:"#F87171",
              border:"1px solid rgba(239,68,68,0.3)",borderRadius:10,cursor:"pointer",
              fontSize:13,fontWeight:600,marginBottom:8,width:"100%"}}>⬛ Stop &amp; Reset</button>
        )}
        {!scraping && !scrapeProg && !resumeOnly && (<>
          <button style={{...styles.btnGenerate, background:"rgba(255,255,255,0.07)",
            border:"1px solid rgba(255,255,255,0.1)",boxShadow:"none"}} onClick={triggerScrape}>
            🔁 Scan now
          </button>
          <button onClick={()=>setKwOpen(o=>!o)} style={{...styles.btnGenerate, background:kwOpen?"#6D28D9":"white", color:kwOpen?"white":"#6D28D9", border:"1.5px solid #6D28D9"}}>
            🔍 Keyword search
          </button>
        </>)}
        {(scraping || scrapeProg) && scrapeProg && (
          <div style={{flex:1, minWidth:280, background:"#1E293B", borderRadius:8, padding:"8px 14px"}}>
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6}}>
              <span style={{fontSize:12, fontWeight:600, color:"#F1F5F9"}}>
                {scrapeProg.phase === "done"
                  ? `✓ Done: ${scrapeProg.added} new jobs added`
                  : scrapeProg.phase === "generating_searches"
                    ? `⚙️ Building personalised searches from your profile...`
                    : scrapeProg.phase === "searching"
                    ? `🔍 Searching: ${scrapeProg.current_search || "..."}`
                    : scrapeProg.phase === "scoring"
                      ? `🤖 Scoring jobs with AI...`
                      : `Starting scrape...`}
              </span>
              <span style={{fontSize:11, color:"#94A3B8"}}>
                {scrapeProg.searches_done}/{scrapeProg.searches_total || "?"} searches
              </span>
            </div>
            <div style={{display:"flex", gap:14, fontSize:11, color:"#CBD5E1", marginBottom:6}}>
              <span>📥 Found: <b style={{color:"#F1F5F9"}}>{scrapeProg.found}</b></span>
              <span>🤖 Scored: <b style={{color:"#F1F5F9"}}>{scrapeProg.scored}</b></span>
              <span>✓ Matched: <b style={{color:"#86EFAC"}}>{scrapeProg.matched}</b></span>
              <span>➕ Added: <b style={{color:"#B8934A"}}>{scrapeProg.added}</b></span>
            </div>
            <div style={{height:5, background:"#0F172A", borderRadius:3, overflow:"hidden"}}>
              <div style={{
                height:"100%",
                width: `${scrapeProg.searches_total ? (scrapeProg.searches_done / scrapeProg.searches_total * 100) : 5}%`,
                background: scrapeProg.phase === "done" ? "#22C55E" : "#B8934A",
                transition: "width 0.5s ease"
              }} />
            </div>
          </div>
        )}
      </div>

      {/* URL add bar */}
      <div style={{background:"#0D1225", padding:"8px 14px", borderBottom:"1px solid rgba(255,255,255,0.05)"}}>
        {/* URL row */}
        <div style={{display:"flex", gap:8, marginBottom: showManual ? 8 : 0}}>
          <input value={urlInput} onChange={e=>setUrl(e.target.value)}
            onKeyDown={e=>e.key==="Enter"&&addJob()}
            placeholder="Paste any job URL (Seek, LinkedIn, company site)…"
            style={styles.urlInput}/>
          <button onClick={()=>setShowM(p=>!p)} style={{
            padding:"10px 12px", background:showManual?"#EFF6FF":"#F8FAFC",
            border:`1px solid ${showManual?"#3B82F6":"#E2E8F0"}`,
            borderRadius:8, cursor:"pointer", fontSize:18, minHeight:44,
            color:showManual?"#1D4ED8":"#94A3B8"}} title="Add details manually">
            ✏️
          </button>
          <button onClick={addJob} disabled={adding||!urlInput.trim()} style={{
            ...styles.btnAdd,
            opacity:urlInput.trim()?1:0.5}}>
            {adding?"Adding…":"+ Add"}
          </button>
        </div>
        {/* Mobile add helper link */}
        <div style={{marginTop:showManual?8:6}}>
          <button onClick={()=>setShowMobileHelp(true)}
            style={{background:"none", border:"none", color:"#B8934A", fontSize:11.5,
              cursor:"pointer", padding:0, fontWeight:600}}>
            📱 Add jobs from your phone (LinkedIn / Seek app) →
          </button>
          {!resumeOnly && (
            <span style={{marginLeft:14, fontSize:11, color:"#64748B"}}>
              {schedule.scrape_enabled
                ? <>⏰ Daily hunt: next scan <b style={{color:"#B8934A"}}>{nextScanLabel}</b> Melbourne</>
                : <>⏸ Daily hunt is paused{isAdmin ? " — resume it in Admin Settings" : ""}</>}
            </span>
          )}
        </div>
        {/* Expandable manual details panel */}
        {showManual && (
          <div style={{background:"#F8FAFC", border:"1px solid #DBEAFE", borderRadius:10, padding:10}}>
            <p style={{fontSize:11, color:"#3B82F6", fontWeight:600, margin:"0 0 8px"}}>
              ✏️ Paste job details — Seek and LinkedIn block auto-fetch, so paste the description here for full scoring
            </p>
            <div style={{display:"flex", gap:6, marginBottom:6}}>
              <input value={manualTitle} onChange={e=>setMTitle(e.target.value)}
                placeholder="Job title (e.g. IT Service Manager)"
                style={{flex:1, padding:"8px 10px", borderRadius:8, border:"1px solid #BFDBFE",
                  fontSize:13, outline:"none", minHeight:40}}/>
              <input value={manualCo} onChange={e=>setMCo(e.target.value)}
                placeholder="Company"
                style={{flex:1, padding:"8px 10px", borderRadius:8, border:"1px solid #BFDBFE",
                  fontSize:13, outline:"none", minHeight:40}}/>
            </div>
            <VoiceSafeTextarea value={manualDesc} onChange={e=>setDesc(e.target.value)}
              placeholder="Paste the full job description here…"
              style={{width:"100%", height:100, padding:"8px 10px", border:"1px solid #BFDBFE",
                borderRadius:8, fontSize:13, boxSizing:"border-box", background:"white",
                resize:"none", lineHeight:1.5}}/>
          </div>
        )}
      </div>

      {/* Status bar */}
      <div style={styles.statusBar}>{status}</div>

      {/* Bulk-action bar (visible in select mode) */}
      {selectMode && (
        <div style={{display:"flex", alignItems:"center", gap:10, flexWrap:"wrap",
          background:"rgba(201,165,90,0.1)", borderBottom:"1px solid rgba(201,165,90,0.25)",
          padding:"9px 14px"}}>
          <button onClick={()=>selectAllVisible(filtered)}
            style={{padding:"6px 12px", borderRadius:8, fontSize:12, fontWeight:600, cursor:"pointer",
              border:"1px solid rgba(201,165,90,0.4)", background:"transparent", color:"#C9A55A"}}>
            {selectedIds.size === filtered.length && filtered.length>0 ? "Deselect all" : "Select all"}
          </button>
          <span style={{fontSize:12, color:"#8893A7", fontWeight:600}}>{selectedIds.size} selected</span>
          <div style={{marginLeft:"auto", display:"flex", gap:8}}>
            <button onClick={bulkArchive} disabled={bulkBusy || !selectedIds.size}
              style={{padding:"6px 14px", borderRadius:8, fontSize:12, fontWeight:700, cursor: selectedIds.size?"pointer":"not-allowed",
                border:"none", background: selectedIds.size?"#64748B":"#334155", color:"white", opacity: selectedIds.size?1:0.5}}>
              🗄 Archive
            </button>
            <button onClick={bulkDelete} disabled={bulkBusy || !selectedIds.size}
              style={{padding:"6px 14px", borderRadius:8, fontSize:12, fontWeight:700, cursor: selectedIds.size?"pointer":"not-allowed",
                border:"none", background: selectedIds.size?"#DC2626":"#4A2020", color:"white", opacity: selectedIds.size?1:0.5}}>
              🗑 Delete
            </button>
          </div>
        </div>
      )}

      {/* Cards / List */}
      <div style={viewMode==="list"
        ? {display:"flex", flexDirection:"column", gap:6, padding:14, paddingBottom:80}
        : {...styles.grid, paddingBottom:80}}>
        {filtered.length === 0
          ? ( !jobsLoaded
            ? <div style={styles.noJobs}>
                <div style={{fontSize:34,marginBottom:12,opacity:0.5}}>⏳</div>
                <div style={{fontSize:13.5,color:"#8893A7"}}>Loading your pipeline…</div>
              </div>
            : <div style={styles.noJobs}>
              <div style={{fontSize:44,marginBottom:14,opacity:0.5}}>{jobs.length===0 ? "🎯" : "🔍"}</div>
              <div style={{fontSize:16,fontWeight:800,color:"#EEF2FF",marginBottom:8,letterSpacing:"-0.3px"}}>
                {jobs.length===0 ? "Your pipeline is ready" : "Nothing under this filter"}
              </div>
              <div style={{fontSize:13,color:"#8893A7",lineHeight:1.6}}>
                {jobs.length===0
                  ? (schedule.scrape_enabled
                      ? <>Paste any LinkedIn or Seek job URL above to fetch and score it right now - or sit back: your daily hunt scans <b style={{color:"#B8934A"}}>{nextScanLabel}</b> Melbourne time and only genuine matches (5+) land here.</>
                      : <>Paste any LinkedIn or Seek job URL above to fetch and score it right now. The automatic daily hunt is currently paused.</>)
                  : "Try a different filter chip above, or switch the score filter back to Any score."}
              </div>
            </div> )
          : viewMode==="list"
          ? filtered.map((job, i) => (
              <JobListRow key={job.id || i} job={job}
                selectMode={selectMode} selected={selectedIds.has(job.id)}
                onToggleSelect={()=>toggleSelect(job.id)}
                onOpen={()=>{ if(!selectMode){ window.location.hash = `job-${job.id}`; setViewMode("tile"); } }}
                onArchive={onArchive} onUnarchive={onUnarchive} onDelete={onDelete} />
            ))
          : filtered.map((job, i) => (
            <div key={job.id || i} style={{position:"relative"}}>
              {selectMode && (
                <div onClick={()=>toggleSelect(job.id)}
                  style={{position:"absolute", inset:0, zIndex:5, cursor:"pointer",
                    borderRadius:16, background: selectedIds.has(job.id)?"rgba(201,165,90,0.14)":"transparent",
                    border:`2px solid ${selectedIds.has(job.id)?"#C9A55A":"transparent"}`}}>
                  <div style={{position:"absolute", top:10, left:10, width:24, height:24, borderRadius:6,
                    display:"flex", alignItems:"center", justifyContent:"center", fontSize:15, fontWeight:800,
                    border:`2px solid ${selectedIds.has(job.id)?"#C9A55A":"#94A3B8"}`,
                    background: selectedIds.has(job.id)?"#C9A55A":"rgba(13,18,37,0.85)", color:"#1A1206"}}>
                    {selectedIds.has(job.id)?"\u2713":""}
                  </div>
                </div>
              )}
              <JobCard job={job} idx={i}
              onStatusChange={onStatusChange}
              onDelete={onDelete}
              onArchive={onArchive}
              onUnarchive={onUnarchive}
              onEstimateSalary={onEstimateSalary}
              onAutoApply={onAutoApply}
              onApproveResume={onApproveResume}
              toast={toast}
              onRebuildResume={onRebuildResume}
              onRefreshJob={onRefreshJob}
              onReloadAll={loadJobs}
              getJobById={(id)=>jobsRef.current.find(j=>j.id===id)}
              pendingQuestions={pendingQuestions[job.id] || []}
              onAnswerQuestion={onAnswerQuestion}
              applyUrlInput={applyUrlInputs[job.id] || ""}
              onApplyUrlChange={v => setApplyUrlInputs(prev => ({...prev, [job.id]: v}))}
              onSaveApplyUrl={onSaveApplyUrl}
            />
            </div>
          ))
        }
      </div>

      {/* ── Floating Action Button — mobile quick-add ── */}
      <button
        onClick={()=>{
          const el=document.querySelector('input[placeholder*="Paste any job"]');
          if(el){el.focus();el.scrollIntoView({behavior:"smooth",block:"center"});}
        }}
        style={{
          position:"fixed", bottom:24, right:20, zIndex:150,
          width:56, height:56, borderRadius:"50%",
          background:"linear-gradient(135deg,#C9A55A,#A8813E)",
          border:"none", cursor:"pointer", fontSize:26, fontWeight:300,
          boxShadow:"0 4px 20px rgba(201,165,90,0.5), 0 0 0 1px rgba(201,165,90,0.25)",
          display:"flex", alignItems:"center", justifyContent:"center",
          color:"white", lineHeight:1,
          WebkitTapHighlightColor:"transparent"
        }}
        title="Add a job"
      >+</button>

      {/* Mobile add guide modal */}
      {showMobileHelp && (
        <div onClick={()=>setShowMobileHelp(false)}
          style={{position:"fixed", inset:0, zIndex:400, background:"rgba(4,6,15,0.8)",
            backdropFilter:"blur(6px)", WebkitBackdropFilter:"blur(6px)", display:"flex",
            alignItems:"center", justifyContent:"center", padding:16}}>
          <div onClick={e=>e.stopPropagation()}
            style={{background:"#0D1225", borderRadius:20, maxWidth:440, width:"100%",
              maxHeight:"88vh", overflowY:"auto", border:"1px solid rgba(201,165,90,0.25)"}}>
            <div style={{padding:"20px 22px 14px", borderBottom:"1px solid rgba(255,255,255,0.06)",
              position:"sticky", top:0, background:"#0D1225", zIndex:1}}>
              <div style={{display:"flex", justifyContent:"space-between", alignItems:"center"}}>
                <div style={{fontSize:17, fontWeight:800, color:"#EEF2FF"}}>Add jobs from your phone</div>
                <button onClick={()=>setShowMobileHelp(false)}
                  style={{background:"none", border:"none", color:"#8893A7", fontSize:22,
                    cursor:"pointer", lineHeight:1, padding:0}}>×</button>
              </div>
              <div style={{fontSize:12.5, color:"#8893A7", marginTop:4, lineHeight:1.4}}>
                Send jobs straight from the LinkedIn or Seek app into your pipeline.
              </div>
            </div>
            <div style={{padding:"18px 22px 22px"}}>
              <div style={{marginBottom:22}}>
                <div style={{display:"flex", alignItems:"center", gap:8, marginBottom:10}}>
                  <div style={{width:26, height:26, borderRadius:8,
                    background:"linear-gradient(135deg,#C9A55A,#A8813E)", display:"flex",
                    alignItems:"center", justifyContent:"center", fontSize:13, fontWeight:800,
                    color:"white"}}>1</div>
                  <div style={{fontSize:14, fontWeight:700, color:"#EEF2FF"}}>Share Sheet (recommended)</div>
                </div>
                <div style={{fontSize:11, color:"#B8934A", fontWeight:700, marginBottom:8,
                  textTransform:"uppercase", letterSpacing:0.5}}>One-time setup</div>
                <ol style={{margin:0, paddingLeft:20, fontSize:12.5, color:"#C4CDE0", lineHeight:1.7}}>
                  <li>Open <b style={{color:"#EEF2FF"}}>aiboostmycareer.com</b> in Safari (iPhone) or Chrome (Android)</li>
                  <li>Tap <b style={{color:"#EEF2FF"}}>Share</b>, then <b style={{color:"#EEF2FF"}}>Add to Home Screen</b></li>
                  <li>Open the app from your home screen once and sign in</li>
                </ol>
                <div style={{fontSize:11, color:"#B8934A", fontWeight:700, margin:"14px 0 8px",
                  textTransform:"uppercase", letterSpacing:0.5}}>Then, for any job</div>
                <ol style={{margin:0, paddingLeft:20, fontSize:12.5, color:"#C4CDE0", lineHeight:1.7}}>
                  <li>In the LinkedIn or Seek app, open the job</li>
                  <li>Tap <b style={{color:"#EEF2FF"}}>Share</b>, choose <b style={{color:"#EEF2FF"}}>AI Boost My Career</b></li>
                  <li>Title and company pre-fill. Paste the description, tap Add</li>
                </ol>
                <div style={{background:"rgba(201,165,90,0.08)", border:"1px solid rgba(201,165,90,0.2)",
                  borderRadius:10, padding:"9px 12px", marginTop:12, fontSize:11.5, color:"#C4CDE0",
                  lineHeight:1.5}}>
                  Why paste the description? LinkedIn and Seek block apps from auto-reading job
                  details unless you are logged in. Sharing sends the link and page title; the
                  description takes one paste.
                </div>
              </div>
              <div>
                <div style={{display:"flex", alignItems:"center", gap:8, marginBottom:10}}>
                  <div style={{width:26, height:26, borderRadius:8, background:"#1E2A44",
                    display:"flex", alignItems:"center", justifyContent:"center", fontSize:13,
                    fontWeight:800, color:"#C9A55A"}}>2</div>
                  <div style={{fontSize:14, fontWeight:700, color:"#EEF2FF"}}>Copy and paste (no setup)</div>
                </div>
                <ol style={{margin:0, paddingLeft:20, fontSize:12.5, color:"#C4CDE0", lineHeight:1.7}}>
                  <li>Copy the job link from the LinkedIn / Seek app</li>
                  <li>Open this app, paste it into the box at the top</li>
                  <li>Tap the pencil button, paste the title, company, and description</li>
                  <li>Tap <b style={{color:"#EEF2FF"}}>+ Add</b></li>
                </ol>
              </div>
              <button onClick={()=>setShowMobileHelp(false)}
                style={{width:"100%", marginTop:20, padding:12,
                  background:"linear-gradient(135deg,#C9A55A,#A8813E)", color:"white", border:"none",
                  borderRadius:12, fontSize:14, fontWeight:700, cursor:"pointer"}}>
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────
const styles = {
  // ─── DESIGN TOKENS ───────────────────────────────────────────────────────
  // Background layers
  // BG0 #07091A  BG1 #0D1225  BG2 #111827  CARD #141D35
  // Gold: solid #C9A55A  dim rgba(201,165,90,0.13)  glow rgba(201,165,90,0.28)
  // Text: primary #EEF2FF  secondary #8893A7  muted #4E5A70
  // ─────────────────────────────────────────────────────────────────────────
  app:          { fontFamily: "-apple-system,'SF Pro Display',BlinkMacSystemFont,'Segoe UI',Arial,sans-serif",
                  background: "#07091A", minHeight: "100vh", fontSize: 14,
                  WebkitTextSizeAdjust: "100%", color: "#EEF2FF" },
  // ── Toolbar ──────────────────────────────────────────────────────────────
  toolbar:      { background: "rgba(13,18,37,0.95)", backdropFilter: "blur(20px)",
                  WebkitBackdropFilter: "blur(20px)",
                  padding: "0 16px", height: 52, display: "flex", alignItems: "center",
                  gap: 10, borderBottom: "1px solid rgba(201,165,90,0.2)",
                  position: "sticky", top: 0, zIndex: 100 },
  toolbarBrand: { marginRight: "auto", minWidth: 0 },
  toolbarTitle: { fontSize: 14, fontWeight: 700, color: "#EEF2FF",
                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                  letterSpacing: "-0.2px" },
  toolbarSub:   { fontSize: 9.5, color: "#4E5A70", letterSpacing: "0.3px" },
  stats:        { display: "flex", gap: 4, flexShrink: 0 },
  statPill:     { background: "rgba(255,255,255,0.04)", borderRadius: 10,
                  padding: "4px 10px", textAlign: "center", minWidth: 44,
                  border: "1px solid rgba(255,255,255,0.06)" },
  statNum:      { fontSize: 15, fontWeight: 800, color: "#C9A55A", lineHeight: 1 },
  statLbl:      { fontSize: 7.5, color: "#4E5A70", marginTop: 2,
                  letterSpacing: "0.6px", textTransform: "uppercase" },
  // ── Action bar ───────────────────────────────────────────────────────────
  actionBar:    { background: "#0D1225", padding: "10px 14px", display: "flex",
                  gap: 8, borderBottom: "1px solid rgba(255,255,255,0.05)", flexWrap: "wrap" },
  btnGenerate:  { background: "linear-gradient(135deg,#C9A55A,#A8813E)", color: "white",
                  border: "none", padding: "9px 18px", borderRadius: 10, cursor: "pointer",
                  fontSize: 13, fontWeight: 700, minHeight: 40, display: "flex",
                  alignItems: "center", gap: 6,
                  boxShadow: "0 2px 12px rgba(201,165,90,0.25)",
                  transition: "opacity 0.15s" },
  // ── Filter bar ───────────────────────────────────────────────────────────
  filterBar:    { background: "#0D1225", padding: "10px 14px", display: "flex", gap: 6,
                  borderBottom: "1px solid rgba(255,255,255,0.05)",
                  overflowX: "auto", WebkitOverflowScrolling: "touch", scrollbarWidth: "none",
                  alignItems: "center" },
  filterBtn:    { background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)",
                  color: "#8893A7", padding: "7px 16px", borderRadius: 20, cursor: "pointer",
                  fontSize: 12, fontWeight: 600, whiteSpace: "nowrap", minHeight: 36,
                  flexShrink: 0, transition: "all 0.15s" },
  filterBtnActive: { background: "rgba(201,165,90,0.15)", borderColor: "rgba(201,165,90,0.4)",
                     color: "#C9A55A" },
  // ── URL / status bars ────────────────────────────────────────────────────
  urlBar:       { background: "#0D1225", padding: "8px 14px", display: "flex",
                  gap: 8, borderBottom: "1px solid rgba(255,255,255,0.05)" },
  urlInput:     { flex: 1, padding: "11px 14px", borderRadius: 12,
                  border: "1px solid rgba(255,255,255,0.1)", fontSize: 14,
                  outline: "none", minHeight: 46, boxSizing: "border-box",
                  background: "rgba(255,255,255,0.04)", color: "#EEF2FF",
                  transition: "border-color 0.15s" },
  btnAdd:       { background: "#1C2B4A", color: "white", border: "1px solid rgba(201,165,90,0.3)",
                  padding: "10px 18px", borderRadius: 12, cursor: "pointer",
                  fontSize: 13, fontWeight: 700, whiteSpace: "nowrap", minHeight: 46,
                  transition: "all 0.15s" },
  statusBar:    { background: "rgba(59,130,246,0.08)", padding: "7px 14px", fontSize: 12,
                  color: "#60A5FA", borderBottom: "1px solid rgba(59,130,246,0.15)",
                  minHeight: 30, lineHeight: 1.4 },
  // ── Job grid ─────────────────────────────────────────────────────────────
  grid:         { display: "grid",
                  gridTemplateColumns: "repeat(auto-fill,minmax(min(100%,340px),1fr))",
                  gap: 12, padding: 14 },
  noJobs:       { gridColumn: "1/-1", textAlign: "center", padding: "80px 20px", color: "#4E5A70" },
  // ── Card ─────────────────────────────────────────────────────────────────
  card:         { background: "linear-gradient(145deg,#141D35,#111827)",
                  borderRadius: 16, border: "1px solid rgba(255,255,255,0.07)",
                  display: "flex", flexDirection: "column",
                  boxShadow: "0 4px 24px rgba(0,0,0,0.3)",
                  transition: "border-color 0.2s, box-shadow 0.2s",
                  WebkitTapHighlightColor: "transparent" },
  cardHead:     { padding: "14px 16px 10px" },
  cardTitleRow: { display: "flex", justifyContent: "space-between",
                  alignItems: "flex-start", gap: 10, marginBottom: 5 },
  cardTitle:    { fontSize: 15, fontWeight: 800, color: "#EEF2FF",
                  lineHeight: 1.25, flex: 1, letterSpacing: "-0.2px" },
  scoreBox:     { width: 38, height: 38, borderRadius: 10, flexShrink: 0,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 15, fontWeight: 800, border: "2px solid" },
  cardCompany:  { fontSize: 11.5, color: "#8893A7", marginBottom: 8,
                  display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap",
                  fontWeight: 500 },
  locPill:      { display: "inline-flex", alignItems: "center", gap: 3, fontSize: 10,
                  fontWeight: 600, padding: "2px 7px", borderRadius: 10, border: "1px solid" },
  tags:         { display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 8 },
  tag:          { fontSize: 10.5, fontWeight: 600, padding: "3px 8px",
                  borderRadius: 10, border: "1px solid transparent" },
  tagSeniority: { background: "rgba(59,130,246,0.12)", color: "#60A5FA",
                  borderColor: "rgba(59,130,246,0.2)" },
  tagType:      { background: "rgba(34,197,94,0.1)", color: "#4ADE80",
                  borderColor: "rgba(34,197,94,0.2)" },
  tagApps:      { background: "rgba(249,115,22,0.1)", color: "#FB923C",
                  borderColor: "rgba(249,115,22,0.2)" },
  tagManual:    { background: "rgba(139,92,246,0.1)", color: "#A78BFA",
                  borderColor: "rgba(139,92,246,0.2)" },
  tagApproved:  { background: "rgba(34,197,94,0.1)", color: "#4ADE80",
                  borderColor: "rgba(34,197,94,0.2)" },
  tagRejected:  { background: "rgba(239,68,68,0.1)", color: "#F87171",
                  borderColor: "rgba(239,68,68,0.2)" },
  tagGenerated: { background: "rgba(201,165,90,0.1)", color: "#C9A55A",
                  borderColor: "rgba(201,165,90,0.2)" },
  tagArchived:  { background: "rgba(100,116,139,0.12)", color: "#8893A7",
                  borderColor: "rgba(100,116,139,0.2)" },
  tagSal:       { background: "rgba(59,130,246,0.1)", color: "#60A5FA",
                  borderColor: "rgba(59,130,246,0.2)" },
  tagSalEst:    { background: "rgba(201,165,90,0.1)", color: "#C9A55A",
                  borderColor: "rgba(201,165,90,0.2)" },
  fitRow:       { display: "flex", alignItems: "center", gap: 8, marginBottom: 8 },
  fitLbl:       { fontSize: 10, color: "#4E5A70", width: 20 },
  fitBg:        { flex: 1, height: 5, background: "rgba(255,255,255,0.07)",
                  borderRadius: 4, overflow: "hidden" },
  fitFill:      { height: "100%", borderRadius: 4 },
  fitTxt:       { fontSize: 13, fontWeight: 700, width: 32, textAlign: "right",
                  color: "#EEF2FF" },
  salRow:       { marginBottom: 8 },
  rationale:    { fontSize: 12.5, color: "#8893A7", lineHeight: 1.65,
                  padding: "0 16px 8px", flex: 1 },
  keywords:     { display: "flex", flexWrap: "wrap", gap: 4, padding: "0 16px 10px" },
  kw:           { fontSize: 11, padding: "3px 8px", borderRadius: 8,
                  background: "rgba(59,130,246,0.1)", color: "#60A5FA",
                  border: "1px solid rgba(59,130,246,0.2)" },
  toggle:       { display: "flex", alignItems: "center", gap: 5, padding: "10px 16px",
                  fontSize: 12, color: "#4E5A70", cursor: "pointer",
                  borderTop: "1px solid rgba(255,255,255,0.05)", minHeight: 44,
                  transition: "color 0.15s" },
  panel:        { fontSize: 12.5, color: "#8893A7", lineHeight: 1.65,
                  background: "rgba(255,255,255,0.03)", borderRadius: 10,
                  padding: "12px 14px", margin: "0 14px 10px", maxHeight: 200,
                  overflowY: "auto", whiteSpace: "pre-wrap",
                  border: "1px solid rgba(255,255,255,0.07)",
                  WebkitOverflowScrolling: "touch" },
  actions:      { display: "flex", gap: 8, padding: "12px 16px",
                  borderTop: "1px solid rgba(255,255,255,0.05)", marginTop: "auto" },
  btnApprove:   { flex: 1, background: "rgba(34,197,94,0.15)", color: "#4ADE80",
                  border: "1px solid rgba(34,197,94,0.3)", padding: "11px 8px",
                  borderRadius: 12, cursor: "pointer", fontSize: 13, fontWeight: 700,
                  minHeight: 48, transition: "all 0.15s" },
  btnReject:    { flex: 1, background: "rgba(239,68,68,0.12)", color: "#F87171",
                  border: "1px solid rgba(239,68,68,0.25)", padding: "11px 8px",
                  borderRadius: 12, cursor: "pointer", fontSize: 13, fontWeight: 700,
                  minHeight: 48, transition: "all 0.15s" },
  btnReset:     { flex: 1, background: "rgba(255,255,255,0.05)", color: "#8893A7",
                  border: "1px solid rgba(255,255,255,0.09)", padding: "11px 8px",
                  borderRadius: 12, cursor: "pointer", fontSize: 13, minHeight: 48,
                  transition: "all 0.15s" },
  btnIcon:      { background: "rgba(255,255,255,0.05)", color: "#8893A7",
                  border: "1px solid rgba(255,255,255,0.09)", padding: "10px 12px",
                  borderRadius: 12, cursor: "pointer", fontSize: 14, minHeight: 48,
                  display: "flex", alignItems: "center", transition: "all 0.15s" },
  // ── Modal ─────────────────────────────────────────────────────────────────
  modalOverlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)",
                  backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
                  zIndex: 200, display: "flex", alignItems: "flex-end",
                  justifyContent: "center" },
  modal:        { background: "#0D1225", borderRadius: "24px 24px 0 0",
                  padding: "24px 20px 40px", width: "100%", maxWidth: 480,
                  boxShadow: "0 -8px 60px rgba(0,0,0,0.5)",
                  border: "1px solid rgba(255,255,255,0.08)" },
  tplBtn:       { flex: 1, padding: 14, borderRadius: 14, border: "2px solid",
                  cursor: "pointer", textAlign: "center", transition: "all 0.15s" },
  btnCancel:    { background: "rgba(255,255,255,0.06)", color: "#8893A7",
                  border: "1px solid rgba(255,255,255,0.09)", padding: "13px 20px",
                  borderRadius: 12, cursor: "pointer", fontSize: 14, minHeight: 48,
                  transition: "all 0.15s" },
  btnConfirm:   { background: "linear-gradient(135deg,#C9A55A,#A8813E)", color: "white",
                  border: "none", padding: "13px 20px", borderRadius: 12,
                  cursor: "pointer", fontSize: 14, fontWeight: 700, minHeight: 48,
                  boxShadow: "0 4px 14px rgba(201,165,90,0.3)", transition: "opacity 0.15s" },
  // ── Login ─────────────────────────────────────────────────────────────────
  loginWrap:    { minHeight: "100vh", display: "flex", alignItems: "center",
                  justifyContent: "center",
                  background: "radial-gradient(ellipse at 50% 0%,rgba(28,43,74,0.8) 0%,#07091A 60%)",
                  padding: "20px 0" },
  loginBox:     { background: "rgba(13,18,37,0.9)", borderRadius: 24,
                  padding: "36px 28px", width: "90%", maxWidth: 380,
                  textAlign: "center",
                  boxShadow: "0 8px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.07)",
                  backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)" },
  loginLogo:    { width: 64, height: 64, borderRadius: 18,
                  background: "linear-gradient(135deg,#1C2B4A,#0D1225)",
                  color: "#C9A55A", fontSize: 24, fontWeight: 800,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  margin: "0 auto 18px",
                  boxShadow: "0 0 0 1px rgba(201,165,90,0.3), 0 4px 20px rgba(201,165,90,0.15)" },
  loginTitle:   { fontSize: 24, fontWeight: 800, color: "#EEF2FF",
                  marginBottom: 6, letterSpacing: "-0.4px" },
  loginSub:     { fontSize: 14, color: "#8893A7", marginBottom: 24, lineHeight: 1.5 },
  loginInput:   { width: "100%", padding: "13px 16px",
                  borderRadius: 12, border: "1px solid rgba(255,255,255,0.1)",
                  fontSize: 16, outline: "none", marginBottom: 10,
                  boxSizing: "border-box", minHeight: 52,
                  background: "rgba(255,255,255,0.04)", color: "#EEF2FF",
                  transition: "border-color 0.15s" },
  loginError:   { color: "#F87171", fontSize: 13, marginBottom: 10 },
  loginBtn:     { width: "100%",
                  background: "linear-gradient(135deg,#C9A55A,#A8813E)",
                  color: "white", border: "none", padding: "14px",
                  borderRadius: 12, cursor: "pointer", fontSize: 16, fontWeight: 700,
                  minHeight: 52, boxShadow: "0 4px 20px rgba(201,165,90,0.35)",
                  transition: "opacity 0.15s" },
}
function ShareHandler() {
  const params   = new URLSearchParams(window.location.search);
  const sharedUrl   = params.get("url")   || "";
  const sharedTitle = params.get("title") || "";
  const sharedText  = params.get("text")  || "";
  const jobUrl = sharedUrl || (sharedText.match(/https?:\/\/\S+/) || [])[0] || "";

  // Parse title/company from page title (e.g. "Senior PM | Accenture | LinkedIn")
  const parseTitleParts = (raw) => {
    const parts = raw.split(/[|\-–—]/).map(s => s.trim()).filter(Boolean);
    // Last part is usually the site name — strip it if it looks like a domain/brand
    const siteParts = ["LinkedIn","Seek","Indeed","Glassdoor","SEEK","seek.com","linkedin.com"];
    const filtered = parts.filter(p => !siteParts.some(s => p.toLowerCase().includes(s.toLowerCase())));
    return { title: filtered[0] || parts[0] || "", company: filtered[1] || "" };
  };
  const parsed = parseTitleParts(sharedTitle);

  // Always show paste form — server-side scraping is blocked for all sites.
  // Title + company are pre-filled from the shared page title. User pastes description.
  const [state,  setState]  = useState("paste");
  const [result, setResult] = useState(null);
  const [desc,   setDesc]   = useState("");
  const [title,  setTitle]  = useState(parsed.title);
  const [co,     setCo]     = useState(parsed.company);
  const [adding, setAdding] = useState(false);

  const doAdd = (extraDesc) => {
    const tok = getToken();
    if (!tok) { setState("noauth"); return; }
    setAdding(true);
    if (state !== "adding") setState("adding");
    fetch(API_URL + "/api/add-job", {
      method:"POST",
      headers:{"Content-Type":"application/json","Authorization":"Bearer "+tok},
      body:JSON.stringify({url:jobUrl, title:title||parsed.title,
        company:co||parsed.company, description:extraDesc||desc})
    }).then(r=>r.json()).then(d=>{
      setAdding(false);
      if(d.ok){ setState("done"); setResult(d.job); }
      else { setState("error"); setResult(d.error==="duplicate"?"Already in your pipeline!":(d.error||"Failed")); }
    }).catch(()=>{ setAdding(false); setState("error"); setResult("Could not reach server."); });
  };

  useEffect(() => {
    if (!jobUrl) { setState("error"); setResult("No URL received."); return; }
    const tok = getToken();
    if (!tok) { setState("noauth"); return; }
    // Don't auto-add — show paste form so user can provide the description
  }, []);  // eslint-disable-line

  const go = () => { window.location.href = "/"; };
  const S = {card:{background:"white",borderRadius:20,padding:24,maxWidth:360,width:"100%",boxShadow:"0 8px 32px rgba(0,0,0,0.12)"},
    logo:{width:48,height:48,background:"#1C2B4A",borderRadius:14,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 14px",fontSize:18,fontWeight:800,color:"#B8934A"},
    h:{fontSize:16,fontWeight:700,color:"#1C2B4A",marginBottom:6,textAlign:"center"},
    sub:{fontSize:13,color:"#64748B",marginBottom:16,textAlign:"center",lineHeight:1.4},
    btn:{width:"100%",padding:13,border:"none",borderRadius:12,fontSize:15,fontWeight:700,cursor:"pointer"},
    inp:{width:"100%",padding:"10px 12px",border:"1px solid #E2E8F0",borderRadius:10,fontSize:14,outline:"none",boxSizing:"border-box",marginBottom:8},
  };
  return (
    <div style={{minHeight:"100vh",background:"#F8FAFC",display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
      <div style={S.card}>
        <div style={S.logo}>ai</div>

        {/* PASTE SCREEN — always shown, title+company pre-filled from page title */}
        {state==="paste" && (<>
          <div style={S.h}>Add to Pipeline</div>
          <div style={{fontSize:11,color:"#94A3B8",textAlign:"center",wordBreak:"break-all",
            marginBottom:12,background:"#F8FAFC",borderRadius:8,padding:"6px 10px"}}>
            {jobUrl.substring(0,55)}{jobUrl.length>55?"…":""}
          </div>
          <div style={{background:"#FFFBEB",border:"1px solid #FDE68A",borderRadius:8,
            padding:"8px 10px",marginBottom:12,fontSize:12,color:"#92400E",lineHeight:1.4}}>
            💡 Go back to the job page, copy the description, then come back and paste it below for a proper AI score.
          </div>
          <input value={title} onChange={e=>setTitle(e.target.value)}
            placeholder="Job title" style={S.inp}/>
          <input value={co} onChange={e=>setCo(e.target.value)}
            placeholder="Company name" style={S.inp}/>
          <VoiceSafeTextarea value={desc} onChange={e=>setDesc(e.target.value)}
            placeholder="Paste the full job description here for best results…"
            style={{...S.inp,height:110,resize:"none",lineHeight:1.5,marginBottom:10}}/>
          <button onClick={()=>doAdd(desc)} disabled={adding}
            style={{...S.btn,background:desc.trim()?"#1C2B4A":"#64748B",color:"white",
              marginBottom:8,opacity:adding?0.7:1}}>
            {adding?"Adding…":desc.trim()?"✓ Add with Description":"Add to Pipeline"}
          </button>
          <button onClick={()=>doAdd("")} disabled={adding}
            style={{...S.btn,background:"#F1F5F9",color:"#64748B",fontSize:12}}>
            Quick Add (no description — score will be low)
          </button>
        </>)}

        {/* ADDING */}
        {state==="adding" && (<>
          <div style={S.h}>Adding to pipeline…</div>
          <div style={{...S.sub}}>Scoring against your profile…</div>
          <div style={{fontSize:11,color:"#94A3B8",textAlign:"center",wordBreak:"break-all"}}>{jobUrl.substring(0,60)}</div>
        </>)}

        {/* DONE */}
        {state==="done" && result && (<>
          <div style={{fontSize:36,textAlign:"center",marginBottom:10}}>✅</div>
          <div style={S.h}>Added to Pipeline!</div>
          <div style={{fontSize:14,fontWeight:600,color:"#1C2B4A",textAlign:"center",marginBottom:3}}>{result.title}</div>
          <div style={{fontSize:13,color:"#64748B",textAlign:"center",marginBottom:10}}>{result.company_name}</div>
          <div style={{textAlign:"center",marginBottom:20}}>
            <span style={{background:"#F0FDF4",color:"#15803D",border:"1px solid #86EFAC",borderRadius:20,padding:"5px 16px",fontSize:14,fontWeight:700}}>
              Score: {result.fit_score}/10
            </span>
          </div>
          <button onClick={go} style={{...S.btn,background:"#1C2B4A",color:"white"}}>View Pipeline</button>
        </>)}

        {/* ERROR */}
        {state==="error" && (<>
          <div style={{fontSize:36,textAlign:"center",marginBottom:10}}>{result==="Already in your pipeline!"?"⚠️":"❌"}</div>
          <div style={S.h}>{result==="Already in your pipeline!"?"Already in your list":"Could not add job"}</div>
          <div style={S.sub}>{result}</div>
          <button onClick={go} style={{...S.btn,background:"#1C2B4A",color:"white"}}>Go to Pipeline</button>
        </>)}

        {/* NO AUTH */}
        {state==="noauth" && (<>
          <div style={{fontSize:36,textAlign:"center",marginBottom:10}}>🔐</div>
          <div style={S.h}>Please log in first</div>
          <div style={S.sub}>Open the app, log in, then share the job again.</div>
          <button onClick={go} style={{...S.btn,background:"#1C2B4A",color:"white"}}>Go to Login</button>
        </>)}
      </div>
    </div>
  );
}

// One-tap mobile trigger: visiting /trigger-digest (while logged in) fires the
// job digest for BOTH Aowias and Wasil independently, no console/DevTools needed.
function TriggerDigestPage() {
  const [status, setStatus] = useState("Starting...");
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const d = await api("/api/automation/job-digest", { method: "POST" });
        if (d && d.ok) {
          setStatus("Started! Each person's search is running independently in the background. Check both inboxes in a few minutes.");
        } else {
          setStatus("Something went wrong: " + (d && d.error ? d.error : "unknown error"));
        }
      } catch (e) {
        setStatus("Request failed: " + e.message);
      }
      setDone(true);
    })();
  }, []);

  return (
    <div style={{ minHeight: "100vh", background: "#0D1225", display: "flex",
      alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ maxWidth: 380, textAlign: "center", color: "#EEF2FF",
        fontFamily: "-apple-system,Segoe UI,Roboto,Arial,sans-serif" }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>{done ? "✅" : "⏳"}</div>
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>
          {done ? "Digest triggered" : "Triggering job digest..."}
        </div>
        <div style={{ fontSize: 13, color: "#8893A7", lineHeight: 1.5 }}>{status}</div>
      </div>
    </div>
  );
}

// Thin router
function VerifyEmailPage() {
  const [state, setState] = useState("verifying");  // verifying | success | already | error
  const [message, setMessage] = useState("Verifying your email...");

  useEffect(() => {
    (async () => {
      // The verification link is APP_BASE_URL/#verify?token=XXX, so the token
      // lives in the HASH, not the query string. Parse it out of the hash.
      let token = "";
      const hash = window.location.hash || "";
      const qIdx = hash.indexOf("?");
      if (qIdx !== -1) {
        token = new URLSearchParams(hash.substring(qIdx + 1)).get("token") || "";
      }
      // Fallback: also check the real query string, just in case.
      if (!token) {
        token = new URLSearchParams(window.location.search).get("token") || "";
      }
      if (!token) {
        setState("error");
        setMessage("This verification link is missing its token. Please use the exact link from your email, or request a new one.");
        return;
      }
      try {
        const r = await fetch(`${API_URL}/api/auth/verify-email`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token }),
        });
        const d = await r.json();
        if (d.ok && d.already) {
          setState("already");
          setMessage("Your email is already verified. You can sign in now.");
        } else if (d.ok) {
          setState("success");
          setMessage("Your email is verified. You can sign in now.");
        } else {
          setState("error");
          setMessage(d.error || "We couldn't verify your email. The link may have expired.");
        }
      } catch (e) {
        setState("error");
        setMessage("Something went wrong reaching the server. Please try again in a moment.");
      }
    })();
  }, []);

  const goToLogin = () => { window.location.href = "/"; };
  const icon = state === "verifying" ? "⏳" : (state === "error" ? "⚠️" : "✅");

  return (
    <div style={{ minHeight: "100vh", background: "#0D1225", display: "flex",
      alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div style={{ maxWidth: 400, width: "100%", textAlign: "center", color: "#EEF2FF",
        fontFamily: "-apple-system,Segoe UI,Roboto,Arial,sans-serif",
        background: "#141F38", borderRadius: 16, padding: "36px 28px",
        border: "1px solid rgba(201,165,90,0.2)" }}>
        <div style={{ fontSize: 40, marginBottom: 14 }}>{icon}</div>
        <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 10 }}>
          {state === "verifying" ? "Verifying your email" :
           state === "error" ? "Verification problem" : "Email verified"}
        </div>
        <div style={{ fontSize: 14, color: "#B8C2D6", lineHeight: 1.6, marginBottom: 22 }}>
          {message}
        </div>
        {state !== "verifying" && (
          <button onClick={goToLogin}
            style={{ background: "linear-gradient(135deg,#C9A55A,#A8813E)", color: "#fff",
              border: "none", borderRadius: 10, padding: "12px 28px", fontSize: 14,
              fontWeight: 700, cursor: "pointer" }}>
            Go to sign in
          </button>
        )}
      </div>
    </div>
  );
}

export default function App() {
  if (window.location.pathname === "/share") return <ShareHandler />;
  if (window.location.pathname === "/trigger-digest") return <TriggerDigestPage />;
  if ((window.location.hash || "").startsWith("#verify")) return <VerifyEmailPage />;
  return <AppMain />;
}
